import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, Input, HostListener, inject } from '@angular/core';
import { CadViewerDialogService } from 'src/app/shared/services/cad-viewer-dialog.service';
import { GisFeatureStoreService, GisStoredFeature, GisStoredFeatureCollection } from 'src/app/shared/services/gis-feature-store.service';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute, LrsDocumentRef, lrsMetaFromDocument, measureKmFromLrsMeta, lrsChainageLabel } from 'src/app/shared/services/lrs.service';
import { GeospatialMapBridgeService, BoqLayerFilter } from 'src/app/shared/services/geospatial-map-bridge.service';
import { GisFocusService } from 'src/app/shared/services/gis-focus.service';
import { OsmMapApiService } from 'src/app/shared/services/osm-map-api.service';
import { combineLatest, firstValueFrom, forkJoin, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OsmNetworkOverlay, OSM_NODES_MIN_ZOOM } from 'src/app/shared/utils/osm-network-overlay';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LrsManagerComponent } from 'src/app/components/lrs/lrs-manager/lrs-manager.component';
import { LrsAssetDetailsComponent } from 'src/app/components/lrs/lrs-asset-details/lrs-asset-details.component';
import { GisAssetBridgePanelComponent } from 'src/app/components/geospatial/gis-asset-bridge-panel/gis-asset-bridge-panel.component';
import { GisLocationSearchComponent } from 'src/app/components/geospatial/gis-location-search/gis-location-search.component';
import type { GeoPlace } from 'src/app/shared/services/geo-search.service';
import { GisProjectMgmtPanelComponent } from 'src/app/components/geospatial/gis-project-mgmt-panel/gis-project-mgmt-panel.component';
import { GisOpsMapAction, GisOpsMapPoint, GisOpsSurveyLrs, GisProjectOpsService } from 'src/app/components/geospatial/gis-project-mgmt-panel/gis-project-ops.service';
import {
  styleForLrsFeature,
  styleUtilityFeature,
  styleCluster,
  preloadLrsIcons,
  applySymbolProperties,
  iconPathForAssetType,
  strokeOptsForGrade,
  clusterDistanceForZoom,
  LRS_DEFAULT_CLUSTER_DISTANCE,
} from 'src/app/shared/utils/lrs-asset-symbology';
import { getLrsAssetTypeMeta, LRS_UTILITY_TYPES } from 'src/app/shared/constants/lrs-asset-types';
import {
  ROAD_DIVIDER_OPTIONS,
  defaultDividerWidth,
  dividerCorridorDash,
  getRoadDividerType,
  separatorCostPerMeter,
} from 'src/app/shared/constants/road-divider-types';
import { LrsDashboardService } from 'src/app/shared/services/lrs-dashboard.service';
import {
  countFeaturesForLeaf,
  defaultLayerVisibility,
  flattenCatalogLeaves,
  isCatalogFeatureVisible,
  toggleCatalogNode,
  type CatalogFeatureProps,
} from 'src/app/shared/constants/lrs-layer-catalog';
import { LrsLayerTreeComponent } from 'src/app/components/lrs/lrs-layer-tree/lrs-layer-tree.component';
import {
  CHAINAGE_INTERVAL_OPTIONS_M,
  DEMO_ALL_LAYERS,
  DEMO_ROAD_COLORS,
  DEMO_TERRAIN_TILE_URL,
  DEMO_UTILITY_COLORS,
} from './gis-technical-demo.config';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import { Draw, Modify, Select, Snap, Translate, Pointer as PointerInteraction } from 'ol/interaction';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { Fill, Stroke, Style, Circle as CircleStyle, Text as TextStyle, Icon, Text, RegularShape } from 'ol/style';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import ImageStatic from 'ol/source/ImageStatic';
import Heatmap from 'ol/layer/Heatmap';
import WFS from 'ol/format/WFS';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import KML from 'ol/format/KML';
import GPX from 'ol/format/GPX';
import { FeatureLike } from 'ol/Feature';
import { defaults as defaultControls, ScaleLine, OverviewMap } from 'ol/control';
import { LineString, Polygon, Point, Circle } from 'ol/geom';
import {
  Extent,
  getCenter,
  createEmpty as createEmptyExtent,
  extend as extendExtent,
  isEmpty as isEmptyExtent,
} from 'ol/extent';
import Overlay from 'ol/Overlay';
import { Feature } from 'ol';
import { getLength as getGeodesicLength, getArea as getGeodesicArea } from 'ol/sphere';
import { parseChainageMeters, formatChainageFromMeters } from '../shared/utils/chainage.util';

// Import Turf.js for geometric calculations (optional, as fallback)
declare const turf: any;

/** Vector type for theming: point, text, line, segment, sub_segment, section */
export type VectorThemeType = 'point' | 'text' | 'line' | 'segment' | 'sub_segment' | 'section';

/** Style definition for one vector type in a palette */
export interface VectorTypeStyle {
  strokeColor: string;
  fillColor: string;
  strokeWidth: number;
  pointRadius?: number;
  textColor?: string;
  fontSize?: number;
  lineDash?: number[];
}

/** Predefined palette: one style per vector type */
export interface LayerPalette {
  id: string;
  name: string;
  point: VectorTypeStyle;
  text: VectorTypeStyle;
  line: VectorTypeStyle;
  segment: VectorTypeStyle;
  sub_segment: VectorTypeStyle;
  section: VectorTypeStyle;
}

/** Capability status used in GIS editor feature matrix. */
export type GISCapabilityStatus = 'available' | 'partial' | 'planned';

/** One capability line in a GIS feature section. */
export interface GISCapabilityItem {
  key: string;
  label: string;
  status: GISCapabilityStatus;
  notes?: string;
}

/** Group of GIS capabilities for help/status reporting. */
export interface GISCapabilitySection {
  title: string;
  items: GISCapabilityItem[];
}

/** Values written back to the road project from the authoritative map geometry. */
export interface GisRoadProjectMetrics {
  /** Road alignment length in kilometres. */
  road_length?: number;
  /** Carriageway width in metres. */
  lane_width_adjustment?: number;
  /** Number of lanes on the designed alignment. */
  lane_configuration?: number;
  /** Mapped project footprint in square metres. */
  size?: number;
}

/** A small audit-friendly summary shown before and after a GIS-to-project update. */
export interface GisRoadProjectMetricSummary {
  metrics: GisRoadProjectMetrics;
  roadFeatureCount: number;
  areaFeatureCount: number;
  source: 'designed-alignment' | 'lrs-centerline' | 'none';
}

/** Predefined theming palettes for layers */
export const PREDEFINED_LAYER_PALETTES: LayerPalette[] = [
  {
    id: 'default',
    name: 'Default Blue',
    point:   { strokeColor: '#2980b9', fillColor: 'rgba(52, 152, 219, 0.8)',  strokeWidth: 2, pointRadius: 6, textColor: '#2c3e50', fontSize: 12, lineDash: [] },
    text:    { strokeColor: '#2c3e50', fillColor: '#2c3e50', strokeWidth: 1, textColor: '#2c3e50', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#3498db', fillColor: 'transparent', strokeWidth: 2, lineDash: [] },
    segment: { strokeColor: '#e74c3c', fillColor: 'transparent', strokeWidth: 3, lineDash: [5, 5], textColor: '#000', fontSize: 10 },
    sub_segment: { strokeColor: '#e67e22', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#7f8c8d', fontSize: 9 },
    section: { strokeColor: '#27ae60', fillColor: 'rgba(39, 174, 96, 0.25)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure',
    point:   { strokeColor: '#34495e', fillColor: 'rgba(52, 73, 94, 0.9)',  strokeWidth: 2, pointRadius: 7, textColor: '#ecf0f1', fontSize: 11, lineDash: [] },
    text:    { strokeColor: '#2c3e50', fillColor: '#2c3e50', strokeWidth: 1, textColor: '#2c3e50', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#5d6d7e', fillColor: 'transparent', strokeWidth: 2, lineDash: [] },
    segment: { strokeColor: '#c0392b', fillColor: 'transparent', strokeWidth: 3, lineDash: [6, 4], textColor: '#1a252f', fontSize: 10 },
    sub_segment: { strokeColor: '#7f8c8d', fillColor: 'transparent', strokeWidth: 2, lineDash: [4, 4], textColor: '#7f8c8d', fontSize: 9 },
    section: { strokeColor: '#16a085', fillColor: 'rgba(22, 160, 133, 0.2)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'road_survey',
    name: 'Road & Survey',
    point:   { strokeColor: '#d35400', fillColor: 'rgba(211, 84, 0, 0.85)',  strokeWidth: 2, pointRadius: 6, textColor: '#fff', fontSize: 10, lineDash: [] },
    text:    { strokeColor: '#fff', fillColor: '#c0392b', strokeWidth: 2, textColor: '#c0392b', fontSize: 11, lineDash: [] },
    line:    { strokeColor: '#e67e22', fillColor: 'transparent', strokeWidth: 3, lineDash: [] },
    segment: { strokeColor: '#c0392b', fillColor: 'transparent', strokeWidth: 4, lineDash: [8, 4], textColor: '#922b21', fontSize: 10 },
    sub_segment: { strokeColor: '#d35400', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#a04000', fontSize: 9 },
    section: { strokeColor: '#27ae60', fillColor: 'rgba(46, 204, 113, 0.2)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'utilities',
    name: 'Utilities',
    point:   { strokeColor: '#8e44ad', fillColor: 'rgba(142, 68, 173, 0.8)',  strokeWidth: 2, pointRadius: 6, textColor: '#fff', fontSize: 10, lineDash: [] },
    text:    { strokeColor: '#2c3e50', fillColor: '#8e44ad', strokeWidth: 1, textColor: '#6c3483', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#9b59b6', fillColor: 'transparent', strokeWidth: 3, lineDash: [5, 5] },
    segment: { strokeColor: '#6c3483', fillColor: 'transparent', strokeWidth: 2, lineDash: [4, 4], textColor: '#4a235a', fontSize: 10 },
    sub_segment: { strokeColor: '#af7ac5', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#6c3483', fontSize: 9 },
    section: { strokeColor: '#1abc9c', fillColor: 'rgba(26, 188, 156, 0.25)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'pastel',
    name: 'Pastel',
    point:   { strokeColor: '#81d4fa', fillColor: 'rgba(129, 212, 250, 0.9)',  strokeWidth: 1, pointRadius: 6, textColor: '#01579b', fontSize: 11, lineDash: [] },
    text:    { strokeColor: '#fff', fillColor: '#5c6bc0', strokeWidth: 1, textColor: '#3949ab', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#64b5f6', fillColor: 'transparent', strokeWidth: 2, lineDash: [] },
    segment: { strokeColor: '#f48fb1', fillColor: 'transparent', strokeWidth: 2, lineDash: [5, 5], textColor: '#ad1457', fontSize: 10 },
    sub_segment: { strokeColor: '#ce93d8', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#6a1b9a', fontSize: 9 },
    section: { strokeColor: '#80cbc4', fillColor: 'rgba(128, 203, 196, 0.35)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'high_contrast',
    name: 'High Contrast',
    point:   { strokeColor: '#000', fillColor: '#ffeb3b', strokeWidth: 2, pointRadius: 7, textColor: '#000', fontSize: 11, lineDash: [] },
    text:    { strokeColor: '#000', fillColor: '#000', strokeWidth: 2, textColor: '#fff', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#000', fillColor: 'transparent', strokeWidth: 3, lineDash: [] },
    segment: { strokeColor: '#f44336', fillColor: 'transparent', strokeWidth: 4, lineDash: [6, 4], textColor: '#000', fontSize: 10 },
    sub_segment: { strokeColor: '#ff9800', fillColor: 'transparent', strokeWidth: 3, lineDash: [4, 4], textColor: '#000', fontSize: 9 },
    section: { strokeColor: '#000', fillColor: 'rgba(0,0,0,0.15)', strokeWidth: 3, lineDash: [] }
  }
];

@Component({
  selector: 'app-gisavieditor',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, CurrencyPipe, MatTooltipModule, LrsManagerComponent, LrsAssetDetailsComponent, GisAssetBridgePanelComponent, GisProjectMgmtPanelComponent, LrsLayerTreeComponent, GisLocationSearchComponent],
  templateUrl: './gisavieditor.component.html',
  styleUrl: './gisavieditor.component.css'
})
export class GisavieditorComponent implements OnInit, OnDestroy, AfterViewInit {
  // Expose Math for template use
  Math = Math;

  /** When true (geospatial workspace), show LRS controls as a sidebar tab. */
  @Input() enableLrsTab = false;
  /** GIS Road BoQ workbench: hide the editor layer dock so the shell explorer can occupy the left. */
  private compactForBoqValue = false;
  @Input()
  set compactForBoq(value: boolean) {
    this.compactForBoqValue = !!value;
    if (this.compactForBoqValue) {
      this.sidebarCollapsed = true;
      this.refreshMapSizeAfterLayout();
      const pending = this.geoMapBridge?.getBoqLayerFilter();
      if (pending) this.applyBoqLayerFilter(pending);
      setTimeout(() => this.publishBoqGeometry(), 200);
    }
  }
  get compactForBoq(): boolean {
    return this.compactForBoqValue;
  }

  /** Active sidebar tab id (layers, drawing, lrs, …). */
  activeSidebarTab = 'layers';
  sidebarNavMoreOpen = false;
  readonly sidebarPrimaryTabs: { id: string; label: string; title: string; lrsOnly?: boolean }[] = [
    { id: 'layers', label: 'Layers', title: 'Map layers' },
    { id: 'drawing', label: 'Draw', title: 'Sketch points, lines, and polygons' },
    { id: 'road', label: 'Road', title: 'Alignment and road network' },
    { id: 'lrs', label: 'LRS', title: 'Linear referencing, routes, and chainage', lrsOnly: true },
  ];
  readonly sidebarMoreTabs: { id: string; label: string; title: string }[] = [
    { id: 'advanced', label: 'Advanced', title: 'Advanced GIS tools' },
    { id: 'analysis', label: 'Analysis', title: 'Spatial analysis' },
    { id: 'survey', label: 'Survey', title: 'Survey tools' },
    { id: 'management', label: 'Mgmt', title: 'Project management' },
    { id: 'biometric', label: 'Bio', title: 'Biometric attendance' },
    { id: 'data', label: 'Data', title: 'Import and export' },
    { id: 'settings', label: 'Config', title: 'Map and editor settings' },
  ];

  get sidebarTabLabel(): string {
    const all = [...this.sidebarPrimaryTabs, ...this.sidebarMoreTabs];
    return all.find((t) => t.id === this.activeSidebarTab)?.label || 'Layers';
  }

  get sidebarTabTitle(): string {
    const all = [...this.sidebarPrimaryTabs, ...this.sidebarMoreTabs];
    return all.find((t) => t.id === this.activeSidebarTab)?.title || 'Map layers, drawing tools, and GIS modules';
  }

  isSidebarMoreTabActive(): boolean {
    return this.sidebarMoreTabs.some((t) => t.id === this.activeSidebarTab);
  }

  onSidebarNavTab(tabId: string): void {
    this.sidebarNavMoreOpen = false;
    this.switchSidebarTab(tabId);
  }

  toggleSidebarNavMore(event?: Event): void {
    event?.stopPropagation();
    this.sidebarNavMoreOpen = !this.sidebarNavMoreOpen;
  }
 
  @ViewChild('mapEl', { static: true }) mapElement!: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarEl') toolbarEl?: ElementRef<HTMLElement>;
  @ViewChild('toolbarPrimary') toolbarPrimary?: ElementRef<HTMLElement>;
  @ViewChild('toolbarOverflowSlot') toolbarOverflowSlot?: ElementRef<HTMLElement>;
  @ViewChild(GisLocationSearchComponent) locationSearch?: GisLocationSearchComponent;
  locationSearchOpen = false;
  locationSearchTop = 64;
  private toolbarResizeObserver?: ResizeObserver;
  // Map and layer variables
  map!: Map;
  vectorSource!: VectorSource;
  roadSource!: VectorSource;
  projectSource!: VectorSource;
  biometricSource!: VectorSource;
  gpsSource!: VectorSource;
  waterSource!: VectorSource;
  separatorSource!: VectorSource;

  // Layer management
  layerRegistry: { [key: string]: VectorLayer } = {};
  layerGroups: { [key: string]: string[] } = {
    'Map & Network': ['osm_road_network', 'osm_vertex_nodes'],
    'Infrastructure': ['drawing', 'road', 'project'],
    'Utilities': ['water', 'separator'],
    'Tracking': ['biometric', 'gps'],
  };
  /** Virtual overlay layers (not in layerRegistry). */
  private readonly overlayLayerIds = new Set<string>(['osm_road_network', 'osm_vertex_nodes']);
  private readonly fixedLayerGroups = new Set(['Map & Network']);
  private readonly layerDisplayNames: Record<string, string> = {
    // drawing: 'Sketch',
    osm_road_network: 'OSM Roads',
    osm_vertex_nodes: 'OSM Nodes',
    drawing: 'Drawing',
    road: 'Road',
    project: 'Project',
    water: 'Water',
    separator: 'Separator',
    biometric: 'Biometric',
    gps: 'GPS Track',
  };
  private readonly layerIcons: Record<string, string> = {
    osm_road_network: '🛣️',
    osm_vertex_nodes: '⚪',
    drawing: '✏️',
    road: '🛤️',
    project: '🏗️',
    water: '💧',
    separator: '➖',
    biometric: '👤',
    gps: '📍',
  };
  layerOrder: string[] = [];
  layerFilters: { [key: string]: any } = {};
  layerStatistics: { [key: string]: any } = {};
  layerMetadata: { [key: string]: any } = {}; // Extended metadata for layers
  layerLocks: { [key: string]: boolean } = {}; // Layer locking
  layerMinZoom: { [key: string]: number } = {}; // Minimum zoom level
  layerMaxZoom: { [key: string]: number } = {}; // Maximum zoom level
  layerStyles: { [key: string]: any } = {}; // Custom layer styles (may include paletteId + overrides)
  /** Palette ID per layer; used with PREDEFINED_LAYER_PALETTES */
  layerPaletteId: { [key: string]: string } = {};

  // UI State for layer management
  showLayerPanel = true;
  /** When true, sidebar is collapsed to a narrow strip to maximise map area. */
  sidebarCollapsed = true;
  toolbarMoreOpen = false;
  private mapResizeObserver: ResizeObserver | null = null;
  private narrowMq?: MediaQueryList;
  private wasNarrow = false;
  private readonly sidebarStorageKey = 'geo-gis-sidebar-collapsed';

  /** Toggle sidebar and refresh map size so it flexes correctly (no overlap). */
  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.persistSidebarCollapsed();
    this.refreshMapSizeAfterLayout();
  }

  collapseSidebarOverlay(): void {
    if (!this.sidebarCollapsed) this.toggleSidebar();
  }

  toggleToolbarMore(event?: Event): void {
    event?.stopPropagation();
    this.toolbarMoreOpen = !this.toolbarMoreOpen;
    this.refreshMapSizeAfterLayout();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (this.sidebarNavMoreOpen && !target?.closest('.sidebar-nav-more')) {
      this.sidebarNavMoreOpen = false;
    }
    if (!this.toolbarMoreOpen) return;
    if (target?.closest('.toolbar-secondary') || target?.closest('.toolbar-more-btn')) return;
    this.toolbarMoreOpen = false;
  }

  @HostListener('document:keydown', ['$event'])
  onToolbarEscape(event: KeyboardEvent): void {
    const t = event.target as HTMLElement | null;
    const tag = (t?.tagName || '').toLowerCase();
    const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || !!t?.isContentEditable;
    const key = event.key.toLowerCase();
    if ((event.ctrlKey || event.metaKey) && !event.altKey && key === 'k' && this.map) {
      event.preventDefault();
      this.openLocationSearch();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && !event.altKey && !typing && this.map) {
      if (key === 'c' && this.selectInteraction?.getFeatures().getLength()) {
        event.preventDefault();
        this.copySelectedFeatures();
        return;
      }
      if (key === 'v' && this.featureClipboard.length) {
        event.preventDefault();
        this.pasteFeatures();
        return;
      }
      if (key === 'd' && this.selectInteraction?.getFeatures().getLength()) {
        event.preventDefault();
        this.duplicateSelectedFeatures();
        return;
      }
    }
    if (this.rotateToolActive && !typing && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (event.key === '[' || event.key === ']' || event.key === '{' || event.key === '}') {
        event.preventDefault();
        const step = event.shiftKey ? 1 : 15;
        const clockwise = event.key === ']' || event.key === '}';
        this.rotateSelectionBy(clockwise ? -step : step);
        return;
      }
      if (key === 'a') {
        event.preventDefault();
        this.promptRotateSelection();
        return;
      }
    }
    if (event.key !== 'Escape') return;
    if (this.sidebarNavMoreOpen) {
      event.preventDefault();
      this.sidebarNavMoreOpen = false;
      return;
    }
    if (!this.toolbarMoreOpen) return;
    event.preventDefault();
    event.stopPropagation();
    this.toolbarMoreOpen = false;
  }

  openLocationSearch(): void {
    this.locationSearchOpen = true;
    this.updateLocationSearchTop();
    setTimeout(() => this.locationSearch?.focus());
  }

  toggleLocationSearch(): void {
    if (this.locationSearchOpen) this.locationSearch?.close();
    else this.openLocationSearch();
  }

  private trackToolbarHeight(): void {
    const el = this.toolbarEl?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.toolbarResizeObserver = new ResizeObserver(() => this.updateLocationSearchTop());
    this.toolbarResizeObserver.observe(el);
    this.updateLocationSearchTop();
  }

  private updateLocationSearchTop(): void {
    const el = this.toolbarEl?.nativeElement;
    const visible = !!el && el.offsetParent !== null && el.offsetHeight > 0;
    const top = visible ? el!.offsetTop + el!.offsetHeight + 8 : 10;
    if (top !== this.locationSearchTop) this.locationSearchTop = top;
  }

  /** Location-search lookup over project features (persistable layers) and LRS routes; results in EPSG:4326. */
  searchProjectFeatures = (query: string): GeoPlace[] => {
    const q = query.trim().toLowerCase();
    if (q.length < 2 || !this.map) return [];
    const projection = this.map.getView().getProjection();
    const results: GeoPlace[] = [];
    const toPlace = (id: string, label: string, sublabel: string, extent: Extent): GeoPlace | null => {
      if (!extent || isEmptyExtent(extent) || !extent.every(Number.isFinite)) return null;
      const bb = transformExtent(extent, projection, 'EPSG:4326') as [number, number, number, number];
      const [lon, lat] = getCenter(bb);
      return { id, kind: 'feature', label, sublabel, lon, lat, bbox: bb };
    };

    for (const route of this.lrsRoutes) {
      if (results.length >= 10) break;
      const hay = `${route.route_code || ''} ${route.route_name || ''}`.toLowerCase();
      if (!hay.includes(q) || !route.geometry) continue;
      const geom = new GeoJSON().readGeometry(route.geometry, { dataProjection: 'EPSG:4326', featureProjection: projection });
      const place = toPlace(`lrs-route:${route.id}`, route.route_code || route.route_name, `LRS route · ${route.route_name || ''}`, geom.getExtent());
      if (place) results.push(place);
    }

    const fields = ['name', 'label', 'title', 'asset_id'];
    for (const layerId of Object.keys(this.layerRegistry)) {
      if (results.length >= 10) break;
      if (!this.isPersistableLayer(layerId)) continue;
      const source = this.layerRegistry[layerId].getSource() as VectorSource;
      for (const feature of source.getFeatures()) {
        if (results.length >= 10) break;
        const match = fields.map((k) => feature.get(k)).find((v) => v != null && String(v).toLowerCase().includes(q));
        if (match == null) continue;
        const geom = feature.getGeometry();
        if (!geom) continue;
        const place = toPlace(`feature:${layerId}:${feature.getId() ?? results.length}`, String(match), `${layerId} · ${geom.getType()}`, geom.getExtent());
        if (place) results.push({ ...place, ref: feature });
      }
    }
    return results;
  };

  /** Run after view init so sidebar DOM exists. Attach tab switching and ensure Draw tab is active on first load. */
  private ensureSidebarTabsReady(): void {
    const tabContents = document.querySelectorAll('.tab-content');
    if (!tabContents.length) return;

    const qp = this.route.snapshot.queryParamMap;
    const initialTab =
      (qp.get('tab') === 'lrs' ||
        qp.get('demo') === 'lrs' ||
        qp.get('demo') === 'showcase' ||
        qp.get('document_id') ||
        qp.get('route_id')) &&
      this.enableLrsTab
        ? 'lrs'
        : 'layers';
    this.switchSidebarTab(initialTab, true, false);
  }

  /** Activate a sidebar tab by id and refresh map layout. */
  switchSidebarTab(tabId: string, refreshMap = true, expandIfCollapsed = true): void {
    const tabContents = document.querySelectorAll('.tab-content');
    const content = document.getElementById(`${tabId}-tab`);
    if (!content) return;

    this.activeSidebarTab = tabId;
    tabContents.forEach(c => c.classList.remove('active'));
    content.classList.add('active');

    if (tabId === 'survey') {
      this.loadFieldSurveyGeometry();
    }

    if (expandIfCollapsed && this.sidebarCollapsed) {
      this.sidebarCollapsed = false;
      this.persistSidebarCollapsed();
    }

    if (refreshMap) {
      this.refreshMapSizeAfterLayout();
      this.geoMapBridge.refreshMapSize();
    }
  }

  /** Switch sidebar to the Layers tab and expand sidebar if collapsed. Used by toolbar Layers button and "Manage layers…" link. */
  switchToLayersTab(): void {
    this.sidebarCollapsed = false;
    this.persistSidebarCollapsed();
    setTimeout(() => {
      this.switchSidebarTab('layers');
    }, 0);
  }

  /** Line width slider change – keep in sync with drawing style. */
  onLineThicknessChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    if (!isNaN(value)) {
      this.lineThickness = value;
      this.updateActiveDrawingStyle();
    }
  }

  /** Symbol size slider change. */
  onSymbolSizeChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value);
    if (!isNaN(value)) {
      this.symbolSize = value;
    }
  }

  /** Call after sidebar expand/collapse so OpenLayers uses the new container size. */
  refreshMapSize(): void {
    this.refreshMapSizeAfterLayout();
  }

  private refreshMapSizeAfterLayout(): void {
    [50, 100, 320].forEach(ms =>
      setTimeout(() => {
        const el = this.mapElement?.nativeElement;
        if (this.map && el?.offsetWidth > 0 && el?.offsetHeight > 0) {
          this.map.updateSize();
          this.map.renderSync();
        }
        this.layoutToolbar();
      }, ms)
    );
  }

  private toolbarPrimaryOrder: HTMLElement[] = [];
  private toolbarLayoutRaf = 0;

  private scheduleToolbarLayout(): void {
    if (this.toolbarLayoutRaf) cancelAnimationFrame(this.toolbarLayoutRaf);
    this.toolbarLayoutRaf = requestAnimationFrame(() => {
      this.toolbarLayoutRaf = 0;
      this.layoutToolbar();
    });
  }

  /** Fit the map ribbon to the pane: hide labels, then park View/Navigate in More. */
  private layoutToolbar(): void {
    const toolbar = this.toolbarEl?.nativeElement;
    const primary = this.toolbarPrimary?.nativeElement;
    const slot = this.toolbarOverflowSlot?.nativeElement;
    if (!toolbar || !primary || !slot) return;

    if (!this.toolbarPrimaryOrder.length) {
      this.toolbarPrimaryOrder = Array.from(primary.children) as HTMLElement[];
    }

    this.toolbarPrimaryOrder.forEach((node) => primary.appendChild(node));
    toolbar.classList.remove('toolbar--icon-only');
    slot.replaceChildren();

    const overflows = (): boolean => primary.scrollWidth > primary.clientWidth + 1;

    if (overflows()) {
      toolbar.classList.add('toolbar--icon-only');
    }

    const moveGroup = (label: string): void => {
      const group = this.toolbarPrimaryOrder.find((el) => el.matches(`.toolbar-group[data-label="${label}"]`));
      if (!group || group.parentElement !== primary) return;
      const index = this.toolbarPrimaryOrder.indexOf(group);
      const prev = index > 0 ? this.toolbarPrimaryOrder[index - 1] : null;
      if (prev?.classList.contains('toolbar-divider') && prev.parentElement === primary) {
        slot.appendChild(prev);
      }
      slot.appendChild(group);
    };

    if (overflows()) moveGroup('View');
    if (overflows()) moveGroup('Navigate & Edit');
    toolbar.classList.toggle('toolbar--has-overflow', slot.childElementCount > 0);
  }
  selectedLayerId: string | null = null;
  layerContextMenu: { visible: boolean; x: number; y: number; layerId: string | null } = {
    visible: false,
    x: 0,
    y: 0,
    layerId: null
  };
  expandedGroups: { [key: string]: boolean } = {}; // Track expanded/collapsed groups
  layerSearchTerm = '';
  draggingLayerId: string | null = null;

  // Interaction variables
  drawInteraction?: Draw;
  selectInteraction?: Select;
  modifyInteraction?: Modify;
  measureInteraction?: any;
  snapInteractions: Snap[] = [];
  translateInteraction?: Translate;
  rotateInteraction?: PointerInteraction;
  rotateToolActive = false;
  private rotatePivotLayer: VectorLayer | null = null;
  private featureClipboard: { feature: Feature; source: VectorSource }[] = [];
  private clipboardAnchor: number[] | null = null;
  private lastPointerCoord: number[] | null = null;

  get hasClipboard(): boolean {
    return this.featureClipboard.length > 0;
  }
  // WMS layers registry
  private wmsLayers: { id: string; layer: ImageLayer<ImageWMS> }[] = [];
  private heatmapLayer: Heatmap | null = null;
  private clusterLayers: { [layerId: string]: VectorLayer } = {};
  private lrsClusterSources: { [layerId: string]: Cluster } = {};
  private lrsLineCompanionLayers: { [layerId: string]: VectorLayer } = {};
  private lrsClusterDistanceRaf = 0;
  private contourLayerIds: string[] = [];
  private elevationApiBase = 'https://api.open-elevation.com/api/v1/lookup';
  freehandMode = false;
  snapEnabled = true;
  /** Optional custom icon URL (SVG/PNG) for next point marker */
  defaultIconUrl = '';
  /** Offline mode: when true, edits are persisted locally and synced when back online */
  offlineMode = false;
  private offlineBannerEl: HTMLElement | null = null;
  private static readonly OFFLINE_STORAGE_KEY = 'gisEditorOfflineLayers';

  // State variables
  currentSymbol = '';
  currentSymbolTitle = '';
  currentLines: Feature[] = [];
  activeLineIndex = -1;
  drawingHistory: any[] = [];
  historyStep = -1;
  currentSegment = 1;
  currentSubSegment = 'A';
  lineThickness = 2;
  symbolSize = 1.0;
  isMultiLineMode = false;
  is3DMode = false;
  private map3DTiltDeg = 58;
  private map3DHeadingDeg = 28;
  private map3DHeightScale = 3;
  private pre3DBaseMapKey: string | null = null;
  currentDrawingMode = ''; // Track current drawing mode for thickness updates
  gpsWatchId: any = null;
  workZone: any = null;
  autoSaveTimer: any = null;
  /** Unsaved feature changes for the project store, keyed by feature UUID (null feature = delete). */
  private gisPending = new globalThis.Map<string, { layer: string; feature: Feature | null }>();
  private gisSyncQueue: Promise<unknown> = Promise.resolve();
  private gisTrackingSuspended = 0;
  private readonly gisTrackedSources = new WeakSet<VectorSource>();
  /** Map-only features (e.g. the project location marker) that are never persisted. */
  private readonly gisTransientFeatures = new WeakSet<Feature>();
  isSavingAllLayers = false;
  thematicStyles: { [key: string]: string } = {};
  layerOffsets: { [key: string]: { x: number; y: number } } = {};
  showNomenclature = true;
  showOverground = true;
  showUnderground = true;
  showCrossover = true;

  // Coordinate input variables
  currentLatitude = 28.6139;
  currentLongitude = 77.2090;
  pinpointMarker: Feature | null = null;

  // Enhanced numbering system variables
  numberingPrefix = 'S-';
  numberingSuffix = '-A';
  startNumber = 1;
  nomenclature = 'Gandhi baba'; // Base name for segments
  nomenclatureAbbreviation = 'Gb'; // Abbreviation for subsegments
  segmentSubSegmentCount: { [key: number]: number } = {}; // Track subsegment count per segment

  // Lane separator variables
  dividerType = 'painted';
  separatorWidth = 0.3;

  // Feature properties variables
  selectedFeature: Feature | null = null;
  featurePropertiesDialog: HTMLElement | null = null;

  // Elevation and 3D variables
  showElevation = true;
  elevationProfileData: any[] = [];

  // Curve drawing variables
  currentCurveType: 'bezier' | 'spline' | 'arc' | 'smooth' = 'smooth';
  curvePrecision = 30;
  curveControlPoints: number[][] = [];
  isCurveDrawing = false;
  curveDrawingListener: any = null;

  // Advanced features variables
  waterFlowDirection = 'north';
  analysisBuffer = 100;
  analysisType = 'intersection';
  exportFormat = 'geojson';
  includeMetadata = true;
  validationRules = 'basic';
  autoFixIssues = true;
  separatorDistance = 2;
  separatorCount = 0;
  placeSymbolHandler: any = null;
  private separatorInsertHandler: ((event: { pixel: number[]; coordinate: number[] }) => void) | null = null;
  private milestoneClickHandlerBound = false;

  // Segment drawing variables
  isSegmentDrawing = false;
  currentSegmentPoints: number[][] = [];
  segmentDrawingListener: any = null;
  nodeCounter = 1;
  segmentCounter = 1;
  numberingMode: 'auto' | 'manual' = 'auto';
  manualSegmentNumber = 1;
  manualNodeNumber = 1;

  // Junction and connection variables
  isConnectingRoads = false;
  isConnectingSegments = false;
  selectedJunction: Feature | null = null;
  firstSegment: Feature | null = null;
  highlightedFeature: Feature | null = null;

  // Popup overlay
  popup!: Overlay;

  // View history for navigation (previous/next view)
  viewHistory: Array<{ center: number[]; zoom: number; timestamp: number }> = [];
  viewHistoryIndex: number = -1;
  maxViewHistorySize: number = 50;
  isNavigatingHistory: boolean = false;

  // Project-related properties
  projectId: string | null = null;
  projectData: any = null;
  isLoadingProject: boolean = false;
  showProjectDetails: boolean = false;
  /** Prevents duplicate writes while the map-derived project quantities are saving. */
  gisMetricsSaving = false;
  lastGisMetricSummary: GisRoadProjectMetricSummary | null = null;
  private projectContextSub?: Subscription;

  // Road specifications
  currentRoadSpecs = {
    type: 'highway',
    width: 12,
    depth: 0.5,
    lanes: 2,
    surface: 'bituminous',
    separatorDistance: 2,
    dividerWidth: 0.3,
    dividerType: 'painted'
  };

  // Default settings
  defaultSettings = {
    center: [77.2090, 28.6139],
    zoom: 15,
    snapTolerance: 15,
    autoSaveInterval: 5,
    showGrid: false,
    showScaleBar: true,
    showNorthArrow: true,
    measurementUnits: 'metric',
    lineColor: '#3498db',
    backgroundColor: '#f0f2f5'
  };

  /**
   * GIS capability matrix aligned with product requirements.
   * This is used for operator help text and implementation tracking.
   */
  readonly gisCapabilitySections: GISCapabilitySection[] = [
    {
      title: '1. Basic Editing Features',
      items: [
        { key: 'basic.point-line-polygon', label: 'Point, Line, and Polygon Editing', status: 'available', notes: 'Draw/modify/select interactions are active.' },
        { key: 'basic.snapping', label: 'Snapping', status: 'available', notes: 'Snap interaction with toggle support.' },
        { key: 'basic.vertex-editing', label: 'Vertex Editing', status: 'available', notes: 'Modify interaction supports vertex-level edits.' },
        { key: 'basic.attribute-editing', label: 'Attribute Editing', status: 'available', notes: 'Feature properties dialog + save flow.' },
        { key: 'basic.layer-management', label: 'Layer Management', status: 'available', notes: 'Add, reorder, group, visibility, lock, style.' }
      ]
    },
    {
      title: '2. Advanced Editing & Geometry Tools',
      items: [
        { key: 'advanced.topology', label: 'Topology Editing', status: 'partial', notes: 'Validation helpers exist; dedicated topology rule editor is pending.' },
        { key: 'advanced.split-merge', label: 'Split & Merge Tools', status: 'partial', notes: 'Split exists; merge workflow can be expanded.' },
        { key: 'advanced.buffer', label: 'Buffer Analysis', status: 'available', notes: 'Client-side + backend-assisted buffer operations.' },
        { key: 'advanced.clip', label: 'Spatial Clipping', status: 'partial', notes: 'Spatial analysis exists; guided clip UI can be expanded.' },
        { key: 'advanced.crs', label: 'Coordinate Transformation', status: 'partial', notes: 'Projection utilities exist; explicit CRS conversion workflow is limited.' },
        { key: 'advanced.georef', label: 'Georeferencing', status: 'available', notes: 'Raster image overlay with extent anchoring is available.' }
      ]
    },
    {
      title: '3. Data Management & Integration',
      items: [
        { key: 'data.attribute-table', label: 'Attribute Table Editor', status: 'partial', notes: 'Feature-level property editor available; full grid editor can be enhanced.' },
        { key: 'data.import-export', label: 'Import & Export (GeoJSON/KML/GPX/etc.)', status: 'available', notes: 'Import/export handlers and dialogs are implemented.' },
        { key: 'data.database', label: 'Database Integration (PostGIS/backend)', status: 'available', notes: 'Layer persistence and backend service integration present.' },
        { key: 'data.wms-wfs', label: 'WMS/WFS Support', status: 'available', notes: 'Dynamic WMS and WFS loading supported.' },
        { key: 'data.collaboration', label: 'Real-time Collaboration', status: 'planned', notes: 'Requires multi-user lock/version stream infrastructure.' }
      ]
    },
    {
      title: '4. Visualization & Customization',
      items: [
        { key: 'viz.symbology', label: 'Layer Styling & Symbology', status: 'available', notes: 'Palette engine + per-layer style controls.' },
        { key: 'viz.opacity', label: 'Opacity & Transparency Control (all layers)', status: 'available', notes: 'Layer-level visibility/transparency controls are present.' },
        { key: 'viz.heatmap', label: 'Heatmaps & Density Analysis', status: 'available', notes: 'Heatmap layer generation is implemented.' },
        { key: 'viz.terrain3d', label: '3D Terrain Visualization', status: 'partial', notes: '3D mode and elevation profile exist; full terrain scene can be extended.' },
        { key: 'viz.labels', label: 'Labels & Annotation', status: 'available', notes: 'Text labels and annotation styling are supported.' }
      ]
    },
    {
      title: '5. GIS Calculations & Analysis',
      items: [
        { key: 'analysis.measure', label: 'Area & Distance Measurement', status: 'available', notes: 'Distance/area/bearing measurement tools active.' },
        { key: 'analysis.elevation', label: 'Elevation & Contour Mapping', status: 'available', notes: 'Elevation profile + contour layer loading supported.' },
        { key: 'analysis.spatial-joins', label: 'Spatial Joins & Queries', status: 'partial', notes: 'Spatial query flows exist; advanced join UI can be expanded.' },
        { key: 'analysis.network', label: 'Network Analysis', status: 'available', notes: 'Network analysis flow is implemented.' }
      ]
    },
    {
      title: '6. User Interaction & UI/UX',
      items: [
        { key: 'ux.undo-redo', label: 'Undo/Redo', status: 'available', notes: 'History stack with toolbar controls.' },
        { key: 'ux.shortcuts', label: 'Keyboard Shortcuts', status: 'partial', notes: 'Some direct actions exist; full shortcut map can be expanded.' },
        { key: 'ux.plugins', label: 'Custom Toolbar & Plugins', status: 'partial', notes: 'Extensible toolbar exists; plugin runtime can be expanded.' },
        { key: 'ux.offline', label: 'Offline Editing Mode', status: 'available', notes: 'Offline storage/sync scaffolding is present.' }
      ]
    },
    {
      title: '7. Core Functions (Import/Export/Layers/Editing)',
      items: [
        { key: 'core.data-io', label: 'Data Import & Export', status: 'available' },
        { key: 'core.layer-management', label: 'Layer Visibility/Order/Grouping', status: 'available' },
        { key: 'core.feature-edit', label: 'Feature Drawing/Editing', status: 'available' },
        { key: 'core.attribute-edit', label: 'Attribute & Metadata Editing', status: 'partial' }
      ]
    },
    {
      title: '8. Collaboration & Sharing',
      items: [
        { key: 'share.role-based-access', label: 'Role-based Access', status: 'planned' },
        { key: 'share.realtime-sync', label: 'Real-time Multi-user Sync', status: 'planned' },
        { key: 'share.outputs', label: 'Map Sharing (web/image/pdf)', status: 'partial' }
      ]
    },
    {
      title: '9. GPS & Field Collection',
      items: [
        { key: 'field.gps-tracking', label: 'GPS Tracking / Location Updates', status: 'available' },
        { key: 'field.mobile-capture', label: 'Mobile Field Capture', status: 'partial', notes: 'Flutter Project Map: GPS→chainage, near-me, LRS events, ROW photo; offline GIS/LRS queue. Attendance offline still open.' }
      ]
    },
    {
      title: '10. Satellite & Digital Visualization',
      items: [
        { key: 'satellite.visualization', label: 'Satellite and Digital Data Visualization', status: 'available' }
      ]
    }
  ];

  // Base maps
  baseMaps: { [key: string]: TileLayer } = {
    osm: new TileLayer({
      source: new OSM()
    }),
    google: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    googleSatellite: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    googleHybrid: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    googleTerrain: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    satellite: new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      })
    }),
    terrain: new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'
      })
    }),
    dark: new TileLayer({
      source: new XYZ({
        url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}.png'
      })
    })
  };

  currentBaseMap = this.baseMaps['google'];
  selectedBaseMapKey = 'google';

  /** Length/area display unit for measure tools and ScaleLine sync. */
  measureDisplayUnit: 'm' | 'km' | 'mi' = 'm';
  private scaleLineControl: ScaleLine | null = null;

  /** OSM Map API road network overlay (iD-style). */
  osmNetworkEnabled = true;
  osmNodesEnabled = true;
  osmNetworkStatus = '';
  osmRoadCount = 0;

  get osmStatusIsError(): boolean {
    return /timed out|busy|unavailable|failed|zoom in/i.test(this.osmNetworkStatus || '');
  }
  readonly osmNodesMinZoom = OSM_NODES_MIN_ZOOM;
  private osmNetworkOverlay?: OsmNetworkOverlay;
  private readonly osmSatelliteBaseKeys = new Set([
    'googleSatellite',
    'googleHybrid',
    'satellite',
  ]);

  // GeoJSON format
  format = new GeoJSON();

  isGisFocusMode = false;
  private focusSub?: Subscription;
  private dashboardSub?: Subscription;
  private chainageDashSub?: Subscription;
  private chainageHudTimer: ReturnType<typeof setTimeout> | null = null;
  chainageHudLabel: string | null = null;
  mapScaleLabel: string | null = null;
  mapZoom: number | null = null;
  private lrsOverviewMap?: OverviewMap;
  private overviewRouteSource = new VectorSource();
  private lrsCalloutOverlays: Overlay[] = [];
  private stationSeekSub?: Subscription;
  private suppressHudUntil = 0;
  private boqRouteSub?: Subscription;
  private boqFilterSub?: Subscription;
  private boqTypeFilter: Set<string> | null = null;
  private boqAlignmentOn = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private lrsService: LrsService,
    private geoMapBridge: GeospatialMapBridgeService,
    private gisFocus: GisFocusService,
    private osmMapApi: OsmMapApiService,
    private lrsDashboard: LrsDashboardService,
    private gisProjectOps: GisProjectOpsService
  ) {}

  ngOnInit(): void {
    this.restoreSidebarCollapsed();
    this.focusSub = this.gisFocus.isFocusMode$.subscribe((on) => {
      this.isGisFocusMode = on;
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.map?.updateSize();
          this.geoMapBridge.refreshMapSize();
        }, 50);
      });
    });
    this.initializeVectorSources();
    this.initializeMap();
    this.setupEventListeners();
    this.setupOfflineSupport();
    this.checkForProjectId();
    this.initializeBackendIntegration();
    this.loadLrsRouteList();
    this.dashboardSub = this.lrsDashboard.actions$.subscribe(() => {
      if (this.enableLrsTab) this.switchSidebarTab('lrs');
    });
    this.chainageDashSub = this.lrsDashboard.chainage$.subscribe((hud) => {
      if (hud) this.chainageHudLabel = this.toKmHudLabel(hud.label, hud.measure_m);
    });
    this.stationSeekSub = this.lrsDashboard.stationSeek$.subscribe((hud) => this.panToLrsStation(hud));
    this.boqRouteSub = this.geoMapBridge.boqRouteSelect$.subscribe((id) => {
      if (!id || !this.compactForBoq) return;
      this.selectedLrsRouteId = id;
      this.lrsDashboard.setRouteId(id);
      this.loadLrsRouteToMap();
    });
    this.boqFilterSub = this.geoMapBridge.boqLayerFilter$.subscribe((f) => {
      if (this.compactForBoq) this.applyBoqLayerFilter(f);
    });
    // Production documents: Document Library + LRS attach (see Documents sidebar section).
    this.route.queryParams.subscribe((params) => {
      if (params['demo'] === 'lrs' || params['demo'] === 'showcase') {
        if (this.enableLrsTab) setTimeout(() => this.switchSidebarTab('lrs'), 200);
        return;
      }
      if (params['demo'] === 'technical' && this.map) {
        setTimeout(() => this.bootstrapTechnicalDemo(), 1200);
      }
      const routeId = params['route_id'] ? parseInt(params['route_id'], 10) : NaN;
      if (Number.isFinite(routeId) && routeId > 0 && routeId !== this.lastLoadedLrsRouteId) {
        this.selectedLrsRouteId = routeId;
        this.lrsDashboard.setRouteId(routeId);
        this.loadLrsRouteToMap();
      }
      const docId = params['document_id'] ? parseInt(params['document_id'], 10) : NaN;
      if (Number.isFinite(docId) && docId > 0) {
        this.selectedLrsDocumentId = docId;
        if (this.enableLrsTab) setTimeout(() => this.switchSidebarTab('lrs'), 200);
        this.layerRegistry[this.lrsDocumentLayerId]?.changed();
      }
    });
    if (!navigator.onLine) {
      this.offlineMode = true;
      this.showOfflineBanner();
      this.restoreLayersFromOfflineStorage();
    }
    console.log('🎯 PostGIS Backend Integration Complete!');
    console.log('📍 Available methods: save/load layers, spatial queries, geometry validation, import/export');
  }

  ngAfterViewInit(): void {
    this.ensureSidebarTabsReady();
    this.bindNarrowSidebar();
    if (this.mapElement) {
      const el = this.mapElement.nativeElement;
      this.map.setTarget(el);
      // Keep map canvas in sync with container size (fixes tiles only in top-left)
      const doUpdateSize = () => {
        if (!this.map || !el) return;
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        if (el.offsetParent !== null && w > 0 && h > 0) {
          this.map.updateSize();
          this.map.renderSync();
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(doUpdateSize);
      });
      [100, 200, 350, 500, 700, 1000, 1500, 2500].forEach(ms => setTimeout(doUpdateSize, ms));
      // ResizeObserver: update when container size changes (sidebar toggle, window resize)
      this.mapResizeObserver = new ResizeObserver(() => {
        doUpdateSize();
        this.osmNetworkOverlay?.refresh();
        this.scheduleToolbarLayout();
      });
      this.mapResizeObserver.observe(el);
      const mapPane = el.closest('.map-container');
      if (mapPane instanceof HTMLElement) {
        this.mapResizeObserver.observe(mapPane);
      }
      this.scheduleToolbarLayout();
    }
    this.trackToolbarHeight();
    this.geoMapBridge.registerGisMap(this.map);
    if (this.compactForBoq) {
      const rid = this.geoMapBridge.getBoqRouteId();
      if (rid) {
        this.selectedLrsRouteId = rid;
        this.lrsDashboard.setRouteId(rid);
        this.loadLrsRouteToMap();
      }
      this.refreshMapSizeAfterLayout();
    }
    this.initOsmNetworkOverlay();
    // Setup scale-based visibility listeners
    this.setupScaleVisibilityListeners();
    // Initialize expanded groups
    Object.keys(this.layerGroups).forEach(group => {
      this.expandedGroups[group] = group === 'Map & Network' || group === 'Infrastructure';
    });
  }

  ngOnDestroy(): void {
    this.toolbarResizeObserver?.disconnect();
    if (this.toolbarLayoutRaf) cancelAnimationFrame(this.toolbarLayoutRaf);
    this.narrowMq?.removeEventListener('change', this.onNarrowChange);
    this.focusSub?.unsubscribe();
    this.dashboardSub?.unsubscribe();
    this.chainageDashSub?.unsubscribe();
    this.stationSeekSub?.unsubscribe();
    this.boqRouteSub?.unsubscribe();
    this.boqFilterSub?.unsubscribe();
    this.projectContextSub?.unsubscribe();
    if (this.gisPending.size) void this.flushGisChanges().catch(() => undefined);
    if (this.chainageHudTimer) clearTimeout(this.chainageHudTimer);
    this.clearLrsCallouts();
    this.gisFocus.setDrawingEscCaptured(false);
    this.cleanup();
  }

  private applyBoqLayerFilter(filter: BoqLayerFilter | null): void {
    if (!filter) {
      this.boqTypeFilter = null;
      this.boqAlignmentOn = true;
      this.syncNhaiSourceVisibility();
      return;
    }
    this.boqTypeFilter = new Set(filter.typeIds);
    this.boqAlignmentOn = filter.alignmentOn;
    this.lrsIncludeChainage = true;
    Object.values(this.layerRegistry).forEach((layer) => {
      const kind = layer.get('lrsKind');
      if (kind === 'centerline') layer.setVisible(filter.alignmentOn);
      if (kind === 'chainage') layer.setVisible(true);
    });
    this.syncNhaiSourceVisibility();
  }

  enterGisFocusMode(): void {
    this.gisFocus.enter();
  }

  exitGisFocusMode(): void {
    this.gisFocus.exit();
  }

  private syncDrawingEscCapture(): void {
    this.gisFocus.setDrawingEscCaptured(this.isMultiLineMode || this.isCurveDrawing);
  }

  private persistSidebarCollapsed(): void {
    if (this.compactForBoqValue) return;
    try {
      sessionStorage.setItem(this.sidebarStorageKey, this.sidebarCollapsed ? '1' : '0');
    } catch {
      /* private mode */
    }
  }

  private restoreSidebarCollapsed(): void {
    if (this.compactForBoqValue) {
      this.sidebarCollapsed = true;
      return;
    }
    try {
      this.sidebarCollapsed = sessionStorage.getItem(this.sidebarStorageKey) !== '0';
    } catch {
      this.sidebarCollapsed = true;
    }
  }

  private bindNarrowSidebar(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    this.narrowMq = window.matchMedia('(max-width: 900px)');
    this.wasNarrow = this.narrowMq.matches;
    if (this.narrowMq.matches) {
      this.sidebarCollapsed = true;
      this.refreshMapSizeAfterLayout();
    }
    this.narrowMq.addEventListener('change', this.onNarrowChange);
  }

  private onNarrowChange = (event: MediaQueryListEvent): void => {
    if (event.matches && !this.wasNarrow) {
      this.sidebarCollapsed = true;
      this.toolbarMoreOpen = false;
      this.refreshMapSizeAfterLayout();
    }
    if (!event.matches) {
      this.toolbarMoreOpen = false;
    }
    this.wasNarrow = event.matches;
  };

  private initializeVectorSources(): void {
    this.vectorSource = new VectorSource();
    this.roadSource = new VectorSource();
    this.projectSource = new VectorSource();
    this.biometricSource = new VectorSource();
    this.gpsSource = new VectorSource();
    this.waterSource = new VectorSource();
    this.separatorSource = new VectorSource();
  }

  private initializeMap(): void {
    // Create popup overlay
    this.popup = new Overlay({
      element: document.getElementById('popup')!,
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -10]
    });

    // Create and register vector layers with default palettes
    this.registerLayer('drawing', this.createDrawingLayer());
    this.layerPaletteId['drawing'] = 'default';
    this.layerRegistry['drawing']?.setZIndex(25);
    this.registerLayer('road', this.createRoadLayer());
    this.layerPaletteId['road'] = 'road_survey';
    this.layerRegistry['road']?.setZIndex(8);
    this.registerLayer('project', this.createProjectLayer());
    this.layerPaletteId['project'] = 'infrastructure';
    this.registerLayer('biometric', this.createBiometricLayer());
    this.layerPaletteId['biometric'] = 'utilities';
    this.registerLayer('gps', this.createGpsLayer());
    this.layerPaletteId['gps'] = 'road_survey';
    this.registerLayer('water', this.createWaterLayer());
    this.layerPaletteId['water'] = 'utilities';
    this.registerLayer('separator', this.createSeparatorLayer());
    this.layerPaletteId['separator'] = 'road_survey';

    // Initialize map with registered layers
    const allLayers = [this.currentBaseMap, ...Object.values(this.layerRegistry)];
    this.map = new Map({
      target: undefined, // Will be set in ngAfterViewInit
      layers: allLayers,
      view: new View({
        center: fromLonLat(this.defaultSettings.center),
        zoom: this.defaultSettings.zoom
      }),
      controls: defaultControls({ attribution: true, zoom: false, rotate: false }),
    });

    this.scaleLineControl = new ScaleLine({
      units: this.measureDisplayUnit === 'mi' ? 'imperial' : 'metric',
    });
    this.map.addControl(this.scaleLineControl);

    this.map.addOverlay(this.popup);

    this.lrsOverviewMap = this.createLrsOverviewMap();
    this.map.addControl(this.lrsOverviewMap);

    // Initialize view history tracking
    this.initializeViewHistory();

    // Setup map event listeners
    this.setupMapEventListeners();
    this.setupJunctionContextMenu();
  }

  private createDrawingLayer(): VectorLayer {
    return new VectorLayer({
      source: this.vectorSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('drawing', feature, resolution)
    });
  }

  private createRoadLayer(): VectorLayer {
    return new VectorLayer({
      source: this.roadSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('road', feature, resolution)
    });
  }

  private createProjectLayer(): VectorLayer {
    return new VectorLayer({
      source: this.projectSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('project', feature, resolution)
    });
  }

  private createBiometricLayer(): VectorLayer {
    return new VectorLayer({
      source: this.biometricSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('biometric', feature, resolution)
    });
  }

  private createGpsLayer(): VectorLayer {
    return new VectorLayer({
      source: this.gpsSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('gps', feature, resolution)
    });
  }

  private createWaterLayer(): VectorLayer {
    return new VectorLayer({
      source: this.waterSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('water', feature, resolution)
    });
  }

  private createSeparatorLayer(): VectorLayer {
    return new VectorLayer({
      source: this.separatorSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('separator', feature, resolution)
    });
  }

  /**
   * Returns the vector source to use for drawing. Uses the selected layer's source when a layer
   * is selected and it is a vector layer and not locked; otherwise uses the drawing layer.
   */
  getCurrentDrawingSource(): VectorSource {
    const layerId = this.selectedLayerId || 'drawing';
    if (this.layerLocks[layerId]) return this.vectorSource;
    const layer = this.layerRegistry[layerId];
    if (!layer) return this.vectorSource;
    const src = layer.getSource();
    if (src instanceof VectorSource) return src;
    return this.vectorSource;
  }

  /** Returns the layer id that is currently the drawing target (selected layer or 'drawing'). */
  getCurrentDrawingLayerId(): string {
    const layerId = this.selectedLayerId || 'drawing';
    if (this.layerLocks[layerId] || !this.layerRegistry[layerId]) return 'drawing';
    return layerId;
  }

  /** Returns the vector source that contains the given feature, or null if not found. */
  private getSourceForFeature(feature: Feature): VectorSource | null {
    const sources = [
      this.vectorSource,
      this.roadSource,
      this.projectSource,
      this.biometricSource,
      this.gpsSource,
      this.waterSource,
      this.separatorSource
    ];
    for (const src of sources) {
      if (src && src.getFeatures().includes(feature)) return src;
    }
    for (const layerId of this.layerOrder) {
      const layer = this.layerRegistry[layerId];
      const src = layer?.getSource();
      if (src instanceof VectorSource && src.getFeatures().includes(feature)) return src;
    }
    return null;
  }

  // Layer management methods
  private registerLayer(id: string, layer: VectorLayer): void {
    this.layerRegistry[id] = layer;
    this.layerOrder.push(id);
    // Initialize metadata
    this.layerMetadata[id] = {
      name: id,
      description: '',
      created: new Date(),
      modified: new Date()
    };
    // Initialize default zoom levels
    this.layerMinZoom[id] = 0;
    this.layerMaxZoom[id] = 24;
    this.updateLayerStatistics(id);
    this.trackLayerForStore(layer);
  }

  /** Apply layer-level color theme to a layer's style function. */
  private applyLayerTheme(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer) return;
    layer.setStyle((feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(layerId, feature, resolution));
    layer.changed();
  }

  /** Set the predefined theming palette for a layer (point, text, line, segment, sub_segment, section). */
  setLayerPalette(layerId: string, paletteId: string): void {
    this.layerPaletteId[layerId] = paletteId;
    if (this.layerStyles[layerId]) {
      this.layerStyles[layerId].paletteId = paletteId;
    }
    this.applyLayerTheme(layerId);
  }

  /** Get style for a feature with optional layer palette. resolution is used for dynamic symbol scaling by zoom. */
  private getLayerStyleWithTheme(layerId: string, feature: FeatureLike, resolution?: number): Style | Style[] {
    const corridorType = feature.get('type');
    if (
      corridorType === 'dummy_road_corridor' ||
      corridorType === 'milestone_corridor'
    ) {
      const lanes = Number(feature.get('lanes')) || 2;
      const roadWidthM = Number(feature.get('roadWidthM')) || 12;
      const dividerWidthM = Number(feature.get('dividerWidthM')) || 0;
      const material = String(feature.get('material') || 'bituminous');
      const dividerType = String(
        feature.get('dividerType') || this.currentRoadSpecs.dividerType || this.dividerType || 'painted'
      );
      return this.buildMilestoneCorridorStyles(
        feature,
        lanes,
        roadWidthM,
        dividerWidthM,
        material,
        dividerType,
        resolution || 1
      );
    }

    const base = this.getEnhancedDrawingStyle(feature, resolution);
    const theme = this.layerStyles[layerId];
    const paletteId = this.layerPaletteId[layerId] || theme?.paletteId;
    const palette = paletteId ? this.getPaletteById(paletteId) : null;
    const baseArr = Array.isArray(base) ? base : [base];

    let result: Style[];
    if (palette) {
      const vectorType = this.getVectorTypeFromFeature(feature);
      const vectorStyle = palette[vectorType];
      result = baseArr.map(s => this.applyVectorTypeStyleToStyle(s, vectorStyle, feature, resolution));
      if (theme && (theme.strokeColor || theme.fillColor || theme.strokeWidth != null)) {
        result = result.map(s => this.applyThemeToStyle(s, theme));
      }
      return result;
    }

    if (!theme || (!theme.strokeColor && theme.strokeWidth == null && !theme.fillColor)) {
      return base;
    }
    return baseArr.map(s => this.applyThemeToStyle(s, theme));
  }

  /** Scale factor for point radius by zoom (resolution). Keeps symbols readable at all zoom levels. */
  private getResolutionScale(resolution: number | undefined): number {
    if (resolution == null || !this.map) return 1;
    const view = this.map.getView();
    const refRes = view.getResolutionForZoom ? view.getResolutionForZoom(12) : 0.5;
    if (!refRes || resolution <= 0) return 1;
    const scale = refRes / resolution;
    return Math.max(0.55, Math.min(1.45, scale));
  }

  /** Resolve vector theme type from feature (geometry + properties). */
  private getVectorTypeFromFeature(feature: FeatureLike): VectorThemeType {
    const type = feature.get('type');
    const geom = feature.getGeometry();
    const geomType = geom ? geom.getType() : '';

    if (type === 'segment' || type === 'LineString' || type === 'MultiLineString') {
      if (feature.get('isSubSegment')) return 'sub_segment';
      if (feature.get('segmentNumber') != null || feature.get('segmentId')) return 'segment';
      return 'line';
    }
    if (type === 'segment_label' || type === 'label' || type === 'junction-label' || feature.get('text')) return 'text';
    if (geomType === 'Point' || geomType === 'MultiPoint') return 'point';
    if (geomType === 'Polygon' || geomType === 'MultiPolygon') return 'section';
    if (geomType === 'LineString' || geomType === 'MultiLineString') return 'line';
    if (geomType === 'Circle') return 'point';
    return 'line';
  }

  private getPaletteById(id: string): LayerPalette | null {
    return PREDEFINED_LAYER_PALETTES.find(p => p.id === id) || null;
  }

  /** Apply predefined vector-type style to a base OpenLayers Style. resolution used for dynamic symbol scaling. */
  private applyVectorTypeStyleToStyle(baseStyle: Style, vt: VectorTypeStyle, feature: FeatureLike, resolution?: number): Style {
    const fillColor = vt.fillColor && vt.fillColor !== 'transparent' ? vt.fillColor : (baseStyle.getFill()?.getColor() ?? 'rgba(52, 152, 219, 0.3)');
    const strokeColor = vt.strokeColor ?? baseStyle.getStroke()?.getColor() ?? '#3498db';
    const strokeWidth = vt.strokeWidth ?? baseStyle.getStroke()?.getWidth() ?? 2;
    const lineDash = vt.lineDash ?? baseStyle.getStroke()?.getLineDash() ?? [];

    let img = baseStyle.getImage();
    const keepSymbolArt =
      !!feature.get('symbol') ||
      !!feature.get('iconUrl') ||
      feature.get('type') === 'road_symbol';
    if (keepSymbolArt) {
      return baseStyle;
    }
    let radius = vt.pointRadius ?? (img && typeof (img as CircleStyle).getRadius === 'function' ? (img as CircleStyle).getRadius() : 6);
    radius = radius * this.getResolutionScale(resolution);
    if (img || vt.pointRadius != null) {
      img = new CircleStyle({
        radius,
        fill: new Fill({ color: vt.fillColor || strokeColor }),
        stroke: new Stroke({ color: strokeColor, width: 1 })
      });
    }

    let textStyle = baseStyle.getText();
    const textColor = vt.textColor ?? (typeof textStyle?.getFill === 'function' ? textStyle.getFill()?.getColor() : '#2c3e50');
    const fontSize = vt.fontSize ?? 12;
    if (textStyle) {
      const text = textStyle.getText();
      textStyle = new Text({
        text: Array.isArray(text) ? text[0] : text,
        font: `${fontSize}px Arial`,
        fill: new Fill({ color: textColor }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      });
    }

    return new Style({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth, lineDash }),
      image: img,
      text: textStyle
    });
  }

  private applyThemeToStyle(baseStyle: Style, theme: { strokeColor?: string; strokeWidth?: number; fillColor?: string }): Style {
    const fillColor = theme.fillColor ?? baseStyle.getFill()?.getColor() ?? 'rgba(52, 152, 219, 0.3)';
    const stroke = baseStyle.getStroke();
    const strokeColor = theme.strokeColor ?? stroke?.getColor() ?? '#3498db';
    const strokeWidth = theme.strokeWidth ?? stroke?.getWidth() ?? 2;
    const img = baseStyle.getImage();
    const circleImg = img && typeof (img as CircleStyle).getRadius === 'function'
      ? new CircleStyle({
          radius: (img as CircleStyle).getRadius(),
          fill: new Fill({ color: strokeColor }),
          stroke: new Stroke({ color: strokeColor, width: 1 })
        })
      : img;
    return new Style({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
      image: circleImg,
      text: baseStyle.getText()
    });
  }

  private updateLayerStatistics(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();
      
      this.layerStatistics[layerId] = {
        featureCount: features.length,
        visible: layer.getVisible(),
        opacity: layer.getOpacity(),
        zIndex: layer.getZIndex(),
        extent: source.getExtent(),
        lastUpdated: new Date()
      };
    }
  }

  // Enhanced layer management
  isOverlayLayer(layerId: string): boolean {
    return this.overlayLayerIds.has(layerId);
  }

  getLayerDisplayName(layerId: string): string {
    return this.layerDisplayNames[layerId] || this.layerMetadata[layerId]?.name || layerId;
  }

  getLayerFullLabel(layerId: string): string {
    if (layerId === 'osm_road_network') return 'OpenStreetMap roads & waterways';
    if (layerId === 'osm_vertex_nodes') return `Vertex dots at zoom ${this.osmNodesMinZoom}+`;
    return this.getLayerDisplayName(layerId);
  }

  isFixedLayerGroup(groupName: string): boolean {
    return this.fixedLayerGroups.has(groupName);
  }

  clearLayerSearch(): void {
    this.layerSearchTerm = '';
    this.updateLayerManagementUI();
  }

  selectLayer(layerId: string): void {
    this.selectedLayerId = layerId;
  }

  getDrawingTargetLabel(): string {
    return this.getLayerDisplayName(this.getCurrentDrawingLayerId());
  }

  getLayerIcon(layerId: string): string {
    return this.layerIcons[layerId] || '📍';
  }

  getLayerVisibilityTooltip(layerId: string): string {
    if (layerId === 'osm_vertex_nodes' && !this.osmNetworkEnabled) {
      return 'Turn on OSM Roads first to show nodes';
    }
    return this.getLayerInfo(layerId).visible ? 'Hide layer' : 'Show layer';
  }

  getLayerNameTooltip(layerId: string): string {
    const label = this.getLayerFullLabel(layerId);
    if (this.selectedLayerId === layerId && !this.isOverlayLayer(layerId)) {
      return `${label} — active drawing layer`;
    }
    return label;
  }

  getLayerCountTooltip(layerId: string): string {
    const n = this.getLayerInfo(layerId)?.featureCount;
    if (n == null) return '';
    const name = this.getLayerDisplayName(layerId);
    return n === 1 ? `1 feature on ${name}` : `${n} features on ${name}`;
  }

  private layerMatchesSearch(layerId: string): boolean {
    if (!this.layerSearchTerm.trim()) return true;
    const q = this.layerSearchTerm.toLowerCase();
    return (
      layerId.toLowerCase().includes(q) ||
      this.getLayerDisplayName(layerId).toLowerCase().includes(q)
    );
  }

  toggleLayerEnhanced(layerId: string, visible: boolean): void {
    if (layerId === 'osm_road_network') {
      this.toggleOsmNetwork(visible);
      return;
    }
    if (layerId === 'osm_vertex_nodes') {
      this.toggleOsmNodes(visible);
      return;
    }
    const layer = this.layerRegistry[layerId];
    if (layer) {
      layer.setVisible(visible);
      this.lrsLineCompanionLayers[layerId]?.setVisible(visible);
      this.updateLayerStatistics(layerId);
      this.updateLayerManagementUI();
    }
  }

  toggleOsmNodes(enabled: boolean): void {
    if (!this.osmNetworkEnabled && enabled) return;
    this.osmNodesEnabled = enabled;
    this.osmNetworkOverlay?.setNodesVisible(enabled);
    this.map?.render();
    this.updateLayerManagementUI();
  }

  setLayerOpacityEnhanced(layerId: string, opacity: number): void {
    if (layerId === 'osm_road_network') {
      this.osmNetworkOverlay?.setNetworkOpacity(opacity);
      return;
    }
    if (layerId === 'osm_vertex_nodes') {
      this.osmNetworkOverlay?.setNodesOpacity(opacity);
      return;
    }
    const layer = this.layerRegistry[layerId];
    if (layer) {
      layer.setOpacity(opacity);
      this.lrsLineCompanionLayers[layerId]?.setOpacity(opacity);
      this.updateLayerStatistics(layerId);
    }
  }

  reorderLayer(layerId: string, newIndex: number): void {
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex !== -1) {
      // Remove from current position
      this.layerOrder.splice(currentIndex, 1);
      // Insert at new position
      this.layerOrder.splice(newIndex, 0, layerId);
      
      // Update z-index based on new order
      this.updateLayerOrder();
    }
  }

  private updateLayerOrder(): void {
    this.layerOrder.forEach((layerId, index) => {
      const layer = this.layerRegistry[layerId];
      if (layer) {
        layer.setZIndex(index + 100); // Base layers start at 100
      }
    });
  }

  groupLayers(groupName: string, layerIds: string[]): void {
    this.layerGroups[groupName] = layerIds;
    this.updateLayerManagementUI();
  }

  ungroupLayers(groupName: string): void {
    if (this.layerGroups[groupName]) {
      delete this.layerGroups[groupName];
      this.updateLayerManagementUI();
    }
  }

  filterLayer(layerId: string, filter: any): void {
    this.layerFilters[layerId] = filter;
    this.applyLayerFilter(layerId);
  }

  private applyLayerFilter(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    const filter = this.layerFilters[layerId];
    
    if (layer && layer.getSource() instanceof VectorSource && filter) {
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();
      
      features.forEach(feature => {
        const shouldShow = this.evaluateFilter(feature, filter);
        feature.set('filtered', !shouldShow);
      });
      
      // Trigger style update
      layer.changed();
    }
  }

  private evaluateFilter(feature: Feature, filter: any): boolean {
    // Simple filter evaluation - can be enhanced
    if (filter.type) {
      return feature.get('type') === filter.type;
    }
    if (filter.properties) {
      const props = feature.get('properties') || {};
      return Object.keys(filter.properties).every(key => 
        props[key] === filter.properties[key]
      );
    }
    return true;
  }

  clearLayerFilter(layerId: string): void {
    delete this.layerFilters[layerId];
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      source.getFeatures().forEach(feature => {
        feature.set('filtered', false);
      });
      layer.changed();
    }
  }

  /** Layer features as a GeoJSON FeatureCollection string in EPSG:4326. */
  exportLayer(layerId: string): string {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      return this.gisStore.writeGeoJsonText(source.getFeatures());
    }
    return '';
  }

  /** Export layer as KML and trigger download */
  exportLayerAsKml(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer || !(layer.getSource() instanceof VectorSource)) return;
    const source = layer.getSource() as VectorSource;
    const features = source.getFeatures();
    if (features.length === 0) {
      this.showInstructions('No features to export');
      return;
    }
    const kmlFormat = new KML();
    const kml = kmlFormat.writeFeatures(features, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${layerId}.kml`;
    a.click();
    URL.revokeObjectURL(a.href);
    this.showInstructions(`Exported ${layerId} as KML`);
  }

  /** Export layer as GPX and trigger download */
  exportLayerAsGpx(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer || !(layer.getSource() instanceof VectorSource)) return;
    const source = layer.getSource() as VectorSource;
    const features = source.getFeatures();
    if (features.length === 0) {
      this.showInstructions('No features to export');
      return;
    }
    const gpxFormat = new GPX();
    const gpx = gpxFormat.writeFeatures(features, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${layerId}.gpx`;
    a.click();
    URL.revokeObjectURL(a.href);
    this.showInstructions(`Exported ${layerId} as GPX`);
  }

  /**
   * Replaces a layer's features with a GeoJSON/KML/GPX file body, creating the layer
   * when needed. `persist: false` keeps the layer map-only (e.g. analysis results).
   */
  importLayer(layerId: string, data: string, opts: { persist?: boolean } = {}): void {
    try {
      const features = this.gisStore.readVectorFile(data);
      if (!this.layerRegistry[layerId]) this.addLayer(layerId);
      const layer = this.layerRegistry[layerId];
      if (!layer || !(layer.getSource() instanceof VectorSource)) return;
      if (opts.persist === false) layer.set('gisTransient', true);
      const source = layer.getSource() as VectorSource;
      source.clear();
      source.addFeatures(features);
      this.updateLayerStatistics(layerId);
      this.showInstructions(`Imported ${features.length} feature(s) into '${layerId}'`);
    } catch (error) {
      console.error('Error importing layer:', error);
      this.showInstructions(`Could not import '${layerId}': the file is not valid GeoJSON, KML or GPX`);
    }
  }

  addLayer(layerId?: string): void {
    // Generate layer ID if not provided
    const newLayerId = layerId || `layer_${Date.now()}`;
    
    // Check if layer already exists
    if (this.layerRegistry[newLayerId]) {
      alert(`Layer "${newLayerId}" already exists. Please choose a different name.`);
      return;
    }

    // Per-layer style and theme so color changes affect only this layer
    if (!this.layerStyles[newLayerId]) {
      this.layerStyles[newLayerId] = {
        strokeColor: '#3498db',
        strokeWidth: 2,
        fillColor: 'rgba(52, 152, 219, 0.2)',
        paletteId: 'default'
      };
    }
    this.layerPaletteId[newLayerId] = this.layerPaletteId[newLayerId] || 'default';

    // Create new vector source and layer with per-layer style function
    const newSource = new VectorSource();
    const newLayer = new VectorLayer({
      source: newSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(newLayerId, feature, resolution),
      zIndex: this.layerOrder.length + 100
    });

    // Register the new layer
    this.registerLayer(newLayerId, newLayer);
    this.map.addLayer(newLayer);
    this.updateLayerManagementUI();
  }

  createLayerGroup(groupName: string, layerIds: string[]): void {
    if (this.layerGroups[groupName]) {
      // Add to existing group
      layerIds.forEach(layerId => {
        if (!this.layerGroups[groupName].includes(layerId)) {
          this.layerGroups[groupName].push(layerId);
        }
      });
    } else {
      // Create new group
      this.layerGroups[groupName] = [...layerIds];
    }
    this.updateLayerManagementUI();
  }

  removeLayerFromGroup(layerId: string, groupName: string): void {
    if (this.layerGroups[groupName]) {
      const index = this.layerGroups[groupName].indexOf(layerId);
      if (index > -1) {
        this.layerGroups[groupName].splice(index, 1);
        // Remove group if empty
        if (this.layerGroups[groupName].length === 0) {
          delete this.layerGroups[groupName];
        }
        this.updateLayerManagementUI();
      }
    }
  }
  duplicateLayer(layerId: string, newLayerId: string): void {
    const originalLayer = this.layerRegistry[layerId];
    if (originalLayer) {
      const newSource = new VectorSource();
      const newLayer = new VectorLayer({
        source: newSource,
        style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(newLayerId, feature, resolution)
      });
      this.registerLayer(newLayerId, newLayer);
      this.map.addLayer(newLayer);
      if (this.layerStyles[layerId]) {
        this.layerStyles[newLayerId] = { ...this.layerStyles[layerId] };
      }
      if (this.layerPaletteId[layerId] !== undefined) {
        this.layerPaletteId[newLayerId] = this.layerPaletteId[layerId];
      }
      this.updateLayerManagementUI();
    }
  }

  deleteLayer(layerId: string): void {
    if (!confirm(`Remove layer "${layerId}"? All features in this layer will be deleted. This cannot be undone.`)) {
      return;
    }
    const layer = this.layerRegistry[layerId];
    if (layer) {
      if (this.isPersistableLayer(layerId)) this.deleteStoredLayer(layerId);
      this.map.removeLayer(layer);
      const companion = this.lrsLineCompanionLayers[layerId];
      if (companion) {
        this.map.removeLayer(companion);
        delete this.lrsLineCompanionLayers[layerId];
      }
      delete this.lrsClusterSources[layerId];
      delete this.layerRegistry[layerId];
      this.layerOrder = this.layerOrder.filter(id => id !== layerId);
      delete this.layerStatistics[layerId];
      delete this.layerFilters[layerId];
      delete this.layerStyles[layerId];
      delete this.layerPaletteId[layerId];
      delete this.layerMetadata[layerId];
      delete this.layerLocks[layerId];
      delete this.layerMinZoom[layerId];
      delete this.layerMaxZoom[layerId];
      Object.keys(this.layerGroups).forEach(groupName => {
        const arr = this.layerGroups[groupName];
        const idx = arr.indexOf(layerId);
        if (idx > -1) {
          arr.splice(idx, 1);
          if (arr.length === 0) delete this.layerGroups[groupName];
        }
      });
      this.updateLayerManagementUI();
    }
  }

  getLayerInfo(layerId: string): any {
    if (layerId === 'osm_road_network') {
      return {
        id: layerId,
        visible: this.osmNetworkEnabled,
        opacity: 1,
        featureCount: this.osmRoadCount,
        locked: true,
        metadata: { name: this.getLayerDisplayName(layerId) },
      };
    }
    if (layerId === 'osm_vertex_nodes') {
      return {
        id: layerId,
        visible: this.osmNodesEnabled && this.osmNetworkEnabled,
        opacity: 1,
        featureCount: this.osmNetworkOverlay?.nodeCount ?? 0,
        locked: true,
        metadata: { name: this.getLayerDisplayName(layerId) },
      };
    }
    const layer = this.layerRegistry[layerId];
    return {
      ...this.layerStatistics[layerId],
      id: layerId,
      group: this.getLayerGroup(layerId),
      filter: this.layerFilters[layerId] || null,
      locked: this.layerLocks[layerId] || false,
      minZoom: this.layerMinZoom[layerId] || 0,
      maxZoom: this.layerMaxZoom[layerId] || 24,
      style: this.layerStyles[layerId] || null,
      metadata: this.layerMetadata[layerId] || {},
      visible: layer ? layer.getVisible() : true,
      opacity: layer ? layer.getOpacity() : 1.0
    };
  }

  // Enhanced layer management methods for QGIS-like functionality
  
  toggleLayerLock(layerId: string): void {
    this.layerLocks[layerId] = !(this.layerLocks[layerId] || false);
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      source.getFeatures().forEach(feature => {
        feature.set('locked', this.layerLocks[layerId]);
      });
    }
  }

  isLayerLocked(layerId: string): boolean {
    return this.layerLocks[layerId] || false;
  }

  setLayerMinZoom(layerId: string, zoom: number): void {
    this.layerMinZoom[layerId] = zoom;
    this.updateLayerScaleVisibility(layerId);
  }

  setLayerMaxZoom(layerId: string, zoom: number): void {
    this.layerMaxZoom[layerId] = zoom;
    this.updateLayerScaleVisibility(layerId);
  }

  private updateLayerScaleVisibility(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer) return;

    const view = this.map.getView();
    const currentZoom = view.getZoom() || 0;
    const minZoom = this.layerMinZoom[layerId] || 0;
    const maxZoom = this.layerMaxZoom[layerId] || 24;

    // Only hide if outside zoom range, but preserve user's visibility setting
    if (currentZoom < minZoom || currentZoom > maxZoom) {
      layer.set('scaleHidden', true);
      if (layer.getVisible()) {
        layer.setVisible(false);
      }
    } else {
      layer.set('scaleHidden', false);
      // Restore visibility if it was hidden only due to scale
      if (!layer.getVisible() && !this.layerStatistics[layerId]?.userHidden) {
        layer.setVisible(true);
      }
    }
  }

  renameLayer(oldId: string, newId: string): void {
    if (this.layerRegistry[newId]) {
      alert(`Layer "${newId}" already exists!`);
      return;
    }

    const layer = this.layerRegistry[oldId];
    if (layer) {
      // Update registry
      this.layerRegistry[newId] = layer;
      delete this.layerRegistry[oldId];

      // Update order
      const index = this.layerOrder.indexOf(oldId);
      if (index !== -1) {
        this.layerOrder[index] = newId;
      }

      // Update groups
      Object.keys(this.layerGroups).forEach(groupName => {
        const index = this.layerGroups[groupName].indexOf(oldId);
        if (index !== -1) {
          this.layerGroups[groupName][index] = newId;
        }
      });

      // Update other references
      if (this.layerStatistics[oldId]) {
        this.layerStatistics[newId] = this.layerStatistics[oldId];
        delete this.layerStatistics[oldId];
      }
      if (this.layerFilters[oldId]) {
        this.layerFilters[newId] = this.layerFilters[oldId];
        delete this.layerFilters[oldId];
      }
      if (this.layerLocks[oldId] !== undefined) {
        this.layerLocks[newId] = this.layerLocks[oldId];
        delete this.layerLocks[oldId];
      }
      if (this.layerMinZoom[oldId] !== undefined) {
        this.layerMinZoom[newId] = this.layerMinZoom[oldId];
        delete this.layerMinZoom[oldId];
      }
      if (this.layerMaxZoom[oldId] !== undefined) {
        this.layerMaxZoom[newId] = this.layerMaxZoom[oldId];
        delete this.layerMaxZoom[oldId];
      }
      if (this.layerStyles[oldId]) {
        this.layerStyles[newId] = this.layerStyles[oldId];
        delete this.layerStyles[oldId];
      }
      if (this.layerPaletteId[oldId] !== undefined) {
        this.layerPaletteId[newId] = this.layerPaletteId[oldId];
        delete this.layerPaletteId[oldId];
      }
      if (this.layerMetadata[oldId]) {
        this.layerMetadata[newId] = this.layerMetadata[oldId];
        delete this.layerMetadata[oldId];
      }

      // Re-apply style so the layer's style function uses newId (closure was oldId)
      this.applyLayerTheme(newId);

      this.gisPending.forEach((entry) => {
        if (entry.layer === oldId) entry.layer = newId;
      });
      if (this.isPersistableLayer(newId)) {
        this.markLayerDirty(newId);
        this.scheduleGisFlush();
      }

      this.updateLayerManagementUI();
    }
  }

  getFilteredLayers(): string[] {
    if (!this.layerSearchTerm) {
      return this.layerOrder;
    }
    const searchLower = this.layerSearchTerm.toLowerCase();
    return this.layerOrder.filter(layerId => {
      const info = this.getLayerInfo(layerId);
      return layerId.toLowerCase().includes(searchLower) ||
             (info.metadata?.name || '').toLowerCase().includes(searchLower);
    });
  }

  getUngroupedLayers(): string[] {
    const groupedIds = new Set(Object.values(this.layerGroups).flat());
    return this.getFilteredLayers().filter(id => !groupedIds.has(id) && !this.isOverlayLayer(id));
  }

  getLayerGroups(): Array<{ name: string; layers: string[] }> {
    const filteredLayers = this.getFilteredLayers();
    return Object.entries(this.layerGroups)
      .filter(([name]) => name !== 'LRS')
      .map(([name, layerIds]) => ({
        name,
        layers: layerIds.filter((id) =>
          this.isOverlayLayer(id) ? this.layerMatchesSearch(id) : filteredLayers.includes(id)
        ),
      }))
      .filter((group) => group.layers.length > 0);
  }

  trackByGroupName(_index: number, group: { name: string }): string {
    return group.name;
  }

  trackByLayerId(_index: number, layerId: string): string {
    return layerId;
  }

  showCreateLayerDialog(): void {
    this.closeCreateLayerDialog();
    const dialogId = `create-layer-dialog-${Date.now()}`;
    const nameId = `createLayerName-${dialogId}`;
    const groupId = `createLayerGroup-${dialogId}`;
    const paletteId = `createLayerPalette-${dialogId}`;
    const saveId = `createLayerSave-${dialogId}`;
    const cancelId = `createLayerCancel-${dialogId}`;
    const groupOptions = Object.keys(this.layerGroups)
      .map(g => `<option value="${g}">${g}</option>`)
      .join('');
    const paletteOptions = PREDEFINED_LAYER_PALETTES
      .map(p => `<option value="${p.id}">${p.name}</option>`)
      .join('');
    const dialog = document.createElement('div');
    dialog.className = 'layer-properties-dialog create-layer-dialog';
    dialog.id = dialogId;
    dialog.innerHTML = `
      <div class="lp-card">
        <div class="lp-header">
          <div class="lp-header-text">
            <h3 class="lp-title">Create New Layer</h3>
            <span class="lp-layer-badge">Name the layer and pick a palette</span>
          </div>
          <button type="button" id="${cancelId}-x" class="lp-close" aria-label="Close">&times;</button>
        </div>
        <div class="lp-body">
          <div class="lp-form-group">
            <label for="${nameId}">Layer name</label>
            <input type="text" id="${nameId}" class="lp-input" placeholder="e.g. drawing, road, water" maxlength="64" autocomplete="off">
          </div>
          <div class="lp-form-group">
            <label for="${paletteId}">Theming palette</label>
            <select id="${paletteId}" class="lp-select" title="Predefined colors for point, text, line, segment, sub-segment, section">
              ${paletteOptions}
            </select>
          </div>
          <div class="lp-form-group">
            <label for="${groupId}">Category (optional)</label>
            <select id="${groupId}" class="lp-select">
              <option value="">No category</option>
              ${groupOptions}
              <option value="__new__">+ New category...</option>
            </select>
          </div>
          <div class="lp-form-group" id="newGroupWrap-${dialogId}" style="display:none;">
            <label for="newGroupName-${dialogId}">New category name</label>
            <input type="text" id="newGroupName-${dialogId}" class="lp-input" placeholder="e.g. Infrastructure">
          </div>
        </div>
        <div class="lp-footer">
          <div class="lp-footer-actions">
            <button type="button" id="${cancelId}" class="lp-btn lp-btn-secondary">Cancel</button>
            <button type="button" id="${saveId}" class="lp-btn lp-btn-primary">Create Layer</button>
          </div>
        </div>
      </div>
    `;
    (this as any).currentCreateLayerDialog = dialog;
    document.body.appendChild(dialog);
    setTimeout(() => (document.getElementById(nameId) as HTMLInputElement | null)?.focus(), 0);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) this.closeCreateLayerDialog(); });
    document.getElementById(groupId)?.addEventListener('change', () => {
      const wrap = document.getElementById(`newGroupWrap-${dialogId}`);
      if (wrap) wrap.style.display = (document.getElementById(groupId) as HTMLSelectElement)?.value === '__new__' ? 'block' : 'none';
    });
    document.getElementById(saveId)?.addEventListener('click', () => {
      const name = (document.getElementById(nameId) as HTMLInputElement)?.value?.trim();
      if (!name) {
        alert('Please enter a layer name.');
        return;
      }
      if (this.layerRegistry[name]) {
        alert(`Layer "${name}" already exists. Choose another name.`);
        return;
      }
      let group = (document.getElementById(groupId) as HTMLSelectElement)?.value || '';
      if (group === '__new__') {
        group = (document.getElementById(`newGroupName-${dialogId}`) as HTMLInputElement)?.value?.trim() || '';
      }
      const selectedPaletteId = (document.getElementById(paletteId) as HTMLSelectElement)?.value || 'default';
      this.createNewLayer(name, 'vector', group, selectedPaletteId);
      this.closeCreateLayerDialog();
    });
    document.getElementById(cancelId)?.addEventListener('click', () => this.closeCreateLayerDialog());
    document.getElementById(`${cancelId}-x`)?.addEventListener('click', () => this.closeCreateLayerDialog());
  }

  closeCreateLayerDialog(): void {
    const existing = (this as any).currentCreateLayerDialog || document.querySelector('.create-layer-dialog');
    if (existing?.parentNode) {
      existing.parentNode.removeChild(existing);
      (this as any).currentCreateLayerDialog = null;
    }
  }

  showImportLayerDialogWrapper(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json,.kml,.gpx';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const content = e.target.result;
          const layerName = prompt('Enter layer name:', file.name.replace(/\.[^/.]+$/, ''));
          if (layerName && layerName.trim()) {
            this.importLayer(layerName.trim(), content);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  exportLayerToFile(layerId: string | null): void {
    if (!layerId) return;
    const geoJson = this.exportLayer(layerId);
    if (geoJson) {
      const blob = new Blob([geoJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${layerId}.geojson`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  toggleGroupExpanded(groupName: string): void {
    this.expandedGroups[groupName] = !(this.expandedGroups[groupName] || false);
  }

  isGroupExpanded(groupName: string): boolean {
    return this.expandedGroups[groupName] !== false; // Default to expanded
  }

  showLayerContextMenu(event: MouseEvent, layerId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.layerContextMenu = {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      layerId: layerId
    };
    this.selectedLayerId = layerId;
  }

  hideLayerContextMenu(): void {
    this.layerContextMenu.visible = false;
  }

  moveLayerUp(layerId: string): void {
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex > 0) {
      this.reorderLayer(layerId, currentIndex - 1);
    }
  }

  moveLayerDown(layerId: string): void {
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex < this.layerOrder.length - 1) {
      this.reorderLayer(layerId, currentIndex + 1);
    }
  }

  moveLayerToTop(layerId: string): void {
    this.reorderLayer(layerId, this.layerOrder.length - 1);
  }

  moveLayerToBottom(layerId: string): void {
    this.reorderLayer(layerId, 0);
  }

  zoomToLayer(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      const extent = source.getExtent();
      if (extent && extent[0] !== Infinity) {
        this.map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 500 });
      }
    }
  }

  clearLayer(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      source.clear();
      this.updateLayerStatistics(layerId);
      this.updateLayerManagementUI();
    }
  }

  // Setup zoom change listener to handle scale-based visibility
  private setupScaleVisibilityListeners(): void {
    const view = this.map.getView();
    view.on('change:resolution', () => {
      this.layerOrder.forEach(layerId => {
        if (this.layerMinZoom[layerId] !== undefined || this.layerMaxZoom[layerId] !== undefined) {
          this.updateLayerScaleVisibility(layerId);
        }
      });
      this.scheduleLrsClusterDistance();
    });
  }

  private getLayerGroup(layerId: string): string | null {
    for (const [groupName, layerIds] of Object.entries(this.layerGroups)) {
      if (layerIds.includes(layerId)) {
        return groupName;
      }
    }
    return null;
  }

  getAllLayersInfo(): any[] {
    return this.layerOrder.map(layerId => this.getLayerInfo(layerId));
  }

  updateLayerManagementUI(): void {
    // Update the layer management panel UI
    this.renderLayerManagementPanel();
  }

  private renderLayerManagementPanel(): void {
    const groupsContainer = document.getElementById('layerGroups');
    const ungroupedContainer = document.getElementById('layerList');
    const noLayersFound = document.getElementById('noLayersFound');
    
    if (!groupsContainer || !ungroupedContainer) return;

    // Get all layer IDs that are in groups
    const groupedLayerIds = new Set(Object.values(this.layerGroups).flat());
    
    // Get ungrouped layers
    const ungroupedLayerIds = this.layerOrder.filter(id => !groupedLayerIds.has(id));

    // Render groups
    groupsContainer.innerHTML = Object.entries(this.layerGroups)
      .filter(([_, layerIds]) => layerIds.length > 0)
      .map(([groupName, layerIds]) => `
        <div class="layer-group">
          <div class="group-header">
            <div class="group-title">
              <h4>${groupName}</h4>
              <span class="group-count">${layerIds.length}</span>
            </div>
            <button class="group-toggle" data-group="${groupName}">▼</button>
          </div>
          <div class="group-layers" data-group="${groupName}">
            ${layerIds.map(layerId => this.renderLayerItem(layerId)).join('')}
          </div>
        </div>
      `).join('');

    // Render ungrouped layers
    if (ungroupedLayerIds.length > 0) {
      ungroupedContainer.innerHTML = ungroupedLayerIds.map(layerId => this.renderLayerItem(layerId)).join('');
      if (noLayersFound) noLayersFound.style.display = 'none';
    } else {
      ungroupedContainer.innerHTML = '';
      if (noLayersFound && this.layerOrder.length === 0) {
        noLayersFound.style.display = 'block';
      } else if (noLayersFound) {
        noLayersFound.style.display = 'none';
      }
    }

    this.setupLayerManagementEvents();
  }

  private renderLayerItem(layerId: string): string {
    const info = this.getLayerInfo(layerId);
    const layer = this.layerRegistry[layerId];
    
    return `
      <div class="layer-item" data-layer="${layerId}">
        <div class="layer-header">
          <div class="layer-controls">
            <input type="checkbox" ${info.visible ? 'checked' : ''} 
                   class="layer-visibility" data-layer="${layerId}">
            <span class="layer-name">${layerId}</span>
            <span class="layer-count">(${info.featureCount})</span>
          </div>
          <div class="layer-actions">
            <button class="layer-edit" data-layer="${layerId}" title="Edit Properties">⚙️</button>
            <button class="layer-duplicate" data-layer="${layerId}" title="Duplicate">📋</button>
            <button class="layer-delete" data-layer="${layerId}" title="Delete">🗑️</button>
          </div>
        </div>
        <div class="layer-details">
          <div class="opacity-control">
            <label>Opacity:</label>
            <input type="range" min="0" max="100" value="${Math.round(info.opacity * 100)}" 
                   class="layer-opacity" data-layer="${layerId}">
            <span class="opacity-value">${Math.round(info.opacity * 100)}%</span>
          </div>
          <div class="layer-stats">
            <small>Features: ${info.featureCount} | Z-Index: ${info.zIndex}</small>
          </div>
        </div>
      </div>
    `;
  }

  private setupLayerManagementEvents(): void {
    // Layer visibility toggles
    document.querySelectorAll('.layer-visibility').forEach(toggle => {
      toggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        const layerId = target.getAttribute('data-layer')!;
        this.toggleLayerEnhanced(layerId, target.checked);
      });
    });

    // Layer opacity controls
    document.querySelectorAll('.layer-opacity').forEach(slider => {
      slider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const layerId = target.getAttribute('data-layer')!;
        const opacity = parseFloat(target.value) / 100;
        this.setLayerOpacityEnhanced(layerId, opacity);
        
        // Update display
        const valueSpan = target.parentElement?.querySelector('.opacity-value');
        if (valueSpan) {
          valueSpan.textContent = `${Math.round(opacity * 100)}%`;
        }
      });
    });

    // Layer action buttons
    document.querySelectorAll('.layer-edit').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const layerId = target.closest('[data-layer]')?.getAttribute('data-layer') || 
                       target.getAttribute('data-layer')!;
        this.editLayerProperties(layerId);
      });
    });

    document.querySelectorAll('.layer-duplicate').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const layerId = target.closest('[data-layer]')?.getAttribute('data-layer') || 
                       target.getAttribute('data-layer')!;
        const newLayerId = `${layerId}_copy_${Date.now()}`;
        this.duplicateLayer(layerId, newLayerId);
      });
    });

    document.querySelectorAll('.layer-delete').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const layerId = target.closest('[data-layer]')?.getAttribute('data-layer') || 
                       target.getAttribute('data-layer')!;
        if (confirm(`Delete layer "${layerId}"?`)) {
          this.deleteLayer(layerId);
        }
      });
    });

    // Group toggles
    document.querySelectorAll('.group-toggle').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const groupName = target.getAttribute('data-group')!;
        const groupLayers = document.querySelector(`.group-layers[data-group="${groupName}"]`);
        if (groupLayers) {
          groupLayers.classList.toggle('collapsed');
          target.textContent = groupLayers.classList.contains('collapsed') ? '▶' : '▼';
        }
      });
    });
  }

  private filterLayersBySearch(searchTerm: string): void {
    const layerItems = document.querySelectorAll('.layer-item');
    layerItems.forEach(item => {
      const layerName = item.getAttribute('data-layer') || '';
      const layerNameElement = item.querySelector('.layer-name');
      const displayName = layerNameElement?.textContent?.toLowerCase() || '';
      
      if (searchTerm === '' || layerName.toLowerCase().includes(searchTerm) || displayName.includes(searchTerm)) {
        (item as HTMLElement).style.display = '';
      } else {
        (item as HTMLElement).style.display = 'none';
      }
    });

    // Also filter groups
    const groups = document.querySelectorAll('.layer-group');
    groups.forEach(group => {
      const visibleLayers = group.querySelectorAll('.layer-item:not([style*="display: none"])');
      if (visibleLayers.length === 0 && searchTerm !== '') {
        (group as HTMLElement).style.display = 'none';
      } else {
        (group as HTMLElement).style.display = '';
      }
    });
  }

  showCreateGroupDialog(): void {
    const groupName = prompt('Enter group name:');
    if (groupName && groupName.trim()) {
      // Get selected layers (could be enhanced with checkboxes)
      const selectedLayers = this.layerOrder.filter(id => {
        const item = document.querySelector(`[data-layer="${id}"]`);
        return item && (item as HTMLElement).style.display !== 'none';
      });
      
      if (selectedLayers.length > 0) {
        this.createLayerGroup(groupName.trim(), selectedLayers);
      } else {
        this.createLayerGroup(groupName.trim(), []);
      }
    }
  }

  private showImportLayerDialog(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json,.kml,.gpx';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const content = e.target.result;
          const layerName = prompt('Enter layer name:', file.name.replace(/\.[^/.]+$/, ''));
          if (layerName && layerName.trim()) {
            this.importLayer(layerName.trim(), content);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  exportAllLayers(): void {
    const allLayersData: any = {
      layers: this.layerOrder.map(layerId => ({
        id: layerId,
        data: this.exportLayer(layerId),
        properties: this.getLayerInfo(layerId)
      })),
      groups: this.layerGroups,
      order: this.layerOrder
    };

    const blob = new Blob([JSON.stringify(allLayersData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_layers_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  editLayerProperties(layerId: string): void {
    // Close any existing dialog first
    this.closeLayerPropertiesDialog();
    
    const info = this.getLayerInfo(layerId);
    const layer = this.layerRegistry[layerId];
    
    // Create unique IDs for this dialog instance
    const dialogId = `layer-props-dialog-${Date.now()}`;
    const layerNameId = `layerName-${dialogId}`;
    const layerZIndexId = `layerZIndex-${dialogId}`;
    const layerOpacityId = `layerOpacity-${dialogId}`;
    const opacityValueId = `opacityValue-${dialogId}`;
    const layerVisibleId = `layerVisible-${dialogId}`;
    const layerGroupId = `layerGroup-${dialogId}`;
    const layerFilterId = `layerFilter-${dialogId}`;
    const saveLayerPropsId = `saveLayerProps-${dialogId}`;
    const cancelLayerPropsId = `cancelLayerProps-${dialogId}`;
    const exportLayerId = `exportLayer-${dialogId}`;
    const strokeColorId = `layerStrokeColor-${dialogId}`;
    const strokeWidthId = `layerStrokeWidth-${dialogId}`;
    const fillColorId = `layerFillColor-${dialogId}`;
    const paletteSelectId = `layerPalette-${dialogId}`;
    const theme = this.layerStyles[layerId] || {};
    const strokeColor = theme.strokeColor || '#3498db';
    const strokeWidth = theme.strokeWidth ?? 2;
    const fillColor = theme.fillColor || 'rgba(52, 152, 219, 0.2)';
    const currentPaletteId = this.layerPaletteId[layerId] || theme.paletteId || 'default';
    const paletteOptionsHtml = PREDEFINED_LAYER_PALETTES
      .map(p => `<option value="${p.id}" ${p.id === currentPaletteId ? 'selected' : ''}>${this.escapeHtml(p.name)}</option>`)
      .join('');
    
    const closeBtnId = `lp-close-${dialogId}`;
    const dialog = document.createElement('div');
    dialog.className = 'layer-properties-dialog';
    dialog.id = dialogId;
    dialog.innerHTML = `
      <div class="lp-card">
        <header class="lp-header">
          <div class="lp-header-text">
            <h2 class="lp-title">Layer properties</h2>
            <span class="lp-layer-badge">${this.escapeHtml(layerId)}</span>
          </div>
          <button type="button" id="${closeBtnId}" class="lp-close" aria-label="Close">&times;</button>
        </header>
        <div class="lp-body">
          <section class="lp-section">
            <h3 class="lp-section-title">General</h3>
            <div class="lp-form-group">
              <label for="${layerNameId}">Layer name</label>
              <input type="text" id="${layerNameId}" value="${this.escapeHtml(layerId)}" class="lp-input">
            </div>
            <div class="lp-form-group">
              <label for="${layerGroupId}">Category</label>
              <select id="${layerGroupId}" class="lp-select">
                <option value="">No category</option>
                ${Object.keys(this.layerGroups).map(group =>
                  `<option value="${group}" ${this.getLayerGroup(layerId) === group ? 'selected' : ''}>${this.escapeHtml(group)}</option>`
                ).join('')}
              </select>
            </div>
            <div class="lp-form-group">
              <label for="${layerFilterId}">Filter expression</label>
              <input type="text" id="${layerFilterId}" placeholder="e.g. type=road" class="lp-input">
            </div>
          </section>
          <section class="lp-section">
            <h3 class="lp-section-title">Display</h3>
            <div class="lp-row lp-row-2">
              <div class="lp-form-group">
                <label for="${layerOpacityId}">Opacity</label>
                <div class="lp-range-wrap">
                  <input type="range" min="0" max="100" id="${layerOpacityId}" value="${Math.round(info.opacity * 100)}" class="lp-range">
                  <span id="${opacityValueId}" class="lp-range-value">${Math.round(info.opacity * 100)}%</span>
                </div>
              </div>
              <div class="lp-form-group">
                <label for="${layerZIndexId}">Z-Index</label>
                <input type="number" id="${layerZIndexId}" value="${info.zIndex}" class="lp-input lp-input-narrow">
              </div>
            </div>
            <div class="lp-form-group lp-form-group-inline">
              <input type="checkbox" id="${layerVisibleId}" ${info.visible ? 'checked' : ''} class="lp-checkbox">
              <label for="${layerVisibleId}" class="lp-checkbox-label">Visible on map</label>
            </div>
          </section>
          <section class="lp-section lp-section-theme">
            <h3 class="lp-section-title">Style</h3>
            <div class="lp-form-group">
              <label for="${paletteSelectId}">Theming palette</label>
              <select id="${paletteSelectId}" class="lp-select" title="Predefined colors for point, text, line, segment, sub-segment, section">
                ${paletteOptionsHtml}
              </select>
            </div>
            <div class="lp-form-group newton-theme-row">
              <label>Stroke color</label>
              <div id="${dialogId}-stroke-wheel" class="newton-wheel-container"></div>
              <input type="color" id="${strokeColorId}" value="${strokeColor}" class="newton-color-input-hidden" title="Stroke color">
            </div>
            <div class="lp-form-group">
              <label for="${strokeWidthId}">Stroke width</label>
              <input type="number" id="${strokeWidthId}" min="1" max="24" value="${strokeWidth}" class="lp-input lp-input-narrow">
            </div>
            <div class="lp-form-group newton-theme-row">
              <label>Fill color</label>
              <div id="${dialogId}-fill-wheel" class="newton-wheel-container"></div>
              <input type="color" id="${fillColorId}" value="${this.hexFromRgba(fillColor)}" class="newton-color-input-hidden" title="Fill color">
            </div>
          </section>
        </div>
        <footer class="lp-footer">
          <button type="button" id="${exportLayerId}" class="lp-btn lp-btn-secondary">Export</button>
          <div class="lp-footer-actions">
            <button type="button" id="${cancelLayerPropsId}" class="lp-btn lp-btn-secondary">Cancel</button>
            <button type="button" id="${saveLayerPropsId}" class="lp-btn lp-btn-primary">Save</button>
          </div>
        </footer>
      </div>
    `;
    
    // Store dialog reference
    (this as any).currentLayerPropertiesDialog = dialog;
    
    document.body.appendChild(dialog);
    
    const strokeWheelEl = document.getElementById(`${dialogId}-stroke-wheel`);
    const fillWheelEl = document.getElementById(`${dialogId}-fill-wheel`);
    if (strokeWheelEl) this.createNewtonColorWheel(strokeWheelEl, strokeColorId, strokeColor);
    if (fillWheelEl) this.createNewtonColorWheel(fillWheelEl, fillColorId, this.hexFromRgba(fillColor));
    
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.closeLayerPropertiesDialog();
    });
    document.getElementById(closeBtnId)?.addEventListener('click', () => this.closeLayerPropertiesDialog());
    
    document.getElementById(layerOpacityId)?.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const value = document.getElementById(opacityValueId);
      if (value) value.textContent = `${target.value}%`;
    });
    
    document.getElementById(saveLayerPropsId)?.addEventListener('click', () => {
      this.saveLayerProperties(layerId, dialog, {
        layerNameId,
        layerZIndexId,
        layerOpacityId,
        layerVisibleId,
        layerGroupId,
        layerFilterId,
        strokeColorId,
        strokeWidthId,
        fillColorId,
        paletteSelectId
      });
    });
    
    document.getElementById(cancelLayerPropsId)?.addEventListener('click', () => {
      this.closeLayerPropertiesDialog();
    });
    
    document.getElementById(exportLayerId)?.addEventListener('click', () => {
      if (layerId) {
        const geoJson = this.exportLayer(layerId);
        if (geoJson) {
          const blob = new Blob([geoJson], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${layerId}.geojson`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }
    });
  }
  
  closeLayerPropertiesDialog(): void {
    const existingDialog = (this as any).currentLayerPropertiesDialog || 
                          document.querySelector('.layer-properties-dialog');
    if (existingDialog && existingDialog.parentNode) {
      existingDialog.parentNode.removeChild(existingDialog);
      (this as any).currentLayerPropertiesDialog = null;
    }
  }

  private saveLayerProperties(layerId: string, dialog: HTMLElement, ids?: {
    layerNameId: string;
    layerZIndexId: string;
    layerOpacityId: string;
    layerVisibleId: string;
    layerGroupId: string;
    layerFilterId: string;
    strokeColorId?: string;
    strokeWidthId?: string;
    fillColorId?: string;
    paletteSelectId?: string;
  }): void {
    const layerNameId = ids?.layerNameId || 'layerName';
    const layerZIndexId = ids?.layerZIndexId || 'layerZIndex';
    const layerOpacityId = ids?.layerOpacityId || 'layerOpacity';
    const layerVisibleId = ids?.layerVisibleId || 'layerVisible';
    const layerGroupId = ids?.layerGroupId || 'layerGroup';
    const layerFilterId = ids?.layerFilterId || 'layerFilter';
    
    const newName = (document.getElementById(layerNameId) as HTMLInputElement)?.value?.trim();
    const zIndex = parseInt((document.getElementById(layerZIndexId) as HTMLInputElement)?.value || '0');
    const opacity = parseFloat((document.getElementById(layerOpacityId) as HTMLInputElement)?.value || '100') / 100;
    const visible = (document.getElementById(layerVisibleId) as HTMLInputElement)?.checked || false;
    const group = (document.getElementById(layerGroupId) as HTMLSelectElement)?.value;
    const filter = (document.getElementById(layerFilterId) as HTMLInputElement)?.value;
    
    const layer = this.layerRegistry[layerId];
    if (layer) {
      layer.setZIndex(zIndex);
      layer.setOpacity(opacity);
      layer.setVisible(visible);
    }
    
    if (group) {
      Object.values(this.layerGroups).forEach(groupLayers => {
        const index = groupLayers.indexOf(layerId);
        if (index > -1) groupLayers.splice(index, 1);
      });
      if (!this.layerGroups[group]) this.layerGroups[group] = [];
      this.layerGroups[group].push(layerId);
    }
    
    if (filter) {
      const filterObj = this.parseFilterExpression(filter);
      this.filterLayer(layerId, filterObj);
    } else {
      this.clearLayerFilter(layerId);
    }
    
    if (ids?.strokeColorId && ids?.strokeWidthId && ids?.fillColorId) {
      const strokeColor = (document.getElementById(ids.strokeColorId) as HTMLInputElement)?.value || '#3498db';
      const strokeWidth = parseInt((document.getElementById(ids.strokeWidthId) as HTMLInputElement)?.value || '2', 10);
      const fillColor = (document.getElementById(ids.fillColorId) as HTMLInputElement)?.value || 'rgba(52, 152, 219, 0.2)';
      this.layerStyles[layerId] = { strokeColor, strokeWidth, fillColor: this.rgbaFromHex(fillColor) };
      this.applyLayerTheme(layerId);
    }
    
    if (ids?.paletteSelectId) {
      const paletteId = (document.getElementById(ids.paletteSelectId) as HTMLSelectElement)?.value || 'default';
      this.setLayerPalette(layerId, paletteId);
    }
    
    this.updateLayerStatistics(layerId);
    this.updateLayerManagementUI();
    this.closeLayerPropertiesDialog();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private hexFromRgba(rgba: string): string {
    if (rgba.startsWith('#')) return rgba;
    const m = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (m) return '#' + [m[1], m[2], m[3]].map(x => parseInt(x, 10).toString(16).padStart(2, '0')).join('');
    return '#3498db';
  }

  private rgbaFromHex(hex: string): string {
    if (hex.startsWith('rgba')) return hex;
    if (hex.length === 7 && hex.startsWith('#')) {
      const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r},${g},${b},0.2)`;
    }
    return hex;
  }

  /** HSL (0-360, 0-100, 0-100) to hex. */
  private hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
      return Math.round(255 * c).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  /** Hex to HSL. Returns { h: 0-360, s: 0-100, l: 0-100 }. */
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    if (!hex || !hex.startsWith('#')) return { h: 210, s: 100, l: 50 };
    const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  /**
   * Creates a Newton-style color wheel (hue ring + lightness slider) and appends to parent.
   * Updates the given color input (hex) on pick.
   */
  private createNewtonColorWheel(parent: HTMLElement, targetInputId: string, initialHex: string): void {
    const size = 150;
    const cx = size / 2;
    const cy = size / 2;
    const innerR = 42;
    const outerR = 68;
    const hsl = this.hexToHsl(initialHex);
    let hue = hsl.h;
    let lightness = hsl.l;

    const wrap = document.createElement('div');
    wrap.className = 'newton-wheel-wrap';
    wrap.innerHTML = `
      <canvas class="newton-wheel-canvas" width="${size}" height="${size}" style="display:block; border-radius:50%; cursor:crosshair;"></canvas>
      <div class="newton-wheel-lightness">
        <label>Lightness</label>
        <input type="range" class="newton-lightness-slider" min="0" max="100" value="${Math.round(lightness)}">
        <span class="newton-lightness-value">${Math.round(lightness)}%</span>
      </div>
    `;
    parent.appendChild(wrap);

    const canvas = wrap.querySelector('.newton-wheel-canvas') as HTMLCanvasElement;
    const slider = wrap.querySelector('.newton-lightness-slider') as HTMLInputElement;
    const valueSpan = wrap.querySelector('.newton-lightness-value') as HTMLElement;
    const targetInput = document.getElementById(targetInputId) as HTMLInputElement;

    const syncToInput = () => {
      const hex = this.hslToHex(hue, 100, lightness);
      if (targetInput) {
        targetInput.value = hex;
        targetInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };

    const drawWheel = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size, size);
      for (let i = 0; i < 360; i++) {
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, outerR, (i - 90) * Math.PI / 180, (i - 89) * Math.PI / 180);
        ctx.arc(cx, cy, innerR, (i - 89) * Math.PI / 180, (i - 90) * Math.PI / 180, true);
        ctx.closePath();
        ctx.fillStyle = `hsl(${i}, 100%, 50%)`;
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(cx, cy, innerR - 2, 0, Math.PI * 2);
      ctx.fillStyle = '#f0f0f0';
      ctx.fill();
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.stroke();
      const markerAngle = (hue - 90) * Math.PI / 180;
      const mr = (innerR + outerR) / 2;
      const mx = cx + mr * Math.cos(markerAngle);
      const my = cy + mr * Math.sin(markerAngle);
      ctx.beginPath();
      ctx.arc(mx, my, 6, 0, Math.PI * 2);
      ctx.fillStyle = this.hslToHex(hue, 100, lightness);
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    };

    const getAngle = (e: MouseEvent): number => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left - (rect.width / 2);
      const y = e.clientY - rect.top - (rect.height / 2);
      let angle = Math.atan2(y, x) * 180 / Math.PI + 90;
      if (angle < 0) angle += 360;
      return angle % 360;
    };

    const onWheelInteraction = (e: MouseEvent) => {
      e.preventDefault();
      hue = getAngle(e);
      drawWheel();
      syncToInput();
    };

    canvas.addEventListener('mousedown', (e) => {
      onWheelInteraction(e);
      const move = (e2: MouseEvent) => { onWheelInteraction(e2); };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });

    slider.addEventListener('input', () => {
      lightness = parseInt(slider.value, 10);
      if (valueSpan) valueSpan.textContent = slider.value + '%';
      drawWheel();
      syncToInput();
    });

    drawWheel();
    syncToInput();
  }

  private parseFilterExpression(filter: string): any {
    // Simple filter parser - can be enhanced for complex expressions
    const parts = filter.split('=');
    if (parts.length === 2) {
      return { [parts[0].trim()]: parts[1].trim() };
    }
    return {};
  }

  private setupMapEventListeners(): void {
    // Pointer move event for coordinates display
    this.map.getViewport().addEventListener('mouseleave', () => (this.lastPointerCoord = null));
    this.map.on('pointermove', (event) => {
      this.lastPointerCoord = event.coordinate.slice();
      const coordinate = toLonLat(event.coordinate);
      const zoom = this.map.getView().getZoom()!;
      const scale = Math.round(591657527.591555 / Math.pow(2, zoom));
      this.mapZoom = Math.round(zoom);
      this.mapScaleLabel = String(scale);

      const coordinatesElement = document.getElementById('coordinates');
      if (coordinatesElement) {
        coordinatesElement.innerHTML = 
          `Lat: ${coordinate[1].toFixed(6)}, Lon: ${coordinate[0].toFixed(6)} | Scale: 1:${scale} | Zoom: ${Math.round(zoom)}`;
      }

      this.scheduleChainageHud(coordinate[0], coordinate[1]);

      // Popup on hover — pick the most informative feature when several overlap
      let best: FeatureLike | null = null;
      let bestScore = -1;
      this.map.forEachFeatureAtPixel(
        event.pixel,
        (hit) => {
          const score = this.hoverInfoScore(hit);
          if (score > bestScore) {
            bestScore = score;
            best = hit;
          }
          return undefined;
        },
        { hitTolerance: 8 }
      );
      const popupEl = this.popup.getElement();
      if (best && popupEl) {
        popupEl.innerHTML = this.getHoverInfo(best);
        this.popup.setPosition(event.coordinate);
        popupEl.style.display = 'block';
      } else if (popupEl) {
        popupEl.style.display = 'none';
      }
    });

    // Click event to populate coordinate inputs
    this.map.on('click', (event) => {
      const coordinate = toLonLat(event.coordinate);
      const latitude = coordinate[1];
      const longitude = coordinate[0];
      
      // Update coordinate input fields
      const latInput = document.getElementById('latitudeInput') as HTMLInputElement;
      const lonInput = document.getElementById('longitudeInput') as HTMLInputElement;
      
      if (latInput && lonInput) {
        latInput.value = latitude.toFixed(6);
        lonInput.value = longitude.toFixed(6);
      }
    });
  }

  private hoverProp(feature: FeatureLike, key: string): string {
    const v = feature.get(key);
    if (v == null || v === '') return '';
    const s = String(v).trim();
    return s && s !== 'undefined' && s !== 'null' ? s : '';
  }

  private escapeHover(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Higher score = more useful hover target when features overlap. */
  private hoverInfoScore(feature: FeatureLike): number {
    if (feature.get('gisOverlay')) return -1;
    if (feature.get('event_type') || feature.get('utility_type') || feature.get('unm')) return 8;
    if (this.hoverProp(feature, 'label') || this.hoverProp(feature, 'info')) return 7;
    if (this.hoverProp(feature, 'route_code') || this.hoverProp(feature, 'route_name')) return 6;
    const members = feature.get('features');
    if (Array.isArray(members) && members.length) return 5;
    if (this.hoverProp(feature, 'name') || this.hoverProp(feature, 'highway') || this.hoverProp(feature, 'ref')) {
      return 4;
    }
    if (this.hoverProp(feature, 'type') || this.hoverProp(feature, 'text')) return 3;
    try {
      const keys = Object.keys(feature.getProperties?.() || {}).filter(
        (k) => k !== 'geometry' && k !== 'features'
      );
      return keys.length ? 1 : 0;
    } catch {
      return 0;
    }
  }

  private formatHoverKm(raw: unknown): string {
    if (raw == null || raw === '') return '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return '';
    // Hover lengths are stored as km; convert to display unit.
    return this.formatLengthM(n * 1000);
  }

  private getHoverInfo(feature: FeatureLike): string {
    const members = feature.get('features') as FeatureLike[] | undefined;
    if (Array.isArray(members) && members.length) {
      if (members.length === 1) return this.getHoverInfo(members[0]);
      const sample = members
        .slice(0, 3)
        .map((m) => this.hoverProp(m, 'label') || this.hoverProp(m, 'event_type') || this.hoverProp(m, 'utility_type'))
        .filter(Boolean);
      const extra = members.length > 3 ? ` +${members.length - 3} more` : '';
      return `<strong>${members.length} assets</strong>${sample.length ? `<br>${this.escapeHover(sample.join(', '))}${extra}` : ''}`;
    }

    const type = this.hoverProp(feature, 'type');
    if (type === 'milestone_corridor') {
      const label = this.hoverProp(feature, 'milestoneLabel') || 'Milestone';
      const lanes = feature.get('lanes');
      const km = feature.get('lengthKm');
      const material = this.hoverProp(feature, 'material');
      return this.escapeHover(
        `${label}${lanes ? ` | ${lanes}L` : ''}${km ? ` | ${km} km` : ''}${material ? ` | ${material}` : ''}`
      );
    }
    if (type === 'milestone_tag') {
      const label = this.hoverProp(feature, 'milestoneLabel') || 'Milestone';
      const ch = this.hoverProp(feature, 'milestoneChainage');
      return this.escapeHover(ch ? `${label} (${ch})` : label);
    }
    if (type === 'milestone_chainage' || type === 'milestone_chainage_end') {
      return this.escapeHover(this.hoverProp(feature, 'label') || 'Chainage');
    }
    if (type === 'milestone_join_dot') return 'Joining Node';
    if (type === 'milestone_direction') return 'Lane Direction';
    if (type === 'milestone_route_meta') {
      return this.escapeHover(this.hoverProp(feature, 'label') || 'Route Metadata');
    }

    const eventType = this.hoverProp(feature, 'event_type');
    const utilityType = this.hoverProp(feature, 'utility_type');
    const routeCode = this.hoverProp(feature, 'route_code');
    const routeName = this.hoverProp(feature, 'route_name');
    const label =
      this.hoverProp(feature, 'label') ||
      this.hoverProp(feature, 'info') ||
      this.hoverProp(feature, 'name') ||
      this.hoverProp(feature, 'text') ||
      this.hoverProp(feature, 'asset_name') ||
      this.hoverProp(feature, 'asset_code');

    const lines: string[] = [];

    if (eventType || utilityType) {
      const typeId = utilityType || eventType;
      const typeLabel = utilityType
        ? LRS_UTILITY_TYPES.find((u) => u.id === typeId)?.label || typeId
        : getLrsAssetTypeMeta(typeId)?.label || typeId;
      lines.push(`<strong>${this.escapeHover(label || typeLabel)}</strong>`);
      if (label && typeLabel && label !== typeLabel) {
        lines.push(this.escapeHover(typeLabel));
      }
      const chainage =
        this.hoverProp(feature, 'chainage_label') ||
        this.formatHoverKm(feature.get('measure_km')) ||
        this.formatHoverKm(feature.get('chainage_km'));
      const chainageTo =
        this.hoverProp(feature, 'chainage_to_label') || this.formatHoverKm(feature.get('measure_to_km'));
      if (chainage) lines.push(this.escapeHover(chainageTo ? `${chainage} – ${chainageTo}` : chainage));
      const side = this.hoverProp(feature, 'side');
      const offset = feature.get('offset_m') ?? feature.get('offset_distance');
      if (side || offset != null) {
        const offsetTxt = offset != null && offset !== '' ? `${Number(offset)} m` : '';
        lines.push(this.escapeHover([side && `Side ${side}`, offsetTxt].filter(Boolean).join(' · ')));
      }
      const depth = feature.get('depth_m');
      if (depth != null && depth !== '') lines.push(this.escapeHover(`Depth ${Number(depth)} m`));
      if (routeCode) lines.push(this.escapeHover(routeCode));
    } else if (routeCode || routeName) {
      lines.push(`<strong>${this.escapeHover(routeCode || routeName)}</strong>`);
      if (routeCode && routeName && routeName !== routeCode) lines.push(this.escapeHover(routeName));
      const lengthM = feature.get('length_m');
      if (lengthM != null && lengthM !== '') {
        lines.push(this.escapeHover(this.formatLengthM(Number(lengthM))));
      }
    } else if (label) {
      lines.push(`<strong>${this.escapeHover(label)}</strong>`);
      if (type && type !== label) lines.push(this.escapeHover(type.replace(/_/g, ' ')));
    } else {
      const highway = this.hoverProp(feature, 'highway');
      const ref = this.hoverProp(feature, 'ref');
      const osmName = this.hoverProp(feature, 'name');
      if (osmName || highway || ref) {
        lines.push(`<strong>${this.escapeHover(osmName || ref || highway)}</strong>`);
        const meta = [highway && highway.replace(/_/g, ' '), ref && ref !== osmName ? ref : '']
          .filter(Boolean)
          .join(' · ');
        if (meta) lines.push(this.escapeHover(meta));
      } else if (type) {
        lines.push(this.escapeHover(type.replace(/_/g, ' ')));
      } else {
        const props = feature.getProperties?.() || {};
        const skip = new Set([
          'geometry',
          'features',
          'style',
          'lrsReadonly',
          'lrsKind',
          'symbol_code',
          'asset_category',
          'boundedBy',
          'layer',
        ]);
        for (const [k, v] of Object.entries(props)) {
          if (skip.has(k) || v == null || v === '' || typeof v === 'object') continue;
          lines.push(`<strong>${this.escapeHover(String(k))}</strong> ${this.escapeHover(String(v))}`);
          if (lines.length >= 4) break;
        }
      }
    }

    const comment = this.hoverProp(feature, 'comment');
    if (comment) lines.push(this.escapeHover(comment));

    return lines.filter(Boolean).join('<br>') || 'Feature';
  }

  private setupEventListeners(): void {
    // Tab switching is attached in ensureSidebarTabsReady() (ngAfterViewInit) so DOM is ready

    // Drawing mode change
    const drawingModeSelect = document.getElementById('drawingMode');
    if (drawingModeSelect) {
      drawingModeSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.handleDrawingModeChange(target.value);
      });
    }

    // Coordinate input event listeners
    this.setupCoordinateInputListeners();

    // Enhanced feature event listeners
    this.setupEnhancedFeatureListeners();

    // Asset type and nomenclature controls
    this.setupAssetControls();

    // Symbol selection setup
    this.setupSymbolSelection();

    // Add other event listeners here...
    this.setupButtonEventListeners();
    this.setupControlEventListeners();
    
    // Setup Analysis tab listeners
    this.setupAnalysisTabListeners();
    
    // Setup Data tab listeners
    this.setupDataTabListeners();
    
    // Setup additional toolbar listeners
    this.setupAdditionalToolbarListeners();
  }

  private setupControlEventListeners(): void {
    // Line thickness and symbol size are handled via Angular (input) in template (onLineThicknessChange, onSymbolSizeChange)

    // Asset type checkboxes
    const showNomenclature = document.getElementById('showNomenclature');
    if (showNomenclature) {
      showNomenclature.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showNomenclature = target.checked;
        this.updateLayerVisibility();
      });
    }

    const showOverground = document.getElementById('showOverground');
    if (showOverground) {
      showOverground.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showOverground = target.checked;
        this.updateLayerVisibility();
      });
    }

    const showUnderground = document.getElementById('showUnderground');
    if (showUnderground) {
      showUnderground.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showUnderground = target.checked;
        this.updateLayerVisibility();
        this.updateAssetVisibility();
      });
    }

    const showCrossover = document.getElementById('showCrossover');
    if (showCrossover) {
      showCrossover.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showCrossover = target.checked;
        this.updateAssetVisibility();
      });
    }
  }

  private updateLayerVisibility(): void {
    // Update layer visibility based on checkboxes
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source === this.vectorSource) {
          // Update drawing layer visibility
          layer.setVisible(this.showNomenclature);
        }
      }
    });
  }

  private setupCoordinateInputListeners(): void {
    // Pinpoint location button
    const pinpointBtn = document.getElementById('pinpointLocation');
    if (pinpointBtn) {
      pinpointBtn.addEventListener('click', () => {
        this.pinpointLocation();
      });
    }

    // Add marker at location button
    const addMarkerBtn = document.getElementById('addMarkerAtLocation');
    if (addMarkerBtn) {
      addMarkerBtn.addEventListener('click', () => {
        this.addMarkerAtLocation();
      });
    }

    // Update current coordinates display when map view changes
    this.map.on('moveend', () => {
      this.updateCurrentCoordinatesDisplay();
    });
  }

  private pinpointLocation(): void {
    const latInput = document.getElementById('latitudeInput') as HTMLInputElement;
    const lonInput = document.getElementById('longitudeInput') as HTMLInputElement;
    
    if (!latInput || !lonInput) {
      this.showInstructions('Coordinate input fields not found');
      return;
    }

    const latitude = parseFloat(latInput.value);
    const longitude = parseFloat(lonInput.value);

    // Validate coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      this.showInstructions('Please enter valid latitude and longitude values');
      latInput.focus();
      return;
    }

    if (latitude < -90 || latitude > 90) {
      this.showInstructions('Latitude must be between -90 and 90 degrees');
      latInput.focus();
      return;
    }

    if (longitude < -180 || longitude > 180) {
      this.showInstructions('Longitude must be between -180 and 180 degrees');
      lonInput.focus();
      return;
    }

    try {
      // Convert to map coordinates (from WGS84 to Web Mercator)
      const coordinate = fromLonLat([longitude, latitude]);
      
      // Update current coordinates
      this.currentLatitude = latitude;
      this.currentLongitude = longitude;

      // Center map on the location with smooth animation
      this.map.getView().animate({
        center: coordinate,
        zoom: 15,
        duration: 1000
      });

      // Update display
      this.updateCurrentCoordinatesDisplay();

      // Clear any existing pinpoint marker
      this.clearPinpointMarker();

      // Add a temporary pinpoint marker
      this.addPinpointMarker(coordinate);

      // Show success message
      this.showInstructions(`📍 Navigated to ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      
      // Hide message after 3 seconds
      setTimeout(() => {
        this.hideInstructions();
      }, 3000);

    } catch (error) {
      console.error('Error navigating to location:', error);
      this.showInstructions('Error navigating to location. Please check coordinates.');
    }
  }

  private addMarkerAtLocation(): void {
    const latInput = document.getElementById('latitudeInput') as HTMLInputElement;
    const lonInput = document.getElementById('longitudeInput') as HTMLInputElement;
    
    if (!latInput || !lonInput) {
      this.showInstructions('Coordinate input fields not found');
      return;
    }

    const latitude = parseFloat(latInput.value);
    const longitude = parseFloat(lonInput.value);

    // Validate coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      this.showInstructions('Please enter valid latitude and longitude values');
      latInput.focus();
      return;
    }

    if (latitude < -90 || latitude > 90) {
      this.showInstructions('Latitude must be between -90 and 90 degrees');
      latInput.focus();
      return;
    }

    if (longitude < -180 || longitude > 180) {
      this.showInstructions('Longitude must be between -180 and 180 degrees');
      lonInput.focus();
      return;
    }

    try {
      // Convert to map coordinates (from WGS84 to Web Mercator)
      const coordinate = fromLonLat([longitude, latitude]);
      
      // Create a permanent marker
      this.createPermanentMarker(coordinate, `📍 Marker at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);

      // Center map on the marker location
      this.map.getView().animate({
        center: coordinate,
        zoom: Math.max(this.map.getView().getZoom() || 10, 12),
        duration: 500
      });

      // Show success message
      this.showInstructions(`📍 Marker added at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      
      // Hide message after 3 seconds
      setTimeout(() => {
        this.hideInstructions();
      }, 3000);

    } catch (error) {
      console.error('Error adding marker:', error);
      this.showInstructions('Error adding marker. Please check coordinates.');
    }
  }

  private addPinpointMarker(coordinate: number[]): void {
    const feature = new Feature({
      geometry: new Point(coordinate),
      type: 'pinpoint',
      timestamp: new Date().toISOString()
    });

    const style = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#e74c3c" stroke="#fff" stroke-width="2"/>
            <circle cx="12" cy="12" r="3" fill="#fff"/>
            <text x="12" y="8" text-anchor="middle" font-size="8" fill="#fff" font-weight="bold">📍</text>
          </svg>
        `),
        scale: 1.5,
        anchor: [0.5, 0.5]
      }),
      text: new Text({
        text: 'PINPOINT',
        font: '10px Arial',
        fill: new Fill({ color: '#e74c3c' }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
        offsetY: -25
      })
    });

    feature.setStyle(style);
    this.pinpointMarker = feature;
    this.getCurrentDrawingSource().addFeature(feature);

    // Auto-clear pinpoint marker after 10 seconds
    setTimeout(() => {
      this.clearPinpointMarker();
    }, 10000);
  }

  private clearPinpointMarker(): void {
    if (this.pinpointMarker) {
      const src = this.getSourceForFeature(this.pinpointMarker);
      if (src) src.removeFeature(this.pinpointMarker);
      this.pinpointMarker = null;
    }
  }

  private createPermanentMarker(coordinate: number[], label: string): void {
    const feature = new Feature({
      geometry: new Point(coordinate),
      type: 'marker',
      label: label,
      timestamp: new Date().toISOString()
    });

    const style = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#3498db"/>
          </svg>
        `),
        scale: 1.2,
        anchor: [0.5, 1]
      }),
      text: new Text({
        text: label,
        font: '12px Arial',
        fill: new Fill({ color: '#2c3e50' }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
        offsetY: -25
      })
    });

    feature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(feature);
  }

  private updateCurrentCoordinatesDisplay(): void {
    const currentCoordsElement = document.getElementById('currentCoords');
    if (currentCoordsElement) {
      const center = this.map.getView().getCenter();
      if (center) {
        // Convert from Web Mercator back to WGS84
        const lonLat = toLonLat(center);
        const lat = lonLat[1];
        const lon = lonLat[0];
        currentCoordsElement.textContent = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
      }
    }
  }

  private setupEnhancedFeatureListeners(): void {
    if (!this.milestoneClickHandlerBound) {
      this.map.on('singleclick', (event: any) => {
        const hit = this.map.forEachFeatureAtPixel(event.pixel, (f: FeatureLike) => f as Feature | null);
        if (!hit) return;
        if (hit.get('type') === 'milestone_tag') {
          this.openMilestoneTagEditor(hit);
        }
      });
      this.milestoneClickHandlerBound = true;
    }

    // Split Segment button
    const splitSegmentBtn = document.getElementById('splitSegment');
    if (splitSegmentBtn) {
      splitSegmentBtn.addEventListener('click', () => {
        this.enableSplitSegment();
      });
    }

    // Bridge Overlay button
    const bridgeOverlayBtn = document.getElementById('bridgeOverlay');
    if (bridgeOverlayBtn) {
      bridgeOverlayBtn.addEventListener('click', () => {
        this.toggleBridgeOverlay();
      });
    }

    // Enhanced numbering system
    // Nomenclature controls
    const nomenclatureInput = document.getElementById('nomenclature');
    if (nomenclatureInput) {
      nomenclatureInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.nomenclature = target.value || '';
      });
    }

    const nomenclatureAbbreviationInput = document.getElementById('nomenclatureAbbreviation');
    if (nomenclatureAbbreviationInput) {
      nomenclatureAbbreviationInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.nomenclatureAbbreviation = target.value || 'Gb';
      });
    }

    const numberingPrefix = document.getElementById('numberingPrefix');
    if (numberingPrefix) {
      numberingPrefix.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.numberingPrefix = target.value;
      });
    }

    const numberingSuffix = document.getElementById('numberingSuffix');
    if (numberingSuffix) {
      numberingSuffix.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.numberingSuffix = target.value;
      });
    }

    const startNumber = document.getElementById('startNumber');
    if (startNumber) {
      startNumber.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.startNumber = parseInt(target.value) || 1;
      });
    }

    // Lane separator controls
    const dividerType = document.getElementById('dividerType');
    if (dividerType) {
      dividerType.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.applyDividerTypeDefaults(target.value);
      });
    }

    const separatorWidth = document.getElementById('separatorWidth');
    if (separatorWidth) {
      separatorWidth.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.separatorWidth = parseFloat(target.value) || 0.3;
        if (this.currentRoadSpecs.dividerType !== 'none') {
          this.currentRoadSpecs.dividerWidth = this.separatorWidth;
        }
      });
    }

    const separatorDistance = document.getElementById('separatorDistance');
    if (separatorDistance) {
      separatorDistance.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.separatorDistance = parseFloat(target.value) || 2;
        this.currentRoadSpecs.separatorDistance = this.separatorDistance;
      });
    }

    // Elevation controls
    const showElevation = document.getElementById('showElevation');
    if (showElevation) {
      showElevation.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showElevation = target.checked;
        this.updateElevationDisplay();
      });
    }

    const elevationProfile = document.getElementById('elevationProfile');
    if (elevationProfile) {
      elevationProfile.addEventListener('click', () => {
        this.showElevationProfile();
      });
    }

    // Feature properties dialog
    this.setupFeaturePropertiesDialog();

    // Enhanced layer management
    this.setupEnhancedLayerManagement();

    // Enhanced measurement tools
    this.setupEnhancedMeasurementTools();
  }

  private enableSplitSegment(): void {
    this.removeInteractions();
    this.showInstructions('Click on a segment to split it at that point');
    
    this.map.on('click', (event: any) => {
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature: FeatureLike) => {
        return feature.getGeometry()?.getType() === 'LineString' ? feature as Feature : null;
      });

      if (feature) {
        this.splitSegmentAtPoint(feature, event.coordinate);
        this.hideInstructions();
      } else {
        this.showInstructions('Please click on a line segment to split it');
      }
    });
  }

  private splitSegmentAtPoint(segment: Feature, splitPoint: number[]): void {
    const geometry = segment.getGeometry() as LineString;
    const coordinates = geometry.getCoordinates();
    
    // Find the closest point on the line
    const closestPoint = this.findClosestPointOnLine(splitPoint, coordinates);
    
    // Split the line into two segments
    const splitIndex = this.findSplitIndex(coordinates, closestPoint);
    
    if (splitIndex > 0 && splitIndex < coordinates.length - 1) {
      const firstPart = coordinates.slice(0, splitIndex + 1);
      const secondPart = coordinates.slice(splitIndex);
      
      // Create new features
      const firstSegment = new Feature({
        geometry: new LineString(firstPart),
        type: 'road',
        properties: { ...segment.getProperties(), splitFrom: true }
      });
      
      const secondSegment = new Feature({
        geometry: new LineString(secondPart),
        type: 'road',
        properties: { ...segment.getProperties(), splitFrom: true }
      });
      
      const src = this.getSourceForFeature(segment) || this.getCurrentDrawingSource();
      src.removeFeature(segment);
      src.addFeature(firstSegment);
      src.addFeature(secondSegment);
      
      this.saveToHistory();
      this.publishBoqGeometry();
      this.showInstructions('Segment split successfully');
      setTimeout(() => this.hideInstructions(), 2000);
    }
  }

  private findSplitIndex(coordinates: number[][], point: number[]): number {
    let minDistance = Infinity;
    let splitIndex = 0;
    
    for (let i = 0; i < coordinates.length - 1; i++) {
      const segmentStart = coordinates[i];
      const segmentEnd = coordinates[i + 1];
      const closestPoint = this.getClosestPointOnSegment(point, segmentStart, segmentEnd);
      const distance = this.calculateDistance(point, closestPoint);
      
      if (distance < minDistance) {
        minDistance = distance;
        splitIndex = i;
      }
    }
    
    return splitIndex;
  }

  private toggleBridgeOverlay(): void {
    // Toggle bridge overlay layer
    const bridgeLayer = this.layerRegistry['bridge'];
    if (bridgeLayer) {
      bridgeLayer.setVisible(!bridgeLayer.getVisible());
    } else {
      this.createBridgeOverlayLayer();
    }
  }

  private createBridgeOverlayLayer(): void {
    const bridgeSource = new VectorSource();
    const bridgeLayer = new VectorLayer({
      source: bridgeSource,
      style: new Style({
        stroke: new Stroke({
          color: '#8B4513',
          width: 4
        }),
        fill: new Fill({
          color: 'rgba(139, 69, 19, 0.3)'
        })
      })
    });
    
    this.map.addLayer(bridgeLayer);
    this.registerLayer('bridge', bridgeLayer);
  }

  private updateElevationDisplay(): void {
    // Update elevation display based on showElevation setting
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source === this.vectorSource) {
          // Update elevation styling
          layer.setStyle((feature: FeatureLike) => {
            return this.getElevationStyle(feature);
          });
        }
      }
    });
  }

  private getElevationStyle(feature: FeatureLike): Style | Style[] {
    const baseStyle = this.getEnhancedDrawingStyle(feature, undefined);
    const baseStyles = Array.isArray(baseStyle) ? baseStyle : [baseStyle as Style];

    if (!this.is3DMode) {
      if (this.showElevation && feature.get('elevation')) {
        const elevation = feature.get('elevation');
        const height = Math.max(0, elevation * 0.1);
        return new Style({
          stroke: new Stroke({
            color: '#e74c3c',
            width: 3
          }),
          fill: new Fill({
            color: `rgba(231, 76, 60, ${0.3 + height * 0.1})`
          }),
          zIndex: height
        });
      }
      return baseStyles[0];
    }

    // 3D viewer styling: add depth shadow and height-sensitive zIndex.
    const elevation = Number(feature.get('elevation') ?? feature.get('height') ?? 18);
    const depth = Math.max(2, Math.min(24, (elevation * this.map3DHeightScale) / 12));
    const shadowStyle = new Style({
      stroke: new Stroke({
        color: 'rgba(15, 23, 42, 0.45)',
        width: depth + 2
      }),
      fill: new Fill({
        color: 'rgba(30, 41, 59, 0.18)'
      }),
      zIndex: depth
    });

    return [shadowStyle, ...baseStyles];
  }

  private showElevationProfile(): void {
    // Show elevation profile for selected features
    const features = this.vectorSource.getFeatures();
    const lineFeatures = features.filter(f => f.getGeometry()?.getType() === 'LineString');
    
    if (lineFeatures.length === 0) {
      this.showInstructions('No line features found for elevation profile');
      return;
    }
    
    // Build list of coords for elevation API (WGS84)
    const allCoords: number[][] = [];
    lineFeatures.forEach(feature => {
      const geometry = feature.getGeometry() as LineString;
      geometry.getCoordinates().forEach(c => allCoords.push(toLonLat(c)));
    });
    // Fetch real elevation when possible (open-elevation.com)
    this.fetchElevationsForCoordinates(allCoords).then(elevations => {
      let idx = 0;
      this.elevationProfileData = lineFeatures.map(feature => {
        const geometry = feature.getGeometry() as LineString;
        const coordinates = geometry.getCoordinates();
        const elevs = coordinates.map(() => elevations[idx++] ?? Math.random() * 100);
        return {
          name: feature.get('name') || 'Unnamed',
          coordinates,
          elevations: elevs
        };
      });
      this.displayElevationProfile();
    }).catch(() => {
      // Fallback mock
      this.elevationProfileData = lineFeatures.map(feature => {
        const geometry = feature.getGeometry() as LineString;
        const coordinates = geometry.getCoordinates();
        return {
          name: feature.get('name') || 'Unnamed',
          coordinates,
          elevations: coordinates.map(() => Math.random() * 100)
        };
      });
      this.displayElevationProfile();
    });
  }

  /** Fetch elevations for [lon, lat] pairs from open-elevation API (batch). */
  private async fetchElevationsForCoordinates(lonLatPairs: number[][]): Promise<number[]> {
    if (!lonLatPairs.length) return [];
    const locations = lonLatPairs.slice(0, 100).map(([lon, lat]) => ({ latitude: lat, longitude: lon }));
    const res = await fetch(this.elevationApiBase, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locations })
    });
    if (!res.ok) throw new Error('Elevation API error');
    const data = await res.json();
    const elevations: number[] = (data.results || []).map((r: { elevation?: number }) => r.elevation ?? 0);
    if (elevations.length < lonLatPairs.length) {
      return [...elevations, ...Array(lonLatPairs.length - elevations.length).fill(0)];
    }
    return elevations;
  }

  private displayElevationProfile(): void {
    // Create elevation profile display
    const profileDialog = document.createElement('div');
    profileDialog.className = 'elevation-profile-dialog';
    profileDialog.innerHTML = `
      <h4>📈 Elevation Profile</h4>
      <div class="profile-chart">
        ${this.elevationProfileData.map(data => `
          <div class="profile-item">
            <h5>${data.name}</h5>
            <div class="elevation-chart">
              ${data.elevations.map((elev: number, i: number) => `
                <div class="elevation-bar" style="height: ${elev}px; width: 10px; background: #3498db; margin: 1px;"></div>
              `).join('')}
            </div>
            <div class="elevation-stats">
              Min: ${Math.min(...data.elevations).toFixed(1)}m | 
              Max: ${Math.max(...data.elevations).toFixed(1)}m | 
              Avg: ${(data.elevations.reduce((a: number, b: number) => a + b, 0) / data.elevations.length).toFixed(1)}m
            </div>
          </div>
        `).join('')}
      </div>
      <button class="btn" onclick="this.parentElement.remove()">Close</button>
    `;
    
    document.body.appendChild(profileDialog);
  }

  private setupFeaturePropertiesDialog(): void {
    this.featurePropertiesDialog = document.getElementById('featurePropertiesDialog');
    
    const saveBtn = document.getElementById('saveFeatureProperties');
    const cancelBtn = document.getElementById('cancelFeatureProperties');
    
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        this.saveFeatureProperties();
      });
    }
    
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        this.hideFeaturePropertiesDialog();
      });
    }
  }

  private showFeaturePropertiesDialog(feature: Feature): void {
    this.selectedFeature = feature;
    
    if (this.featurePropertiesDialog) {
      // Populate form with existing properties
      const nameInput = document.getElementById('featureName') as HTMLInputElement;
      const widthInput = document.getElementById('featureWidth') as HTMLInputElement;
      const depthInput = document.getElementById('featureDepth') as HTMLInputElement;
      const lanesSelect = document.getElementById('featureLanes') as HTMLSelectElement;
      const materialSelect = document.getElementById('featureMaterial') as HTMLSelectElement;
      const statusSelect = document.getElementById('featureStatus') as HTMLSelectElement;
      const commentsTextarea = document.getElementById('featureComments') as HTMLTextAreaElement;
      
      if (nameInput) nameInput.value = feature.get('name') || '';
      if (widthInput) widthInput.value = feature.get('width') || '12';
      if (depthInput) depthInput.value = feature.get('depth') || '0.5';
      if (lanesSelect) lanesSelect.value = feature.get('lanes') || '2';
      if (materialSelect) materialSelect.value = feature.get('material') || 'bituminous';
      if (statusSelect) statusSelect.value = feature.get('status') || 'planned';
      if (commentsTextarea) commentsTextarea.value = feature.get('comments') || '';
      
      this.featurePropertiesDialog!.style.display = 'block';
    }
  }

  private hideFeaturePropertiesDialog(): void {
    if (this.featurePropertiesDialog) {
      this.featurePropertiesDialog.style.display = 'none';
    }
    this.selectedFeature = null;
  }

  private saveFeatureProperties(): void {
    if (!this.selectedFeature) return;
    
    const nameInput = document.getElementById('featureName') as HTMLInputElement;
    const widthInput = document.getElementById('featureWidth') as HTMLInputElement;
    const depthInput = document.getElementById('featureDepth') as HTMLInputElement;
    const lanesSelect = document.getElementById('featureLanes') as HTMLSelectElement;
    const materialSelect = document.getElementById('featureMaterial') as HTMLSelectElement;
    const statusSelect = document.getElementById('featureStatus') as HTMLSelectElement;
    const commentsTextarea = document.getElementById('featureComments') as HTMLTextAreaElement;
    
    // Update feature properties
    this.selectedFeature.set('name', nameInput?.value || '');
    this.selectedFeature.set('width', parseFloat(widthInput?.value || '12'));
    this.selectedFeature.set('depth', parseFloat(depthInput?.value || '0.5'));
    this.selectedFeature.set('lanes', parseInt(lanesSelect?.value || '2'));
    this.selectedFeature.set('material', materialSelect?.value || 'bituminous');
    this.selectedFeature.set('status', statusSelect?.value || 'planned');
    this.selectedFeature.set('comments', commentsTextarea?.value || '');
    
    // Update feature style based on new properties
    this.updateFeatureStyle(this.selectedFeature);
    
    this.hideFeaturePropertiesDialog();
    this.showInstructions('Feature properties saved successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private updateFeatureStyle(feature: Feature): void {
    const status = feature.get('status');
    const width = feature.get('width') || 2;
    
    let color = '#3498db'; // Default blue
    switch (status) {
      case 'planned': color = '#f39c12'; break; // Orange
      case 'under_construction': color = '#e74c3c'; break; // Red
      case 'completed': color = '#27ae60'; break; // Green
      case 'maintenance': color = '#9b59b6'; break; // Purple
    }
    
    const style = new Style({
      stroke: new Stroke({
        color: color,
        width: Math.max(2, width / 2)
      }),
      fill: new Fill({
        color: color + '40' // Add transparency
      })
    });
    
    feature.setStyle(style);
  }

  private setupEnhancedLayerManagement(): void {
    const addNewLayerBtn = document.getElementById('addNewLayer');
    const importLayerBtn = document.getElementById('importLayer');
    const exportAllLayersBtn = document.getElementById('exportAllLayers');

    if (addNewLayerBtn) {
      addNewLayerBtn.addEventListener('click', () => {
        this.showAddLayerDialog();
      });
    }

    if (importLayerBtn) {
      importLayerBtn.addEventListener('click', () => {
        this.showImportLayerDialog();
      });
    }

    if (exportAllLayersBtn) {
      exportAllLayersBtn.addEventListener('click', () => {
        this.exportAllLayers();
      });
    }
  }

  private setupEnhancedMeasurementTools(): void {
    const angleBtn = document.getElementById('angleBtn');
    const slopeBtn = document.getElementById('slopeBtn');
    const volumeBtn = document.getElementById('volumeBtn');

    if (angleBtn) {
      angleBtn.addEventListener('click', () => {
        this.enableAngleMeasurement();
      });
    }

    if (slopeBtn) {
      slopeBtn.addEventListener('click', () => {
        this.enableSlopeMeasurement();
      });
    }

    if (volumeBtn) {
      volumeBtn.addEventListener('click', () => {
        this.enableVolumeMeasurement();
      });
    }
  }

  private enableAngleMeasurement(): void {
    this.removeInteractions();
    this.showInstructions('Click three points to measure angle');
    
    let points: number[][] = [];
    
    this.map.on('click', (event: any) => {
      points.push(event.coordinate);
      
      if (points.length === 1) {
        this.showInstructions('Click second point');
      } else if (points.length === 2) {
        this.showInstructions('Click third point to complete angle measurement');
      } else if (points.length === 3) {
        const angle = this.calculateAngle(points[0], points[1], points[2]);
        this.showMeasurementResult(`Angle: ${angle.toFixed(2)}°`);
        points = [];
        this.hideInstructions();
      }
    });
  }

  private calculateAngle(point1: number[], vertex: number[], point2: number[]): number {
    const v1 = [point1[0] - vertex[0], point1[1] - vertex[1]];
    const v2 = [point2[0] - vertex[0], point2[1] - vertex[1]];
    
    const dot = v1[0] * v2[0] + v1[1] * v2[1];
    const mag1 = Math.sqrt(v1[0] * v1[0] + v1[1] * v1[1]);
    const mag2 = Math.sqrt(v2[0] * v2[0] + v2[1] * v2[1]);
    
    const cosAngle = dot / (mag1 * mag2);
    const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle)));
    
    return (angle * 180) / Math.PI;
  }

  private enableSlopeMeasurement(): void {
    this.removeInteractions();
    this.showInstructions('Click two points to measure slope');
    
    let points: number[][] = [];
    
    this.map.on('click', (event: any) => {
      points.push(event.coordinate);
      
      if (points.length === 1) {
        this.showInstructions('Click second point');
      } else if (points.length === 2) {
        const slope = this.calculateSlope(points[0], points[1]);
        const distance = this.calculateDistance(points[0], points[1]);
        this.showMeasurementResult(`Slope: ${slope.toFixed(2)}% | Distance: ${distance.toFixed(2)}m`);
        points = [];
        this.hideInstructions();
      }
    });
  }

  private calculateSlope(point1: number[], point2: number[]): number {
    const dx = point2[0] - point1[0];
    const dy = point2[1] - point1[1];
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Mock elevation calculation (in real implementation, you'd use elevation data)
    const elevation1 = Math.random() * 100;
    const elevation2 = Math.random() * 100;
    const elevationDiff = elevation2 - elevation1;
    
    return (elevationDiff / distance) * 100;
  }

  private enableVolumeMeasurement(): void {
    this.removeInteractions();
    this.showInstructions('Click points to define area, then enter depth for volume calculation');
    
    let points: number[][] = [];
    
    this.map.on('click', (event: any) => {
      points.push(event.coordinate);
      
      if (points.length < 3) {
        this.showInstructions(`Point ${points.length + 1} - Click to add more points or double-click to finish`);
      }
    });
    
    this.map.on('dblclick', () => {
      if (points.length >= 3) {
        this.showVolumeInputDialog(points);
        points = [];
        this.hideInstructions();
      }
    });
  }

  private showVolumeInputDialog(points: number[][]): void {
    const dialog = document.createElement('div');
    dialog.className = 'volume-input-dialog';
    dialog.innerHTML = `
      <h4>📦 Volume Calculation</h4>
      <div class="control-group">
        <label>Depth (m):</label>
        <input type="number" id="volumeDepth" min="0.1" max="100" step="0.1" value="1.0">
      </div>
      <div class="control-group">
        <label>Material Density (kg/m³):</label>
        <select id="materialDensity">
          <option value="2400">Concrete (2400)</option>
          <option value="2200">Asphalt (2200)</option>
          <option value="1800">Soil (1800)</option>
          <option value="1600">Gravel (1600)</option>
        </select>
      </div>
      <div class="btn-group">
        <button class="btn" onclick="this.calculateVolume()">Calculate</button>
        <button class="btn" onclick="this.parentElement.remove()">Cancel</button>
      </div>
    `;
    
    // Add calculate function
    (dialog as any).calculateVolume = () => {
      const depth = parseFloat((document.getElementById('volumeDepth') as HTMLInputElement)?.value || '1.0');
      const density = parseFloat((document.getElementById('materialDensity') as HTMLSelectElement)?.value || '2400');
      
      const area = this.calculatePolygonArea(points);
      const volume = area * depth;
      const weight = volume * density;
      
      this.showMeasurementResult(
        `Volume: ${volume.toFixed(2)} m³ | Weight: ${(weight/1000).toFixed(2)} tonnes | Area: ${area.toFixed(2)} m²`
      );
      
      dialog.remove();
    };
    
    document.body.appendChild(dialog);
  }

  private calculatePolygonArea(points: number[][]): number {
    let area = 0;
    const n = points.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i][0] * points[j][1];
      area -= points[j][0] * points[i][1];
    }
    
    return Math.abs(area) / 2;
  }

  private setupJunctionButtons(): void {
    // Junction buttons
    document.querySelectorAll('.junction-btn').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const junctionType = target.getAttribute('data-junction') || '';
        this.addJunction(junctionType);
        
        // Update active state
        document.querySelectorAll('.junction-btn').forEach(b => b.classList.remove('active'));
        target.classList.add('active');
      });
    });

    // Connect road to junction button
    const connectRoadBtn = document.getElementById('connectRoadToJunction');
    if (connectRoadBtn) {
      connectRoadBtn.addEventListener('click', () => this.connectRoadToJunction());
    }

    // Connect segments button
    const connectSegmentsBtn = document.getElementById('connectSegments');
    if (connectSegmentsBtn) {
      connectSegmentsBtn.addEventListener('click', () => this.connectSegments());
    }
    const highlightJunctionsBtn = document.getElementById('highlightJunctionsBtn');
    if (highlightJunctionsBtn) {
      highlightJunctionsBtn.addEventListener('click', () => this.highlightJunctionIntersections());
    }

    // Add comment button
    const addCommentBtn = document.getElementById('addComment');
    if (addCommentBtn) {
      addCommentBtn.addEventListener('click', () => this.addSegmentComment());
    }

    // Overlay controls
    const overlayRoadsBtn = document.getElementById('overlayRoads');
    if (overlayRoadsBtn) {
      overlayRoadsBtn.addEventListener('click', () => this.overlayRoads());
    }

    const clearOverlayBtn = document.getElementById('clearOverlay');
    if (clearOverlayBtn) {
      clearOverlayBtn.addEventListener('click', () => this.clearOverlay());
    }

    const applyOffsetBtn = document.getElementById('applyOffset');
    if (applyOffsetBtn) {
      applyOffsetBtn.addEventListener('click', () => this.applyLayerOffset());
    }

    // Overlay opacity control
    const overlayOpacitySlider = document.getElementById('overlayOpacity');
    if (overlayOpacitySlider) {
      overlayOpacitySlider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const opacity = parseFloat(target.value) / 100;
        this.updateOverlayOpacity(opacity);
      });
    }
  }

  private updateOverlayOpacity(opacity: number): void {
    // Update overlay layer opacity
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source === this.roadSource || source === this.projectSource) {
          layer.setOpacity(opacity);
        }
      }
    });
  }

  private setupRoadInfrastructureButtons(): void {
    // Symbol popup button
    const symbolPopupBtn = document.getElementById('symbolPopupBtn');
    if (symbolPopupBtn) {
      symbolPopupBtn.addEventListener('click', () => this.toggleSymbolGrid());
    }

    // 3D view controls
    const enable3DBtn = document.getElementById('enable3D');
    if (enable3DBtn) {
      enable3DBtn.addEventListener('click', () => this.enable3DView());
    }

    const viewTopBtn = document.getElementById('viewTop');
    if (viewTopBtn) {
      viewTopBtn.addEventListener('click', () => this.setViewAngle('top'));
    }

    const viewSideBtn = document.getElementById('viewSide');
    if (viewSideBtn) {
      viewSideBtn.addEventListener('click', () => this.setViewAngle('side'));
    }

    const viewAngleBtn = document.getElementById('viewAngle');
    if (viewAngleBtn) {
      viewAngleBtn.addEventListener('click', () => this.setViewAngle('angle'));
    }

    const heightScaleInput = document.getElementById('heightScale') as HTMLInputElement | null;
    if (heightScaleInput) {
      this.map3DHeightScale = parseFloat(heightScaleInput.value || '3') || 3;
      heightScaleInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.map3DHeightScale = Math.max(1, Math.min(10, parseFloat(target.value || '3') || 3));
        if (this.is3DMode) this.updateElevationDisplay();
      });
    }

    // Measurement tools
    const pointDistanceBtn = document.getElementById('pointDistance');
    if (pointDistanceBtn) {
      pointDistanceBtn.addEventListener('click', () => this.enablePointToPointDistance());
    }

    const endToEndDistanceBtn = document.getElementById('endToEndDistance');
    if (endToEndDistanceBtn) {
      endToEndDistanceBtn.addEventListener('click', () => this.enableEndToEndDistance());
    }

    const calculateGeometryBtn = document.getElementById('calculateGeometry');
    if (calculateGeometryBtn) {
      calculateGeometryBtn.addEventListener('click', () => this.calculateGeometry());
    }

    const takeSnapshotBtn = document.getElementById('takeSnapshot');
    if (takeSnapshotBtn) {
      takeSnapshotBtn.addEventListener('click', () => this.takeSnapshot());
    }

    const calculateGpsErrorBtn = document.getElementById('calculateGpsError');
    if (calculateGpsErrorBtn) {
      calculateGpsErrorBtn.addEventListener('click', () => this.calculateGpsError());
    }

    const generateDummyRoadInfraBtn = document.getElementById('generateDummyRoadInfra');
    if (generateDummyRoadInfraBtn) {
      generateDummyRoadInfraBtn.addEventListener('click', () => this.generateDummyRoadInfrastructureData());
    }
    const drawManualRoadCorridorBtn = document.getElementById('drawManualRoadCorridor');
    if (drawManualRoadCorridorBtn) {
      drawManualRoadCorridorBtn.addEventListener('click', () => this.startManualRoadCorridorDraw());
    }
    const clearDummyRoadInfraBtn = document.getElementById('clearDummyRoadInfra');
    if (clearDummyRoadInfraBtn) {
      clearDummyRoadInfraBtn.addEventListener('click', () => this.clearDummyRoadInfrastructureData());
    }

    // Layer thematic selector
    const layerThematicSelect = document.getElementById('layerThematic');
    if (layerThematicSelect) {
      layerThematicSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.applyLayerThematic(target.value);
      });
    }
    const toggleHeatmapOverlayBtn = document.getElementById('toggleHeatmapOverlayBtn');
    if (toggleHeatmapOverlayBtn) {
      toggleHeatmapOverlayBtn.addEventListener('click', () => this.toggleHeatmapOverlay());
    }
    const loadContourBtn = document.getElementById('loadContourBtn');
    if (loadContourBtn) {
      loadContourBtn.addEventListener('click', () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.geojson,.json';
        input.onchange = (e: Event) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = () => {
              const text = reader.result as string;
              const name = (file.name || 'contours').replace(/\.[^.]+$/, '');
              this.loadContourLayer(text, name);
            };
            reader.readAsText(file);
          }
        };
        input.click();
      });
    }
    const addDemLayerBtn = document.getElementById('addDemLayerBtn');
    if (addDemLayerBtn) {
      addDemLayerBtn.addEventListener('click', () => {
        const url = prompt('DEM tile URL template (use {z}, {x}, {y} for tile coords):', 'https://example.com/terrain/{z}/{x}/{y}.png');
        if (url) this.addDemLayer(url, 'DEM');
      });
    }
  }

  private toggleSymbolGrid(): void {
    const symbolGrid = document.getElementById('symbolGrid');
    if (symbolGrid) {
      symbolGrid.style.display = symbolGrid.style.display === 'none' ? 'grid' : 'none';
    }
  }

  private isMultiLaneRoadSymbol(symbol: string): boolean {
    return /^🛣️[2468]$/.test(symbol);
  }

  private laneCountFromSymbol(symbol: string): number {
    const n = parseInt(symbol.replace(/[^\d]/g, ''), 10);
    return n === 2 || n === 4 || n === 6 || n === 8 ? n : 2;
  }

  private setupSymbolSelection(): void {
    const existingButtons = document.querySelectorAll('.symbol-btn');
    existingButtons.forEach((button) => {
      const newButton = button.cloneNode(true);
      button.parentNode?.replaceChild(newButton, button);
    });

    document.querySelectorAll('.symbol-btn').forEach((button) => {
      button.addEventListener('click', (event) => {
        const btn = (event.target as HTMLElement).closest('.symbol-btn') as HTMLElement | null;
        if (!btn) return;
        const symbol = btn.getAttribute('data-symbol') || '';
        const title = btn.getAttribute('title') || '';
        this.selectGisMapSymbol(symbol, title, btn);
      });
    });
  }

  private selectGisMapSymbol(symbol: string, title: string, activeBtn?: HTMLElement): void {
    this.currentSymbol = symbol;
    this.currentSymbolTitle = title;
    document.querySelectorAll('.symbol-btn').forEach((b) => b.classList.remove('active'));
    activeBtn?.classList.add('active');

    if (this.isMultiLaneRoadSymbol(symbol)) {
      this.currentSymbol = '';
      this.startMultiLaneRoadDrawing(this.laneCountFromSymbol(symbol), title);
      return;
    }

    this.showInstructions(`Selected symbol: ${title || symbol} — click on the map to place`);
    this.removeInteractions();
    this.addInteraction('Point');
  }

  private placeSymbol(coordinate: number[], symbol: string, title: string): void {
    if (this.isMultiLaneRoadSymbol(symbol)) {
      this.startMultiLaneRoadDrawing(this.laneCountFromSymbol(symbol), title);
      return;
    }

    const symbolFeature = new Feature({
      geometry: new Point(coordinate),
      type: 'road_symbol',
      symbol,
      title,
      symbolSize: this.symbolSize,
      timestamp: new Date().toISOString(),
    });
    this.getCurrentDrawingSource().addFeature(symbolFeature);
    this.saveToHistory();
    this.showInstructions(`${title || symbol} placed successfully`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private startMultiLaneRoadDrawing(laneCount: number, title: string): void {
    this.removeInteractions();
    this.showInstructions(`Drawing ${laneCount}-lane road. Click to add points, double-click to finish.`);

    const lanes = Math.max(2, laneCount);
    const sketchStyle = new Style({
      stroke: new Stroke({
        color: 'rgba(44, 62, 80, 0.95)',
        width: Math.max(4, lanes),
        lineDash: lanes > 4 ? [10, 5] : undefined,
      }),
    });

    this.drawInteraction = new Draw({
      source: this.roadSource,
      type: 'LineString',
      style: sketchStyle,
    });

    this.drawInteraction.on('drawend', (event) => {
      const raw = event.feature;
      const geom = raw.getGeometry();
      this.roadSource.removeFeature(raw);
      this.map.removeInteraction(this.drawInteraction!);
      this.drawInteraction = undefined;

      if (!(geom instanceof LineString)) {
        this.showInstructions('Invalid geometry.');
        setTimeout(() => this.hideInstructions(), 1800);
        return;
      }
      const coords = geom.getCoordinates();
      if (coords.length < 2) {
        this.showInstructions('Need at least two points.');
        setTimeout(() => this.hideInstructions(), 2000);
        return;
      }

      const widthM = Math.max(7, lanes * 3.5);
      const n = this.addRoadCorridorFromCenterLine(
        new LineString(coords),
        title || `${lanes}-lane road`,
        this.currentRoadSpecs.type || 'highway',
        lanes,
        widthM,
        Math.max(0.1, this.currentRoadSpecs.depth || 0.5),
        Math.max(0, this.currentRoadSpecs.dividerWidth || 0),
        this.currentRoadSpecs.dividerType || this.dividerType || 'painted',
        Math.max(0.5, this.currentRoadSpecs.separatorDistance || this.separatorDistance || 2),
        this.currentRoadSpecs.surface || 'bituminous'
      );

      this.saveToHistory();
      this.map.getView().changed();
      this.showInstructions(`${title || lanes + '-lane road'} created (${n} features). Select it to edit lanes and width.`);
      setTimeout(() => this.hideInstructions(), 2800);
    });

    this.map.addInteraction(this.drawInteraction);
  }

  private enable3DView(): void {
    this.is3DMode = !this.is3DMode;
    const enableBtn = document.getElementById('enable3D') as HTMLButtonElement | null;

    if (this.is3DMode) {
      this.pre3DBaseMapKey = this.getCurrentBaseMapKey();
      this.changeBaseMap('terrain');
      this.showElevation = true;
      this.setViewAngle('angle');
      if (enableBtn) {
        enableBtn.textContent = '🧭 Disable 3D View';
        enableBtn.classList.add('active');
      }
      this.showInstructions('3D viewer enabled (terrain + perspective + elevation scale)');
    } else {
      this.map3DTiltDeg = 0;
      this.map3DHeadingDeg = 0;
      this.apply3DViewState();
      if (this.pre3DBaseMapKey) this.changeBaseMap(this.pre3DBaseMapKey);
      if (enableBtn) {
        enableBtn.textContent = '🎮 Enable 3D View';
        enableBtn.classList.remove('active');
      }
      this.showInstructions('3D viewer disabled');
    }

    this.updateElevationDisplay();
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private applyLayerThematic(thematic: string): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(feature => {
      const currentStyle = feature.getStyle();
      let newStyle = currentStyle;
      
      switch (thematic) {
        case 'heat':
          newStyle = this.getHeatMapStyle(feature);
          break;
        case 'cluster':
          newStyle = this.getClusterStyle(feature);
          break;
        case 'default':
        default:
          newStyle = this.getDefaultStyle(feature);
          break;
      }
      
      feature.setStyle(newStyle);
    });
    
    this.showInstructions(`Applied ${thematic} thematic styling`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private getHeatMapStyle(feature: Feature): Style {
    const type = feature.get('type') || 'default';
    const colors: { [key: string]: string } = {
      'LineString': '#ff4757',
      'Polygon': '#ff6b6b',
      'Point': '#ffa502',
      'Circle': '#ff6348',
      'junction': '#ff3838',
      'default': '#ff4757'
    };
    
    return new Style({
      stroke: new Stroke({
        color: colors[type] || colors['default'],
        width: 3
      }),
      fill: new Fill({
        color: `${colors[type] || colors['default']}40`
      })
    });
  }

  private getClusterStyle(feature: Feature): Style {
    const type = feature.get('type') || 'default';
    const colors: { [key: string]: string } = {
      'LineString': '#3742fa',
      'Polygon': '#2f3542',
      'Point': '#ff9ff3',
      'Circle': '#54a0ff',
      'junction': '#5f27cd',
      'default': '#3742fa'
    };
    
    return new Style({
      stroke: new Stroke({
        color: colors[type] || colors['default'],
        width: 2,
        lineDash: [5, 5]
      }),
      fill: new Fill({
        color: `${colors[type] || colors['default']}60`
      })
    });
  }

  private getDefaultStyle(feature: Feature): Style {
    return this.getDrawingStyle(feature)[0];
  }

  private setViewAngle(angle: string): void {
    if (!this.is3DMode) {
      this.showInstructions('Enable 3D View first');
      setTimeout(() => this.hideInstructions(), 1200);
      return;
    }

    if (angle === 'top') {
      this.map3DTiltDeg = 12;
      this.map3DHeadingDeg = 0;
    } else if (angle === 'side') {
      this.map3DTiltDeg = 70;
      this.map3DHeadingDeg = 86;
    } else {
      this.map3DTiltDeg = 58;
      this.map3DHeadingDeg = 28;
    }

    this.apply3DViewState();
  }

  private apply3DViewState(): void {
    if (!this.mapElement?.nativeElement || !this.map) return;
    const mapHost = this.mapElement.nativeElement;
    mapHost.classList.toggle('map-3d-enabled', this.is3DMode);
    mapHost.style.setProperty('--map-3d-tilt', `${this.map3DTiltDeg}deg`);
    mapHost.style.setProperty('--map-3d-heading', `${this.map3DHeadingDeg}deg`);

    const view = this.map.getView();
    view.animate({
      rotation: (this.map3DHeadingDeg * Math.PI) / 180,
      duration: 240
    });

    // Keep OL canvas synced after CSS transform changes.
    setTimeout(() => this.map?.updateSize(), 80);
  }

  private getCurrentBaseMapKey(): string {
    const key = Object.keys(this.baseMaps).find(k => this.baseMaps[k] === this.currentBaseMap);
    return key || 'osm';
  }

  private enablePointToPointDistance(): void {
    this.enableDistanceMeasurement();
  }

  private enableEndToEndDistance(): void {
    this.enableDistanceMeasurement();
  }

  private calculateGeometry(): void {
    const features = this.vectorSource.getFeatures();
    
    if (features.length === 0) {
      this.showInstructions('No features found. Draw some shapes first.');
      return;
    }
    
    let totalArea = 0;
    let totalLength = 0;
    let polygonCount = 0;
    let lineCount = 0;
    let circleCount = 0;

    features.forEach(feature => {
      const geom = feature.getGeometry();
      if (geom) {
        const geomType = geom.getType();
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          totalArea += parseFloat(this.calculateArea(feature).squareMeters);
          polygonCount++;
        } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
          totalLength += parseFloat(this.calculateLength(feature).meters);
          lineCount++;
        } else if (geomType === 'Circle') {
          const areaResult = this.calculateArea(feature);
          const lengthResult = this.calculateLength(feature);
          totalArea += parseFloat(areaResult.squareMeters);
          totalLength += parseFloat(lengthResult.meters); // Circumference
          circleCount++;
        }
      }
    });

    const areaKm = totalArea / 1000000;
    const lengthKm = totalLength / 1000;

    let result = `
📊 Geometry Summary:
━━━━━━━━━━━━━━━━━━━━━
Total Features: ${features.length}
• Polygons: ${polygonCount}
• Lines: ${lineCount}
• Circles: ${circleCount}

📐 Total Area: ${totalArea.toFixed(2)} m² (${areaKm.toFixed(4)} km²)
📏 Total Length: ${totalLength.toFixed(2)} m (${lengthKm.toFixed(4)} km)
    `;

    this.showMeasurementResult(result);
  }

  private takeSnapshot(): void {
    this.showInstructions('Taking snapshot...');
    
    // Create a canvas element to capture the map
    const mapElement = this.mapElement.nativeElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      canvas.width = mapElement.offsetWidth;
      canvas.height = mapElement.offsetHeight;
      
      // Draw map background
      ctx.fillStyle = '#f0f2f5';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add timestamp
      ctx.fillStyle = '#000';
      ctx.font = '14px Arial';
      ctx.fillText(`GIS Editor Snapshot - ${new Date().toLocaleString()}`, 10, 30);
      
      // Add project info
      ctx.fillText('Project: NH-44 Delhi-Mumbai', 10, 50);
      ctx.fillText('Scale: 1:50000', 10, 70);
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `gis-snapshot-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
          
          this.showInstructions('Snapshot saved successfully');
          setTimeout(() => this.hideInstructions(), 2000);
        }
      });
    }
  }

  private calculateGpsError(): void {
    // Implementation for GPS error calculation
    console.log('Calculating GPS error...');
  }

  private setupAdvancedControls(): void {
    // Enhanced curve controls
    this.setupCurveControls();

    // Lane separator controls
    const insertSeparatorBtn = document.getElementById('insertSeparator');
    if (insertSeparatorBtn) {
      insertSeparatorBtn.addEventListener('click', () => this.insertSeparator());
    }

    const calculateSeparatorBtn = document.getElementById('calculateSeparator');
    if (calculateSeparatorBtn) {
      calculateSeparatorBtn.addEventListener('click', () => this.calculateSeparator());
    }
    this.syncSeparatorCount();

    // Water features
    const drawNallaBtn = document.getElementById('drawNalla');
    if (drawNallaBtn) {
      drawNallaBtn.addEventListener('click', () => this.drawNalla());
    }

    const addWaterExitBtn = document.getElementById('addWaterExit');
    if (addWaterExitBtn) {
      addWaterExitBtn.addEventListener('click', () => this.addWaterExit());
    }

    // Enhanced advanced controls
    this.setupAdvancedAnalysisControls();
    this.setupExportImportControls();
    this.setupAdvancedToolsControls();
    this.setupWaterFeatureControls();
  }

  private setupCurveControls(): void {
    // Curve type selection
    const curveType = document.getElementById('curveType');
    if (curveType) {
      curveType.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.currentCurveType = target.value as 'bezier' | 'spline' | 'arc' | 'smooth';
      });
    }

    // Start curve drawing
    const startCurveBtn = document.getElementById('startCurveDrawing');
    if (startCurveBtn) {
      startCurveBtn.addEventListener('click', () => {
        this.startCurveDrawing();
      });
    }

    // Finish curve drawing
    const finishCurveBtn = document.getElementById('finishCurveDrawing');
    if (finishCurveBtn) {
      finishCurveBtn.addEventListener('click', () => {
        this.finishCurveDrawing();
      });
    }

    // Cancel curve drawing
    const cancelCurveBtn = document.getElementById('cancelCurveDrawing');
    if (cancelCurveBtn) {
      cancelCurveBtn.addEventListener('click', () => {
        this.cancelCurveDrawing();
      });
    }

    // Curve precision control
    const curvePrecisionSlider = document.getElementById('curvePrecision');
    if (curvePrecisionSlider) {
      curvePrecisionSlider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const precision = parseInt(target.value);
        const precisionValue = document.getElementById('precisionValue');
        if (precisionValue) {
          precisionValue.textContent = precision.toString();
        }
        this.curvePrecision = precision;
        
        // Update preview if currently drawing
        if (this.isCurveDrawing && this.curveControlPoints.length >= 2) {
          this.drawCurvePreview();
        }
      });
    }

    // Curve smoothness control
    const curveSmoothnessSlider = document.getElementById('curveSmoothness');
    if (curveSmoothnessSlider) {
      curveSmoothnessSlider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const smoothness = parseFloat(target.value);
        const smoothnessValue = document.getElementById('smoothnessValue');
        if (smoothnessValue) {
          smoothnessValue.textContent = smoothness.toFixed(1);
        }
        
        // Update preview if currently drawing
        if (this.isCurveDrawing && this.curveControlPoints.length >= 2) {
          this.drawCurvePreview();
        }
      });
    }

    // Show control points toggle
    const showControlPoints = document.getElementById('showControlPoints');
    if (showControlPoints) {
      showControlPoints.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.updateControlPointsVisibility(target.checked);
      });
    }
  }

  private updateControlPointsVisibility(visible: boolean): void {
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      if (feature.get('type') === 'control_point') {
        feature.setStyle(visible ? this.getControlPointStyle(feature) : null);
      }
    });
  }

  private getControlPointStyle(feature: Feature): Style {
    const pointNumber = feature.get('pointNumber');
    return new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: '#e74c3c' }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      }),
      text: new Text({
        text: pointNumber.toString(),
        font: '12px Arial',
        fill: new Fill({ color: '#fff' }),
        stroke: new Stroke({ color: '#000', width: 2 }),
        offsetY: -15
      })
    });
  }

  private addMultiCurveInteraction(): void {
    this.removeInteractions();
    // Implementation for multi-curve
    console.log('Multi-curve interaction');
  }

  private addBezierCurveInteraction(): void {
    this.removeInteractions();
    // Implementation for Bézier curve
    console.log('Bézier curve interaction');
  }

  private addSplineCurveInteraction(): void {
    this.removeInteractions();
    // Implementation for spline curve
    console.log('Spline curve interaction');
  }

  private insertSeparator(): void {
    this.updateRoadSpecs();
    const activeDividerType = this.currentRoadSpecs.dividerType || this.dividerType;
    if (activeDividerType === 'none') {
      this.showInstructions('Choose a divider type (not undivided) before inserting');
      setTimeout(() => this.hideInstructions(), 2500);
      return;
    }

    this.clearSeparatorInsertHandler();
    this.removeInteractions();
    this.showInstructions('Click a road centerline to insert a perpendicular lane separator');

    this.separatorInsertHandler = (event) => {
      const hit = this.map.forEachFeatureAtPixel(
        event.pixel,
        (feat) => {
          const f = feat as Feature;
          if (f.getGeometry()?.getType() !== 'LineString') return undefined;
          if (!this.isEditableRoadFeature(f)) return undefined;
          return f;
        },
        { hitTolerance: 8, layerFilter: (layer) => layer === this.layerRegistry['road'] || layer === this.layerRegistry['drawing'] }
      );

      if (hit) {
        this.createLaneSeparator(hit, event.coordinate);
        this.clearSeparatorInsertHandler();
        this.hideInstructions();
      } else {
        this.showInstructions('Click on a road centerline (generated or drawn road)');
      }
    };
    this.map.on('click', this.separatorInsertHandler);
  }

  private clearSeparatorInsertHandler(): void {
    if (this.separatorInsertHandler) {
      this.map.un('click', this.separatorInsertHandler);
      this.separatorInsertHandler = null;
    }
  }

  private createLaneSeparator(roadFeature: Feature, coordinate: number[]): void {
    const geometry = roadFeature.getGeometry() as LineString;
    const roadCoords = geometry.getCoordinates();
    const dividerType = String(
      this.currentRoadSpecs.dividerType || roadFeature.get('dividerType') || this.dividerType || 'painted'
    );
    if (dividerType === 'none') {
      this.showInstructions('Divider type is undivided — pick a divider type first');
      setTimeout(() => this.hideInstructions(), 2000);
      return;
    }

    const spanM = this.roadCorridorSpanMeters(roadFeature);
    const separatorLine = this.createPerpendicularSeparator(roadCoords, coordinate, spanM);

    const separatorFeature = new Feature({
      geometry: new LineString(separatorLine),
      type: 'lane_separator',
      dividerType,
      width: this.currentRoadSpecs.dividerWidth || this.separatorWidth,
      distance: this.separatorDistance,
      roadId: roadFeature.getId(),
      corridorId: roadFeature.get('corridorId') || null,
    });

    separatorFeature.setStyle(this.getLaneSeparatorStyle(dividerType));
    this.separatorSource.addFeature(separatorFeature);
    this.syncSeparatorCount();

    this.showInstructions('Lane separator inserted on separator layer');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /** Full cross-road span for perpendicular separators (road + shoulders + median). */
  private roadCorridorSpanMeters(roadFeature: Feature): number {
    const specs = roadFeature.get('roadSpecs') || {};
    const roadWidthM = Number(roadFeature.get('roadWidthM') ?? specs.width ?? this.currentRoadSpecs.width ?? 12);
    const lanes = Number(roadFeature.get('lanes') ?? specs.lanes ?? this.currentRoadSpecs.lanes ?? 2);
    const dividerWidthM = Number(roadFeature.get('dividerWidthM') ?? specs.dividerWidth ?? this.currentRoadSpecs.dividerWidth ?? 0);
    const shoulderM = 2.5;
    const medianM = lanes >= 4 && dividerWidthM > 0 ? dividerWidthM : 0;
    return Math.max(6, roadWidthM + shoulderM * 2 + medianM);
  }

  private createPerpendicularSeparator(roadCoords: number[][], point: number[], spanM: number): [number, number][] {
    const closestPoint = this.findClosestPointOnLine(point, roadCoords);
    const segmentIndex = this.findSegmentIndex(roadCoords, closestPoint);
    const segmentStart = roadCoords[segmentIndex];
    const segmentEnd = roadCoords[segmentIndex + 1] || segmentStart;

    const dx = segmentEnd[0] - segmentStart[0];
    const dy = segmentEnd[1] - segmentStart[1];
    const length = Math.sqrt(dx * dx + dy * dy) || 1;

    const perpX = -dy / length;
    const perpY = dx / length;
    const halfSpan = Math.max(3, spanM) / 2;

    return [
      [closestPoint[0] + perpX * halfSpan, closestPoint[1] + perpY * halfSpan],
      [closestPoint[0] - perpX * halfSpan, closestPoint[1] - perpY * halfSpan],
    ];
  }

  private findSegmentIndex(coords: number[][], point: number[]): number {
    let minDistance = Infinity;
    let segmentIndex = 0;
    
    for (let i = 0; i < coords.length - 1; i++) {
      const closestPoint = this.getClosestPointOnSegment(point, coords[i], coords[i + 1]);
      const distance = this.calculateDistance(point, closestPoint);
      
      if (distance < minDistance) {
        minDistance = distance;
        segmentIndex = i;
      }
    }
    
    return segmentIndex;
  }

  private getLaneSeparatorStyle(dividerType: string): Style {
    const spec = getRoadDividerType(dividerType);
    return new Style({
      stroke: new Stroke({
        color: spec.strokeColor,
        width: spec.strokePx,
        lineDash: spec.lineDash
      })
    });
  }

  private calculateSeparator(): void {
    const separators = this.separatorSource.getFeatures().filter((f) => f.get('type') === 'lane_separator');
    
    let totalLength = 0;
    let totalCost = 0;
    
    separators.forEach(separator => {
      const geometry = separator.getGeometry() as LineString;
      const length = geometry.getLength();
      totalLength += length;
      
      const dividerType = separator.get('dividerType');
      const costPerMeter = this.getSeparatorCost(dividerType);
      totalCost += length * costPerMeter;
    });
    
    const result = `
      Total Separators: ${separators.length}
      Total Length: ${totalLength.toFixed(2)}m
      Estimated Cost: ₹${totalCost.toFixed(2)}
      Average Length: ${separators.length > 0 ? (totalLength / separators.length).toFixed(2) : 0}m
    `;
    
    this.showMeasurementResult(result);
  }

  private getSeparatorCost(dividerType: string): number {
    return separatorCostPerMeter(dividerType);
  }

  private syncSeparatorCount(): void {
    this.separatorCount = this.separatorSource.getFeatures().filter((f) => f.get('type') === 'lane_separator').length;
    const countElement = document.getElementById('separatorCount');
    if (countElement) {
      countElement.textContent = this.separatorCount.toString();
    }
  }

  private drawNalla(): void {
    this.removeInteractions();
    this.showInstructions('Click points to draw nalla/stream. Right-click to finish.');
    
    let nallaPoints: number[][] = [];
    
    this.map.on('click', (event: any) => {
      nallaPoints.push(event.coordinate);
      
      if (nallaPoints.length === 1) {
        this.showInstructions('Click more points or right-click to finish');
      } else {
        this.drawNallaPreview(nallaPoints);
      }
    });
    
    this.map.getViewport().addEventListener('contextmenu', (event: any) => {
      event.preventDefault();
      if (nallaPoints.length >= 2) {
        this.finishNallaDrawing(nallaPoints);
        nallaPoints = [];
        this.hideInstructions();
      }
    });
  }

  private drawNallaPreview(points: number[][]): void {
    // Remove existing preview
    this.removeNallaPreview();
    
    const nallaFeature = new Feature({
      geometry: new LineString(points),
      type: 'nalla_preview'
    });
    
    const style = new Style({
      stroke: new Stroke({
        color: '#00bfff',
        width: 3,
        lineDash: [10, 5]
      })
    });
    
    nallaFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(nallaFeature);
  }

  private removeNallaPreview(): void {
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      if (feature.get('type') === 'nalla_preview') {
        this.vectorSource.removeFeature(feature);
      }
    });
  }

  private finishNallaDrawing(points: number[][]): void {
    this.removeNallaPreview();
    
    const nallaFeature = new Feature({
      geometry: new LineString(points),
      type: 'nalla',
      flowDirection: this.waterFlowDirection,
      width: 2,
      depth: 1
    });
    
    const style = new Style({
      stroke: new Stroke({
        color: '#00bfff',
        width: 3
      })
    });
    
    nallaFeature.setStyle(style);
    this.vectorSource.addFeature(nallaFeature);
    
    this.showInstructions('Nalla/stream created successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private addWaterExit(): void {
    this.removeInteractions();
    this.showInstructions('Click to place water exit point');
    
    this.map.on('click', (event: any) => {
      const waterExitFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'water_exit',
        flowDirection: this.waterFlowDirection,
        capacity: 1000 // liters per hour
      });
      
      const style = new Style({
        image: new Icon({
          src: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="#00bfff"/>
            </svg>
          `),
          scale: 1.5,
          anchor: [0.5, 0.5]
        })
      });
      
      waterExitFeature.setStyle(style);
      this.getCurrentDrawingSource().addFeature(waterExitFeature);
      
      this.hideInstructions();
      this.showInstructions('Water exit added successfully');
      setTimeout(() => this.hideInstructions(), 2000);
    });
  }

  private setupAdvancedAnalysisControls(): void {
    // Analysis buttons
    const analyzeRoadNetworkBtn = document.getElementById('analyzeRoadNetwork');
    if (analyzeRoadNetworkBtn) {
      analyzeRoadNetworkBtn.addEventListener('click', () => this.analyzeRoadNetwork());
    }

    const analyzeTrafficFlowBtn = document.getElementById('analyzeTrafficFlow');
    if (analyzeTrafficFlowBtn) {
      analyzeTrafficFlowBtn.addEventListener('click', () => this.analyzeTrafficFlow());
    }

    const analyzeDrainageBtn = document.getElementById('analyzeDrainage');
    if (analyzeDrainageBtn) {
      analyzeDrainageBtn.addEventListener('click', () => this.analyzeDrainage());
    }

    const analyzeElevationBtn = document.getElementById('analyzeElevation');
    if (analyzeElevationBtn) {
      analyzeElevationBtn.addEventListener('click', () => this.analyzeElevation());
    }

    // Analysis controls
    const analysisBuffer = document.getElementById('analysisBuffer');
    if (analysisBuffer) {
      analysisBuffer.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.analysisBuffer = parseInt(target.value);
      });
    }

    const analysisType = document.getElementById('analysisType');
    if (analysisType) {
      analysisType.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.analysisType = target.value;
      });
    }
  }

  private setupExportImportControls(): void {
    // Export buttons
    const exportRoadSpecsBtn = document.getElementById('exportRoadSpecs');
    if (exportRoadSpecsBtn) {
      exportRoadSpecsBtn.addEventListener('click', () => this.exportRoadSpecs());
    }

    const exportAnalysisBtn = document.getElementById('exportAnalysis');
    if (exportAnalysisBtn) {
      exportAnalysisBtn.addEventListener('click', () => this.exportAnalysis());
    }

    // Import buttons
    const importCADBtn = document.getElementById('importCAD');
    if (importCADBtn) {
      importCADBtn.addEventListener('click', () => this.importCAD());
    }

    const importGISBtn = document.getElementById('importGIS');
    if (importGISBtn) {
      importGISBtn.addEventListener('click', () => this.importGIS());
    }

    // Export controls
    const exportFormat = document.getElementById('exportFormat');
    if (exportFormat) {
      exportFormat.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.exportFormat = target.value;
      });
    }

    const includeMetadata = document.getElementById('includeMetadata');
    if (includeMetadata) {
      includeMetadata.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.includeMetadata = target.checked;
      });
    }
  }

  private setupAdvancedToolsControls(): void {
    // Advanced tools buttons
    const autoGenerateRoadsBtn = document.getElementById('autoGenerateRoads');
    if (autoGenerateRoadsBtn) {
      autoGenerateRoadsBtn.addEventListener('click', () => this.autoGenerateRoads());
    }

    const optimizeNetworkBtn = document.getElementById('optimizeNetwork');
    if (optimizeNetworkBtn) {
      optimizeNetworkBtn.addEventListener('click', () => this.optimizeNetwork());
    }

    const validateGeometryBtn = document.getElementById('validateGeometry');
    if (validateGeometryBtn) {
      validateGeometryBtn.addEventListener('click', () => this.validateGeometry());
    }

    const repairGeometryBtn = document.getElementById('repairGeometry');
    if (repairGeometryBtn) {
      repairGeometryBtn.addEventListener('click', () => this.repairGeometry());
    }

    // Validation controls
    const validationRules = document.getElementById('validationRules');
    if (validationRules) {
      validationRules.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.validationRules = target.value;
      });
    }

    const autoFixIssues = document.getElementById('autoFixIssues');
    if (autoFixIssues) {
      autoFixIssues.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.autoFixIssues = target.checked;
      });
    }
  }

  private setupAssetControls(): void {
    // Asset type selector
    const assetTypeSelect = document.getElementById('assetType');
    if (assetTypeSelect) {
      assetTypeSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.updateAssetVisibility();
      });
    }

    // Nomenclature controls
    const showNomenclatureCheckbox = document.getElementById('showNomenclature');
    if (showNomenclatureCheckbox) {
      showNomenclatureCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showNomenclature = target.checked;
        this.updateNomenclatureVisibility();
      });
    }

    const showOvergroundCheckbox = document.getElementById('showOverground');
    if (showOvergroundCheckbox) {
      showOvergroundCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showOverground = target.checked;
        this.updateAssetVisibility();
      });
    }

    const showUndergroundCheckbox = document.getElementById('showUnderground');
    if (showUndergroundCheckbox) {
      showUndergroundCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showUnderground = target.checked;
        this.updateAssetVisibility();
      });
    }

    const showCrossoverCheckbox = document.getElementById('showCrossover');
    if (showCrossoverCheckbox) {
      showCrossoverCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showCrossover = target.checked;
        this.updateAssetVisibility();
      });
    }
  }

  private updateAssetVisibility(): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(feature => {
      const assetType = feature.get('assetType') || feature.get('grade') || 'overground';
      const isVisible =
        (assetType === 'overground' && this.showOverground) ||
        (assetType === 'underground' && this.showUnderground) ||
        (assetType === 'crossover' && this.showCrossover) ||
        (assetType === 'at_grade' && this.showOverground);
      
      feature.setStyle(isVisible ? feature.getStyle() : null);
    });
  }

  private updateNomenclatureVisibility(): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(feature => {
      const currentStyle = feature.getStyle();
      if (currentStyle) {
        const styles = Array.isArray(currentStyle) ? currentStyle : [currentStyle];
        
        styles.forEach(style => {
          if (style instanceof Style) {
            const text = style.getText();
            if (text) {
              // Update text visibility by recreating the style
              const newStyle = new Style({
                text: new Text({
                  text: text.getText(),
                  font: text.getFont(),
                  fill: text.getFill(),
                  stroke: text.getStroke(),
                  offsetX: text.getOffsetX(),
                  offsetY: text.getOffsetY(),
                  rotation: text.getRotation(),
                  scale: text.getScale()
                })
              });
              feature.setStyle(newStyle);
            }
          }
        });
      }
    });
  }

  private setupWaterFeatureControls(): void {
    // Water flow direction
    const waterFlowDirection = document.getElementById('waterFlowDirection');
    if (waterFlowDirection) {
      waterFlowDirection.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.waterFlowDirection = target.value;
      });
    }

    // Add culvert button
    const addCulvertBtn = document.getElementById('addCulvert');
    if (addCulvertBtn) {
      addCulvertBtn.addEventListener('click', () => this.addCulvert());
    }
  }

  // Analysis Methods
  private analyzeRoadNetwork(): void {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    
    let totalLength = 0;
    let intersectionCount = 0;
    let averageWidth = 0;
    
    roads.forEach(road => {
      const geometry = road.getGeometry() as LineString;
      totalLength += geometry.getLength();
      averageWidth += road.get('thickness') || 2;
    });
    
    averageWidth = roads.length > 0 ? averageWidth / roads.length : 0;
    
    // Find intersections
    for (let i = 0; i < roads.length; i++) {
      for (let j = i + 1; j < roads.length; j++) {
        if (this.checkIntersection(roads[i], roads[j])) {
          intersectionCount++;
        }
      }
    }
    
    const result = `
      Road Network Analysis:
      Total Roads: ${roads.length}
      Total Length: ${totalLength.toFixed(2)}m
      Intersections: ${intersectionCount}
      Average Width: ${averageWidth.toFixed(1)}m
      Network Density: ${(roads.length / (totalLength / 1000)).toFixed(2)} roads/km
    `;
    
    this.showMeasurementResult(result);
  }

  private analyzeTrafficFlow(): void {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    
    let totalCapacity = 0;
    let bottleneckCount = 0;
    
    roads.forEach(road => {
      const lanes = road.get('lanes') || 2;
      const width = road.get('thickness') || 2;
      const capacity = lanes * 1000; // vehicles per hour per lane
      totalCapacity += capacity;
      
      if (width < 3 || lanes < 2) {
        bottleneckCount++;
      }
    });
    
    const result = `
      Traffic Flow Analysis:
      Total Capacity: ${totalCapacity.toLocaleString()} vehicles/hour
      Bottlenecks: ${bottleneckCount}
      Average Capacity: ${roads.length > 0 ? (totalCapacity / roads.length).toFixed(0) : 0} vehicles/hour
      Network Efficiency: ${((roads.length - bottleneckCount) / roads.length * 100).toFixed(1)}%
    `;
    
    this.showMeasurementResult(result);
  }

  private analyzeDrainage(): void {
    const features = this.vectorSource.getFeatures();
    const nallas = features.filter(f => f.get('type') === 'nalla');
    const waterExits = features.filter(f => f.get('type') === 'water_exit');
    
    let totalDrainageLength = 0;
    let totalCapacity = 0;
    
    nallas.forEach(nalla => {
      const geometry = nalla.getGeometry() as LineString;
      totalDrainageLength += geometry.getLength();
    });
    
    waterExits.forEach(exit => {
      totalCapacity += exit.get('capacity') || 1000;
    });
    
    const result = `
      Drainage Analysis:
      Nallas/Streams: ${nallas.length}
      Total Length: ${totalDrainageLength.toFixed(2)}m
      Water Exits: ${waterExits.length}
      Total Capacity: ${totalCapacity.toLocaleString()} L/hour
      Coverage: ${(totalDrainageLength / 1000).toFixed(2)} km
    `;
    
    this.showMeasurementResult(result);
  }

  private analyzeElevation(): void {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    
    let totalElevationChange = 0;
    let steepSegments = 0;
    
    roads.forEach(road => {
      const geometry = road.getGeometry() as LineString;
      const coords = geometry.getCoordinates();
      
      for (let i = 1; i < coords.length; i++) {
        const elevationChange = Math.abs(coords[i][2] - coords[i-1][2]) || 0;
        totalElevationChange += elevationChange;
        
        if (elevationChange > 5) { // 5m elevation change
          steepSegments++;
        }
      }
    });
    
    const result = `
      Elevation Analysis:
      Total Elevation Change: ${totalElevationChange.toFixed(2)}m
      Steep Segments (>5m): ${steepSegments}
      Average Grade: ${roads.length > 0 ? (totalElevationChange / roads.length).toFixed(2) : 0}m
      Terrain Difficulty: ${steepSegments > roads.length * 0.3 ? 'High' : 'Moderate'}
    `;
    
    this.showMeasurementResult(result);
  }

  private checkIntersection(road1: Feature, road2: Feature): boolean {
    const geom1 = road1.getGeometry() as LineString;
    const geom2 = road2.getGeometry() as LineString;
    
    const coords1 = geom1.getCoordinates();
    const coords2 = geom2.getCoordinates();
    
    // Simple intersection check
    for (let i = 0; i < coords1.length - 1; i++) {
      for (let j = 0; j < coords2.length - 1; j++) {
        if (this.linesIntersect(coords1[i], coords1[i+1], coords2[j], coords2[j+1])) {
          return true;
        }
      }
    }
    
    return false;
  }

  private linesIntersect(p1: number[], p2: number[], p3: number[], p4: number[]): boolean {
    return this.getSegmentIntersection(p1, p2, p3, p4) !== null;
  }

  /** Return intersection point [x,y] of segments p1-p2 and p3-p4, or null if none. */
  private getSegmentIntersection(p1: number[], p2: number[], p3: number[], p4: number[]): number[] | null {
    const x1 = p1[0], y1 = p1[1];
    const x2 = p2[0], y2 = p2[1];
    const x3 = p3[0], y3 = p3[1];
    const x4 = p4[0], y4 = p4[1];
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-10) return null;
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
    }
    return null;
  }

  private intersectionHighlightSource: VectorSource | null = null;
  private intersectionHighlightLayer: VectorLayer | null = null;

  /** Auto-detect line-line intersections and highlight them with point markers. */
  highlightJunctionIntersections(): void {
    if (this.intersectionHighlightLayer) {
      this.map.removeLayer(this.intersectionHighlightLayer);
      this.intersectionHighlightLayer = null;
      this.intersectionHighlightSource = null;
      this.showInstructions('Intersection highlights cleared');
      return;
    }
    const source = this.getCurrentDrawingSource();
    const features = source.getFeatures();
    const lines = features.filter(f => {
      const g = f.getGeometry();
      return g && (g.getType() === 'LineString' || g.getType() === 'MultiLineString');
    }) as Feature[];
    const points: number[][] = [];
    const tol = 0.1;
    for (let i = 0; i < lines.length; i++) {
      const coords1 = (lines[i].getGeometry() as LineString).getCoordinates();
      for (let j = i + 1; j < lines.length; j++) {
        const coords2 = (lines[j].getGeometry() as LineString).getCoordinates();
        for (let a = 0; a < coords1.length - 1; a++) {
          for (let b = 0; b < coords2.length - 1; b++) {
            const pt = this.getSegmentIntersection(coords1[a], coords1[a + 1], coords2[b], coords2[b + 1]);
            if (pt && !points.some(p => Math.abs(p[0] - pt[0]) < tol && Math.abs(p[1] - pt[1]) < tol)) {
              points.push(pt);
            }
          }
        }
      }
    }
    this.intersectionHighlightSource = new VectorSource();
    points.forEach(pt => {
      this.intersectionHighlightSource!.addFeature(new Feature({
        geometry: new Point(pt),
        type: 'junction-highlight'
      }));
    });
    this.intersectionHighlightLayer = new VectorLayer({
      source: this.intersectionHighlightSource,
      style: new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: 'rgba(231, 76, 60, 0.9)' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }),
        zIndex: 60
      }),
      zIndex: 60
    });
    this.map.addLayer(this.intersectionHighlightLayer);
    this.showInstructions(`${points.length} junction(s) detected and highlighted`);
  }

  // Export/Import Methods
  private exportRoadSpecs(): void {
    const features = this.vectorSource.getFeatures();
    const roadSpecs = features.map(feature => ({
      id: feature.getId(),
      type: feature.get('type'),
      geometry: this.format.writeGeometry(feature.getGeometry()!),
      properties: {
        thickness: feature.get('thickness'),
        lanes: feature.get('lanes'),
        material: feature.get('material'),
        status: feature.get('status'),
        width: feature.get('width'),
        depth: feature.get('depth')
      }
    }));
    
    const exportData = {
      format: this.exportFormat,
      timestamp: new Date().toISOString(),
      metadata: this.includeMetadata ? {
        project: 'NH-44 Delhi-Mumbai',
        version: '1.0',
        totalFeatures: roadSpecs.length
      } : undefined,
      features: roadSpecs
    };
    
    this.downloadFile(JSON.stringify(exportData, null, 2), 'road-specs.json');
    this.showInstructions('Road specifications exported successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private exportAnalysis(): void {
    const analysisData = {
      roadNetwork: this.getRoadNetworkStats(),
      trafficFlow: this.getTrafficFlowStats(),
      drainage: this.getDrainageStats(),
      elevation: this.getElevationStats(),
      timestamp: new Date().toISOString()
    };
    
    this.downloadFile(JSON.stringify(analysisData, null, 2), 'analysis-report.json');
    this.showInstructions('Analysis report exported successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private readonly cadViewerDialog = inject(CadViewerDialogService);
  private readonly gisStore = inject(GisFeatureStoreService);

  private importCAD(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.dxf,.dwg';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.processCADFile(file);
      }
    };
    input.click();
  }

  private importGIS(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json,.kml,.gpx,.zip';
    input.onchange = (event: any) => {
      const file: File | undefined = event.target.files[0];
      if (!file) return;
      if (/\.(zip|shp)$/i.test(file.name)) this.importShapefileToLrs(file);
      else this.processGISFile(file);
    };
    input.click();
  }

  private processCADFile(file: File): void {
    this.showInstructions(`Uploading ${file.name}…`);
    const projectId = Number(this.projectId);
    this.apiService
      .uploadCadDrawing(file, {
        contextType: 'gis',
        contextId: this.projectId,
        projectId: Number.isFinite(projectId) && projectId > 0 ? projectId : null,
      })
      .subscribe({
        next: (drawing) => {
          this.hideInstructions();
          this.cadViewerDialog.open({ drawing });
        },
        error: (err) => {
          this.showInstructions(err?.error?.error || 'CAD upload failed');
          setTimeout(() => this.hideInstructions(), 3000);
        },
      });
  }

  private processGISFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const features = this.gisStore.readVectorFile(e.target?.result as string);
        this.getCurrentDrawingSource().addFeatures(features);
        this.showInstructions(`Imported ${features.length} feature(s) from ${file.name}`);
        setTimeout(() => this.hideInstructions(), 2000);
      } catch (error) {
        this.showInstructions('Error importing GIS file');
        setTimeout(() => this.hideInstructions(), 3000);
      }
    };
    reader.readAsText(file);
  }

  private importGeoJSONData(data: any): void {
    const features = this.gisStore.readGeoJsonText(data);
    features.forEach((f) => f.setId(undefined));
    this.getCurrentDrawingSource().addFeatures(features.filter((f) => !!f.getGeometry()));
  }

  // Advanced Tools Methods
  private autoGenerateRoads(): void {
    this.showInstructions('Auto-generating road network...');
    
    // Mock road generation
    setTimeout(() => {
      const mockRoads = this.generateMockRoadNetwork();
      mockRoads.forEach(road => {
        this.getCurrentDrawingSource().addFeature(road);
      });
      
      this.showInstructions(`Generated ${mockRoads.length} road segments`);
      setTimeout(() => this.hideInstructions(), 3000);
    }, 2000);
  }

  private generateMockRoadNetwork(): Feature[] {
    const roads: Feature[] = [];
    const center = this.map.getView().getCenter()!;
    
    // Generate a simple grid network
    for (let i = 0; i < 5; i++) {
      const startX = center[0] + (i - 2) * 1000;
      const endX = center[0] + (i - 2) * 1000;
      const startY = center[1] - 2000;
      const endY = center[1] + 2000;
      
      const road = new Feature({
        geometry: new LineString([[startX, startY], [endX, endY]]),
        type: 'auto_generated_road',
        thickness: 3,
        lanes: 2
      });
      
      road.setStyle(new Style({
        stroke: new Stroke({
          color: '#ff6b6b',
          width: 3
        })
      }));
      
      roads.push(road);
    }
    
    return roads;
  }

  private generateDummyRoadInfrastructureData(): void {
    this.updateRoadSpecs();
    const center = this.map.getView().getCenter();
    if (!center) return;

    // Remove previously generated dummy infra first.
    this.roadSource.getFeatures()
      .filter(f => f.get('dummyRoadInfra') === true)
      .forEach(f => this.roadSource.removeFeature(f));

    type RoadSeed = {
      centerLine: number[][];
      lanes: number;
      roadWidthM: number;
      dividerWidthM: number;
      material: string;
      roadType: string;
      depthM: number;
      name: string;
    };

    const selectedRoadType = this.currentRoadSpecs.type || 'highway';
    const selectedLanes = Math.max(1, this.currentRoadSpecs.lanes || 2);
    const selectedWidth = Math.max(3.5, this.currentRoadSpecs.width || 12);
    const selectedDividerWidth = Math.max(0, this.currentRoadSpecs.dividerWidth || 0);
    const selectedDividerType = this.currentRoadSpecs.dividerType || this.dividerType || 'painted';
    const selectedSeparatorDistance = Math.max(0.5, this.currentRoadSpecs.separatorDistance || this.separatorDistance || 2);
    const selectedMaterial = this.currentRoadSpecs.surface || 'bituminous';
    const selectedDepth = Math.max(0.1, this.currentRoadSpecs.depth || 0.5);

    // Geometry shape varies by selected road type, but all generated features use selected specs.
    const typePattern: Record<string, number[][]> = {
      highway: [
        [center[0] - 2400, center[1] - 650],
        [center[0] - 1200, center[1] - 520],
        [center[0], center[1] - 430],
        [center[0] + 1300, center[1] - 470],
        [center[0] + 2500, center[1] - 320]
      ],
      arterial: [
        [center[0] - 2100, center[1] - 420],
        [center[0] - 900, center[1] - 320],
        [center[0] + 200, center[1] - 280],
        [center[0] + 1500, center[1] - 230]
      ],
      collector: [
        [center[0] - 1800, center[1] - 250],
        [center[0] - 600, center[1] - 180],
        [center[0] + 700, center[1] - 160],
        [center[0] + 1700, center[1] - 130]
      ],
      local: [
        [center[0] - 1400, center[1] - 120],
        [center[0] - 350, center[1] - 85],
        [center[0] + 650, center[1] - 95],
        [center[0] + 1250, center[1] - 70]
      ],
      service: [
        [center[0] - 1500, center[1] + 260],
        [center[0] - 500, center[1] + 220],
        [center[0] + 500, center[1] + 210],
        [center[0] + 1500, center[1] + 240]
      ]
    };

    const mainLine = typePattern[selectedRoadType] || typePattern['highway'];

    const seeds: RoadSeed[] = [
      {
        centerLine: mainLine,
        lanes: selectedLanes,
        roadWidthM: selectedWidth,
        dividerWidthM: selectedDividerWidth,
        material: selectedMaterial,
        roadType: selectedRoadType,
        depthM: selectedDepth,
        name: `${selectedRoadType.toUpperCase()} Corridor`
      }
    ];

    let totalAdded = 0;
    seeds.forEach((seed) => {
      const centerGeom = new LineString(seed.centerLine);
      totalAdded += this.addRoadCorridorFromCenterLine(
        centerGeom,
        seed.name,
        seed.roadType,
        seed.lanes,
        seed.roadWidthM,
        seed.depthM,
        seed.dividerWidthM,
        selectedDividerType,
        selectedSeparatorDistance,
        seed.material
      );
    });

    this.showInstructions(`Generated dummy road infra (${totalAdded} features): single corridor with selected parameters`);
    setTimeout(() => this.hideInstructions(), 2500);
  }

  /**
   * Adds corridor geometry, lane guides, and label (same styling as Draw Road).
   * @returns Approximate feature count added for status messages.
   */
  private addRoadCorridorFromCenterLine(
    centerGeom: LineString,
    corridorName: string,
    roadType: string,
    lanes: number,
    roadWidthM: number,
    depthM: number,
    dividerWidthM: number,
    dividerType: string,
    separatorDistance: number,
    material: string,
    opts?: { source?: VectorSource; demoOverlay?: boolean }
  ): number {
    const source = opts?.source || this.roadSource;
    const geom = centerGeom.clone() as LineString;
    const corridorId = `corridor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const demoProps = opts?.demoOverlay
      ? { demoInfra: true, demoCorridorOverlay: true }
      : {};
    const centerFeature = new Feature({
      geometry: geom,
      type: 'dummy_road_corridor',
      dummyRoadInfra: true,
      corridorId,
      info: `${corridorName} | ${roadType} | ${lanes}L | W ${roadWidthM.toFixed(1)}m | D ${depthM.toFixed(1)}m | Divider ${dividerWidthM.toFixed(1)}m (${dividerType}) | Sep ${separatorDistance.toFixed(1)}m | ${material}`,
      lanes,
      roadWidthM,
      dividerWidthM,
      dividerType,
      depthM,
      material,
      roadType,
      name: corridorName,
      ...demoProps,
      roadSpecs: {
        type: roadType,
        width: roadWidthM,
        depth: depthM,
        lanes,
        surface: material,
        separatorDistance,
        dividerWidth: dividerWidthM,
        dividerType,
      },
    });
    source.addFeature(centerFeature);
    let added = 1;
    const beforeGuideCount = source.getFeatures().length;
    this.addMilestoneLaneGuides(
      source,
      geom,
      lanes,
      roadWidthM,
      dividerWidthM,
      material,
      true,
      corridorId,
      dividerType,
      opts?.demoOverlay === true
    );
    added += Math.max(0, source.getFeatures().length - beforeGuideCount);

    const labelPt = geom.getCoordinateAt(0.5);
    const textFeature = new Feature({
      geometry: new Point(labelPt),
      type: 'dummy_pavement_label',
      dummyRoadInfra: true,
      corridorId,
      info: `Pavement: ${material}`,
      ...demoProps,
    });
    textFeature.setStyle(this.scaleAwareStyle(new Style({
      text: new Text({
        text: `${corridorName} | ${material}`,
        font: '700 11px Arial',
        fill: new Fill({ color: '#f8fafc' }),
        stroke: new Stroke({ color: '#0f172a', width: 4 }),
        backgroundFill: new Fill({ color: 'rgba(15, 23, 42, 0.82)' }),
        backgroundStroke: new Stroke({ color: '#334155', width: 1.4 }),
        padding: [4, 8, 4, 8],
        offsetY: -10
      })
    }), 10));
    source.addFeature(textFeature);
    added += 1;
    return added;
  }

  private clearDemoCorridorOverlays(): void {
    const sources = [this.roadSource];
    const demoRoad = this.getDemoLayerSource('road_cross_section');
    if (demoRoad) sources.push(demoRoad);
    for (const src of sources) {
      src.getFeatures()
        .filter((f) => f.get('demoCorridorOverlay') === true)
        .forEach((f) => src.removeFeature(f));
    }
  }

  private addDemoCorridorFromCenterline(centerLine: LineString, name: string, source?: VectorSource): number {
    const dividerType = this.currentRoadSpecs.dividerType || this.dividerType || 'painted';
    const dividerWidthM =
      dividerType === 'none' ? 0 : (this.currentRoadSpecs.dividerWidth ?? defaultDividerWidth(dividerType));
    return this.addRoadCorridorFromCenterLine(
      centerLine,
      name,
      this.currentRoadSpecs.type || 'highway',
      4,
      14,
      Math.max(0.1, this.currentRoadSpecs.depth || 0.5),
      dividerWidthM,
      dividerType,
      Math.max(0.5, this.currentRoadSpecs.separatorDistance || this.separatorDistance || 2),
      this.currentRoadSpecs.surface || 'bituminous',
      { source: source || this.roadSource, demoOverlay: true }
    );
  }

  /**
   * Trace a road centerline on the map; styling uses current Road Specifications and Lane Separators.
   */
  private startManualRoadCorridorDraw(): void {
    this.updateRoadSpecs();
    if (!this.map) return;

    this.removeInteractions();
    this.showInstructions('Draw road: click to add points along centerline, double-click to finish.');

    const sketchStyle = new Style({
      stroke: new Stroke({
        color: 'rgba(241, 196, 15, 0.95)',
        width: 3,
        lineDash: [14, 8]
      })
    });

    this.drawInteraction = new Draw({
      source: this.roadSource,
      type: 'LineString',
      style: sketchStyle
    });

    this.drawInteraction.on('drawend', (event) => {
      const raw = event.feature;
      const geom = raw.getGeometry();
      this.roadSource.removeFeature(raw);
      this.map.removeInteraction(this.drawInteraction!);
      this.drawInteraction = undefined;

      if (!(geom instanceof LineString)) {
        this.showInstructions('Invalid geometry.');
        setTimeout(() => this.hideInstructions(), 1800);
        return;
      }
      const coords = geom.getCoordinates();
      if (coords.length < 2) {
        this.showInstructions('Need at least two points.');
        setTimeout(() => this.hideInstructions(), 2000);
        return;
      }

      const selectedRoadType = this.currentRoadSpecs.type || 'highway';
      const selectedLanes = Math.max(1, this.currentRoadSpecs.lanes || 2);
      const selectedWidth = Math.max(3.5, this.currentRoadSpecs.width || 12);
      const selectedDividerWidth = Math.max(0, this.currentRoadSpecs.dividerWidth || 0);
      const selectedDividerType = this.currentRoadSpecs.dividerType || this.dividerType || 'painted';
      const selectedSeparatorDistance = Math.max(0.5, this.currentRoadSpecs.separatorDistance || this.separatorDistance || 2);
      const selectedMaterial = this.currentRoadSpecs.surface || 'bituminous';
      const selectedDepth = Math.max(0.1, this.currentRoadSpecs.depth || 0.5);
      const corridorName = `${selectedRoadType.toUpperCase()} Drawn`;

      const centerGeom = new LineString(coords);
      const n = this.addRoadCorridorFromCenterLine(
        centerGeom,
        corridorName,
        selectedRoadType,
        selectedLanes,
        selectedWidth,
        selectedDepth,
        selectedDividerWidth,
        selectedDividerType,
        selectedSeparatorDistance,
        selectedMaterial
      );

      this.saveToHistory();
      this.map.getView().changed();
      this.showInstructions(`Road drawn (${n} features). Clear Road removes it, or draw again.`);
      setTimeout(() => this.hideInstructions(), 2800);
    });

    this.map.addInteraction(this.drawInteraction);
  }

  private clearDummyRoadInfrastructureData(): void {
    let removed = 0;
    [this.roadSource, this.vectorSource].forEach(source => {
      source.getFeatures()
        .filter(f => f.get('dummyRoadInfra') === true)
        .forEach(f => {
          source.removeFeature(f);
          removed++;
        });
    });
    this.showInstructions(removed > 0 ? `Cleared dummy road infra (${removed} features)` : 'No dummy road infra found');
    setTimeout(() => this.hideInstructions(), 1800);
  }

  private optimizeNetwork(): void {
    this.showInstructions('Optimizing road network...');
    
    setTimeout(() => {
      const features = this.vectorSource.getFeatures();
      const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
      
      let optimized = 0;
      roads.forEach(road => {
        // Mock optimization - adjust thickness based on lanes
        const lanes = road.get('lanes') || 2;
        const optimalThickness = lanes * 1.5;
        road.set('thickness', optimalThickness);
        optimized++;
      });
      
      this.showInstructions(`Optimized ${optimized} road segments`);
      setTimeout(() => this.hideInstructions(), 3000);
    }, 2000);
  }

  private validateGeometry(): void {
    const features = this.vectorSource.getFeatures();
    const issues: string[] = [];
    
    features.forEach(feature => {
      const geometry = feature.getGeometry();
      if (geometry) {
        // Check for self-intersections
        if (geometry.getType() === 'LineString') {
          const coords = (geometry as LineString).getCoordinates();
          if (this.hasSelfIntersection(coords)) {
            issues.push(`Self-intersection in feature ${feature.getId()}`);
          }
        }
        
        // Check for duplicate coordinates
        if (this.hasDuplicateCoordinates(geometry)) {
          issues.push(`Duplicate coordinates in feature ${feature.getId()}`);
        }
      }
    });
    
    if (issues.length === 0) {
      this.showInstructions('No geometry issues found');
    } else {
      this.showInstructions(`Found ${issues.length} geometry issues`);
      console.log('Geometry Issues:', issues);
    }
    
    setTimeout(() => this.hideInstructions(), 3000);
  }

  private repairGeometry(): void {
    this.showInstructions('Repairing geometry issues...');
    
    setTimeout(() => {
      const features = this.vectorSource.getFeatures();
      let repaired = 0;
      
      features.forEach(feature => {
        const geometry = feature.getGeometry();
        if (geometry && this.hasDuplicateCoordinates(geometry)) {
          // Remove duplicate coordinates
          if (geometry.getType() === 'LineString') {
            const coords = (geometry as LineString).getCoordinates();
            const uniqueCoords = this.removeDuplicateCoordinates(coords);
            feature.setGeometry(new LineString(uniqueCoords));
            repaired++;
          }
        }
      });
      
      this.showInstructions(`Repaired ${repaired} geometry issues`);
      setTimeout(() => this.hideInstructions(), 3000);
    }, 2000);
  }

  private addCulvert(): void {
    this.removeInteractions();
    this.showInstructions('Click to place culvert');
    
    this.map.on('click', (event: any) => {
      const culvertFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'culvert',
        diameter: 1.5, // meters
        material: 'concrete',
        capacity: 5000 // liters per hour
      });
      
      const style = new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#8b4513' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }),
        text: new Text({
          text: 'C',
          font: '12px Arial',
          fill: new Fill({ color: '#fff' }),
          offsetY: -15
        })
      });
      
      culvertFeature.setStyle(style);
      this.getCurrentDrawingSource().addFeature(culvertFeature);
      
      this.hideInstructions();
      this.showInstructions('Culvert added successfully');
      setTimeout(() => this.hideInstructions(), 2000);
    });
  }

  // Helper Methods
  private downloadFile(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private getRoadNetworkStats(): any {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    return {
      totalRoads: roads.length,
      totalLength: roads.reduce((sum, road) => sum + (road.getGeometry() as LineString).getLength(), 0)
    };
  }

  private getTrafficFlowStats(): any {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    return {
      totalCapacity: roads.reduce((sum, road) => sum + ((road.get('lanes') || 2) * 1000), 0),
      bottlenecks: roads.filter(road => (road.get('thickness') || 2) < 3).length
    };
  }

  private getDrainageStats(): any {
    const features = this.vectorSource.getFeatures();
    const nallas = features.filter(f => f.get('type') === 'nalla');
    const exits = features.filter(f => f.get('type') === 'water_exit');
    return {
      nallas: nallas.length,
      waterExits: exits.length,
      totalCapacity: exits.reduce((sum, exit) => sum + (exit.get('capacity') || 1000), 0)
    };
  }

  private getElevationStats(): any {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    return {
      totalElevationChange: roads.reduce((sum, road) => {
        const coords = (road.getGeometry() as LineString).getCoordinates();
        return sum + coords.reduce((elevSum, coord, i) => {
          return i > 0 ? elevSum + Math.abs(coord[2] - coords[i-1][2]) : elevSum;
        }, 0);
      }, 0)
    };
  }

  private hasSelfIntersection(coords: number[][]): boolean {
    for (let i = 0; i < coords.length - 3; i++) {
      for (let j = i + 2; j < coords.length - 1; j++) {
        if (this.linesIntersect(coords[i], coords[i+1], coords[j], coords[j+1])) {
          return true;
        }
      }
    }
    return false;
  }

  private hasDuplicateCoordinates(geometry: any): boolean {
    if (geometry.getType() === 'LineString') {
      const coords = geometry.getCoordinates();
      for (let i = 1; i < coords.length; i++) {
        if (coords[i][0] === coords[i-1][0] && coords[i][1] === coords[i-1][1]) {
          return true;
        }
      }
    }
    return false;
  }

  private removeDuplicateCoordinates(coords: number[][]): number[][] {
    return coords.filter((coord, index) => {
      if (index === 0) return true;
      const prev = coords[index - 1];
      return coord[0] !== prev[0] || coord[1] !== prev[1];
    });
  }

  private setupSurveyControls(): void {
    // GPS controls
    const enableGPSBtn = document.getElementById('enableGPS');
    if (enableGPSBtn) {
      enableGPSBtn.addEventListener('click', () => this.enableGPS());
    }

    const addSurveyPointBtn = document.getElementById('addSurveyPoint');
    if (addSurveyPointBtn) {
      addSurveyPointBtn.addEventListener('click', () => this.addSurveyPoint());
    }

    const addSurveyAreaBtn = document.getElementById('addSurveyArea');
    if (addSurveyAreaBtn) {
      addSurveyAreaBtn.addEventListener('click', () => this.addSurveyArea());
    }

    // Testing controls
    const passTestBtn = document.getElementById('passTest');
    if (passTestBtn) {
      passTestBtn.addEventListener('click', () => this.passTest());
    }

    const failTestBtn = document.getElementById('failTest');
    if (failTestBtn) {
      failTestBtn.addEventListener('click', () => this.failTest());
    }
  }

  private setupManagementControls(): void {
    const showProjectParamsBtn = document.getElementById('showProjectParams');
    if (showProjectParamsBtn) {
      showProjectParamsBtn.addEventListener('click', () => this.showProjectParams());
    }
  }

  private setupBiometricControls(): void {
    const markAttendanceBtn = document.getElementById('markAttendance');
    if (markAttendanceBtn) {
      markAttendanceBtn.addEventListener('click', () => this.markAttendance());
    }

    const viewAttendanceBtn = document.getElementById('viewAttendance');
    if (viewAttendanceBtn) {
      viewAttendanceBtn.addEventListener('click', () => this.viewAttendance());
    }

    const exportAttendanceBtn = document.getElementById('exportAttendance');
    if (exportAttendanceBtn) {
      exportAttendanceBtn.addEventListener('click', () => this.exportAttendance());
    }

    const attendanceMapBtn = document.getElementById('attendanceMap');
    if (attendanceMapBtn) {
      attendanceMapBtn.addEventListener('click', () => this.showAttendanceMap());
    }

    const setWorkZoneBtn = document.getElementById('setWorkZone');
    if (setWorkZoneBtn) {
      setWorkZoneBtn.addEventListener('click', () => this.setWorkZone());
    }

    const trackWorkersBtn = document.getElementById('trackWorkers');
    if (trackWorkersBtn) {
      trackWorkersBtn.addEventListener('click', () => this.trackWorkers());
    }
  }

  private setupSettingsControls(): void {
    const saveDefaultsBtn = document.getElementById('saveDefaults');
    if (saveDefaultsBtn) {
      saveDefaultsBtn.addEventListener('click', () => this.saveDefaults());
    }

    const loadDefaultsBtn = document.getElementById('loadDefaults');
    if (loadDefaultsBtn) {
      loadDefaultsBtn.addEventListener('click', () => this.loadDefaults());
    }

    const resetDefaultsBtn = document.getElementById('resetDefaults');
    if (resetDefaultsBtn) {
      resetDefaultsBtn.addEventListener('click', () => this.resetDefaults());
    }

    // Settings panel controls
    const settingsBtn = document.getElementById('settingsBtn');
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => this.toggleSettingsPanel());
    }

    const applySettingsBtn = document.getElementById('applySettings');
    if (applySettingsBtn) {
      applySettingsBtn.addEventListener('click', () => this.applySettings());
    }

    const closeSettingsBtn = document.getElementById('closeSettings');
    if (closeSettingsBtn) {
      closeSettingsBtn.addEventListener('click', () => this.closeSettingsPanel());
    }
  }

  // Survey methods
  private enableGPS(): void {
    if (navigator.geolocation) {
      this.gpsWatchId = navigator.geolocation.watchPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          
          // Update GPS accuracy display
          const gpsAccuracyElement = document.getElementById('gpsAccuracy');
          if (gpsAccuracyElement) {
            gpsAccuracyElement.textContent = accuracy.toFixed(1);
          }
          
          // Add GPS point to map
          const gpsFeature = new Feature({
            geometry: new Point(fromLonLat([lon, lat])),
            type: 'gps',
            accuracy: accuracy,
            timestamp: new Date()
          });
          
          this.gpsSource.addFeature(gpsFeature);
        },
        (error) => {
          console.error('GPS Error:', error);
        },
        { enableHighAccuracy: true }
      );
    }
  }

  private addSurveyPoint(): void {
    this.removeInteractions();
    this.map.on('click', (event: any) => {
      const surveyFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'survey',
        timestamp: new Date(),
        info: 'Survey Point'
      });
      
      this.getCurrentDrawingSource().addFeature(surveyFeature);
      this.updateSurveyPointCount();
      this.saveToHistory();
    });
  }

  private addSurveyArea(): void {
    this.removeInteractions();
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(52, 152, 219, 0.2)'
        }),
        stroke: new Stroke({
          color: '#3498db',
          width: 2
        })
      })
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'survey-area');
      feature.set('timestamp', new Date());
      feature.set('info', 'Survey Area');
      this.updateSurveyPointCount();
      this.saveToHistory();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private updateSurveyPointCount(): void {
    const surveyPoints = this.vectorSource.getFeatures().filter(f => f.get('type') === 'survey');
    const surveyPointCountElement = document.getElementById('surveyPointCount');
    if (surveyPointCountElement) {
      surveyPointCountElement.textContent = surveyPoints.length.toString();
    }
  }

  private fieldSurveyTypes = new Set(['survey', 'survey-area', 'gps']);

  private collectFieldSurveyFeatures(): Feature[] {
    const seen = new Set<Feature>();
    const features: Feature[] = [];
    const sources = [this.vectorSource, this.gpsSource, this.getCurrentDrawingSource()].filter(Boolean);
    for (const source of sources) {
      for (const feature of source.getFeatures()) {
        if (!this.fieldSurveyTypes.has(String(feature.get('type') || '')) || seen.has(feature)) continue;
        seen.add(feature);
        features.push(feature);
      }
    }
    return features;
  }

  private collectFieldSurveyGeoJson(): { type: 'FeatureCollection'; features: unknown[] } {
    const features = this.collectFieldSurveyFeatures();
    const fc = this.format.writeFeaturesObject(features, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    }) as { type?: string; features?: unknown[] };
    return {
      type: 'FeatureCollection',
      features: Array.isArray(fc.features) ? fc.features : [],
    };
  }

  saveFieldSurvey(): void {
    if (!this.projectId) {
      this.showInstructions('Select a project before saving the field survey');
      return;
    }
    const fieldGeometry = this.collectFieldSurveyGeoJson();
    const persist = (id: number) => {
      this.apiService.updateSurveyFieldGeometry(id, fieldGeometry).subscribe({
        next: () => this.showInstructions('Field survey saved to the project survey'),
        error: (err) => this.showInstructions(err?.error?.message || 'Failed to save field survey'),
      });
    };
    this.apiService.listProjectSurveyStages({ project_id: this.projectId, limit: 1 }).subscribe({
      next: (res: { data?: Array<{ id?: number }> }) => {
        const row = res?.data?.[0];
        if (row?.id) {
          persist(Number(row.id));
          return;
        }
        this.apiService.saveProjectSurveyStage({
          project_id: this.projectId,
          project_name: this.projectData?.project_name,
          stage: 'Survey',
          status: 'in_progress',
          remarks: { source: 'gis' },
        }).subscribe({
          next: (created: { data?: { id?: number } }) => {
            const id = Number(created?.data?.id);
            if (!id) {
              this.showInstructions('Survey row created but could not save geometry');
              return;
            }
            persist(id);
          },
          error: (err) => this.showInstructions(err?.error?.message || 'Could not create a survey row'),
        });
      },
      error: (err) => this.showInstructions(err?.error?.message || 'Survey lookup failed'),
    });
  }

  private loadFieldSurveyGeometry(): void {
    if (!this.projectId) return;
    this.apiService.listProjectSurveyStages({ project_id: this.projectId, limit: 1 }).subscribe({
      next: (res: { data?: Array<{ field_geometry?: { type?: string; features?: unknown[] } }> }) => {
        const geom = res?.data?.[0]?.field_geometry;
        if (!geom || geom.type !== 'FeatureCollection' || !Array.isArray(geom.features) || !geom.features.length) {
          return;
        }
        this.replaceFieldSurveyFeatures(geom);
      },
      error: () => undefined,
    });
  }

  private replaceFieldSurveyFeatures(fc: { type?: string; features?: unknown[] }): void {
    const sources = [this.vectorSource, this.gpsSource, this.getCurrentDrawingSource()].filter(Boolean);
    for (const source of sources) {
      source.getFeatures()
        .filter((feature) => this.fieldSurveyTypes.has(String(feature.get('type') || '')))
        .forEach((feature) => source.removeFeature(feature));
    }
    const features = this.format.readFeatures(fc, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    features.forEach((feature) => {
      const type = String(feature.get('type') || '');
      if (type === 'gps') this.gpsSource.addFeature(feature);
      else this.getCurrentDrawingSource().addFeature(feature);
    });
    this.updateSurveyPointCount();
  }

  private passTest(): void {
    const testsPassedElement = document.getElementById('testsPassed');
    const testsPendingElement = document.getElementById('testsPending');
    
    if (testsPassedElement && testsPendingElement) {
      const passed = parseInt(testsPassedElement.textContent || '0') + 1;
      const pending = parseInt(testsPendingElement.textContent || '0') - 1;
      
      testsPassedElement.textContent = passed.toString();
      testsPendingElement.textContent = pending.toString();
    }
  }

  private failTest(): void {
    const testsFailedElement = document.getElementById('testsFailed');
    const testsPendingElement = document.getElementById('testsPending');
    
    if (testsFailedElement && testsPendingElement) {
      const failed = parseInt(testsFailedElement.textContent || '0') + 1;
      const pending = parseInt(testsPendingElement.textContent || '0') - 1;
      
      testsFailedElement.textContent = failed.toString();
      testsPendingElement.textContent = pending.toString();
    }
  }

  // Management methods
  private showProjectParams(): void {
    const parameterDisplay = document.getElementById('parameterDisplay');
    if (parameterDisplay) {
      parameterDisplay.style.display = parameterDisplay.style.display === 'none' ? 'block' : 'none';
    }
  }

  // Biometric methods
  private markAttendance(): void {
    const presentCountElement = document.getElementById('presentCount');
    if (presentCountElement) {
      const present = parseInt(presentCountElement.textContent || '0') + 1;
      presentCountElement.textContent = present.toString();
    }
  }

  private viewAttendance(): void {
    console.log('Viewing attendance details...');
  }

  private exportAttendance(): void {
    console.log('Exporting attendance data...');
  }

  private showAttendanceMap(): void {
    this.plotProjectAttendanceOnMap();
  }

  private trackWorkers(): void {
    this.plotProjectAttendanceOnMap();
  }

  private setWorkZone(): void {
    this.removeInteractions();
    this.map.on('click', (event: any) => {
      if (!this.workZone) {
        // Start creating work zone
        this.workZone = {
          center: event.coordinate,
          radius: 100 // meters
        };
      } else {
        // Complete work zone
        this.workZone.radius = this.calculateDistance(this.workZone.center, event.coordinate);
        this.createWorkZoneFeature();
        this.workZone = null;
      }
    });
  }

  private createWorkZoneFeature(): void {
    if (this.workZone) {
      const workZoneFeature = new Feature({
        geometry: new Circle(this.workZone.center, this.workZone.radius),
        type: 'work-zone',
        info: 'Work Zone'
      });
      
      this.getCurrentDrawingSource().addFeature(workZoneFeature);
      this.saveToHistory();
    }
  }

  private calculateDistance(coord1: number[], coord2: number[]): number {
    // Simple distance calculation
    const dx = coord1[0] - coord2[0];
    const dy = coord1[1] - coord2[1];
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Settings methods
  private toggleSettingsPanel(): void {
    const settingsPanel = document.getElementById('settingsPanel');
    if (settingsPanel) {
      settingsPanel.style.display = settingsPanel.style.display === 'none' ? 'block' : 'none';
    }
  }

  private applySettings(): void {
    // Apply settings from the settings panel
    const snapToGrid = (document.getElementById('snapToGrid') as HTMLInputElement)?.checked;
    const showCoords = (document.getElementById('showCoords') as HTMLInputElement)?.checked;
    const autoSave = (document.getElementById('autoSave') as HTMLInputElement)?.checked;
    const defaultLineColor = (document.getElementById('defaultLineColor') as HTMLInputElement)?.value;
    const backgroundColor = (document.getElementById('backgroundColor') as HTMLInputElement)?.value;
    
    this.snapEnabled = !!snapToGrid;
    this.defaultSettings.lineColor = defaultLineColor || this.defaultSettings.lineColor;
    this.defaultSettings.backgroundColor = backgroundColor || this.defaultSettings.backgroundColor;
    this.applySnapState();
    console.log('Settings applied:', { snapToGrid, showCoords, autoSave, defaultLineColor, backgroundColor });
  }

  /** Collect all vector sources for Snap interaction. */
  private getSnapSources(): VectorSource[] {
    const sources: VectorSource[] = [
      this.vectorSource,
      this.roadSource,
      this.projectSource,
      this.biometricSource,
      this.gpsSource,
      this.waterSource,
      this.separatorSource
    ];
    this.layerOrder.forEach(layerId => {
      const layer = this.layerRegistry[layerId];
      if (layer?.getSource() instanceof VectorSource) {
        sources.push(layer.getSource() as VectorSource);
      }
    });
    return sources;
  }

  /** Add or remove Snap interaction based on snapEnabled. One Snap per vector source. */
  private applySnapState(): void {
    this.snapInteractions.forEach(s => this.map.removeInteraction(s));
    this.snapInteractions = [];
    if (!this.snapEnabled || !this.map) return;
    const tolerance = this.defaultSettings.snapTolerance ?? 15;
    this.getSnapSources().forEach(source => {
      const snap = new Snap({
        source,
        pixelTolerance: tolerance
      });
      this.map.addInteraction(snap);
      this.snapInteractions.push(snap);
    });
  }

  private closeSettingsPanel(): void {
    const settingsPanel = document.getElementById('settingsPanel');
    if (settingsPanel) {
      settingsPanel.style.display = 'none';
    }
  }

  private saveDefaults(): void {
    localStorage.setItem('gisEditorDefaults', JSON.stringify(this.defaultSettings));
    console.log('Defaults saved');
  }

  private loadDefaults(): void {
    const saved = localStorage.getItem('gisEditorDefaults');
    if (saved) {
      this.defaultSettings = { ...this.defaultSettings, ...JSON.parse(saved) };
      console.log('Defaults loaded');
    }
  }

  private resetDefaults(): void {
    localStorage.removeItem('gisEditorDefaults');
    this.defaultSettings = {
      center: [77.2090, 28.6139],
      zoom: 10,
      snapTolerance: 15,
      autoSaveInterval: 5,
      showGrid: false,
      showScaleBar: true,
      showNorthArrow: true,
      measurementUnits: 'metric',
      lineColor: '#3498db',
      backgroundColor: '#f0f2f5'
    };
    console.log('Defaults reset');
  }

  private setupLayerManagement(): void {
    // Layer toggle controls
    const drawingLayerToggle = document.getElementById('drawingLayerToggle');
    if (drawingLayerToggle) {
      drawingLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('drawing', target.checked);
      });
    }

    const roadLayerToggle = document.getElementById('roadLayerToggle');
    if (roadLayerToggle) {
      roadLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('road', target.checked);
      });
    }

    const projectLayerToggle = document.getElementById('projectLayerToggle');
    if (projectLayerToggle) {
      projectLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('project', target.checked);
      });
    }

    const biometricLayerToggle = document.getElementById('biometricLayerToggle');
    if (biometricLayerToggle) {
      biometricLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('biometric', target.checked);
      });
    }

    const gpsLayerToggle = document.getElementById('gpsLayerToggle');
    if (gpsLayerToggle) {
      gpsLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('gps', target.checked);
      });
    }

    const waterLayerToggle = document.getElementById('waterLayerToggle');
    if (waterLayerToggle) {
      waterLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('water', target.checked);
      });
    }

    // Layer opacity controls
    const drawingOpacity = document.getElementById('drawingOpacity');
    if (drawingOpacity) {
      drawingOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('drawing', parseFloat(target.value) / 100);
      });
    }

    const roadOpacity = document.getElementById('roadOpacity');
    if (roadOpacity) {
      roadOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('road', parseFloat(target.value) / 100);
      });
    }

    const projectOpacity = document.getElementById('projectOpacity');
    if (projectOpacity) {
      projectOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('project', parseFloat(target.value) / 100);
      });
    }

    const biometricOpacity = document.getElementById('biometricOpacity');
    if (biometricOpacity) {
      biometricOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('biometric', parseFloat(target.value) / 100);
      });
    }

    const gpsLayerOpacity = document.getElementById('gpsLayerOpacity');
    if (gpsLayerOpacity) {
      gpsLayerOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('gps', parseFloat(target.value) / 100);
      });
    }

    const waterOpacity = document.getElementById('waterOpacity');
    if (waterOpacity) {
      waterOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('water', parseFloat(target.value) / 100);
      });
    }

    // Initialize enhanced layer management UI
    this.updateLayerManagementUI();
    this.setupEnhancedLayerManagementEvents();
  }

  private setupEnhancedLayerManagementEvents(): void {
    // Layer Management Controls
    const addNewLayerBtn = document.getElementById('addNewLayer');
    if (addNewLayerBtn) {
      addNewLayerBtn.addEventListener('click', () => {
        const layerName = prompt('Enter layer name:');
        if (layerName && layerName.trim()) {
          this.addLayer(layerName.trim());
        }
      });
    }

    const importLayerBtn = document.getElementById('importLayer');
    if (importLayerBtn) {
      importLayerBtn.addEventListener('click', () => {
        this.showImportLayerDialog();
      });
    }

    const exportAllLayersBtn = document.getElementById('exportAllLayers');
    if (exportAllLayersBtn) {
      exportAllLayersBtn.addEventListener('click', () => {
        this.exportAllLayers();
      });
    }

    const createGroupBtn = document.getElementById('createGroup');
    if (createGroupBtn) {
      createGroupBtn.addEventListener('click', () => {
        this.showCreateGroupDialog();
      });
    }

    // Layer search
    const layerSearchInput = document.getElementById('layerSearchInput');
    if (layerSearchInput) {
      layerSearchInput.addEventListener('input', (event) => {
        const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
        this.filterLayersBySearch(searchTerm);
      });
    }
  }

  private showAddLayerDialog(): void {
    const dialog = document.createElement('div');
    dialog.className = 'add-layer-dialog';
    dialog.innerHTML = `
      <div class="dialog-content">
        <h3>Add New Layer</h3>
        <div class="form-group">
          <label>Layer Name:</label>
          <input type="text" id="newLayerName" placeholder="Enter layer name">
        </div>
        <div class="form-group">
          <label>Layer Type:</label>
          <select id="newLayerType">
            <option value="vector">Vector Layer</option>
            <option value="tile">Tile Layer</option>
            <option value="image">Image Layer</option>
          </select>
        </div>
        <div class="form-group">
          <label>Group:</label>
          <select id="newLayerGroup">
            <option value="">No Group</option>
            ${Object.keys(this.layerGroups).map(group => 
              `<option value="${group}">${group}</option>`
            ).join('')}
          </select>
        </div>
        <div class="button-group">
          <button id="createLayer" class="btn">Create</button>
          <button id="cancelAddLayer" class="btn">Cancel</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    document.getElementById('createLayer')?.addEventListener('click', () => {
      const name = (document.getElementById('newLayerName') as HTMLInputElement)?.value;
      const type = (document.getElementById('newLayerType') as HTMLSelectElement)?.value;
      const group = (document.getElementById('newLayerGroup') as HTMLSelectElement)?.value;
      
      if (name) {
        this.createNewLayer(name, type, group);
        document.body.removeChild(dialog);
      }
    });
    
    document.getElementById('cancelAddLayer')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });
  }

  // --- LRS network integration (load routes + assets from the LRS API) ---

  lrsRoutes: LrsRoute[] = [];
  selectedLrsRouteId: number | null = null;
  lrsIncludeUtilities = true;
  lrsIncludeChainage = true;
  /** Toggleable ROW (right-of-way) corridor band, rendered from lrs.routes.row_width_m. */
  lrsIncludeRowBand = true;
  private lastLrsRouteFeature: unknown = null;
  nhaiLayerVisibility: Record<string, boolean> = defaultLayerVisibility(true);
  nhaiLayerCounts: Record<string, number> = {};
  nhaiExpandedGroups: Record<string, boolean> = {
    basemaps: true,
    'nhai-road': true,
    'ug-utilities': true,
    'og-utilities': true,
    crossings: true,
    reference: true,
  };
  /** Chainage marker interval in metres (50 or 100 — user selectable). */
  chainageIntervalM = 100;
  readonly chainageIntervalOptions = CHAINAGE_INTERVAL_OPTIONS_M;
  readonly demoLayerOptions = DEMO_ALL_LAYERS;
  lrsLoading = false;
  lrsStatus = '';

  /** Desk.md layers 1–6 stack toggles (Layers tab). */
  deskStack: Record<'l1_roadgraph' | 'l2_routes' | 'l3_calibration' | 'l4_sections' | 'l5_events' | 'l6_assets', boolean> = {
    l1_roadgraph: false,
    l2_routes: true,
    l3_calibration: true,
    l4_sections: false,
    l5_events: true,
    l6_assets: true,
  };
  readonly deskStackLayers: { key: 'l1_roadgraph' | 'l2_routes' | 'l3_calibration' | 'l4_sections' | 'l5_events' | 'l6_assets'; label: string }[] = [
    { key: 'l1_roadgraph', label: 'L1 Road graph (nodes / edges)' },
    { key: 'l2_routes', label: 'L2 Routes / hierarchy' },
    { key: 'l3_calibration', label: 'L3 Calibration & measured route' },
    { key: 'l4_sections', label: 'L4 Sections / segments' },
    { key: 'l5_events', label: 'L5 Events' },
    { key: 'l6_assets', label: 'L6 Assets (highway + utilities)' },
  ];
  deskStackStatus = '';
  deskPublishBusy = false;
  deskPublishStatus = '';
  deskEventTypeFilter: string[] = [];
  deskEventTypeOptions: { id: string; label: string; color?: string }[] = [];
  private deskOverlaySources: Partial<Record<string, VectorSource>> = {};
  private deskOverlayLayers: Partial<Record<string, VectorLayer<VectorSource>>> = {};

  // --- Technical demo mode ---
  demoLoading = false;
  demoStatus = '';
  demoTerrainOn = false;
  demoShowOffsets = true;
  demoTagTargetLayerId = 'water_network';
  lrsIncludeCarriageways = true;
  private demoCorridorLine: LineString | null = null;
  private demoActiveRouteCode: string | null = null;
  private demoTerrainLayer: TileLayer | null = null;
  boqList: { boq_id?: number; boq_name?: string; project_id?: number }[] = [];
  bomList: { id?: number; bom_name?: string; project_id?: number }[] = [];
  boqBomLoading = false;
  lrsLinkedDocuments: LrsDocumentRef[] = [];
  selectedLrsDocumentId: number | null = null;
  private readonly lrsDocumentLayerId = 'LRS documents';
  private lastLoadedLrsRouteId: number | null = null;

  get lrsChainageIntervalKm(): number {
    return this.chainageIntervalM / 1000;
  }

  loadLrsRouteList(): void {
    this.lrsStatus = 'Loading LRS routes…';
    this.lrsService.listRoutes().subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
        if (!this.selectedLrsRouteId && this.lrsRoutes.length) {
          this.selectedLrsRouteId = this.lrsRoutes[0].id;
        }
        this.lrsStatus = `${this.lrsRoutes.length} LRS routes available`;
      },
      error: () => {
        this.lrsStatus = 'LRS service unavailable';
      },
    });
  }

  onLrsRouteSelect(value: string): void {
    this.selectedLrsRouteId = value ? parseInt(value, 10) : null;
    this.lrsDashboard.setRouteId(this.selectedLrsRouteId);
  }

  /** Editable road feature panel (select a road on map → edit specs). */
  roadEditDetail: {
    feature: Feature;
    name: string;
    roadType: string;
    lanes: number;
    widthM: number;
    depthM: number;
    surface: string;
    dividerType: string;
    dividerWidthM: number;
    separatorDistanceM: number;
    comment: string;
  } | null = null;

  readonly roadTypeOptions = [
    { value: 'highway', label: 'Highway' },
    { value: 'arterial', label: 'Arterial' },
    { value: 'collector', label: 'Collector' },
    { value: 'local', label: 'Local Street' },
    { value: 'service', label: 'Service Road' },
  ];

  readonly roadSurfaceOptions = [
    { value: 'bituminous', label: 'Bituminous' },
    { value: 'concrete', label: 'Concrete' },
    { value: 'gravel', label: 'Gravel' },
    { value: 'earthen', label: 'Earthen' },
  ];

  readonly roadDividerOptions = ROAD_DIVIDER_OPTIONS;

  readonly roadLaneOptions = [1, 2, 4, 6, 8];

  /** Read-only chainage marker card; full details use lrsDetailId. */
  lrsAssetDetail: {
    kind: 'asset' | 'utility' | 'chainage';
    typeId: string;
    typeLabel: string;
    iconPath: string;
    label: string;
    chainage: string;
    chainageTo?: string;
    side?: string;
    offsetM?: number | null;
    depthM?: number | null;
    description?: string;
    routeCode?: string;
  } | null = null;
  lrsDetailKind: 'event' | 'utility' | null = null;
  lrsDetailId: number | null = null;
  private lrsClickHandlerBound = false;

  /** Paint road geometry first, then add LRS asset / overlay layers. */
  private afterRoadGeometryReady(cb: () => void): void {
    const run = () => {
      this.map?.renderSync();
      cb();
      this.map?.render();
    };
    if (!this.map) {
      setTimeout(run, 50);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  loadLrsRouteToMap(): void {
    const routeId = this.selectedLrsRouteId;
    if (!routeId) {
      this.lrsStatus = 'Select an LRS route first';
      return;
    }
    const routeMeta = this.lrsRoutes.find((r) => r.id === routeId);
    const code = routeMeta?.route_code || `route-${routeId}`;

    this.lrsLoading = true;
    this.lastLoadedLrsRouteId = routeId;
    this.lrsStatus = `Loading ${code} road geometry…`;
    this.lrsDashboard.setRouteId(routeId);
    if (routeMeta && routeMeta.length_m != null) {
      this.lrsDashboard.setRouteMeta({
        origin_m: routeMeta.origin_measure_m || 0,
        length_m: routeMeta.length_m,
      });
    }
    this.lrsService.getRouteMap(routeId).subscribe({
      next: (res) => {
        if (res.data?.route) {
          this.upsertLrsLayer(
            `LRS ${code} centerline`,
            { type: 'FeatureCollection', features: [res.data.route] },
            'centerline'
          );
          this.lastLrsRouteFeature = res.data.route;
          this.renderLrsRowBand(code, routeMeta?.row_width_m ?? null);
          this.overlayDemoCorridorFromLrsCenterline(code);
          this.zoomToLayer(`LRS ${code} centerline`);
        }
        this.lrsStatus = `${code}: road geometry loaded — loading assets…`;

        this.afterRoadGeometryReady(() => {
          preloadLrsIcons().then(() => {
            const assetCount = res.data.events?.features?.length || 0;
            if (assetCount) {
              this.upsertLrsLayer(`LRS ${code} assets`, res.data.events, 'assets');
            }
            this.bindLrsAssetClick();
            this.lrsLoading = false;
            this.lrsStatus = `${code}: centerline + ${assetCount} assets loaded`;
            this.demoActiveRouteCode = code;
            this.loadLrsDocuments(routeId);

            if (this.lrsIncludeUtilities) this.loadLrsUtilities(routeId, code);
            if (this.lrsIncludeChainage) this.loadLrsChainageMarkers(routeId, code);
            if (this.lrsIncludeCarriageways) this.loadLrsCarriagewaysAndLanes(routeId, code);
            this.applyDeskStackVisibility();
            if (this.deskStack.l3_calibration) this.loadDeskCalibration();
            if (this.deskStack.l4_sections) this.loadDeskSections();
            if (this.deskStack.l5_events) this.loadDeskEvents();
            if (this.deskStack.l6_assets) this.loadDeskAssetsCombined();
            if (this.deskStack.l1_roadgraph) this.loadDeskRoadGraph();
          });
        });
      },
      error: (e) => {
        this.lrsLoading = false;
        this.lrsStatus = e?.error?.error || 'Failed to load LRS route';
      },
    });
  }

  /**
   * ROW corridor band: translucent buffer of half `row_width_m` each side of the
   * loaded centerline. Gives encroachment / land-acquisition context on the map.
   */
  private renderLrsRowBand(code: string, rowWidthM: number | null | undefined): void {
    const layerId = `LRS ${code} ROW band`;
    const existing = this.layerRegistry[layerId];
    const width = Number(rowWidthM) || 0;
    if (!this.lrsIncludeRowBand || !(width > 0) || !this.lastLrsRouteFeature) {
      if (existing) existing.setVisible(false);
      return;
    }
    const routeFeature = this.lastLrsRouteFeature as {
      type: string;
      geometry: unknown;
      properties?: Record<string, unknown>;
    };
    const banded = {
      ...routeFeature,
      properties: { ...(routeFeature.properties || {}), row_width_m: width, info: `ROW ${width} m` },
    };
    this.upsertLrsLayer(layerId, { type: 'FeatureCollection', features: [banded] }, 'row_band');
    this.layerRegistry[layerId]?.setVisible(true);
    if (this.layerMetadata[layerId]) {
      this.layerMetadata[layerId].description = `Right of Way corridor (${width} m, read-only)`;
    }
  }

  toggleLrsRowBand(checked: boolean): void {
    this.lrsIncludeRowBand = checked;
    const code = this.demoActiveRouteCode;
    if (!code) return;
    if (!checked) {
      this.layerRegistry[`LRS ${code} ROW band`]?.setVisible(false);
      return;
    }
    const routeMeta = this.lrsRoutes.find((r) => r.id === this.selectedLrsRouteId);
    this.renderLrsRowBand(code, routeMeta?.row_width_m ?? null);
  }

  onDeskStackToggle(
    key: 'l1_roadgraph' | 'l2_routes' | 'l3_calibration' | 'l4_sections' | 'l5_events' | 'l6_assets',
    checked: boolean
  ): void {
    this.deskStack[key] = checked;
    this.applyDeskStackVisibility();
    if (checked) this.loadDeskStackLayer(key);
  }

  onDeskEventTypeFilterChange(): void {
    if (this.deskStack.l5_events) this.loadDeskStackLayer('l5_events');
  }

  private applyDeskStackVisibility(): void {
    const code = this.demoActiveRouteCode;
    this.setDeskOverlayVisible('desk-l1-roadgraph', this.deskStack.l1_roadgraph);
    if (code) {
      this.layerRegistry[`LRS ${code} centerline`]?.setVisible(this.deskStack.l2_routes);
      this.layerRegistry[`LRS ${code} ROW band`]?.setVisible(this.deskStack.l2_routes && this.lrsIncludeRowBand);
      this.layerRegistry[`LRS ${code} assets`]?.setVisible(this.deskStack.l5_events || this.deskStack.l6_assets);
      this.layerRegistry[`LRS ${code} utilities`]?.setVisible(this.deskStack.l6_assets && this.lrsIncludeUtilities);
    }
    this.setDeskOverlayVisible('desk-l3-calibration', this.deskStack.l3_calibration);
    this.setDeskOverlayVisible('desk-l4-sections', this.deskStack.l4_sections);
    this.setDeskOverlayVisible('desk-l5-events', this.deskStack.l5_events);
    this.setDeskOverlayVisible('desk-l6-assets', this.deskStack.l6_assets);
  }

  private ensureDeskOverlay(
    id: string,
    style: Style | Style[] | ((f: FeatureLike, r: number) => Style | Style[])
  ): VectorSource {
    if (this.deskOverlaySources[id]) return this.deskOverlaySources[id]!;
    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      style,
      zIndex: id.includes('l1') ? 14 : id.includes('l4') ? 10 : 13,
    });
    layer.set('lrsReadonly', true);
    layer.set('deskStack', true);
    this.deskOverlaySources[id] = source;
    this.deskOverlayLayers[id] = layer;
    this.map?.addLayer(layer);
    this.overlayLayerIds.add(id);
    this.registerLayer(id, layer);
    if (this.layerMetadata[id]) {
      this.layerMetadata[id].description = 'Desk stack overlay';
      this.layerMetadata[id].readonly = true;
    }
    return source;
  }

  private setDeskOverlayVisible(id: string, visible: boolean): void {
    const layer = this.deskOverlayLayers[id] || this.layerRegistry[id];
    layer?.setVisible(visible);
  }

  private loadDeskStackLayer(
    key: 'l1_roadgraph' | 'l2_routes' | 'l3_calibration' | 'l4_sections' | 'l5_events' | 'l6_assets'
  ): void {
    if (key === 'l1_roadgraph') this.loadDeskRoadGraph();
    else if (key === 'l2_routes') {
      if (this.selectedLrsRouteId) this.loadLrsRouteToMap();
    } else if (key === 'l3_calibration') this.loadDeskCalibration();
    else if (key === 'l4_sections') this.loadDeskSections();
    else if (key === 'l5_events') this.loadDeskEvents();
    else if (key === 'l6_assets') this.loadDeskAssetsCombined();
  }

  private loadDeskRoadGraph(): void {
    if (!this.projectId) {
      this.deskStackStatus = 'Open a project to load RoadGraph';
      return;
    }
    this.deskStackStatus = 'Loading road graph…';
    this.lrsService.getRoadGraph(String(this.projectId)).subscribe({
      next: (res) => {
        const src = this.ensureDeskOverlay(
          'desk-l1-roadgraph',
          (f) => {
            const g = f.getGeometry();
            if (g instanceof Point) {
              return new Style({
                image: new CircleStyle({
                  radius: 7,
                  fill: new Fill({ color: '#0f766e' }),
                  stroke: new Stroke({ color: '#fff', width: 2 }),
                }),
              });
            }
            return new Style({
              stroke: new Stroke({ color: '#0f766e', width: 3 }),
            });
          }
        );
        src.clear();
        const fc = {
          type: 'FeatureCollection' as const,
          features: [
            ...(res.data.nodes || []).map((n) => ({
              type: 'Feature' as const,
              geometry: n.geometry,
              properties: { ...n, deskKind: 'node' },
            })),
            ...(res.data.edges || []).map((e) => ({
              type: 'Feature' as const,
              geometry: e.geometry,
              properties: { ...e, deskKind: 'edge' },
            })),
          ],
        };
        const feats = this.format.readFeatures(JSON.stringify(fc), {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }) as Feature[];
        src.addFeatures(feats);
        this.setDeskOverlayVisible('desk-l1-roadgraph', true);
        this.deskStackStatus = `L1: ${res.data.nodes?.length || 0} nodes, ${res.data.edges?.length || 0} edges`;
      },
      error: (e) => {
        this.deskStackStatus = e?.error?.error || 'Failed to load road graph';
      },
    });
  }

  private loadDeskCalibration(): void {
    const routeId = this.selectedLrsRouteId;
    if (!routeId) {
      this.deskStackStatus = 'Select an LRS route for calibration';
      return;
    }
    const code = this.lrsRoutes.find((r) => r.id === routeId)?.route_code || `route-${routeId}`;
    this.layerRegistry[`LRS ${code} centerline`]?.setVisible(true);
    this.lrsService.listCalibrationPoints(routeId).subscribe({
      next: (res) => {
        const src = this.ensureDeskOverlay(
          'desk-l3-calibration',
          new Style({
            image: new CircleStyle({
              radius: 5,
              fill: new Fill({ color: '#c2410c' }),
              stroke: new Stroke({ color: '#fff', width: 1.5 }),
            }),
            text: new TextStyle({
              font: '11px sans-serif',
              fill: new Fill({ color: '#9a3412' }),
              offsetY: -12,
              text: '',
            }),
          })
        );
        src.clear();
        const features = (res.data || [])
          .filter((p) => p.geometry)
          .map((p) => ({
            type: 'Feature' as const,
            geometry: p.geometry,
            properties: {
              label: p.label || p.chainage_label || `${((p.measure_m || 0) / 1000).toFixed(3)} km`,
              measure_m: p.measure_m,
            },
          }));
        const feats = this.format.readFeatures(
          JSON.stringify({ type: 'FeatureCollection', features }),
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        ) as Feature[];
        feats.forEach((f) => {
          const label = String(f.get('label') || '');
          f.setStyle(
            new Style({
              image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: '#c2410c' }),
                stroke: new Stroke({ color: '#fff', width: 1.5 }),
              }),
              text: new TextStyle({
                text: label,
                font: '11px sans-serif',
                fill: new Fill({ color: '#9a3412' }),
                offsetY: -12,
              }),
            })
          );
        });
        src.addFeatures(feats);
        this.setDeskOverlayVisible('desk-l3-calibration', true);
        this.deskStackStatus = `L3: ${feats.length} calibration points`;
      },
      error: (e) => {
        this.deskStackStatus = e?.error?.error || 'Failed to load calibration';
      },
    });
  }

  private loadDeskSections(): void {
    const routeId = this.selectedLrsRouteId;
    if (!routeId) {
      this.deskStackStatus = 'Select an LRS route for sections';
      return;
    }
    forkJoin({
      sections: this.lrsService.listSections(routeId).pipe(catchError(() => of({ success: false, data: [] as never[] }))),
      segments: this.lrsService.listRouteSegments(routeId).pipe(catchError(() => of({ success: false, data: [] as never[] }))),
      route: this.lrsService.getRouteMap(routeId).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ sections, segments, route }) => {
        const src = this.ensureDeskOverlay('desk-l4-sections', (f) => {
          const kind = String(f.get('deskKind') || 'section');
          const color =
            kind === 'sub_segment' ? '#a855f7' : kind === 'segment' ? '#2563eb' : '#f59e0b';
          const width = kind === 'section' ? 10 : kind === 'segment' ? 6 : 3;
          return new Style({
            stroke: new Stroke({ color, width, lineCap: 'butt' }),
          });
        });
        src.clear();
        const centerline = route?.data?.route?.geometry as GeoJSON.LineString | undefined;
        const features: { type: 'Feature'; geometry: GeoJSON.Geometry; properties: Record<string, unknown> }[] = [];
        for (const seg of segments.data || []) {
          if (!seg.geometry) continue;
          features.push({
            type: 'Feature',
            geometry: seg.geometry,
            properties: {
              deskKind: seg.parent_segment_id ? 'sub_segment' : 'segment',
              section_id: seg.section_id,
              carriageway_side: seg.carriageway_side,
              label: seg.segment_code,
            },
          });
        }
        // Section bands: reuse centerline when segment geom missing; style thicker via deskKind
        for (const sec of sections.data || []) {
          if (centerline) {
            features.push({
              type: 'Feature',
              geometry: centerline,
              properties: {
                deskKind: 'section',
                label: sec.section_code,
                from_m: sec.from_measure_m,
                to_m: sec.to_measure_m,
              },
            });
          }
        }
        const feats = this.format.readFeatures(
          JSON.stringify({ type: 'FeatureCollection', features }),
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        ) as Feature[];
        src.addFeatures(feats);
        this.setDeskOverlayVisible('desk-l4-sections', true);
        this.deskStackStatus = `L4: ${(sections.data || []).length} sections, ${(segments.data || []).length} segments`;
      },
      error: (e) => {
        this.deskStackStatus = e?.error?.error || 'Failed to load sections';
      },
    });
  }

  private loadDeskEvents(): void {
    const routeId = this.selectedLrsRouteId;
    if (!routeId) {
      this.deskStackStatus = 'Select an LRS route for events';
      return;
    }
    this.lrsService.listEventTypes().subscribe({
      next: (typeRes) => {
        if (typeRes.data?.length) {
          this.deskEventTypeOptions = typeRes.data.map((t) => ({
            id: t.id,
            label: t.label,
            color: t.color,
          }));
        }
      },
      error: () => {
        /* keep frontend constants as fallback via empty options */
      },
    });
    this.lrsService.listEvents(routeId).subscribe({
      next: (res) => {
        let features = (res.data?.features || []) as GeoJSON.Feature[];
        if (this.deskEventTypeFilter?.length) {
          const allow = new Set(this.deskEventTypeFilter);
          features = features.filter((f) => allow.has(String(f.properties?.['event_type'] || '')));
        }
        const src = this.ensureDeskOverlay('desk-l5-events', (f) => {
          const et = String(f.get('event_type') || '');
          const meta = getLrsAssetTypeMeta(et);
          const color = meta?.color || '#64748b';
          const g = f.getGeometry();
          if (g instanceof Point) {
            return new Style({
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#fff', width: 1.5 }),
              }),
            });
          }
          return new Style({ stroke: new Stroke({ color, width: 4 }) });
        });
        src.clear();
        const feats = this.format.readFeatures(
          JSON.stringify({ type: 'FeatureCollection', features }),
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        ) as Feature[];
        src.addFeatures(feats);
        this.setDeskOverlayVisible('desk-l5-events', true);
        this.deskStackStatus = `L5: ${feats.length} events`;
      },
      error: (e) => {
        this.deskStackStatus = e?.error?.error || 'Failed to load events';
      },
    });
  }

  private loadDeskAssetsCombined(): void {
    const routeId = this.selectedLrsRouteId;
    if (!routeId) {
      this.deskStackStatus = 'Select an LRS route for assets';
      return;
    }
    const code = this.lrsRoutes.find((r) => r.id === routeId)?.route_code || `route-${routeId}`;
    this.lrsIncludeUtilities = true;
    this.loadLrsUtilities(routeId, code);
    this.layerRegistry[`LRS ${code} assets`]?.setVisible(true);
    this.layerRegistry[`LRS ${code} utilities`]?.setVisible(true);
    this.setDeskOverlayVisible('desk-l6-assets', true);
    this.deskStackStatus = 'L6: highway assets + utilities';
  }

  publishRoadGraph(): void {
    if (!this.projectId) {
      this.deskPublishStatus = 'Open a road project first';
      return;
    }
    const sources = [this.roadSource, this.vectorSource].filter(Boolean);
    const nodes: {
      client_key: string;
      junction_type?: string;
      geometry: GeoJSON.Point;
      properties?: Record<string, unknown>;
    }[] = [];
    const edges: {
      from_node_key?: string;
      to_node_key?: string;
      geometry: GeoJSON.LineString;
      road_ownership?: string;
      properties?: Record<string, unknown>;
    }[] = [];
    const nodeCoords: { key: string; coord: number[] }[] = [];

    for (const src of sources) {
      for (const f of src.getFeatures()) {
        const type = String(f.get('type') || '');
        const geom = f.getGeometry();
        if (type === 'junction' && geom instanceof Point) {
          const lonLat = toLonLat(geom.getCoordinates());
          const key = `n${nodes.length}`;
          nodes.push({
            client_key: key,
            junction_type: String(f.get('junctionType') || 'junction'),
            geometry: { type: 'Point', coordinates: lonLat },
            properties: { info: f.get('info') },
          });
          nodeCoords.push({ key, coord: lonLat });
        }
      }
    }
    for (const src of sources) {
      for (const f of src.getFeatures()) {
        const geom = f.getGeometry();
        if (!(geom instanceof LineString)) continue;
        const type = String(f.get('type') || '');
        const isRoad =
          !!f.get('roadType') ||
          type === 'road' ||
          type === 'alignment_route' ||
          type === 'dummy_road_corridor' ||
          src === this.roadSource;
        if (!isRoad || type === 'junction-label') continue;
        const coords = geom.getCoordinates().map((c) => toLonLat(c));
        if (coords.length < 2) continue;
        const start = coords[0];
        const end = coords[coords.length - 1];
        const snap = (pt: number[]) => {
          let best: string | undefined;
          let bestD = 0.00025; // ~25m at equator
          for (const n of nodeCoords) {
            const d = Math.hypot(n.coord[0] - pt[0], n.coord[1] - pt[1]);
            if (d < bestD) {
              bestD = d;
              best = n.key;
            }
          }
          return best;
        };
        edges.push({
          from_node_key: snap(start),
          to_node_key: snap(end),
          geometry: { type: 'LineString', coordinates: coords },
          road_ownership: String(f.get('roadType') || f.get('info') || ''),
          properties: { type, roadType: f.get('roadType') },
        });
      }
    }
    if (!nodes.length && !edges.length) {
      this.deskPublishStatus = 'Draw junctions and road alignments first';
      return;
    }
    this.deskPublishBusy = true;
    this.deskPublishStatus = 'Publishing road graph…';
    this.lrsService.putRoadGraph(String(this.projectId), { nodes, edges }).subscribe({
      next: (res) => {
        this.deskPublishBusy = false;
        this.deskPublishStatus = `Published ${res.data.nodes?.length || 0} nodes, ${res.data.edges?.length || 0} edges`;
        this.deskStack.l1_roadgraph = true;
        this.loadDeskRoadGraph();
      },
      error: (e) => {
        this.deskPublishBusy = false;
        this.deskPublishStatus = e?.error?.error || 'Publish road graph failed';
      },
    });
  }

  publishAlignmentToLrs(): void {
    const geom = this.pickRoadAlignmentGeometry();
    if (!geom) {
      this.deskPublishStatus = 'Draw a road alignment (LineString) first';
      return;
    }
    this.deskPublishBusy = true;
    if (this.selectedLrsRouteId) {
      this.deskPublishStatus = 'Updating LRS route geometry…';
      this.lrsService.realignRoute(this.selectedLrsRouteId, { geometry: geom, notes: 'Published from Road tab' }).subscribe({
        next: () => {
          this.deskPublishBusy = false;
          this.deskPublishStatus = 'Alignment published to selected LRS route';
          this.deskStack.l3_calibration = true;
          this.loadLrsRouteToMap();
          this.loadDeskCalibration();
        },
        error: (e) => {
          this.deskPublishBusy = false;
          this.deskPublishStatus = e?.error?.error || 'Publish alignment failed';
        },
      });
      return;
    }
    this.deskPublishStatus = 'Creating LRS route from alignment…';
    this.lrsService.listNetworks({ project_id: this.projectId ? String(this.projectId) : undefined }).subscribe({
      next: (netRes) => {
        const ensureNet$ = netRes.data?.length
          ? of(netRes.data[0])
          : this.lrsService
              .createNetwork({
                network_code: `NET-${this.projectId || Date.now()}`,
                network_name: `Project ${this.projectId || 'road'} network`,
                project_id: this.projectId ? String(this.projectId) : undefined,
              })
              .pipe(map((r) => r.data));
        ensureNet$.subscribe({
          next: (net) => {
            const code = `ROUTE-${this.projectId || Date.now()}`;
            this.lrsService
              .createRoute({
                route_code: code,
                route_name: code,
                geometry: geom,
                project_id: this.projectId ? String(this.projectId) : undefined,
                network_id: net.id,
              })
              .subscribe({
                next: (res) => {
                  this.deskPublishBusy = false;
                  this.selectedLrsRouteId = res.data.id;
                  this.deskPublishStatus = `Created LRS route ${res.data.route_code}`;
                  this.loadLrsRouteList();
                  this.deskStack.l2_routes = true;
                  this.deskStack.l3_calibration = true;
                  this.loadLrsRouteToMap();
                  this.loadDeskCalibration();
                },
                error: (e) => {
                  this.deskPublishBusy = false;
                  this.deskPublishStatus = e?.error?.error || 'Create route failed';
                },
              });
          },
          error: (e) => {
            this.deskPublishBusy = false;
            this.deskPublishStatus = e?.error?.error || 'Network required for route';
          },
        });
      },
      error: (e) => {
        this.deskPublishBusy = false;
        this.deskPublishStatus = e?.error?.error || 'Failed to list networks';
      },
    });
  }

  validateRoadGraphTopology(): void {
    if (!this.projectId) {
      this.deskPublishStatus = 'Open a project first';
      return;
    }
    this.deskPublishStatus = 'Running network QC…';
    this.lrsService
      .validateNetworkQc({
        project_id: String(this.projectId),
        route_id: this.selectedLrsRouteId || undefined,
      })
      .subscribe({
        next: (res) => {
          const issues = res.data?.issues?.length ?? 0;
          this.deskPublishStatus = `Network QC: ${issues} issue(s)`;
        },
        error: (e) => {
          this.deskPublishStatus = e?.error?.error || 'Network QC failed';
        },
      });
  }

  private pickRoadAlignmentGeometry(): GeoJSON.LineString | null {
    let best: LineString | null = null;
    let bestLen = 0;
    for (const src of [this.roadSource, this.vectorSource]) {
      if (!src) continue;
      for (const f of src.getFeatures()) {
        const g = f.getGeometry();
        if (!(g instanceof LineString)) continue;
        const type = String(f.get('type') || '');
        if (type === 'junction-label') continue;
        const len = g.getLength();
        if (len > bestLen) {
          bestLen = len;
          best = g;
        }
      }
    }
    if (!best) return null;
    return {
      type: 'LineString',
      coordinates: best.getCoordinates().map((c) => toLonLat(c)),
    };
  }

  private loadLrsUtilities(routeId: number, code: string): void {
    this.lrsService.listUtilityAssets(routeId).subscribe({
      next: (res) => {
        const features = (res.data || [])
          .filter((u) => u.geometry)
          .map((u) => ({
            type: 'Feature' as const,
            geometry: u.geometry as GeoJSON.Geometry,
            properties: {
              id: u.id,
              utility_type: u.utility_type,
              label: u.label,
              depth_m: u.depth_m,
              grade: u.grade,
              measure_km: u.measure_m != null ? u.measure_m / 1000 : null,
              measure_m: u.measure_m,
              measure_to_m: u.measure_to_m,
              lateral_offset_m: u.lateral_offset_m,
              edge_offset_m: u.edge_offset_m,
              side: u.side,
              diameter_mm: u.diameter_mm,
              lifecycle_status: u.lifecycle_status,
              condition: u.condition,
              owner_name: u.owner_name,
              material: u.material,
            },
          }));
        if (features.length) {
          this.upsertLrsLayer(
            `LRS ${code} utilities`,
            { type: 'FeatureCollection', features },
            'utilities'
          );
          this.lrsStatus += ` · ${features.length} utilities`;
        }
      },
      error: () => {
        /* utilities are optional — keep route load result */
      },
    });
  }

  private loadLrsCarriagewaysAndLanes(routeId: number, code: string): void {
    this.lrsService.listCarriageways(routeId).subscribe({
      next: (res) => {
        const features = (res.data || [])
          .filter((c) => c.geometry)
          .map((c) => ({
            type: 'Feature' as const,
            geometry: c.geometry as GeoJSON.Geometry,
            properties: {
              carriageway_code: c.carriageway_code,
              lane_count: c.lane_count,
              direction: c.direction,
              offset_m: c.lateral_offset_m,
            },
          }));
        if (features.length) {
          this.upsertLrsLayer(`LRS ${code} carriageways`, { type: 'FeatureCollection', features }, 'carriageways');
          this.lrsStatus += ` · ${features.length} carriageways`;
        }
      },
      error: () => {
        /* optional layer */
      },
    });
    this.lrsService.listLanes(routeId).subscribe({
      next: (res) => {
        const features = (res.data || [])
          .filter((l) => l.geometry)
          .map((l) => ({
            type: 'Feature' as const,
            geometry: l.geometry as GeoJSON.Geometry,
            properties: {
              lane_code: l.lane_code,
              lane_number: l.lane_number,
              transition_type: l.transition_type,
              start_offset_m: l.start_offset_m,
              end_offset_m: l.end_offset_m,
            },
          }));
        if (features.length) {
          this.upsertLrsLayer(`LRS ${code} lanes`, { type: 'FeatureCollection', features }, 'lanes');
          this.lrsStatus += ` · ${features.length} lanes`;
        }
      },
      error: () => {
        /* optional layer */
      },
    });
  }

  private loadLrsDocuments(routeId: number): void {
    this.lrsService.listDocumentsByLrs({ route_id: routeId }).subscribe({
      next: (res) => {
        this.lrsLinkedDocuments = res.data || [];
        this.plotLrsDocumentMarkers(this.lrsLinkedDocuments, routeId);
      },
      error: () => {
        this.lrsLinkedDocuments = [];
        this.plotLrsDocumentMarkers([], routeId);
      },
    });
  }

  private loadLrsChainageMarkers(routeId: number, code: string): void {
    this.lrsService.generateChainageMarkers(routeId, this.lrsChainageIntervalKm, false).subscribe({
      next: (res) => {
        if (res.data?.features?.length) {
          this.upsertLrsLayer(`LRS ${code} chainage`, res.data, 'chainage');
        }
      },
      error: () => {
        /* chainage markers are optional */
      },
    });
  }

  /** Same symbology as the LRS manager map (shared style engine). */
  private lrsLayerStyle(
    kind: 'centerline' | 'assets' | 'utilities' | 'chainage' | 'carriageways' | 'lanes' | 'row_band'
  ): (feature: FeatureLike, resolution?: number) => Style | Style[] {
    const inner = this.lrsLayerStyleInner(kind);
    // Centerline and ROW band visibility is handled by their own toggles, not the NHAI catalog.
    if (kind === 'centerline' || kind === 'row_band') return inner;
    return (f, res) => {
      const members = f.get('features') as Feature[] | undefined;
      if (Array.isArray(members) && members.length) {
        if (!members.some((m) => this.isNhaiFeatureVisible(m, kind))) return [];
        return styleCluster(f, res ?? 1);
      }
      if (!this.isNhaiFeatureVisible(f, kind)) return [];
      return inner(f, res);
    };
  }

  onNhaiVisibility(ev: { nodeId: string; checked: boolean }): void {
    this.nhaiLayerVisibility = toggleCatalogNode(ev.nodeId, ev.checked, this.nhaiLayerVisibility);
    if (this.nhaiLayerVisibility['ref-chainage'] && this.selectedLrsRouteId && this.demoActiveRouteCode) {
      this.lrsIncludeChainage = true;
      this.loadLrsChainageMarkers(this.selectedLrsRouteId, this.demoActiveRouteCode);
    }
    this.syncNhaiSourceVisibility();
  }

  private isNhaiFeatureVisible(feature: FeatureLike, kind?: string): boolean {
    const layerKind = kind || feature.get('lrsKind');
    if (layerKind === 'documents') return true;
    if (this.compactForBoq && this.boqTypeFilter) {
      if (layerKind === 'centerline') return this.boqAlignmentOn;
      if (layerKind === 'chainage' || layerKind === 'carriageways' || layerKind === 'lanes') return true;
      const tid = String(feature.get('utility_type') || feature.get('event_type') || '');
      if (tid && !this.boqTypeFilter.has(tid)) return false;
    }
    const props: CatalogFeatureProps = {
      event_type: feature.get('event_type'),
      utility_type: feature.get('utility_type'),
      grade: feature.get('grade'),
      depth_m: feature.get('depth_m'),
      lrsKind: layerKind,
    };
    return isCatalogFeatureVisible(props, this.nhaiLayerVisibility);
  }

  private refreshNhaiLayerCounts(): void {
    const features: CatalogFeatureProps[] = [];
    Object.values(this.layerRegistry).forEach((layer) => {
      const kind = layer.get('lrsKind');
      if (!kind) return;
      const src = layer.getSource() as VectorSource | null;
      src?.getFeatures().forEach((f) => {
        features.push({
          event_type: f.get('event_type'),
          utility_type: f.get('utility_type'),
          grade: f.get('grade'),
          depth_m: f.get('depth_m'),
          lrsKind: kind,
        });
      });
    });
    const counts: Record<string, number> = {};
    for (const leaf of flattenCatalogLeaves(false)) {
      counts[leaf.id] = countFeaturesForLeaf(leaf, features);
    }
    this.nhaiLayerCounts = counts;
  }

  private syncNhaiSourceVisibility(): void {
    const vis = this.nhaiLayerVisibility;
    Object.values(this.layerRegistry).forEach((layer) => {
      const kind = layer.get('lrsKind');
      if (!kind) return;
      if (kind === 'chainage') layer.setVisible(vis['ref-chainage'] !== false);
      else if (kind === 'carriageways' || kind === 'lanes') layer.setVisible(vis['road-corridor'] !== false);
      layer.changed();
    });
    Object.entries(this.lrsLineCompanionLayers).forEach(([parentId, companion]) => {
      const parent = this.layerRegistry[parentId];
      companion.setVisible(parent ? parent.getVisible() : false);
    });
    this.map?.render();
  }

  private lrsLayerStyleInner(
    kind: 'centerline' | 'assets' | 'utilities' | 'chainage' | 'carriageways' | 'lanes' | 'row_band'
  ): (feature: FeatureLike, resolution?: number) => Style | Style[] {
    switch (kind) {
      case 'centerline':
        return () => new Style({ stroke: new Stroke({ color: '#1d4ed8', width: 5 }) });
      case 'row_band':
        // Translucent ROW corridor: stroke width = full ROW width in metres (half each side of centerline).
        return (f, res) => {
          const rowWidthM = Number(f.get('row_width_m')) || 0;
          if (!(rowWidthM > 0)) return [];
          const px = this.metersWidthToStrokePixels(rowWidthM, res ?? 1);
          return [
            new Style({
              stroke: new Stroke({ color: 'rgba(16, 185, 129, 0.16)', width: px, lineCap: 'butt' }),
            }),
            new Style({
              stroke: new Stroke({ color: 'rgba(5, 150, 105, 0.55)', width: 1.25, lineDash: [10, 8] }),
            }),
          ];
        };
      case 'assets':
        return (f, res) => styleForLrsFeature(f, res ?? 1);
      case 'utilities':
        return (f, res) => this.styleLrsUtilityWithOffset(f, res ?? 1);
      case 'carriageways':
        return (f) =>
          new Style({
            stroke: new Stroke({ color: '#94a3b8', width: 3, lineDash: [6, 4] }),
            text: this.demoShowOffsets
              ? new TextStyle({
                  text: `Carr ${f.get('carriageway_code') || ''} ${f.get('offset_m') != null ? f.get('offset_m') + 'm' : ''}`,
                  placement: 'line',
                  font: '8px sans-serif',
                  fill: new Fill({ color: '#475569' }),
                })
              : undefined,
          });
      case 'lanes':
        return (f) => {
          const transition = String(f.get('transition_type') || 'through');
          const colors: Record<string, string> = {
            through: '#0ea5e9',
            merge: '#16a34a',
            diverge: '#f59e0b',
            weave: '#a855f7',
          };
          const off =
            f.get('start_offset_m') != null
              ? `${f.get('start_offset_m')}–${f.get('end_offset_m')} m`
              : '';
          return new Style({
            stroke: new Stroke({
              color: colors[transition] || '#0ea5e9',
              width: 2.5,
              lineDash: transition === 'through' ? undefined : [8, 5],
            }),
            text: this.demoShowOffsets
              ? new TextStyle({
                  text: `${f.get('lane_code') || 'Lane'} ${off}`,
                  placement: 'line',
                  font: '8px sans-serif',
                  fill: new Fill({ color: '#0f172a' }),
                })
              : undefined,
          });
        };
      case 'chainage':
        return (f, res) => {
          const showLabel = (res ?? 1) <= 6;
          return new Style({
            image: new CircleStyle({
              radius: (res ?? 1) > 20 ? 2 : 3,
              fill: new Fill({ color: '#fbbf24' }),
            }),
            text: showLabel
              ? new TextStyle({
                  text: String(f.get('chainage_label') || f.get('label') || ''),
                  offsetY: -10,
                  font: '9px sans-serif',
                  stroke: new Stroke({ color: '#fff', width: 2 }),
                })
              : undefined,
          });
        };
    }
  }

  private styleLrsUtilityWithOffset(feature: FeatureLike, resolution: number): Style | Style[] {
    const base = styleUtilityFeature(feature, resolution);
    if (!this.demoShowOffsets || resolution > 4) return base;
    const off = feature.get('offset_m');
    const depth = feature.get('depth_m');
    const label = [feature.get('label'), off != null ? `↔${off}m` : '', depth != null ? `↓${depth}m` : '']
      .filter(Boolean)
      .join(' ');
    if (!label) return base;
    const styles = Array.isArray(base) ? [...base] : [base];
    styles.push(
      new Style({
        text: new TextStyle({
          text: label,
          offsetY: -16,
          font: '8px sans-serif',
          fill: new Fill({ color: '#1e293b' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
      })
    );
    return styles.length === 1 ? styles[0] : styles;
  }

  /** Create or refresh a read-only LRS layer from a GeoJSON FeatureCollection (EPSG:4326). */
  private upsertLrsLayer(
    layerId: string,
    featureCollection: { type: 'FeatureCollection'; features: unknown[] },
    kind: 'centerline' | 'assets' | 'utilities' | 'chainage' | 'carriageways' | 'lanes' | 'row_band'
  ): void {
    const clusterPoints = kind === 'assets' || kind === 'utilities';
    let layer = this.layerRegistry[layerId];
    if (!layer) {
      let source: VectorSource = new VectorSource();
      if (clusterPoints) {
        const pointSrc = new VectorSource();
        const cluster = new Cluster({
          source: pointSrc,
          distance: LRS_DEFAULT_CLUSTER_DISTANCE,
          minDistance: 12,
        });
        this.lrsClusterSources[layerId] = cluster;
        source = cluster;
        const lineLayer = new VectorLayer({
          source: new VectorSource(),
          style: this.lrsLayerStyle(kind),
          declutter: true,
          zIndex: 11,
        });
        lineLayer.set('lrsReadonly', true);
        lineLayer.set('lrsKind', kind);
        lineLayer.set('lrsCompanionOf', layerId);
        this.lrsLineCompanionLayers[layerId] = lineLayer;
        this.map.addLayer(lineLayer);
      }
      layer = new VectorLayer({
        source,
        style: this.lrsLayerStyle(kind),
        declutter: true,
        zIndex:
          kind === 'row_band'
            ? 2
            : kind === 'centerline'
              ? 4
              : kind === 'carriageways'
                ? 11
                : kind === 'lanes'
                  ? 13
                  : kind === 'chainage'
                    ? 15
                    : 12,
      });
      layer.set('lrsReadonly', true);
      layer.set('lrsKind', kind);
      this.registerLayer(layerId, layer);
      this.layerMetadata[layerId].description = 'LRS data (read-only)';
      this.layerMetadata[layerId].readonly = true;
      this.map.addLayer(layer);
      this.overlayLayerIds.add(layerId);
    } else if (kind === 'centerline') {
      layer.setZIndex(4);
    }

    const features = this.format.readFeatures(JSON.stringify(featureCollection), {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    }) as Feature[];
    features.forEach((f) => {
      applySymbolProperties(f);
      f.set('lrsReadonly', true);
      f.set('lrsKind', kind);
    });

    const cluster = this.lrsClusterSources[layerId];
    const companion = this.lrsLineCompanionLayers[layerId];
    if (cluster) {
      const pointSrc = cluster.getSource() as VectorSource;
      pointSrc?.clear();
      companion?.getSource()?.clear();
      const points = features.filter((f) => f.getGeometry() instanceof Point);
      const lines = features.filter((f) => !(f.getGeometry() instanceof Point));
      pointSrc?.addFeatures(points);
      companion?.getSource()?.addFeatures(lines);
      this.applyLrsClusterDistance();
    } else {
      const source = layer.getSource() as VectorSource;
      source.clear();
      source.addFeatures(features);
    }
    if (kind === 'centerline') {
      this.syncOverviewCenterline(features);
    }
    this.updateLayerStatistics(layerId);
    this.refreshNhaiLayerCounts();
    this.syncNhaiSourceVisibility();
    this.updateLayerManagementUI();
  }

  private scheduleLrsClusterDistance(): void {
    if (this.lrsClusterDistanceRaf) cancelAnimationFrame(this.lrsClusterDistanceRaf);
    this.lrsClusterDistanceRaf = requestAnimationFrame(() => {
      this.lrsClusterDistanceRaf = 0;
      this.applyLrsClusterDistance();
    });
  }

  private applyLrsClusterDistance(): void {
    const zoom = this.map?.getView().getZoom();
    if (zoom == null) return;
    const dist = clusterDistanceForZoom(zoom, LRS_DEFAULT_CLUSTER_DISTANCE);
    Object.values(this.lrsClusterSources).forEach((cluster) => {
      cluster.setDistance(dist);
      cluster.setMinDistance(dist > 0 ? Math.min(14, dist) : 0);
    });
  }

  /** Display-only pavement on the road layer; LRS GeoJSON stays a LineString. */
  private overlayDemoCorridorFromLrsCenterline(routeCode: string): void {
    this.clearDemoCorridorOverlays();
    const layer = this.layerRegistry[`LRS ${routeCode} centerline`];
    const src = layer?.getSource();
    if (!(src instanceof VectorSource)) return;
    const feat = src.getFeatures().find((f) => f.getGeometry() instanceof LineString);
    const geom = feat?.getGeometry();
    if (!(geom instanceof LineString)) return;
    this.addDemoCorridorFromCenterline(geom, `${routeCode} corridor`);
    this.layerRegistry['road']?.setZIndex(8);
    layer.setZIndex(4);
  }

  /** Click on an LRS feature opens the read-only detail card (consistent with LRS manager). */
  private bindLrsAssetClick(): void {
    if (this.lrsClickHandlerBound) return;
    this.lrsClickHandlerBound = true;
    this.map.on('singleclick', (event: { pixel: number[] }) => {
      const documentHit = this.map.forEachFeatureAtPixel(
        event.pixel,
        (f: FeatureLike) => f as Feature,
        {
          hitTolerance: 8,
          layerFilter: (layer) => layer.get('lrsKind') === 'documents',
        }
      );
      if (documentHit) {
        this.selectLrsDocumentFromFeature(documentHit as Feature);
        return;
      }
      if (this.activeSidebarTab === 'lrs') return;
      let pickedKind: string | undefined;
      const hit = this.map.forEachFeatureAtPixel(
        event.pixel,
        (f: FeatureLike, layer) => {
          pickedKind = layer?.get('lrsKind');
          return f as Feature;
        },
        {
          hitTolerance: 8,
          layerFilter: (layer) =>
            layer.get('lrsReadonly') === true &&
            layer.get('lrsKind') !== 'centerline' &&
            layer.get('lrsKind') !== 'row_band',
        }
      );
      if (hit) {
        if (pickedKind === 'documents' || (hit as Feature).get('lrsKind') === 'documents') {
          this.selectLrsDocumentFromFeature(hit as Feature);
        } else {
          this.openLrsAssetDetail(hit as Feature, pickedKind);
        }
      } else if (this.lrsAssetDetail || this.lrsDetailId) {
        this.closeLrsAssetDetail();
      }
    });
  }

  private openLrsAssetDetail(f: Feature, layerKind?: string): void {
    const eventType = String(f.get('event_type') || '');
    const utilityType = String(f.get('utility_type') || '');
    const isUtility = layerKind === 'utilities' || (!!utilityType && !eventType);
    const isChainage = layerKind === 'chainage' || eventType === 'chainage';
    const typeId = isUtility ? utilityType : eventType;
    const assetId = Number(f.get('id') || f.getId());

    if (isChainage || !assetId) {
      this.lrsDetailId = null;
      this.lrsDetailKind = null;
      this.closeRoadEditDetail();
      this.lrsAssetDetail = null;
      this.clearLrsCallouts();
      return;
    }

    const typeLabel = isUtility
      ? LRS_UTILITY_TYPES.find((u) => u.id === typeId)?.label || typeId
      : getLrsAssetTypeMeta(typeId)?.label || typeId || 'Asset';

    this.closeRoadEditDetail();
    this.lrsAssetDetail = null;
    this.lrsDetailKind = isUtility ? 'utility' : 'event';
    this.lrsDetailId = assetId;
    const measureM = f.get('measure_m') != null ? Number(f.get('measure_m')) : undefined;
    this.lrsDashboard.selectAsset({
      kind: this.lrsDetailKind,
      id: assetId,
      measure_m: measureM,
      label: String(f.get('label') || typeLabel),
      typeId,
    });
    if (measureM != null) {
      const label = this.toKmHudLabel('', measureM);
      this.lrsDashboard.setChainage({ measure_m: measureM, label });
      this.chainageHudLabel = label;
    }
    this.refreshLrsCallouts(f, typeLabel, isUtility ? utilityType : eventType);
  }

  closeLrsAssetDetail(): void {
    this.lrsAssetDetail = null;
    this.lrsDetailId = null;
    this.lrsDetailKind = null;
    this.lrsDashboard.selectAsset(null);
    this.clearLrsCallouts();
  }

  onGisLrsAssetSaved(): void {
    this.lrsDashboard.markDirty();
    if (this.selectedLrsRouteId) this.loadLrsRouteToMap();
  }

  onGisLrsAssetRetired(): void {
    this.onGisLrsAssetSaved();
    this.closeLrsAssetDetail();
  }

  openGisLinkedAsset(ev: { kind: 'event' | 'utility'; id: number }): void {
    this.lrsDetailKind = ev.kind;
    this.lrsDetailId = ev.id;
    this.lrsDashboard.selectAsset({ kind: ev.kind, id: ev.id });
  }

  private scheduleChainageHud(lng: number, lat: number): void {
    if (Date.now() < this.suppressHudUntil) return;
    const routeId = this.lrsDashboard.getRouteId() || this.selectedLrsRouteId;
    if (!routeId) return;
    if (this.chainageHudTimer) clearTimeout(this.chainageHudTimer);
    this.chainageHudTimer = setTimeout(() => {
      this.lrsService.gpsToChainage(routeId, lng, lat).subscribe({
        next: (res) => {
          this.chainageHudLabel = this.toKmHudLabel(res.data.chainage_label, res.data.measure_m);
          this.lrsDashboard.setChainage({
            measure_m: res.data.measure_m,
            label: this.chainageHudLabel,
          });
        },
        error: () => {},
      });
    }, 280);
  }

  private refreshLrsCallouts(primary: Feature, typeLabel: string, typeId: string): void {
    this.clearLrsCallouts();
    const coord = this.featureCalloutCoord(primary);
    if (!coord) return;
    const spec = this.calloutSpec(primary, typeLabel, typeId);
    this.addLrsCallout(coord, spec.title, spec.sub, spec.color);
    const nearby = this.nearbyLrsCalloutFeatures(coord, primary).slice(0, 3);
    for (const f of nearby) {
      const c = this.featureCalloutCoord(f);
      if (!c) continue;
      const et = String(f.get('event_type') || '');
      const ut = String(f.get('utility_type') || '');
      const tid = ut || et;
      const label = ut
        ? LRS_UTILITY_TYPES.find((u) => u.id === tid)?.label || tid
        : getLrsAssetTypeMeta(tid)?.label || tid;
      const s = this.calloutSpec(f, label, tid);
      this.addLrsCallout(c, s.title, s.sub, s.color);
    }
  }

  private calloutSpec(f: Feature, typeLabel: string, typeId: string): { title: string; sub: string; color: string } {
    const dia = f.get('diameter_mm');
    const span = f.get('span_m') || f.get('properties')?.span_m;
    const depth = f.get('depth_m');
    const bits: string[] = [];
    if (dia != null) bits.push(`Dia: ${dia}mm`);
    if (span != null) bits.push(`Span: ${span}m`);
    if (depth != null) bits.push(`Depth: ${depth}m`);
    const color =
      LRS_UTILITY_TYPES.find((u) => u.id === typeId)?.color ||
      getLrsAssetTypeMeta(typeId)?.color ||
      '#0f172a';
    return {
      title: String(f.get('label') || typeLabel),
      sub: bits.join(' · ') || typeLabel,
      color,
    };
  }

  private featureCalloutCoord(f: Feature): number[] | null {
    const geom = f.getGeometry();
    if (!geom) return null;
    if (geom instanceof Point) return geom.getCoordinates();
    if (geom instanceof LineString) return geom.getCoordinateAt(0.5);
    const extent = geom.getExtent();
    return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
  }

  private nearbyLrsCalloutFeatures(origin: number[], skip: Feature): Feature[] {
    const scored: { f: Feature; d: number }[] = [];
    Object.values(this.layerRegistry).forEach((layer) => {
      if (layer.get('lrsKind') !== 'utilities' && layer.get('lrsKind') !== 'assets') return;
      const src = layer.getSource();
      if (!(src instanceof VectorSource)) return;
      src.getFeatures().forEach((f) => {
        if (f === skip) return;
        const c = this.featureCalloutCoord(f);
        if (!c) return;
        const d = Math.hypot(c[0] - origin[0], c[1] - origin[1]);
        scored.push({ f, d });
      });
    });
    scored.sort((a, b) => a.d - b.d);
    return scored.map((s) => s.f);
  }

  private addLrsCallout(coord: number[], title: string, sub: string, color: string): void {
    const el = document.createElement('div');
    el.className = 'lrs-map-callout';
    const dot = document.createElement('span');
    dot.className = 'lrs-map-callout-dot';
    dot.style.background = color;
    const text = document.createElement('div');
    const strong = document.createElement('strong');
    strong.textContent = title;
    const small = document.createElement('small');
    small.textContent = sub;
    text.append(strong, small);
    el.append(dot, text);
    const overlay = new Overlay({
      element: el,
      positioning: 'bottom-left',
      offset: [10, -8],
      stopEvent: false,
    });
    overlay.setPosition(coord);
    this.map.addOverlay(overlay);
    this.lrsCalloutOverlays.push(overlay);
  }

  private clearLrsCallouts(): void {
    for (const o of this.lrsCalloutOverlays) {
      this.map?.removeOverlay(o);
      o.getElement()?.remove();
    }
    this.lrsCalloutOverlays = [];
  }

  // =========================================================================
  // Technical demo — road + utility layers, LRS, chainage, geo-docs, terrain
  // =========================================================================

  setChainageIntervalM(m: number): void {
    if (CHAINAGE_INTERVAL_OPTIONS_M.includes(m as 50 | 100)) {
      this.chainageIntervalM = m;
      this.refreshDemoChainageMarkers();
    }
  }

  onDemoShowOffsetsChange(checked: boolean): void {
    this.demoShowOffsets = checked;
    this.refreshDemoUtilityOffsetLabels();
    this.refreshLrsThematicLayers();
    if (this.demoCorridorLine) {
      const src = this.getDemoLayerSource('road_cross_section');
      src
        ?.getFeatures()
        .filter((f) => f.get('assetTag') === 'chainage')
        .forEach((f) => src!.removeFeature(f));
      this.seedLocalChainageMarkers(this.demoCorridorLine);
    }
  }

  toggleDemo3DView(): void {
    this.enable3DView();
  }

  loadDemoBoqBom(): void {
    if (!this.projectId) {
      this.demoStatus = 'Open a GIS project to load BOQ/BOM';
      return;
    }
    this.boqBomLoading = true;
    this.boqList = [];
    this.bomList = [];
    const pid = this.projectId;
    this.apiService.getBoQs({ project_id: pid, limit: 20 }).subscribe({
      next: (res: { success?: boolean; data?: unknown[] }) => {
        if (res?.success && Array.isArray(res.data)) this.boqList = res.data as typeof this.boqList;
        this.apiService.getBoms({ project_id: pid, limit: 20 }).subscribe({
          next: (bomRes: { success?: boolean; data?: unknown[] }) => {
            if (bomRes?.data && Array.isArray(bomRes.data)) {
              this.bomList = bomRes.data as typeof this.bomList;
            }
            this.boqBomLoading = false;
            this.demoStatus = `BOQ: ${this.boqList.length} · BOM: ${this.bomList.length}`;
          },
          error: () => {
            this.boqBomLoading = false;
          },
        });
      },
      error: () => {
        this.apiService.getBoms({ project_id: pid, limit: 20 }).subscribe({
          next: (bomRes: { success?: boolean; data?: unknown[] }) => {
            if (bomRes?.data && Array.isArray(bomRes.data)) {
              this.bomList = bomRes.data as typeof this.bomList;
            }
            this.boqBomLoading = false;
          },
          error: () => {
            this.boqBomLoading = false;
          },
        });
      },
    });
  }

  openDemoBoq(boqId: number): void {
    this.router.navigate(['/home/boq', boqId, 'view']);
  }

  openDemoBom(bomId: number): void {
    this.router.navigate(['/home/bom'], { queryParams: { project_id: this.projectId, bom_id: bomId } });
  }

  assignSelectedToDemoLayer(): void {
    if (!this.selectInteraction) {
      this.showInstructions('Use Select tool (toolbar) then pick features');
      setTimeout(() => this.hideInstructions(), 2000);
      return;
    }
    const selected = this.selectInteraction.getFeatures();
    const count = selected.getLength();
    if (!count) {
      this.demoStatus = 'Select map features to tag to a layer';
      return;
    }
    this.ensureDemoLayers();
    const dest = this.getDemoLayerSource(this.demoTagTargetLayerId);
    if (!dest) return;
    const moved: Feature[] = [];
    selected.forEach((f) => {
      const feat = f as Feature;
      const src = this.getSourceForFeature(feat);
      if (src) src.removeFeature(feat);
      this.tagFeature(feat, this.demoTagTargetLayerId, String(feat.get('assetTag') || 'user_asset'));
      dest.addFeature(feat);
      moved.push(feat);
    });
    selected.clear();
    this.updateLayerStatistics(this.demoTagTargetLayerId);
    this.updateLayerManagementUI();
    this.demoStatus = `Tagged ${moved.length} feature(s) → ${this.demoTagTargetLayerId}`;
  }

  private refreshDemoChainageMarkers(): void {
    if (this.demoCorridorLine) {
      const src = this.getDemoLayerSource('road_cross_section');
      src
        ?.getFeatures()
        .filter((f) => f.get('assetTag') === 'chainage')
        .forEach((f) => src!.removeFeature(f));
      this.seedLocalChainageMarkers(this.demoCorridorLine);
    }
    if (this.selectedLrsRouteId && this.demoActiveRouteCode && this.lrsIncludeChainage) {
      this.loadLrsChainageMarkers(this.selectedLrsRouteId, this.demoActiveRouteCode);
    }
  }

  private refreshLrsThematicLayers(): void {
    if (!this.demoActiveRouteCode) return;
    const code = this.demoActiveRouteCode;
    [`LRS ${code} utilities`, `LRS ${code} carriageways`, `LRS ${code} lanes`].forEach((id) => {
      this.layerRegistry[id]?.changed();
    });
  }

  private refreshDemoUtilityOffsetLabels(): void {
    if (!this.demoCorridorLine) return;
    for (const def of DEMO_ALL_LAYERS) {
      if (def.category !== 'utility') continue;
      const src = this.getDemoLayerSource(def.id);
      src
        ?.getFeatures()
        .filter((f) => f.get('demoInfra') === true)
        .forEach((f) => src!.removeFeature(f));
    }
    this.seedDemoUtilityNetworks(this.demoCorridorLine);
  }

  /** One-click LRS corridor demo (NH-DEMO-48). Opens the LRS tab and seeds via query param. */
  bootstrapLrsDemo(): void {
    this.switchSidebarTab('lrs');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { demo: 'lrs', tab: 'lrs', demo_run: String(Date.now()) },
      queryParamsHandling: 'merge',
    });
  }

  /** One-click technical demo for Maria / stakeholder walkthrough. */
  bootstrapTechnicalDemo(): void {
    if (!this.map) return;
    this.demoLoading = true;
    this.demoStatus = 'Preparing technical demo…';
    this.ensureDemoLayers();
    this.toggleDemoTerrain(true);
    const center = this.map.getView().getCenter() || fromLonLat([78.47, 17.39]);
    const corridor = this.buildDemoCenterline(center);
    this.demoCorridorLine = corridor;
    this.clearDemoFeatures();
    this.seedDemoRoadCrossSection(corridor);
    this.seedDemoUtilityNetworks(corridor);
    this.seedDemoRoadAssetLayers(corridor);
    this.seedLocalChainageMarkers(corridor);
    this.lrsIncludeUtilities = true;
    this.lrsIncludeChainage = true;
    this.loadLrsDemoShowcase();
    this.loadDemoBoqBom();
    this.map.getView().fit(corridor.getExtent(), { padding: [80, 80, 80, 80], duration: 600 });
    this.demoLoading = false;
    this.demoStatus =
      'Demo loaded — 4-lane corridor (same as Draw Road), utilities, drainage, ITS, chainage @ ' +
      `${this.chainageIntervalM} m. Toggle layers in panel.`;
    this.showInstructions('Technical demo ready — explore layers, LRS, geo-docs, BOM link.');
    setTimeout(() => this.hideInstructions(), 3500);
  }

  loadLrsDemoShowcase(): void {
    this.lrsService.seedDemo({ project_id: this.projectId ? String(this.projectId) : 'TECH-DEMO', replace: false }).subscribe({
      next: (res) => {
        const routeId = res.data?.route_id;
        if (routeId) {
          this.selectedLrsRouteId = routeId;
          this.loadLrsRouteToMap();
        }
        this.demoStatus += ' · LRS demo corridor linked';
      },
      error: () => {
        this.demoStatus += ' · LRS seed skipped (API offline)';
        if (this.lrsRoutes.length) {
          this.selectedLrsRouteId = this.lrsRoutes[0].id;
          this.loadLrsRouteToMap();
        }
      },
    });
  }

  toggleDemoTerrain(on?: boolean): void {
    const enable = on ?? !this.demoTerrainOn;
    this.demoTerrainOn = enable;
    if (enable) {
      if (!this.demoTerrainLayer) {
        this.demoTerrainLayer = new TileLayer({
          source: new XYZ({ url: DEMO_TERRAIN_TILE_URL, crossOrigin: 'anonymous' }),
          opacity: 0.55,
          zIndex: 2,
        });
      }
      if (!this.map.getLayers().getArray().includes(this.demoTerrainLayer)) {
        this.map.addLayer(this.demoTerrainLayer);
      }
      this.changeBaseMap('osm');
    } else if (this.demoTerrainLayer) {
      this.map.removeLayer(this.demoTerrainLayer);
    }
  }

  openConstructionBom(): void {
    const q = this.projectId ? { project_id: this.projectId } : {};
    this.router.navigate(['/home/bom'], { queryParams: q });
  }

  openConstructionBoq(): void {
    const q = this.projectId ? { project_id: this.projectId } : {};
    this.router.navigate(['/home/boq'], { queryParams: q });
  }

  openDocumentLibrary(): void {
    const q: Record<string, string> = {};
    if (this.projectId) q['project_id'] = this.projectId;
    void this.router.navigate(['/home/document-list'], { queryParams: q });
  }

  lrsDocumentChainage(doc: LrsDocumentRef): string {
    return lrsChainageLabel(lrsMetaFromDocument(doc));
  }

  downloadLrsDocument(doc: LrsDocumentRef, event?: Event): void {
    event?.stopPropagation();
    this.apiService.downloadDocument(doc.id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = doc.file_name || doc.document_name || `document-${doc.id}`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {},
    });
  }

  focusLrsDocument(doc: LrsDocumentRef): void {
    this.selectedLrsDocumentId = doc.id;
    this.layerRegistry[this.lrsDocumentLayerId]?.changed();
    const lrs = lrsMetaFromDocument(doc);
    const km = measureKmFromLrsMeta(lrs);
    if (km != null) {
      this.panToLrsStation({ measure_m: km * 1000, label: lrsChainageLabel(lrs) });
    }
    if (this.enableLrsTab) this.switchSidebarTab('lrs');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { document_id: doc.id, tab: 'lrs' },
      queryParamsHandling: 'merge',
    });
  }

  openLrsDocumentOnMap(doc: LrsDocumentRef): void {
    this.focusLrsDocument(doc);
  }

  private selectLrsDocumentFromFeature(f: Feature): void {
    const id = Number(f.get('document_id') || f.get('id'));
    const doc = this.lrsLinkedDocuments.find((d) => d.id === id);
    if (doc) this.focusLrsDocument(doc);
  }

  private ensureLrsDocumentLayer(): VectorLayer {
    let layer = this.layerRegistry[this.lrsDocumentLayerId];
    if (!layer) {
      layer = new VectorLayer({
        source: new VectorSource(),
        style: (f) => this.styleLrsDocumentFeature(f),
        zIndex: 16,
      });
      layer.set('lrsReadonly', true);
      layer.set('lrsKind', 'documents');
      this.registerLayer(this.lrsDocumentLayerId, layer);
      this.layerMetadata[this.lrsDocumentLayerId].description = 'LRS-tagged documents (read-only)';
      this.layerMetadata[this.lrsDocumentLayerId].readonly = true;
      this.map?.addLayer(layer);
      this.overlayLayerIds.add(this.lrsDocumentLayerId);
    }
    return layer;
  }

  private styleLrsDocumentFeature(feature: FeatureLike): Style {
    const selected = Number(feature.get('document_id')) === this.selectedLrsDocumentId;
    return new Style({
      image: new CircleStyle({
        radius: selected ? 8 : 6,
        fill: new Fill({ color: selected ? '#1d4ed8' : '#7c3aed' }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      text: new TextStyle({
        text: String(feature.get('label') || ''),
        offsetY: -12,
        font: selected ? '700 10px sans-serif' : '9px sans-serif',
        fill: new Fill({ color: '#1e293b' }),
        stroke: new Stroke({ color: '#fff', width: 3 }),
      }),
    });
  }

  private plotLrsDocumentMarkers(docs: LrsDocumentRef[], routeId: number): void {
    const layer = this.ensureLrsDocumentLayer();
    const source = layer.getSource() as VectorSource;
    source.clear();
    const tagged = docs
      .map((doc) => ({ doc, km: measureKmFromLrsMeta(lrsMetaFromDocument(doc)) }))
      .filter((row): row is { doc: LrsDocumentRef; km: number } => row.km != null);
    if (!tagged.length) {
      this.updateLayerStatistics(this.lrsDocumentLayerId);
      this.updateLayerManagementUI();
      return;
    }
    const requests = tagged.map(({ doc, km }) =>
      this.lrsService.interpolate(routeId, km).pipe(
        map((res) => ({ doc, loc: res.data })),
        catchError(() => of(null))
      )
    );
    forkJoin(requests).subscribe((rows) => {
      const features: Feature[] = [];
      for (const row of rows) {
        if (!row) continue;
        const coords = row.loc?.closest_point?.coordinates || row.loc?.point?.coordinates;
        if (!coords || coords.length < 2) continue;
        const f = new Feature({
          geometry: new Point(fromLonLat([coords[0], coords[1]])),
        });
        f.set('lrsReadonly', true);
        f.set('lrsKind', 'documents');
        f.set('document_id', row.doc.id);
        f.set('id', row.doc.id);
        f.set('label', row.doc.document_name || `#${row.doc.id}`);
        features.push(f);
      }
      source.clear();
      source.addFeatures(features);
      this.updateLayerStatistics(this.lrsDocumentLayerId);
      this.updateLayerManagementUI();
    });
  }

  private ensureDemoLayers(): void {
    for (const def of DEMO_ALL_LAYERS) {
      if (!this.layerRegistry[def.id]) {
        this.createNewLayer(def.id, 'line', def.group, def.paletteId);
        const layer = this.layerRegistry[def.id];
        if (layer) {
          layer.setZIndex(def.zIndex);
          this.layerMetadata[def.id].description = def.description;
          this.layerMetadata[def.id].demoLayer = true;
        }
      }
    }
    this.updateLayerManagementUI();
  }

  private getDemoLayerSource(layerId: string): VectorSource | null {
    const layer = this.layerRegistry[layerId];
    const src = layer?.getSource();
    return src instanceof VectorSource ? src : null;
  }

  private tagFeature(feature: Feature, layerId: string, assetTag: string, extra?: Record<string, unknown>): void {
    feature.set('demoLayer', layerId);
    feature.set('assetTag', assetTag);
    feature.set('demoInfra', true);
    if (extra) Object.entries(extra).forEach(([k, v]) => feature.set(k, v));
  }

  private clearDemoFeatures(): void {
    this.clearDemoCorridorOverlays();
    for (const def of DEMO_ALL_LAYERS) {
      const src = this.getDemoLayerSource(def.id);
      src?.getFeatures()
        .filter((f) => f.get('demoInfra') === true)
        .forEach((f) => src!.removeFeature(f));
    }
  }

  private buildDemoCenterline(anchor: number[]): LineString {
    return new LineString([
      [anchor[0] - 2200, anchor[1] - 400],
      [anchor[0] - 800, anchor[1] - 280],
      [anchor[0], anchor[1] - 200],
      [anchor[0] + 900, anchor[1] - 160],
      [anchor[0] + 2100, anchor[1] - 80],
    ]);
  }

  /** Drawn-road corridor on the road layer (same path as Draw Road / multi-lane). */
  private seedDemoRoadCrossSection(centerLine: LineString): void {
    this.addDemoCorridorFromCenterline(centerLine, 'NH demo corridor');
  }

  private seedDemoUtilityNetworks(centerLine: LineString): void {
    const specs: {
      layerId: keyof typeof DEMO_UTILITY_COLORS;
      assets: { tag: string; offsetM: number; depthM: number; label: string; linear?: boolean }[];
    }[] = [
      {
        layerId: 'water_network',
        assets: [
          { tag: 'transmission_main', offsetM: -22, depthM: 2.2, label: 'Water trunk main', linear: true },
          { tag: 'distribution_line', offsetM: -18, depthM: 1.4, label: 'Distribution main', linear: true },
          { tag: 'prv', offsetM: -16, depthM: 1.2, label: 'PRV-01', linear: false },
          { tag: 'hydrant', offsetM: -14, depthM: 0.8, label: 'Hydrant H-12', linear: false },
        ],
      },
      {
        layerId: 'telecom_network',
        assets: [
          { tag: 'ofc_backbone', offsetM: 18, depthM: 1.0, label: 'OFC backbone', linear: true },
          { tag: 'ofc_distribution', offsetM: 14, depthM: 0.9, label: 'OFC distribution', linear: true },
          { tag: 'splice_pit', offsetM: 12, depthM: 0.7, label: 'Splice pit SP-3', linear: false },
          { tag: 'cabinet', offsetM: 10, depthM: 0, label: 'FD Cabinet', linear: false },
        ],
      },
      {
        layerId: 'electrical_network',
        assets: [
          { tag: 'ht_transmission', offsetM: -28, depthM: 1.5, label: '11 kV feeder', linear: true },
          { tag: 'lt_distribution', offsetM: -24, depthM: 1.0, label: 'LT distribution', linear: true },
          { tag: 'transformer', offsetM: -20, depthM: 0, label: 'DT-50 kVA', linear: false },
          { tag: 'pole', offsetM: -26, depthM: 0, label: 'HT pole P-88', linear: false },
        ],
      },
      {
        layerId: 'gas_network',
        assets: [
          { tag: 'gas_transmission', offsetM: 26, depthM: 1.8, label: 'Gas transmission', linear: true },
          { tag: 'gas_distribution', offsetM: 22, depthM: 1.2, label: 'Gas distribution', linear: true },
          { tag: 'valve', offsetM: 20, depthM: 1.0, label: 'Section valve V-2', linear: false },
          { tag: 'regulator', offsetM: 24, depthM: 0.5, label: 'Regulator RG-1', linear: false },
        ],
      },
    ];

    for (const spec of specs) {
      const src = this.getDemoLayerSource(spec.layerId);
      if (!src) continue;
      const colors = DEMO_UTILITY_COLORS[spec.layerId];
      for (const a of spec.assets) {
        const geom = a.linear
          ? this.createOffsetLineString(centerLine, a.offsetM)
          : new Point(centerLine.getCoordinateAt(0.35 + (Math.abs(a.offsetM) % 5) * 0.08));
        if (!geom) continue;
        const f = new Feature({ geometry: geom, type: `demo_${a.tag}` });
        this.tagFeature(f, spec.layerId, a.tag, {
          label: a.label,
          depth_m: a.depthM,
          offset_m: a.offsetM,
          info: `${a.label} @ ${a.offsetM} m offset, ${a.depthM} m depth`,
        });
        const strokeColor =
          a.tag.includes('transmission') || a.tag.includes('backbone') || a.tag.includes('ht')
            ? colors.main
            : a.tag.includes('distribution') || a.tag.includes('lt')
              ? colors.dist
              : colors.ancillary;
        const offsetLabel = this.demoShowOffsets
          ? [a.label, `↔${a.offsetM}m`, a.depthM ? `↓${a.depthM}m` : ''].filter(Boolean).join(' ')
          : a.label.split(' ')[0];
        if (a.linear) {
          f.setStyle(
            new Style({
              stroke: new Stroke({
                color: strokeColor,
                width: a.tag.includes('transmission') || a.tag.includes('ht') ? 4 : 3,
                lineDash: a.tag.includes('distribution') ? [8, 6] : undefined,
              }),
              text: this.demoShowOffsets
                ? new TextStyle({
                    text: offsetLabel,
                    placement: 'line',
                    font: '8px sans-serif',
                    fill: new Fill({ color: '#1e293b' }),
                    stroke: new Stroke({ color: '#fff', width: 2 }),
                  })
                : undefined,
            })
          );
        } else {
          f.setStyle(
            new Style({
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({ color: strokeColor }),
                stroke: new Stroke({ color: '#fff', width: 2 }),
              }),
              text: new TextStyle({
                text: offsetLabel,
                offsetY: -12,
                font: '8px sans-serif',
                fill: new Fill({ color: '#1e293b' }),
              }),
            })
          );
        }
        src.addFeature(f);
      }
    }
  }

  private seedDemoRoadAssetLayers(centerLine: LineString): void {
    const drainageSrc = this.getDemoLayerSource('drainage_system');
    if (drainageSrc) {
      [-12, 12].forEach((off, i) => {
        const g = this.createOffsetLineString(centerLine, off);
        if (!g) return;
        const f = new Feature({ geometry: g, type: 'demo_storm_drain' });
        this.tagFeature(f, 'drainage_system', 'storm_drain', { label: `Side drain ${i + 1}` });
        f.setStyle(new Style({ stroke: new Stroke({ color: '#14b8a6', width: 3, lineDash: [6, 4] }) }));
        drainageSrc.addFeature(f);
      });
      const culvertPt = centerLine.getCoordinateAt(0.55);
      const cf = new Feature({ geometry: new Point(culvertPt), type: 'demo_culvert' });
      this.tagFeature(cf, 'drainage_system', 'culvert', { label: 'Culvert C-400' });
      cf.setStyle(new Style({ image: new CircleStyle({ radius: 5, fill: new Fill({ color: '#0891b2' }) }) }));
      drainageSrc.addFeature(cf);
    }

    const furnSrc = this.getDemoLayerSource('road_furniture');
    if (furnSrc) {
      const items = [
        { at: 0.2, label: 'KM stone', color: '#2563eb' },
        { at: 0.45, label: 'Crash barrier', color: '#dc2626' },
        { at: 0.7, label: 'Street light', color: '#ca8a04' },
      ];
      items.forEach((it) => {
        const f = new Feature({ geometry: new Point(centerLine.getCoordinateAt(it.at)), type: 'demo_furniture' });
        this.tagFeature(f, 'road_furniture', it.label.toLowerCase().replace(/\s/g, '_'), { label: it.label });
        f.setStyle(
          new Style({
            image: new CircleStyle({ radius: 6, fill: new Fill({ color: it.color }), stroke: new Stroke({ color: '#fff', width: 2 }) }),
          })
        );
        furnSrc.addFeature(f);
      });
    }

    const itsSrc = this.getDemoLayerSource('its_system');
    if (itsSrc) {
      const itsItems = [
        { at: 0.3, label: 'CCTV', color: '#7c3aed' },
        { at: 0.5, label: 'VMS', color: '#9333ea' },
        { at: 0.65, label: 'SOS', color: '#ef4444' },
        { at: 0.85, label: 'ATMS', color: '#6366f1' },
      ];
      itsItems.forEach((it) => {
        const f = new Feature({ geometry: new Point(centerLine.getCoordinateAt(it.at)), type: 'demo_its' });
        this.tagFeature(f, 'its_system', it.label.toLowerCase(), { label: it.label });
        f.setStyle(
          new Style({
            image: new RegularShape({
              points: 4,
              radius: 7,
              angle: Math.PI / 4,
              fill: new Fill({ color: it.color }),
              stroke: new Stroke({ color: '#fff', width: 1.5 }),
            }),
          })
        );
        itsSrc.addFeature(f);
      });
    }
  }

  /** Chainage markers along demo corridor (50 m or 100 m). */
  private seedLocalChainageMarkers(centerLine: LineString): void {
    const src = this.getDemoLayerSource('road_cross_section');
    if (!src) return;
    const lenM = parseFloat(this.calculateLength(new Feature({ geometry: centerLine })).meters || '0') || 0;
    const step = this.chainageIntervalM;
    const count = Math.floor(lenM / step);
    for (let i = 0; i <= count; i++) {
      const m = i * step;
      const frac = lenM > 0 ? Math.min(1, m / lenM) : 0;
      const pt = centerLine.getCoordinateAt(frac);
      const ch = `CH ${Math.floor(m / 1000)}+${String(m % 1000).padStart(3, '0')}`;
      const f = new Feature({ geometry: new Point(pt), type: 'demo_chainage', chainage_label: ch });
      this.tagFeature(f, 'road_cross_section', 'chainage', { label: ch, measure_m: m });
      f.setStyle(
        new Style({
          image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#fbbf24' }), stroke: new Stroke({ color: '#92400e', width: 1 }) }),
          text: new TextStyle({
            text: this.demoShowOffsets ? ch : '',
            offsetY: -12,
            font: '9px sans-serif',
            fill: new Fill({ color: '#0f172a' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
          }),
        })
      );
      src.addFeature(f);
    }
  }

  /** Build closed corridor polygon between left/right offset lines (map metres). */
  private createCorridorPolygonFromCenterline(
    centerLine: LineString,
    leftOffsetM: number,
    rightOffsetM: number
  ): Polygon | null {
    const left = this.createOffsetLineString(centerLine, leftOffsetM);
    const right = this.createOffsetLineString(centerLine, rightOffsetM);
    if (!left || !right) return null;
    const lCoords = left.getCoordinates();
    const rCoords = [...right.getCoordinates()].reverse();
    if (lCoords.length < 2 || rCoords.length < 2) return null;
    const ring = [...lCoords, ...rCoords, lCoords[0]];
    return new Polygon([ring]);
  }

  createNewLayer(name: string, type: string, group: string, paletteId?: string): void {
    const newSource = new VectorSource();
    const layerId = name;
    const pid = paletteId || 'default';
    if (!this.layerStyles[layerId]) {
      this.layerStyles[layerId] = {
        strokeColor: '#3498db',
        strokeWidth: 2,
        fillColor: 'rgba(52, 152, 219, 0.2)',
        paletteId: pid
      };
    } else if (this.layerStyles[layerId]) {
      this.layerStyles[layerId].paletteId = pid;
    }
    this.layerPaletteId[layerId] = pid;
    const newLayer = new VectorLayer({
      source: newSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(layerId, feature, resolution)
    });
    
    this.registerLayer(name, newLayer);
    this.map.addLayer(newLayer);
    
    if (group) {
      if (!this.layerGroups[group]) this.layerGroups[group] = [];
      this.layerGroups[group].push(name);
    }
    
    this.updateLayerManagementUI();
  }

  private showImportLayerOptions(geoJsonContent: string): void {
    const dialog = document.createElement('div');
    dialog.className = 'import-layer-dialog';
    dialog.innerHTML = `
      <div class="dialog-content">
        <h3>Import Layer</h3>
        <div class="form-group">
          <label>Layer Name:</label>
          <input type="text" id="importLayerName" placeholder="Enter layer name">
        </div>
        <div class="form-group">
          <label>Group:</label>
          <select id="importLayerGroup">
            <option value="">No Group</option>
            ${Object.keys(this.layerGroups).map(group => 
              `<option value="${group}">${group}</option>`
            ).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>Import Options:</label>
          <label><input type="checkbox" id="clearExisting" checked> Clear existing features</label>
        </div>
        <div class="button-group">
          <button id="importLayer" class="btn">Import</button>
          <button id="cancelImport" class="btn">Cancel</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    document.getElementById('importLayer')?.addEventListener('click', () => {
      const name = (document.getElementById('importLayerName') as HTMLInputElement)?.value;
      const group = (document.getElementById('importLayerGroup') as HTMLSelectElement)?.value;
      const clearExisting = (document.getElementById('clearExisting') as HTMLInputElement)?.checked;
      
      if (name) {
        this.importLayerToNewLayer(name, geoJsonContent, group, clearExisting);
        document.body.removeChild(dialog);
      }
    });
    
    document.getElementById('cancelImport')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });
  }

  private importLayerToNewLayer(name: string, geoJsonContent: string, group: string, clearExisting: boolean): void {
    const newSource = new VectorSource();
    this.layerPaletteId[name] = 'default';
    if (!this.layerStyles[name]) {
      this.layerStyles[name] = { paletteId: 'default' };
    } else {
      this.layerStyles[name].paletteId = 'default';
    }
    const newLayer = new VectorLayer({
      source: newSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(name, feature, resolution)
    });
    
    this.registerLayer(name, newLayer);
    this.map.addLayer(newLayer);
    
    if (group) {
      if (!this.layerGroups[group]) this.layerGroups[group] = [];
      this.layerGroups[group].push(name);
    }
    
    this.importLayer(name, geoJsonContent);
    this.updateLayerManagementUI();
  }

  private setupThemeSelection(): void {
    this.syncBaseMapSelectUi();
  }

  onBaseMapSelect(event: Event): void {
    const theme = (event.target as HTMLSelectElement).value || 'google';
    this.pickBaseMap(theme);
  }

  pickBaseMap(theme: string): void {
    this.changeBaseMap(theme);
    this.syncBaseMapSelectUi();
    this.syncOsmNetworkWithBaseMap(theme);
  }

  setMeasureDisplayUnit(unit: 'm' | 'km' | 'mi' | string): void {
    const next: 'm' | 'km' | 'mi' =
      unit === 'km' ? 'km' : unit === 'mi' || unit === 'imperial' ? 'mi' : 'm';
    this.measureDisplayUnit = next;
    this.defaultSettings.measurementUnits = next === 'mi' ? 'imperial' : 'metric';
    this.syncScaleLineUnits();
    const sel = document.getElementById('measurementUnits') as HTMLSelectElement | null;
    if (sel && sel.value !== next) sel.value = next;
  }

  private syncScaleLineUnits(): void {
    const olUnits = this.measureDisplayUnit === 'mi' ? 'imperial' : 'metric';
    if (this.scaleLineControl) {
      this.scaleLineControl.setUnits(olUnits as 'metric' | 'imperial');
      return;
    }
    if (!this.map) return;
    const existing = this.map.getControls().getArray().find((c) => c instanceof ScaleLine) as ScaleLine | undefined;
    if (existing) {
      this.scaleLineControl = existing;
      existing.setUnits(olUnits as 'metric' | 'imperial');
    }
  }

  /** Format geodesic metres using the toolbar unit preference. */
  formatLengthM(meters: number): string {
    const m = Number(meters) || 0;
    if (this.measureDisplayUnit === 'km') return `${(m / 1000).toFixed(3)} km`;
    if (this.measureDisplayUnit === 'mi') return `${(m / 1609.344).toFixed(3)} mi`;
    return `${m.toFixed(2)} m`;
  }

  formatAreaM2(areaM2: number): string {
    const a = Number(areaM2) || 0;
    if (this.measureDisplayUnit === 'km') return `${(a / 1_000_000).toFixed(4)} km²`;
    if (this.measureDisplayUnit === 'mi') return `${(a / 2_589_988.110336).toFixed(4)} mi²`;
    return `${a.toFixed(2)} m²`;
  }

  /** Convert a user-entered length in the current display unit to metres. */
  private displayUnitToMeters(value: number): number {
    if (this.measureDisplayUnit === 'km') return value * 1000;
    if (this.measureDisplayUnit === 'mi') return value * 1609.344;
    return value;
  }

  private selectedEditableFeatures(action: string): Feature[] | null {
    const selected = this.selectInteraction?.getFeatures().getArray() as Feature[] | undefined;
    if (!selected) {
      this.showInstructions(`Use Select tool, then ${action}`);
      return null;
    }
    if (!selected.length) {
      this.showInstructions(`Select one or more features to ${action.toLowerCase()}`);
      return null;
    }
    return selected;
  }

  private extentCenterOf(features: Feature[]): number[] | null {
    const ext = createEmptyExtent();
    features.forEach((f) => {
      const g = f.getGeometry();
      if (g) extendExtent(ext, g.getExtent());
    });
    return isEmptyExtent(ext) ? null : getCenter(ext);
  }

  /** Road copies need their own corridorId, otherwise refreshing them would delete the original's guides. */
  private cloneForPaste(feature: Feature, dx: number, dy: number): Feature {
    const clone = feature.clone();
    clone.setId(undefined);
    clone.getGeometry()?.translate(dx, dy);
    if (clone.get('corridorId')) clone.set('corridorId', `corridor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    return clone;
  }

  /** Rebuild lane and divider guides for decorated roads after their geometry changed. */
  private syncRoadDecorations(features: Feature[]): void {
    features.forEach((f) => {
      if (f.get('corridorId') && this.isEditableRoadFeature(f) && this.getSourceForFeature(f) === this.roadSource) {
        this.refreshRoadCorridorDecorations(f);
      }
    });
  }

  private addPastedFeatures(items: { feature: Feature; source: VectorSource }[], dx: number, dy: number): Feature[] {
    const clones: Feature[] = [];
    items.forEach(({ feature, source }) => {
      const clone = this.cloneForPaste(feature, dx, dy);
      source.addFeature(clone);
      clones.push(clone);
    });
    this.syncRoadDecorations(clones);
    this.enableTranslate();
    const selected = this.selectInteraction?.getFeatures();
    clones.forEach((c) => selected?.push(c));
    this.saveToHistory();
    return clones;
  }

  copySelectedFeatures(): void {
    const selected = this.selectedEditableFeatures('Copy');
    if (!selected) return;
    this.featureClipboard = selected.map((feature) => ({
      feature: feature.clone(),
      source: this.getSourceForFeature(feature) || this.getCurrentDrawingSource(),
    }));
    this.clipboardAnchor = this.extentCenterOf(this.featureClipboard.map((c) => c.feature));
    this.showInstructions(`Copied ${selected.length} feature(s) — Ctrl/Cmd+V pastes at the cursor`);
  }

  pasteFeatures(): void {
    if (!this.featureClipboard.length || !this.clipboardAnchor) {
      this.showInstructions('Nothing to paste — select features and press Ctrl/Cmd+C first');
      return;
    }
    const target = this.lastPointerCoord || this.map.getView().getCenter();
    if (!target) return;
    const dx = target[0] - this.clipboardAnchor[0];
    const dy = target[1] - this.clipboardAnchor[1];
    const clones = this.addPastedFeatures(this.featureClipboard, dx, dy);
    this.showInstructions(`Pasted ${clones.length} feature(s) — drag to adjust`);
  }

  duplicateSelectedFeatures(): void {
    const selected = this.selectedEditableFeatures('Duplicate');
    if (!selected) return;
    const nudgeM = 8;
    const items = selected.map((feature) => ({
      feature,
      source: this.getSourceForFeature(feature) || this.getCurrentDrawingSource(),
    }));
    const clones = this.addPastedFeatures(items, nudgeM, -nudgeM);
    this.showInstructions(`Duplicated ${clones.length} feature(s) — drag to adjust`);
  }

  createParallelLines(): void {
    if (typeof turf === 'undefined' || !turf.lineOffset) {
      this.showInstructions('Parallel tool requires Turf.js (lineOffset)');
      return;
    }
    if (!this.selectInteraction) {
      this.showInstructions('Use Select tool, then Parallel');
      return;
    }
    const selected = this.selectInteraction.getFeatures().getArray();
    const lines = selected.filter((f) => {
      const g = f.getGeometry();
      return g instanceof LineString;
    });
    if (!lines.length) {
      this.showInstructions('Select a line feature first');
      return;
    }
    const unitLabel = this.measureDisplayUnit;
    const distRaw = window.prompt(`Offset distance (${unitLabel})`, unitLabel === 'm' ? '10' : unitLabel === 'km' ? '0.01' : '0.006');
    if (distRaw == null) return;
    const distVal = parseFloat(distRaw);
    if (!Number.isFinite(distVal) || distVal === 0) {
      this.showInstructions('Enter a non-zero offset distance');
      return;
    }
    const sideRaw = (window.prompt('Side: left, right, or both', 'both') || 'both').trim().toLowerCase();
    const sides: Array<'left' | 'right'> =
      sideRaw === 'left' ? ['left'] : sideRaw === 'right' ? ['right'] : ['left', 'right'];
    const offsetM = this.displayUnitToMeters(Math.abs(distVal));
    const offsetKm = offsetM / 1000;
    const created: Feature[] = [];
    for (const feature of lines) {
      const geom = feature.getGeometry() as LineString;
      const base = this.format.writeFeatureObject(new Feature({ geometry: geom.clone() }), {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      for (const side of sides) {
        const signedKm = side === 'left' ? offsetKm : -offsetKm;
        try {
          const offsetGeo = turf.lineOffset(base, signedKm, { units: 'kilometers' });
          if (!offsetGeo?.geometry) continue;
          const line = this.format.readGeometry(offsetGeo.geometry, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          });
          if (!(line instanceof LineString)) continue;
          const parallel = new Feature({ geometry: line });
          parallel.set('type', 'parallel_line');
          parallel.set('parallelSide', side);
          parallel.set('parallelOffsetM', offsetM);
          parallel.set(
            'info',
            `Parallel ${side} · ${this.formatLengthM(offsetM)}`
          );
          parallel.setStyle(
            new Style({
              stroke: new Stroke({
                color: side === 'left' ? '#0ea5e9' : '#f97316',
                width: 3,
                lineDash: [8, 6],
                lineCap: 'round',
              }),
            })
          );
          const targetSrc = this.getSourceForFeature(feature) || this.getCurrentDrawingSource();
          targetSrc.addFeature(parallel);
          created.push(parallel);
        } catch (e) {
          console.warn('Parallel offset failed', e);
        }
      }
    }
    if (!created.length) {
      this.showInstructions('Could not create parallel line(s)');
      return;
    }
    this.selectInteraction.getFeatures().clear();
    created.forEach((f) => this.selectInteraction!.getFeatures().push(f));
    this.saveToHistory();
    this.showInstructions(`Created ${created.length} parallel line(s)`);
  }

  toggleOsmNetwork(enabled: boolean): void {
    this.osmNetworkEnabled = enabled;
    this.osmNetworkOverlay?.setEnabled(enabled);
    this.osmNetworkOverlay?.setNetworkVisible(enabled);
    if (!enabled) {
      this.osmNodesEnabled = false;
      this.osmNetworkOverlay?.setNodesVisible(false);
      this.osmNetworkStatus = '';
      this.osmRoadCount = 0;
    } else {
      this.osmNetworkOverlay?.setNodesVisible(this.osmNodesEnabled);
      this.osmNetworkOverlay?.refresh();
    }
    this.map?.render();
  }

  private initOsmNetworkOverlay(): void {
    if (!this.map || this.osmNetworkOverlay) return;
    this.osmNetworkOverlay = new OsmNetworkOverlay(this.osmMapApi, {
      onStatus: (message) => {
        this.osmNetworkStatus = message;
        this.osmRoadCount = this.osmNetworkOverlay?.roadCount ?? 0;
        this.updateLayerManagementUI();
      },
    });
    this.osmNetworkOverlay.attach(this.map);
    this.osmNetworkOverlay.setEnabled(this.osmNetworkEnabled);
    this.osmNetworkOverlay.setNetworkVisible(this.osmNetworkEnabled);
    this.osmNetworkOverlay.setNodesVisible(this.osmNodesEnabled);
    this.syncOsmNetworkWithBaseMap(this.selectedBaseMapKey);
  }

  /** Prefer OSM vectors on satellite/hybrid basemaps (iD-style). */
  private syncOsmNetworkWithBaseMap(baseMapKey?: string): void {
    const key = baseMapKey || this.selectedBaseMapKey;
    if (this.osmSatelliteBaseKeys.has(key) && !this.osmNetworkEnabled) {
      this.toggleOsmNetwork(true);
    } else if (this.osmNetworkEnabled) {
      this.osmNetworkOverlay?.refresh();
    }
  }

  private syncBaseMapSelectUi(): void {
    this.selectedBaseMapKey = this.getCurrentBaseMapKey();
    const sel = document.getElementById('baseMapSelect') as HTMLSelectElement | null;
    if (sel && sel.value !== this.selectedBaseMapKey) {
      sel.value = this.selectedBaseMapKey;
    }
  }

  private toggleLayer(layerName: string, visible: boolean): void {
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        let shouldToggle = false;
        
        switch (layerName) {
          case 'drawing':
            shouldToggle = source === this.vectorSource;
            break;
          case 'road':
            shouldToggle = source === this.roadSource;
            break;
          case 'project':
            shouldToggle = source === this.projectSource;
            break;
          case 'biometric':
            shouldToggle = source === this.biometricSource;
            break;
          case 'gps':
            shouldToggle = source === this.gpsSource;
            break;
          case 'water':
            shouldToggle = source === this.waterSource;
            break;
        }
        
        if (shouldToggle) {
          layer.setVisible(visible);
        }
      }
    });
  }

  private setLayerOpacity(layerName: string, opacity: number): void {
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        let shouldSetOpacity = false;
        
        switch (layerName) {
          case 'drawing':
            shouldSetOpacity = source === this.vectorSource;
            break;
          case 'road':
            shouldSetOpacity = source === this.roadSource;
            break;
          case 'project':
            shouldSetOpacity = source === this.projectSource;
            break;
          case 'biometric':
            shouldSetOpacity = source === this.biometricSource;
            break;
          case 'gps':
            shouldSetOpacity = source === this.gpsSource;
            break;
          case 'water':
            shouldSetOpacity = source === this.waterSource;
            break;
        }
        
        if (shouldSetOpacity) {
          layer.setOpacity(opacity);
        }
      }
    });
  }

  private changeBaseMap(theme: string): void {
    const layers = this.map.getLayers().getArray();
    const currentBaseLayer = layers.find(layer => layer instanceof TileLayer);
    if (currentBaseLayer) {
      this.map.removeLayer(currentBaseLayer);
    }
    if (theme === 'custom') {
      const url = prompt(
        'Enter tile URL template (use {z}, {x}, {y} for tile coordinates):',
        'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
      );
      if (url) {
        this.baseMaps['custom'] = new TileLayer({
          source: new XYZ({ url, attributions: 'Custom' })
        });
        this.map.getLayers().insertAt(0, this.baseMaps['custom']);
        this.currentBaseMap = this.baseMaps['custom'];
      }
      this.syncBaseMapSelectUi();
      return;
    }
    const newBaseMap = this.baseMaps[theme];
    if (newBaseMap) {
      this.map.getLayers().insertAt(0, newBaseMap);
      this.currentBaseMap = newBaseMap;
    }
    this.syncBaseMapSelectUi();
    this.syncOsmNetworkWithBaseMap(theme);
  }

  private setupAdditionalButtonListeners(): void {
    // Pan button
    const panBtn = document.getElementById('panBtn');
    if (panBtn) {
      panBtn.addEventListener('click', () => this.removeInteractions());
    }

    // Modify button
    const modifyBtn = document.getElementById('modifyBtn');
    if (modifyBtn) {
      modifyBtn.addEventListener('click', () => this.enableModify());
    }
    const moveBtn = document.getElementById('moveBtn');
    if (moveBtn) {
      moveBtn.addEventListener('click', () => this.enableTranslate());
    }
    const rotateBtn = document.getElementById('rotateBtn');
    if (rotateBtn) {
      rotateBtn.addEventListener('click', () => this.enableRotate());
    }

    // Delete button
    const deleteBtn = document.getElementById('deleteBtn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => this.enableDelete());
    }

    // Zoom buttons
    const zoomInBtn = document.getElementById('zoomIn');
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }

    const zoomOutBtn = document.getElementById('zoomOut');
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }

    // Clear button
    const clearBtn = document.getElementById('clearBtn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearAll());
    }

    // Export button
    const exportBtn = document.getElementById('exportBtn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.exportData());
    }

    // Import button
    const importBtn = document.getElementById('importBtn');
    if (importBtn) {
      importBtn.addEventListener('click', () => this.importData());
    }

    // Segment drawing buttons
    const startSegmentBtn = document.getElementById('startSegmentDrawing');
    if (startSegmentBtn) {
      startSegmentBtn.addEventListener('click', () => this.startSegmentDrawing());
    }

    const stopSegmentBtn = document.getElementById('stopSegmentDrawing');
    if (stopSegmentBtn) {
      stopSegmentBtn.addEventListener('click', () => this.stopSegmentDrawing());
    }

    // Numbering mode selector
    const numberingTypeSelect = document.getElementById('numberingType');
    if (numberingTypeSelect) {
      numberingTypeSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.setNumberingMode(target.value as 'auto' | 'manual');
      });
    }

    // Reset numbering button
    const resetNumbersBtn = document.getElementById('resetNumbers');
    if (resetNumbersBtn) {
      resetNumbersBtn.addEventListener('click', () => this.resetNumbering());
    }

    // Manual numbering inputs
    const manualNodeNumberInput = document.getElementById('manualNodeNumber');
    if (manualNodeNumberInput) {
      manualNodeNumberInput.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.setManualNodeNumber(parseInt(target.value));
      });
    }

    const manualSegmentNumberInput = document.getElementById('manualSegmentNumber');
    if (manualSegmentNumberInput) {
      manualSegmentNumberInput.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.setManualSegmentNumber(parseInt(target.value));
      });
    }
  }

  private setupButtonEventListeners(): void {
    // Toolbar buttons
    this.setupToolbarButtons();
    
    // Select button
    const selectBtn = document.getElementById('selectBtn');
    if (selectBtn) {
      selectBtn.addEventListener('click', () => this.enableSelect());
    }

    // Symbol buttons are wired in setupSymbolSelection() (one handler; no dual map-click).

    // Add more button event listeners
    this.setupAdditionalButtonListeners();
    this.setupJunctionButtons();
    this.setupRoadInfrastructureButtons();
    this.setupAdvancedControls();
    this.setupSurveyControls();
    this.setupManagementControls();
    this.setupBiometricControls();
    this.setupSettingsControls();
    this.setupLayerManagement();
    this.setupThemeSelection();
  }

  private setupToolbarButtons(): void {
    // Zoom controls
    const zoomInBtn = document.getElementById('zoomIn');
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }

    const zoomOutBtn = document.getElementById('zoomOut');
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }

    const zoomFitBtn = document.getElementById('zoomFit');
    if (zoomFitBtn) {
      zoomFitBtn.addEventListener('click', () => this.zoomFit());
    }

    const zoomExtentBtn = document.getElementById('zoomExtent');
    if (zoomExtentBtn) {
      zoomExtentBtn.addEventListener('click', () => this.zoomExtent());
    }

    // View history navigation
    const zoomPreviousBtn = document.getElementById('zoomPrevious');
    if (zoomPreviousBtn) {
      zoomPreviousBtn.addEventListener('click', () => this.previousView());
    }

    const zoomNextBtn = document.getElementById('zoomNext');
    if (zoomNextBtn) {
      zoomNextBtn.addEventListener('click', () => this.nextView());
    }

    // Pan button
    const panBtn = document.getElementById('panBtn');
    if (panBtn) {
      panBtn.addEventListener('click', () => this.enablePan());
    }

    // Edit button
    const modifyBtn = document.getElementById('modifyBtn');
    if (modifyBtn) {
      modifyBtn.addEventListener('click', () => this.enableModify());
    }
    const moveBtn = document.getElementById('moveBtn');
    if (moveBtn) {
      moveBtn.addEventListener('click', () => this.enableTranslate());
    }

    // Delete button
    const deleteBtn = document.getElementById('deleteBtn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => this.enableDelete());
    }

    // Measurement tools
    const measureBtn = document.getElementById('measureBtn');
    if (measureBtn) {
      measureBtn.addEventListener('click', () => this.enableDistanceMeasurement());
    }

    const splitBtn = document.getElementById('splitBtn');
    if (splitBtn) {
      splitBtn.addEventListener('click', () => this.enableSplitSegment());
    }

    const profileBtn = document.getElementById('profileBtn');
    if (profileBtn) {
      profileBtn.addEventListener('click', () => this.showElevationProfile());
    }

    const areaBtn = document.getElementById('areaBtn');
    if (areaBtn) {
      areaBtn.addEventListener('click', () => this.enableAreaMeasurement());
    }

    const bearingBtn = document.getElementById('bearingBtn');
    if (bearingBtn) {
      bearingBtn.addEventListener('click', () => this.enableBearingMeasurement());
    }

    // Undo/Redo
    const undoBtn = document.getElementById('undoBtn');
    if (undoBtn) {
      undoBtn.addEventListener('click', () => this.undo());
    }

    const redoBtn = document.getElementById('redoBtn');
    if (redoBtn) {
      redoBtn.addEventListener('click', () => this.redo());
    }

    // Clear button
    const clearBtn = document.getElementById('clearBtn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearAll());
    }

    // Export/Import
    const exportBtn = document.getElementById('exportBtn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.exportData());
    }

    const importBtn = document.getElementById('importBtn');
    if (importBtn) {
      importBtn.addEventListener('click', () => this.importData());
    }

    // Print button
    const printBtn = document.getElementById('printBtn');
    if (printBtn) {
      printBtn.addEventListener('click', () => this.printMap());
    }

    // Fullscreen/Mini-map
    const fullscreenBtn = document.getElementById('fullscreenBtn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
    }

    const miniMapBtn = document.getElementById('miniMapBtn');
    if (miniMapBtn) {
      miniMapBtn.addEventListener('click', () => this.toggleMiniMap());
    }

    // Help button
    const helpBtn = document.getElementById('helpBtn');
    if (helpBtn) {
      helpBtn.addEventListener('click', () => this.showHelp());
    }
  }

  handleDrawingModeChange(mode: string): void {
    this.removeInteractions();
    this.currentDrawingMode = mode; // Store current drawing mode
    
    if (mode) {
      if (mode === 'Point') {
        this.addPointInteraction();
      } else if (mode === 'Text') {
        this.addTextInteraction();
      } else if (mode === 'MilestoneTag') {
        this.addMilestoneTagInteraction();
      } else if (mode === 'Circle') {
        this.addCircleInteraction();
      } else if (mode === 'Polygon') {
        this.addPolygonInteraction();
      } else if (mode === 'CurveLine') {
        this.addCurveInteraction();
      } else if (mode === 'ProposedAlignment') {
        this.addAlignmentRouteInteraction('proposed');
      } else if (mode === 'AlternateRoute') {
        this.addAlignmentRouteInteraction('alternate');
      } else if (mode === 'ExistingRoad') {
        this.addAlignmentRouteInteraction('existing');
      } else if (mode.endsWith('Duct')) {
        this.addDuctInteraction(mode);
      } else if (mode === 'MultiLineString') {
        this.addMultiLineInteraction();
      } else {
        this.isMultiLineMode = false;
        this.syncDrawingEscCapture();
        const miniToolbar = document.getElementById('miniToolbar');
        if (miniToolbar) {
          miniToolbar.style.display = 'none';
        }
        this.addInteraction(mode);
      }
    } else {
      this.currentDrawingMode = ''; // Clear when no mode selected
    }
  }

  private addAlignmentRouteInteraction(routeType: 'proposed' | 'alternate' | 'existing'): void {
    this.removeInteractions();
    this.showInstructions(`Alignment mode: ${routeType}. Click to draw route, double-click to finish.`);

    const styleByType: Record<'proposed' | 'alternate' | 'existing', { stroke: string; width: number; dash?: number[]; label: string }> = {
      proposed: { stroke: '#2ecc71', width: 5, label: 'Proposed Alignment' },
      alternate: { stroke: '#f39c12', width: 4, dash: [10, 8], label: 'Alternate Route' },
      existing: { stroke: '#8e9aa6', width: 3, label: 'Existing Road' }
    };
    const visual = styleByType[routeType];

    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: visual.stroke,
          width: visual.width,
          lineDash: visual.dash
        })
      })
    });

    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'alignment_route');
      feature.set('routeType', routeType);
      feature.set('routeLabel', visual.label);
      feature.set('info', visual.label);
      feature.setStyle(new Style({
        stroke: new Stroke({
          color: visual.stroke,
          width: visual.width,
          lineDash: visual.dash,
          lineCap: 'round'
        })
      }));

      this.addAlignmentChainageMarkers(feature, routeType);
      this.saveToHistory();
      this.showInstructions(`${visual.label} created with chainage markers`);
      setTimeout(() => this.hideInstructions(), 1600);
    });

    this.map.addInteraction(this.drawInteraction);
    this.applySnapState();
  }

  private addAlignmentChainageMarkers(routeFeature: Feature, routeType: 'proposed' | 'alternate' | 'existing'): void {
    const geometry = routeFeature.getGeometry();
    if (!(geometry instanceof LineString)) return;
    const coordinates = geometry.getCoordinates();
    if (coordinates.length < 2) return;

    // Measured chainage labels: geodesic length along the drawn line (same approach as
    // milestone corridor decorations), formatted with the shared chainage utility.
    const geodesicMeters = Math.max(getGeodesicLength(geometry, { projection: 'EPSG:3857' }) || 0, 1);
    const source = this.getCurrentDrawingSource();
    const niceIntervals = [50, 100, 250, 500, 1000, 2000, 5000, 10000, 20000];
    const interval = niceIntervals.find((i) => geodesicMeters / i <= 5) || 50000;
    const labelDistances: number[] = [0];
    for (let d = interval; d < geodesicMeters; d += interval) {
      labelDistances.push(d);
    }
    labelDistances.push(geodesicMeters);

    labelDistances.forEach((distanceMeters) => {
      const frac = Math.min(1, Math.max(0, distanceMeters / geodesicMeters));
      const coord = geometry.getCoordinateAt(frac);
      const label = formatChainageFromMeters(distanceMeters);
      const markerFeature = new Feature({
        geometry: new Point(coord),
        type: 'alignment_chainage',
        routeType,
        label,
        measureM: Math.round(distanceMeters)
      });
      markerFeature.setStyle(new Style({
        image: new CircleStyle({
          radius: 4.5,
          fill: new Fill({ color: '#00d2ff' }),
          stroke: new Stroke({ color: '#ffffff', width: 1.5 })
        }),
        text: new Text({
          text: label,
          font: '600 11px "Roboto Mono", monospace',
          offsetY: -16,
          fill: new Fill({ color: '#00d2ff' }),
          stroke: new Stroke({ color: '#03152f', width: 3 })
        })
      }));
      source.addFeature(markerFeature);
    });
  }

  // Complete drawing tools implementation
  private addPointInteraction(): void {
    this.removeInteractions();
    this.showInstructions('Click to place point markers');
    
    this.map.on('click', (event: any) => {
      const pointFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'point_marker',
        symbol: this.defaultIconUrl ? undefined : '📍',
        iconUrl: this.defaultIconUrl || undefined,
        size: this.symbolSize,
        timestamp: new Date().toISOString()
      });
      if (!this.defaultIconUrl) {
        const style = new Style({
          image: new CircleStyle({
            radius: 8 * this.symbolSize,
            fill: new Fill({ color: '#e74c3c' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          }),
          text: new Text({
            text: '📍',
            font: `${16 * this.symbolSize}px Arial`,
            fill: new Fill({ color: '#fff' }),
            offsetY: -20
          })
        });
        pointFeature.setStyle(style);
      }
      this.getCurrentDrawingSource().addFeature(pointFeature);
      this.saveToHistory();
    });
  }

  private addTextInteraction(): void {
    this.removeInteractions();
    
    this.map.on('click', (event: any) => {
      const textContent = (document.getElementById('textContent') as HTMLInputElement)?.value || '';
      const textColor = (document.getElementById('textColor') as HTMLInputElement)?.value || '#000000';
      const textSize = parseInt((document.getElementById('textSize') as HTMLSelectElement)?.value || '14');
      
      const textFeature = new Feature({
        geometry: new Point(event.coordinate),
        text: textContent,
        textColor: textColor,
        textSize: textSize,
        type: 'text'
      });
      
      this.getCurrentDrawingSource().addFeature(textFeature);
      this.saveToHistory();
    });
  }

  private addMilestoneTagInteraction(): void {
    this.removeInteractions();
    this.showInstructions('Milestone mode: draw route line, double-click to create dots/chainage/shoulders/divider/direction.');

    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'LineString'
    });

    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      this.updateRoadSpecs();
      const labelInput = document.getElementById('milestoneLabel') as HTMLInputElement | null;
      const categoryInput = document.getElementById('milestoneCategory') as HTMLSelectElement | null;
      const chainageInput = document.getElementById('milestoneChainage') as HTMLInputElement | null;

      const label = (labelInput?.value || 'Milestone').trim();
      const category = (categoryInput?.value || 'general').trim();
      const chainageRaw = (chainageInput?.value || '').trim();

      // Validate start chainage: reject the draw instead of silently falling back to CH 120+000.
      let chainageStartM = 0;
      if (chainageRaw) {
        const parsed = parseChainageMeters(chainageRaw);
        if (parsed == null) {
          this.showInstructions(`Invalid start chainage "${chainageRaw}" — use CH km+mmm, e.g. CH 120+000`);
          setTimeout(() => {
            try {
              this.getCurrentDrawingSource().removeFeature(feature);
            } catch (_e) { /* feature not yet added */ }
            setTimeout(() => this.hideInstructions(), 2600);
          }, 0);
          return;
        }
        chainageStartM = parsed;
      }
      const chainage = formatChainageFromMeters(chainageStartM);
      const lanes = Math.max(1, Number(this.currentRoadSpecs.lanes) || 2);
      const roadWidthM = Math.max(3, Number(this.currentRoadSpecs.width) || 12);
      const dividerWidthM = Math.max(0, Number(this.currentRoadSpecs.dividerWidth) || Number(this.separatorWidth) || 0.3);
      const material = String(this.currentRoadSpecs.surface || 'bituminous');
      const lengthMeters = parseFloat(this.calculateLength(feature).meters || '0') || 0;

      feature.set('type', 'milestone_corridor');
      feature.set('milestoneLabel', label);
      feature.set('milestoneCategory', category);
      feature.set('milestoneChainageStart', chainage);
      feature.set('lanes', lanes);
      feature.set('roadWidthM', roadWidthM);
      feature.set('dividerWidthM', dividerWidthM);
      feature.set('material', material);
      feature.set('dividerType', String(this.currentRoadSpecs.dividerType || this.dividerType || 'painted'));
      feature.set('lengthMeters', lengthMeters);
      feature.set('lengthKm', +(lengthMeters / 1000).toFixed(3));
      feature.set('info', `${label} | ${lanes}L | ${(lengthMeters / 1000).toFixed(2)} km | ${material}`);
      feature.set('comment', feature.get('comment') || '');
      this.addMilestoneCorridorDecorations(feature, label, category, chainage, lanes, roadWidthM, dividerWidthM, material);
      this.saveToHistory();
      this.showInstructions('Milestone corridor created');
      setTimeout(() => this.hideInstructions(), 1400);
    });

    this.map.addInteraction(this.drawInteraction);
    this.applySnapState();
  }

  /**
   * Map width in meters to stroke width in pixels (EPSG:3857 uses meters as map units).
   * `maxPx` avoids unbounded strokes; lane bands use a shared total width so stripes do not all
   * clamp to the same pixel width when zoomed in (which merged alternating lane colors into one).
   */
  private metersWidthToStrokePixels(meters: number, resolution: number, maxPx: number = 2000): number {
    const res = resolution > 0 ? resolution : 1;
    const px = meters / res;
    return Math.max(3, Math.min(maxPx, px));
  }

  /** Slightly darker alternate band for adjacent lane paint. */
  private laneAlternatePavementColor(hex: string): string {
    if (!hex || !hex.startsWith('#') || hex.length < 7) {
      return '#e8c468';
    }
    const f = 0.86;
    const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const toHex = (n: number) => clamp(n * f).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private buildMilestoneCorridorStyles(
    _feature: FeatureLike,
    lanes: number,
    roadWidthM: number,
    dividerWidthM: number,
    material: string,
    dividerType: string,
    resolution: number
  ): Style[] {
    const materialPalette: Record<string, { road: string; shoulder: string; divider: string }> = {
      bituminous: { road: DEMO_ROAD_COLORS.pavement, shoulder: DEMO_ROAD_COLORS.shoulder, divider: '#8c5a2b' },
      concrete: { road: '#d6d9de', shoulder: DEMO_ROAD_COLORS.shoulder, divider: '#f59e0b' },
      gravel: { road: '#c4a484', shoulder: DEMO_ROAD_COLORS.shoulderEdge, divider: '#8c5a2b' },
      earthen: { road: '#b88a5a', shoulder: DEMO_ROAD_COLORS.median, divider: '#7c5a3a' }
    };
    const colors = materialPalette[material] || materialPalette['bituminous'];
    const roadW = Math.max(2.5, Math.min(60, roadWidthM));
    const shoulderEachM = 2.5;
    const outerM = roadW + shoulderEachM * 2;
    const shoulderPx = this.metersWidthToStrokePixels(outerM, resolution);
    const isDivided = lanes >= 4 && dividerWidthM > 0 && dividerType !== 'none';
    const medianGapPx = this.metersWidthToStrokePixels(Math.max(0.4, dividerWidthM), resolution);
    const medianEdgePx = Math.max(1.2, this.metersWidthToStrokePixels(Math.min(dividerWidthM, 0.5) || 0.25, resolution, 120));
    const dividerDash = dividerCorridorDash(dividerType);

    const baseGeom = (f: FeatureLike) => f.getGeometry() || undefined;

    const styles: Style[] = [
      new Style({
        geometry: baseGeom,
        stroke: new Stroke({
          color: colors.shoulder,
          width: shoulderPx,
          lineCap: 'round',
          lineJoin: 'round'
        })
      })
    ];

    const roadAlt = this.laneAlternatePavementColor(colors.road);

    // Total road width in pixels (one cap for the whole pavement — split across lanes so stripes stay distinct when zoomed in).
    const roadTotalPx = this.metersWidthToStrokePixels(roadW, resolution);

    if (!isDivided && lanes >= 2) {
      const halfW = roadW / 2;
      const laneWidthM = roadW / lanes;
      const lanePx = roadTotalPx / lanes;
      for (let i = 0; i < lanes; i++) {
        const offsetM = -halfW + (i + 0.5) * laneWidthM;
        const c = i % 2 === 0 ? colors.road : roadAlt;
        styles.push(
          new Style({
            geometry: (f) => {
              const g = f.getGeometry();
              if (!(g instanceof LineString)) return g;
              return this.createOffsetLineString(g, offsetM) || g;
            },
            stroke: new Stroke({
              color: c,
              width: lanePx,
              lineCap: 'butt',
              lineJoin: 'round'
            })
          })
        );
      }
      // Two-lane undivided: dashed centerline on axis (lane interior stripes already show two colors).
      if (lanes === 2) {
        const laneMarkPx = Math.max(1.2, this.metersWidthToStrokePixels(0.12, resolution, 80));
        const dashScale = resolution > 25 ? 1.6 : 1;
        styles.push(
          new Style({
            geometry: baseGeom,
            stroke: new Stroke({
              color: '#ffffff',
              width: laneMarkPx,
              lineDash: [10 * dashScale, 8 * dashScale],
              lineCap: 'round'
            })
          })
        );
      }
    } else if (!isDivided && lanes === 1) {
      styles.push(
        new Style({
          geometry: baseGeom,
          stroke: new Stroke({
            color: colors.road,
            width: roadTotalPx,
            lineCap: 'round',
            lineJoin: 'round'
          })
        })
      );
    } else if (isDivided) {
      const lanesPerSide = Math.max(1, Math.floor(lanes / 2));
      const carriagewayWidthM = roadW / 2;
      const laneWidthInCarM = carriagewayWidthM / lanesPerSide;
      const carTotalPx = roadTotalPx / 2;
      const lanePxCar = carTotalPx / lanesPerSide;

      for (let j = 0; j < lanesPerSide; j++) {
        const offsetM = -dividerWidthM / 2 - (j + 0.5) * laneWidthInCarM;
        const c = j % 2 === 0 ? colors.road : roadAlt;
        styles.push(
          new Style({
            geometry: (f) => {
              const g = f.getGeometry();
              if (!(g instanceof LineString)) return g;
              return this.createOffsetLineString(g, offsetM) || g;
            },
            stroke: new Stroke({
              color: c,
              width: lanePxCar,
              lineCap: 'butt',
              lineJoin: 'round'
            })
          })
        );
      }
      for (let j = 0; j < lanesPerSide; j++) {
        const offsetM = dividerWidthM / 2 + (j + 0.5) * laneWidthInCarM;
        const c = j % 2 === 0 ? roadAlt : colors.road;
        styles.push(
          new Style({
            geometry: (f) => {
              const g = f.getGeometry();
              if (!(g instanceof LineString)) return g;
              return this.createOffsetLineString(g, offsetM) || g;
            },
            stroke: new Stroke({
              color: c,
              width: lanePxCar,
              lineCap: 'butt',
              lineJoin: 'round'
            })
          })
        );
      }

      styles.push(
        new Style({
          geometry: baseGeom,
          stroke: new Stroke({
            color: colors.shoulder,
            width: medianGapPx,
            lineCap: 'round',
            lineJoin: 'round'
          })
        }),
        new Style({
          geometry: baseGeom,
          stroke: new Stroke({
            color: colors.divider,
            width: medianEdgePx,
            lineDash: dividerDash,
            lineCap: 'round',
            lineJoin: 'round'
          })
        })
      );
    }

    return styles;
  }

  private addMilestoneCorridorDecorations(
    routeFeature: Feature,
    label: string,
    category: string,
    chainageStart: string,
    lanes: number,
    roadWidthM: number,
    dividerWidthM: number,
    material: string
  ): void {
    const geometry = routeFeature.getGeometry();
    if (!(geometry instanceof LineString)) return;
    const source = this.getCurrentDrawingSource();
    this.addMilestoneLaneGuides(source, geometry, lanes, roadWidthM, dividerWidthM, material);

    // Route midpoint label tag (clickable edit tag)
    const midPoint = geometry.getCoordinateAt(0.5);
    const tagFeature = new Feature({
      geometry: new Point(midPoint),
      type: 'milestone_tag',
      milestoneLabel: label,
      milestoneCategory: category,
      milestoneChainage: chainageStart,
      info: chainageStart ? `${label} (${chainageStart})` : label,
      comment: routeFeature.get('comment') || ''
    });
    tagFeature.setStyle(this.createMilestoneTagStyle(label, category, chainageStart));
    source.addFeature(tagFeature);

    // Route metadata tag links drawing to road specs.
    const metaPoint = geometry.getCoordinateAt(0.08);
    const lengthKm = Number(routeFeature.get('lengthKm') || 0).toFixed(2);
    const metaLabel = `${lanes}L | ${lengthKm}km | W ${roadWidthM.toFixed(1)}m | Divider ${dividerWidthM.toFixed(1)}m | ${material}`;
    const metaFeature = new Feature({
      geometry: new Point(metaPoint),
      type: 'milestone_route_meta',
      label: metaLabel
    });
    metaFeature.setStyle(this.scaleAwareStyle(new Style({
      text: new Text({
        text: metaLabel,
        font: '700 11px Arial',
        fill: new Fill({ color: '#e2e8f0' }),
        stroke: new Stroke({ color: '#0f172a', width: 4 }),
        backgroundFill: new Fill({ color: 'rgba(15, 23, 42, 0.85)' }),
        backgroundStroke: new Stroke({ color: '#334155', width: 1.5 }),
        padding: [4, 8, 4, 8],
        offsetY: -14
      })
    }), 11));
    source.addFeature(metaFeature);

    // Joining dots + lane direction arrows
    const routeLength = Math.max(geometry.getLength(), 1);
    const step = Math.max(80, routeLength / Math.max(6, lanes * 2));
    const dotCount = Math.max(4, Math.floor(routeLength / step));
    // Arrow spacing scales with lane count:
    // - low lanes: fewer arrows
    // - high lanes: denser arrows
    const laneScale = Math.max(1, lanes);
    const arrowStepByLane = Math.max(1, Math.round(6 / laneScale)); // 1-lane=>6, 2-lane=>3, 4-lane=>2, 6+/8=>1
    const arrowRadius = Math.min(12, 7 + laneScale * 0.5);
    const arrowFont = `${Math.min(15, 10 + laneScale)}px Arial`;
    for (let i = 1; i <= dotCount; i++) {
      const frac = i / (dotCount + 1);
      const coord = geometry.getCoordinateAt(frac);
      const dot = new Feature({
        geometry: new Point(coord),
        type: 'milestone_join_dot',
        info: 'Joining Node'
      });
      dot.setStyle(this.scaleAwareStyle(new Style({
        image: new CircleStyle({
          radius: 5.5,
          fill: new Fill({ color: '#f59e0b' }),
          stroke: new Stroke({ color: '#ffffff', width: 2 })
        })
      }), 8));
      source.addFeature(dot);

      // Lane direction marker density follows lane count.
      if (i % arrowStepByLane === 0) {
        const arrow = new Feature({
          geometry: new Point(coord),
          type: 'milestone_direction',
          info: 'Lane Direction'
        });
        arrow.setStyle(this.scaleAwareStyle(new Style({
          image: new CircleStyle({
            radius: arrowRadius,
            fill: new Fill({ color: '#f59e0b' }),
            stroke: new Stroke({ color: '#fbbf24', width: 1.5 })
          }),
          text: new Text({
            text: '➤',
            font: `700 ${arrowFont}`,
            fill: new Fill({ color: '#ffffff' }),
            stroke: new Stroke({ color: '#7c2d12', width: 2 })
          })
        }), 9));
        source.addFeature(arrow);
      }
    }

    // Chainage labels along route (distance-based spacing on actual route length)
    const startMeters = parseChainageMeters(chainageStart) ?? 0;
    const intervalInput = document.getElementById('milestoneChainageInterval') as HTMLInputElement | null;
    const intervalMeters = Math.max(50, parseInt(intervalInput?.value || '500', 10) || 500);
    const routeMeters = Math.max(geometry.getLength(), 1);

    const labelDistances: number[] = [];
    for (let d = intervalMeters; d < routeMeters; d += intervalMeters) {
      labelDistances.push(d);
    }
    // If corridor is shorter than one interval, still place one label at midpoint.
    if (labelDistances.length === 0) {
      labelDistances.push(routeMeters / 2);
    }

    labelDistances.forEach((distanceMeters) => {
      const frac = Math.min(0.999, Math.max(0.001, distanceMeters / routeMeters));
      const coord = geometry.getCoordinateAt(frac);
      const chainageMeters = startMeters + distanceMeters;
      const chainageText = formatChainageFromMeters(chainageMeters);
      const chFeature = new Feature({
        geometry: new Point(coord),
        type: 'milestone_chainage',
        label: chainageText,
        info: chainageText
      });
      chFeature.setStyle(this.scaleAwareStyle(new Style({
        text: new Text({
          text: chainageText,
          font: '700 12px "Roboto Mono", monospace',
          fill: new Fill({ color: '#1f2937' }),
          stroke: new Stroke({ color: '#e2e8f0', width: 6 }),
          backgroundFill: new Fill({ color: '#dbeafe' }),
          backgroundStroke: new Stroke({ color: '#94a3b8', width: 1.5 }),
          padding: [4, 8, 4, 8],
          offsetY: -16
        })
      }), 10));
      source.addFeature(chFeature);
    });

    // End chainage marker at route endpoint for closure.
    const routeEnd = geometry.getLastCoordinate();
    const endChainageText = formatChainageFromMeters(startMeters + routeMeters);
    const endFeature = new Feature({
      geometry: new Point(routeEnd),
      type: 'milestone_chainage_end',
      label: endChainageText,
      info: endChainageText
    });
    endFeature.setStyle(this.scaleAwareStyle(new Style({
      text: new Text({
        text: endChainageText,
        font: '700 12px "Roboto Mono", monospace',
        fill: new Fill({ color: '#0f172a' }),
        stroke: new Stroke({ color: '#f8fafc', width: 6 }),
        backgroundFill: new Fill({ color: '#fde68a' }),
        backgroundStroke: new Stroke({ color: '#d97706', width: 1.5 }),
        padding: [4, 8, 4, 8],
        offsetY: -14
      })
    }), 10));
    source.addFeature(endFeature);
  }

  private addMilestoneLaneGuides(
    source: VectorSource,
    centerLine: LineString,
    lanes: number,
    roadWidthM: number,
    dividerWidthM: number,
    material: string,
    markAsDummyInfra: boolean = false,
    corridorId?: string,
    dividerType: string = 'painted',
    demoOverlay: boolean = false
  ): void {
    const effectiveDividerWidth = dividerType === 'none' ? 0 : dividerWidthM;
    const tagCorridor = (props: Record<string, unknown>) => {
      if (corridorId) props['corridorId'] = corridorId;
      if (markAsDummyInfra) props['dummyRoadInfra'] = true;
      if (demoOverlay) {
        props['demoInfra'] = true;
        props['demoCorridorOverlay'] = true;
      }
      return props;
    };
    const safeLanes = Math.max(1, lanes || 1);
    const safeWidth = Math.max(3.2, roadWidthM || 12);
    const dividerW = effectiveDividerWidth;
    const isDivided = safeLanes >= 4 && dividerW > 0;
    const laneWidth = safeWidth / safeLanes;
    const halfWidth = safeWidth / 2;
    const materialPalette: Record<string, { road: string; shoulder: string }> = {
      bituminous: { road: DEMO_ROAD_COLORS.pavement, shoulder: DEMO_ROAD_COLORS.shoulder },
      concrete: { road: '#d6d9de', shoulder: DEMO_ROAD_COLORS.shoulder },
      gravel: { road: '#c4a484', shoulder: DEMO_ROAD_COLORS.shoulderEdge },
      earthen: { road: '#b88a5a', shoulder: DEMO_ROAD_COLORS.median }
    };
    const colors = materialPalette[material] || materialPalette['bituminous'];
    const edgeColor = DEMO_ROAD_COLORS.shoulderEdge;
    const carriagewayWidthM = safeWidth / 2;

    if (isDivided) {
      // Dual carriageway: offsets are in map meters (EPSG:3857), not pixels.
      const leftCenterOffsetM = -(dividerW / 2 + carriagewayWidthM / 2);
      const rightCenterOffsetM = dividerW / 2 + carriagewayWidthM / 2;
      const leftCarrGeom = this.createOffsetLineString(centerLine, leftCenterOffsetM);
      const rightCarrGeom = this.createOffsetLineString(centerLine, rightCenterOffsetM);

      [leftCarrGeom, rightCarrGeom].forEach((cg, idx) => {
        if (!cg) return;
        const carr = new Feature(tagCorridor({
          geometry: cg,
          type: 'milestone_carriageway',
          side: idx === 0 ? 'left' : 'right',
          info: `Carriageway (${idx === 0 ? 'Left' : 'Right'})`
        }));
        carr.setStyle((_, resolution) => {
          const res = resolution || 1;
          const wPx = this.metersWidthToStrokePixels(carriagewayWidthM, res);
          return new Style({
            stroke: new Stroke({
              color: colors.road,
              width: wPx,
              lineCap: 'round',
              lineJoin: 'round'
            })
          });
        });
        source.addFeature(carr);
      });

      const median = new Feature(tagCorridor({
        geometry: centerLine.clone(),
        type: 'milestone_median_fill',
        info: 'Central Median'
      }));
      median.setStyle((_, resolution) => {
        const res = resolution || 1;
        const wPx = this.metersWidthToStrokePixels(Math.max(0.4, dividerW), res);
        return new Style({
          stroke: new Stroke({
            color: colors.shoulder,
            width: wPx,
            lineCap: 'round',
            lineJoin: 'round'
          })
        });
      });
      source.addFeature(median);
    }

    // Shoulder edges (both sides)
    const outerHalf = isDivided ? (safeWidth + dividerW) / 2 : halfWidth;
    const leftEdge = this.createOffsetLineString(centerLine, -outerHalf);
    const rightEdge = this.createOffsetLineString(centerLine, outerHalf);

    [leftEdge, rightEdge].forEach((edgeGeom, idx) => {
      if (!edgeGeom) return;
      const edgeFeature = new Feature(tagCorridor({
        geometry: edgeGeom,
        type: 'milestone_shoulder_edge',
        side: idx === 0 ? 'left' : 'right',
        info: `Shoulder Edge (${idx === 0 ? 'Left' : 'Right'})`
      }));
      edgeFeature.setStyle(this.scaleAwareStyle(new Style({
        stroke: new Stroke({
          color: edgeColor,
          width: 1.6
        })
      }), 12));
      source.addFeature(edgeFeature);
    });

    if (isDivided) {
      // Central median edges (both sides of divider gap)
      const medianLeft = this.createOffsetLineString(centerLine, -dividerW / 2);
      const medianRight = this.createOffsetLineString(centerLine, dividerW / 2);
      [medianLeft, medianRight].forEach((medianGeom, idx) => {
        if (!medianGeom) return;
        const medianEdge = new Feature(tagCorridor({
          geometry: medianGeom,
          type: 'milestone_median_edge',
          side: idx === 0 ? 'left' : 'right',
          info: `Median Edge (${idx === 0 ? 'Left' : 'Right'})`
        }));
        medianEdge.setStyle(this.scaleAwareStyle(new Style({
          stroke: new Stroke({
            color: '#7c5a2a',
            width: 1.3
          })
        }), 11));
        source.addFeature(medianEdge);
      });

      // Lane dividers per carriageway (excluding median gap).
      const lanesPerSide = Math.max(1, Math.floor(safeLanes / 2));
      const carriagewayWidth = safeWidth / 2;
      const perLane = carriagewayWidth / lanesPerSide;
      for (let i = 1; i < lanesPerSide; i++) {
        const offsetFromMedian = i * perLane;
        const leftOffset = -dividerW / 2 - offsetFromMedian;
        const rightOffset = dividerW / 2 + offsetFromMedian;
        [leftOffset, rightOffset].forEach((offset, sideIdx) => {
          const dividerGeom = this.createOffsetLineString(centerLine, offset);
          if (!dividerGeom) return;
          const dividerFeature = new Feature(tagCorridor({
            geometry: dividerGeom,
            type: 'milestone_lane_divider',
            laneIndex: i,
            side: sideIdx === 0 ? 'left' : 'right',
            info: `Lane Divider (${sideIdx === 0 ? 'L' : 'R'}-${i})`
          }));
          dividerFeature.setStyle((_, resolution) => {
            const res = resolution || 1;
            if (res > 18) {
              return null;
            }
            const wPx = Math.max(1.1, this.metersWidthToStrokePixels(0.1, res));
            return new Style({
              stroke: new Stroke({
                color: '#fff7d6',
                width: wPx,
                lineDash: [10, 8]
              })
            });
          });
          source.addFeature(dividerFeature);
        });
      }
    } else {
      // Internal lane separators for undivided roads (offsets in meters).
      for (let i = 1; i < safeLanes; i++) {
        if (safeLanes === 2 && i === 1) {
          continue;
        }
        const offset = -halfWidth + i * laneWidth;
        const dividerGeom = this.createOffsetLineString(centerLine, offset);
        if (!dividerGeom) continue;

        const dividerFeature = new Feature(tagCorridor({
          geometry: dividerGeom,
          type: 'milestone_lane_divider',
          laneIndex: i,
          info: `Lane Divider ${i}/${safeLanes - 1}`
        }));
        dividerFeature.setStyle((_, resolution) => {
          const res = resolution || 1;
          if (res > 18) {
            return null;
          }
          const wPx = Math.max(1.1, this.metersWidthToStrokePixels(0.1, res));
          return new Style({
            stroke: new Stroke({
              color: '#fff7d6',
              width: wPx,
              lineDash: [10, 8]
            })
          });
        });
        source.addFeature(dividerFeature);
      }
    }
  }

  private createOffsetLineString(base: LineString, offsetMeters: number): LineString | null {
    const coords = base.getCoordinates();
    if (!coords || coords.length < 2) return null;

    const segNormals: number[][] = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i];
      const b = coords[i + 1];
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len = Math.hypot(dx, dy);
      if (!len) {
        segNormals.push([0, 0]);
        continue;
      }
      segNormals.push([-dy / len, dx / len]);
    }

    const out: number[][] = [];
    for (let i = 0; i < coords.length; i++) {
      let nx = 0;
      let ny = 0;
      if (i === 0) {
        nx = segNormals[0][0];
        ny = segNormals[0][1];
      } else if (i === coords.length - 1) {
        nx = segNormals[segNormals.length - 1][0];
        ny = segNormals[segNormals.length - 1][1];
      } else {
        nx = segNormals[i - 1][0] + segNormals[i][0];
        ny = segNormals[i - 1][1] + segNormals[i][1];
        const nLen = Math.hypot(nx, ny);
        if (nLen > 0) {
          nx /= nLen;
          ny /= nLen;
        }
      }
      out.push([coords[i][0] + nx * offsetMeters, coords[i][1] + ny * offsetMeters]);
    }

    return new LineString(out);
  }

  private toKmHudLabel(label: string, measureM?: number): string {
    if (measureM != null && Number.isFinite(measureM)) {
      return formatChainageFromMeters(measureM).replace(/^CH\s*/i, 'KM ');
    }
    const raw = String(label || '').trim();
    if (/^CH\s*/i.test(raw)) return raw.replace(/^CH\s*/i, 'KM ');
    if (/^KM\s/i.test(raw)) return raw;
    const km = raw.match(/^([\d.]+)\s*km$/i);
    if (km) return this.toKmHudLabel('', parseFloat(km[1]) * 1000);
    return raw || 'KM 0+000';
  }

  private panToLrsStation(hud: { measure_m: number; label: string }): void {
    const routeId = this.lrsDashboard.getRouteId() || this.selectedLrsRouteId;
    if (!routeId || !this.map) return;
    this.suppressHudUntil = Date.now() + 600;
    this.chainageHudLabel = this.toKmHudLabel(hud.label, hud.measure_m);
    this.lrsService.geometryAtOffset(routeId, hud.measure_m / 1000, 0).subscribe({
      next: (res) => {
        const coords =
          res.data?.center_point?.coordinates || res.data?.offset_point?.coordinates;
        if (!coords || coords.length < 2) return;
        this.map.getView().animate({
          center: fromLonLat([coords[0], coords[1]]),
          duration: 280,
        });
      },
      error: () => {},
    });
  }

  private createMilestoneTagStyle(label: string, category: string, chainage: string): Style {
    const categoryColor: Record<string, string> = {
      tender: '#f59e0b',
      survey: '#06b6d4',
      design: '#22c55e',
      progress: '#3b82f6',
      general: '#f59e0b'
    };
    const color = categoryColor[category] || categoryColor['general'];
    const text = chainage ? `${label} (${chainage})` : label;

    return new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#ffffff', width: 2 })
      }),
      text: new Text({
        text,
        font: '700 12px Arial',
        offsetY: -20,
        fill: new Fill({ color: '#ffffff' }),
        stroke: new Stroke({ color: '#111827', width: 4 }),
        padding: [4, 8, 4, 8],
        backgroundFill: new Fill({ color: 'rgba(0,0,0,0.70)' }),
        backgroundStroke: new Stroke({ color, width: 1.5 })
      })
    });
  }

  private openMilestoneTagEditor(feature: Feature): void {
    const currentLabel = String(feature.get('milestoneLabel') || 'Milestone');
    const currentCategory = String(feature.get('milestoneCategory') || 'general');
    const currentChainage = String(feature.get('milestoneChainage') || '');

    const nextLabel = prompt('Milestone label:', currentLabel);
    if (nextLabel === null) return;
    const nextCategory = prompt('Milestone category (tender/survey/design/progress/general):', currentCategory);
    if (nextCategory === null) return;
    const nextChainage = prompt('Chainage (optional):', currentChainage);
    if (nextChainage === null) return;

    feature.set('milestoneLabel', nextLabel.trim() || 'Milestone');
    feature.set('milestoneCategory', nextCategory.trim() || 'general');
    feature.set('milestoneChainage', nextChainage.trim());
    feature.setStyle(this.createMilestoneTagStyle(
      String(feature.get('milestoneLabel')),
      String(feature.get('milestoneCategory')),
      String(feature.get('milestoneChainage'))
    ));
    this.saveToHistory();
    this.showInstructions('Milestone tag updated');
    setTimeout(() => this.hideInstructions(), 1200);
  }

  private addCircleInteraction(): void {
    this.removeInteractions();
    
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'Circle',
      style: this.createDrawingStyle(thickness)
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'circle');
      feature.set('thickness', thickness);
      feature.set('symbolSize', this.symbolSize);
      this.saveToHistory();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private addPolygonInteraction(): void {
    this.removeInteractions();
    this.showInstructions('Click to start polygon, double-click to finish');
    
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(52, 152, 219, 0.3)'
        }),
        stroke: new Stroke({
          color: this.defaultSettings.lineColor,
          width: thickness
        })
      })
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'polygon');
      feature.set('thickness', thickness);
      feature.set('area', this.calculateArea(feature).squareMeters);
      this.saveToHistory();
      this.hideInstructions();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private addMultiLineInteraction(): void {
    this.removeInteractions();
    this.isMultiLineMode = true;
    this.syncDrawingEscCapture();
    this.showInstructions('Click to start multi-line drawing, right-click to finish');
    
    const miniToolbar = document.getElementById('miniToolbar');
    if (miniToolbar) {
      miniToolbar.style.display = 'block';
    }
    
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    let currentLinePoints: number[][] = [];
    let lineCount = 0;
    
    this.map.on('click', (event: any) => {
      currentLinePoints.push(event.coordinate);
      
      if (currentLinePoints.length === 1) {
        this.showInstructions('Click to add more points, right-click to finish line');
      } else {
        // Create line segment
        const lineFeature = new Feature({
          geometry: new LineString(currentLinePoints),
          type: 'multi_line',
          lineNumber: lineCount + 1,
          thickness: thickness
        });
        
        const style = new Style({
          stroke: new Stroke({
            color: this.defaultSettings.lineColor,
            width: thickness,
            lineCap: 'round'
          })
        });
        
        lineFeature.setStyle(style);
        this.getCurrentDrawingSource().addFeature(lineFeature);
        
        // Update mini toolbar
        const activeLineCount = document.getElementById('activeLineCount');
        const currentLineId = document.getElementById('currentLineId');
        if (activeLineCount) activeLineCount.textContent = (lineCount + 1).toString();
        if (currentLineId) currentLineId.textContent = (lineCount + 1).toString();
        
        lineCount++;
        this.saveToHistory();
      }
    });
    
    this.map.getViewport().addEventListener('contextmenu', (event: any) => {
      event.preventDefault();
      if (currentLinePoints.length >= 2) {
        // Finish current line
        const lineFeature = new Feature({
          geometry: new LineString(currentLinePoints),
          type: 'multi_line',
          lineNumber: lineCount + 1,
          thickness: this.lineThickness
        });
        
        const style = new Style({
          stroke: new Stroke({
            color: this.defaultSettings.lineColor,
            width: this.lineThickness,
            lineCap: 'round'
          })
        });
        
        lineFeature.setStyle(style);
        this.getCurrentDrawingSource().addFeature(lineFeature);
        
        lineCount++;
        currentLinePoints = [];
        this.saveToHistory();
        this.showInstructions('Line finished. Click to start new line, ESC to exit');
      }
    });
    
    // ESC to exit multi-line mode
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isMultiLineMode) {
        event.preventDefault();
        this.exitMultiLineMode();
      }
    });
  }

  private exitMultiLineMode(): void {
    this.isMultiLineMode = false;
    setTimeout(() => this.syncDrawingEscCapture());
    const miniToolbar = document.getElementById('miniToolbar');
    if (miniToolbar) {
      miniToolbar.style.display = 'none';
    }
    this.hideInstructions();
    this.removeInteractions();
  }

  private addCurveInteraction(): void {
    this.removeInteractions();
    this.startCurveDrawing();
  }

  private startCurveDrawing(): void {
    this.isCurveDrawing = true;
    this.syncDrawingEscCapture();
    this.curveControlPoints = [];
    
    // Update UI
    const startBtn = document.getElementById('startCurveDrawing');
    const finishBtn = document.getElementById('finishCurveDrawing');
    const cancelBtn = document.getElementById('cancelCurveDrawing');
    
    if (startBtn) startBtn.style.display = 'none';
    if (finishBtn) finishBtn.style.display = 'inline-block';
    if (cancelBtn) cancelBtn.style.display = 'inline-block';
    
    this.showInstructions('Click points to define curve. Right-click to finish, ESC to cancel.');
    
    // Set up click listener for curve points
    this.curveDrawingListener = this.map.on('click', (event: any) => {
      this.addCurvePoint(event.coordinate);
    });
    
    // Set up right-click to finish
    this.map.getViewport().addEventListener('contextmenu', (event: any) => {
      event.preventDefault();
      if (this.curveControlPoints.length >= 2) {
        this.finishCurveDrawing();
      }
    });
    
    // Set up ESC key to cancel
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isCurveDrawing) {
        event.preventDefault();
        this.cancelCurveDrawing();
      }
    });
  }

  private addCurvePoint(coordinate: number[]): void {
    this.curveControlPoints.push(coordinate);
    
    // Add visual control point
    this.addControlPointMarker(coordinate, this.curveControlPoints.length);
    
    // Update instructions
    if (this.curveControlPoints.length === 1) {
      this.showInstructions('Click second point to define curve direction');
    } else if (this.curveControlPoints.length === 2) {
      this.showInstructions('Click more points or right-click to finish curve');
    } else {
      this.showInstructions(`Point ${this.curveControlPoints.length} - Click more or right-click to finish`);
    }
    
    // Draw preview curve if we have enough points
    if (this.curveControlPoints.length >= 2) {
      this.drawCurvePreview();
    }
  }

  private addControlPointMarker(coordinate: number[], pointNumber: number): void {
    const marker = new Feature({
      geometry: new Point(coordinate),
      type: 'control_point',
      pointNumber: pointNumber
    });
    
    const style = new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: '#e74c3c' }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      }),
      text: new Text({
        text: pointNumber.toString(),
        font: '12px Arial',
        fill: new Fill({ color: '#fff' }),
        stroke: new Stroke({ color: '#000', width: 2 }),
        offsetY: -15
      })
    });
    
    marker.setStyle(style);
    this.getCurrentDrawingSource().addFeature(marker);
  }

  private drawCurvePreview(): void {
    // Remove existing preview
    this.removeCurvePreview();
    
    const curveCoordinates = this.generateCurveCoordinates(this.curveControlPoints);
    
    const previewFeature = new Feature({
      geometry: new LineString(curveCoordinates),
      type: 'curve_preview'
    });
    
    const style = new Style({
      stroke: new Stroke({
        color: '#3498db',
        width: this.lineThickness + 1,
        lineDash: [5, 5]
      })
    });
    
    previewFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(previewFeature);
  }

  private removeCurvePreview(): void {
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      if (feature.get('type') === 'curve_preview') {
        this.vectorSource.removeFeature(feature);
      }
    });
  }

  private generateCurveCoordinates(points: number[][]): [number, number][] {
    if (points.length < 2) return [];
    
    const precision = this.curvePrecision;
    const coordinates: [number, number][] = [];
    
    switch (this.currentCurveType) {
      case 'bezier':
        return this.generateBezierCurve(points, precision);
      case 'spline':
        return this.generateSplineCurve(points, precision);
      case 'arc':
        return this.generateArcCurve(points, precision);
      case 'smooth':
      default:
        return this.generateSmoothCurve(points, precision);
    }
  }

  private generateBezierCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 2) return [];
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const p2 = points[i + 2] || p1;
      const p3 = points[i + 3] || p2;
      
        for (let t = 0; t <= 1; t += 1 / steps) {
          const x = this.cubicBezier(p0[0], p1[0], p2[0], p3[0], t);
          const y = this.cubicBezier(p0[1], p1[1], p2[1], p3[1], t);
          coordinates.push([x, y] as [number, number]);
        }
    }
    
    return coordinates;
  }

  private cubicBezier(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;
    
    return uuu * p0 + 3 * uu * t * p1 + 3 * u * tt * p2 + ttt * p3;
  }

  private generateSplineCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 3) return this.generateSmoothCurve(points, precision);
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    // Catmull-Rom spline
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = i > 0 ? points[i - 1] : points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1];
      
      for (let t = 0; t <= 1; t += 1 / steps) {
        const x = this.catmullRom(p0[0], p1[0], p2[0], p3[0], t);
        const y = this.catmullRom(p0[1], p1[1], p2[1], p3[1], t);
        coordinates.push([x, y] as [number, number]);
      }
    }
    
    return coordinates;
  }

  private catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const t2 = t * t;
    const t3 = t2 * t;
    
    return 0.5 * (
      (2 * p1) +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3
    );
  }

  private generateArcCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 3) return this.generateSmoothCurve(points, precision);
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    // Create circular arc through three points
    for (let i = 0; i < points.length - 2; i += 2) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2];
      
      const center = this.calculateArcCenter(p1, p2, p3);
      if (center) {
        const radius = this.calculateDistance(center, p1);
        const startAngle = Math.atan2(p1[1] - center[1], p1[0] - center[0]);
        const endAngle = Math.atan2(p3[1] - center[1], p3[0] - center[0]);
        
        for (let j = 0; j <= steps; j++) {
          const t = j / steps;
          const angle = startAngle + (endAngle - startAngle) * t;
          const x = center[0] + radius * Math.cos(angle);
          const y = center[1] + radius * Math.sin(angle);
          coordinates.push([x, y] as [number, number]);
        }
      }
    }
    
    return coordinates;
  }

  private calculateArcCenter(p1: number[], p2: number[], p3: number[]): number[] | null {
    const ax = p1[0];
    const ay = p1[1];
    const bx = p2[0];
    const by = p2[1];
    const cx = p3[0];
    const cy = p3[1];
    
    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 1e-10) return null; // Points are collinear
    
    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;
    
    return [ux, uy];
  }

  private generateSmoothCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 2) return [];
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    // Simple smooth curve using quadratic interpolation
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      
      for (let t = 0; t <= 1; t += 1 / steps) {
        const x = this.quadraticInterpolation(p1[0], p2[0], p3[0], t);
        const y = this.quadraticInterpolation(p1[1], p2[1], p3[1], t);
        coordinates.push([x, y] as [number, number]);
      }
    }
    
    return coordinates;
  }

  private quadraticInterpolation(p1: number, p2: number, p3: number, t: number): number {
    const u = 1 - t;
    return u * u * p1 + 2 * u * t * p2 + t * t * p3;
  }

  private finishCurveDrawing(): void {
    if (this.curveControlPoints.length < 2) {
      this.showInstructions('Need at least 2 points to create a curve');
      return;
    }
    
    const curveCoordinates = this.generateCurveCoordinates(this.curveControlPoints);
    
    const curveFeature = new Feature({
      geometry: new LineString(curveCoordinates),
      type: 'curve',
      curveType: this.currentCurveType,
      controlPoints: this.curveControlPoints,
      thickness: this.lineThickness,
      precision: this.curvePrecision
    });
    
    const style = new Style({
        stroke: new Stroke({
          color: this.defaultSettings.lineColor,
          width: this.lineThickness,
          lineCap: 'round'
      })
    });
    
    curveFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(curveFeature);
    
    this.cleanupCurveDrawing();
      this.saveToHistory();
    this.showInstructions('Curve created successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private cancelCurveDrawing(): void {
    this.cleanupCurveDrawing();
    this.hideInstructions();
  }

  private cleanupCurveDrawing(): void {
    this.isCurveDrawing = false;
    setTimeout(() => this.syncDrawingEscCapture());
    this.curveControlPoints = [];
    
    // Remove preview and control points
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      const type = feature.get('type');
      if (type === 'curve_preview' || type === 'control_point') {
        this.vectorSource.removeFeature(feature);
      }
    });
    
    // Update UI
    const startBtn = document.getElementById('startCurveDrawing');
    const finishBtn = document.getElementById('finishCurveDrawing');
    const cancelBtn = document.getElementById('cancelCurveDrawing');
    
    if (startBtn) startBtn.style.display = 'inline-block';
    if (finishBtn) finishBtn.style.display = 'none';
    if (cancelBtn) cancelBtn.style.display = 'none';
    
    // Remove event listeners
    if (this.curveDrawingListener) {
      this.map.un('click', this.curveDrawingListener);
      this.curveDrawingListener = null;
    }
  }

  private addInteraction(type: string): void {
    // Use this.lineThickness to ensure it's synced with the slider
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    this.updateRoadSpecs();
    
    const useFreehand = (type === 'LineString' || type === 'Polygon') && this.freehandMode;
    const sketchStyle =
      type === 'Point' && this.currentSymbol
        ? this.createSymbolStyle(this.currentSymbol, this.symbolSize)
        : this.createDrawingStyle(thickness, this.currentRoadSpecs);
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: type as any,
      style: sketchStyle,
      freehand: useFreehand
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('thickness', thickness);
      feature.set('symbolSize', this.symbolSize);
      feature.set('roadSpecs', {...this.currentRoadSpecs});
      const grade = (document.getElementById('assetType') as HTMLSelectElement)?.value || 'overground';
      feature.set('underground', grade === 'underground');
      feature.set('assetType', grade);
      feature.set('grade', grade);
      feature.set('info', 'Feature Info');
      
      if (this.currentSymbol && type === 'Point') {
        feature.set('symbol', this.currentSymbol);
        feature.set('type', 'road_symbol');
        if (this.currentSymbolTitle) feature.set('title', this.currentSymbolTitle);
        setTimeout(() => {
          this.removeInteractions();
          this.enableSelect();
        }, 0);
      }
      
      // Update nomenclature from UI if available
      const nomenclatureInput = document.getElementById('nomenclature') as HTMLInputElement;
      const abbreviationInput = document.getElementById('nomenclatureAbbreviation') as HTMLInputElement;
      if (nomenclatureInput) {
        this.nomenclature = nomenclatureInput.value || 'Gandhi baba';
      }
      if (abbreviationInput) {
        this.nomenclatureAbbreviation = abbreviationInput.value || 'Gb';
      }
      
      // Apply auto-numbering for segments if enabled
      if (this.isMultiLineMode) {
        this.currentLines.push(feature);
        this.updateLineCount();
        const numberingType = (document.getElementById('numberingType') as HTMLSelectElement)?.value || 'auto';
        if (numberingType === 'auto') {
          this.autoNumberSegment(feature);
        } else {
          // Manual numbering - still generate ID and label
          const segmentNumber = this.manualSegmentNumber;
          const currentSource = this.getCurrentDrawingSource();
          const existingMainSegment = currentSource.getFeatures().find(f => {
            const fSegmentNumber = f.get('segmentNumber');
            const fIsSubSegment = f.get('isSubSegment');
            return fSegmentNumber === segmentNumber && !fIsSubSegment;
          });
          
          let segmentId: string;
          let segmentLabel: string;
          let isSubSegment = false;
          
          if (existingMainSegment) {
            // Subsegment
            isSubSegment = true;
            if (!this.segmentSubSegmentCount[segmentNumber]) {
              this.segmentSubSegmentCount[segmentNumber] = 1;
            } else {
              this.segmentSubSegmentCount[segmentNumber]++;
            }
            const subSegmentNum = this.segmentSubSegmentCount[segmentNumber];
            segmentId = `s${segmentNumber}_${subSegmentNum}`;
            segmentLabel = `${this.nomenclatureAbbreviation} ${segmentId}`;
          } else {
            // Main segment
            segmentId = `s${segmentNumber}`;
            segmentLabel = `${this.nomenclature} segment ${segmentNumber}`;
            this.segmentSubSegmentCount[segmentNumber] = 0;
          }
          
          feature.set('segmentNumber', segmentNumber);
          feature.set('segmentId', segmentId);
          feature.set('label', segmentLabel);
          feature.set('isSubSegment', isSubSegment);
          feature.set('nomenclature', this.nomenclature);
          feature.set('nomenclatureAbbreviation', this.nomenclatureAbbreviation);
        }
      }
      
      this.saveToHistory();
      this.updateMeasurementDisplay(feature);
    });
    
    this.map.addInteraction(this.drawInteraction);
    this.applySnapState();
  }

  private addDuctInteraction(type: string): void {
    this.removeInteractions();
    
    const grade = (document.getElementById('assetType') as HTMLSelectElement)?.value || 'overground';
    const isUnderground = grade === 'underground';
    const ductColor = grade === 'crossover' ? '#c026d3' : isUnderground ? '#8e44ad' : '#f39c12';
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: ductColor,
          width: thickness + 1,
          lineDash: isUnderground ? [10, 5] : grade === 'crossover' ? [14, 4, 4, 4] : undefined
        })
      })
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', type.toLowerCase());
      feature.set('thickness', thickness);
      feature.set('underground', isUnderground);
      feature.set('assetType', grade);
      feature.set('grade', grade);
      feature.set('info', `${type} Duct`);
      this.saveToHistory();
      this.syncDrawnDuctToLrs(feature, type, grade);
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private ductTypeToLrsUtility(type: string): string {
    const t = type.toLowerCase();
    if (t.includes('telecom') || t.includes('ofc')) return 'ofc';
    if (t.includes('electric')) return 'electric_lt';
    if (t.includes('gas') || t.includes('oil')) return 'oil_gas';
    if (t.includes('water')) return 'water';
    if (t.includes('sewer')) return 'sewer';
    if (t.includes('drain')) return 'drainage';
    return 'ofc';
  }

  /** When an LRS route is selected, persist GIS duct drawings as corridor utilities. */
  private syncDrawnDuctToLrs(feature: Feature, ductType: string, grade: string): void {
    const routeId = this.selectedLrsRouteId;
    if (!routeId) return;
    const geom = feature.getGeometry();
    if (!(geom instanceof LineString)) return;
    const coords = geom.getCoordinates();
    if (!coords?.length) return;
    const start = toLonLat(coords[0]);
    const end = toLonLat(coords[coords.length - 1]);
    this.lrsService.locate(routeId, start[0], start[1]).subscribe({
      next: (fromRes) => {
        this.lrsService.locate(routeId, end[0], end[1]).subscribe({
          next: (toRes) => {
            const fromKm = fromRes.data.measure_km;
            const toKm = toRes.data.measure_km;
            const lo = Math.min(fromKm, toKm);
            const hi = Math.max(fromKm, toKm);
            const body: Record<string, unknown> = {
              utility_type: this.ductTypeToLrsUtility(ductType),
              measure_km: lo,
              label: `${ductType} ${grade}`,
              grade,
              classification: grade === 'crossover' ? 'crossover' : grade === 'underground' ? 'underground' : 'overground',
              depth_m: grade === 'underground' ? 1.2 : null,
            };
            if (hi - lo > 0.001) body['measure_to_km'] = hi;
            this.lrsService.createUtilityAsset(routeId, body).subscribe({
              next: () => {
                this.lrsStatus = `Saved ${grade} ${ductType} to LRS route`;
                this.loadLrsUtilities(routeId, this.demoActiveRouteCode || `route-${routeId}`);
              },
              error: (e) => {
                this.lrsStatus = e?.error?.error || 'Could not save duct to LRS';
              },
            });
          },
        });
      },
    });
  }

  private enableSelect(): void {
    this.removeInteractions();
    this.selectInteraction = new Select({
      multi: true,
      hitTolerance: 16,
      filter: this.editableFeatureFilter,
    });
    this.map.addInteraction(this.selectInteraction);
    this.applySnapState();
    this.selectInteraction.on('select', (e) => {
      if (e.selected.length > 0) {
        const feature = e.selected[0] as Feature;
        if (this.isEditableRoadFeature(feature)) {
          this.openRoadEditDetail(feature);
        } else {
          this.closeRoadEditDetail();
          this.showFeatureInfo(feature);
        }
      } else {
        this.closeRoadEditDetail();
      }
    });
  }

  /** Convert a thin sketched multi-lane line into a full corridor so Edit can change lanes/width, not only a vertex. */
  private upgradeThinMultiLaneRoadIfNeeded(feature: Feature): void {
    const type = String(feature.get('type') || '');
    if (type !== 'multi_lane_road') return;
    if (feature.get('corridorId') && feature.get('dummyRoadInfra')) return;

    const lanes = Math.max(2, Number(feature.get('lanes')) || 2);
    const widthM = Math.max(7, Number(feature.get('width') || feature.get('roadWidthM') || lanes * 3.5));
    feature.set('type', 'dummy_road_corridor');
    feature.set('dummyRoadInfra', true);
    feature.set('lanes', lanes);
    feature.set('roadWidthM', widthM);
    if (!feature.get('name')) {
      feature.set('name', feature.get('title') || `${lanes}-lane road`);
    }
    feature.setStyle(undefined);

    const src = this.getSourceForFeature(feature);
    if (src && src !== this.roadSource) {
      src.removeFeature(feature);
      this.roadSource.addFeature(feature);
    }

    this.refreshRoadCorridorDecorations(feature);
  }

  private isEditableRoadFeature(feature: Feature): boolean {
    const type = String(feature.get('type') || '');
    return !!(
      feature.get('roadSpecs') ||
      feature.get('roadType') ||
      feature.get('lanes') != null ||
      feature.get('roadWidthM') != null ||
      type === 'dummy_road_corridor' ||
      type === 'multi_lane_road' ||
      type === 'road' ||
      type === 'road_corridor'
    );
  }

  openRoadEditDetail(feature: Feature): void {
    this.upgradeThinMultiLaneRoadIfNeeded(feature);
    this.lrsAssetDetail = null;
    this.roadEditDetail = this.buildRoadEditDetail(feature);
    const featureInfo = document.getElementById('featureInfo');
    if (featureInfo) featureInfo.style.display = 'none';
  }

  closeRoadEditDetail(): void {
    this.roadEditDetail = null;
  }

  private buildRoadEditDetail(feature: Feature): NonNullable<typeof this.roadEditDetail> {
    const specs = feature.get('roadSpecs') || {};
    return {
      feature,
      name: String(feature.get('name') || specs.name || 'Road corridor'),
      roadType: String(feature.get('roadType') || specs.type || 'highway'),
      lanes: Number(feature.get('lanes') ?? specs.lanes ?? 2),
      widthM: Number(feature.get('roadWidthM') ?? specs.width ?? 12),
      depthM: Number(feature.get('depthM') ?? specs.depth ?? 0.5),
      surface: String(feature.get('material') || specs.surface || 'bituminous'),
      dividerType: String(feature.get('dividerType') || specs.dividerType || 'painted'),
      dividerWidthM: Number(feature.get('dividerWidthM') ?? specs.dividerWidth ?? 0.3),
      separatorDistanceM: Number(specs.separatorDistance ?? 2),
      comment: String(feature.get('comment') || ''),
    };
  }

  onRoadDividerTypeChange(): void {
    if (!this.roadEditDetail) return;
    this.roadEditDetail.dividerWidthM = defaultDividerWidth(this.roadEditDetail.dividerType);
  }

  private applyDividerTypeDefaults(type: string): void {
    const widthM = defaultDividerWidth(type);
    this.dividerType = type;
    this.separatorWidth = widthM;
    this.currentRoadSpecs.dividerType = type;
    this.currentRoadSpecs.dividerWidth = widthM;
    const widthInput = document.getElementById('separatorWidth') as HTMLInputElement | null;
    if (widthInput) widthInput.value = String(widthM);
  }

  private refreshRoadCorridorDecorations(centerFeature: Feature): void {
    const geom = centerFeature.getGeometry();
    if (!(geom instanceof LineString)) return;

    let corridorId = centerFeature.get('corridorId') as string | undefined;
    if (!corridorId) {
      corridorId = `corridor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      centerFeature.set('corridorId', corridorId);
    }

    const decorationTypes = new Set([
      'milestone_carriageway',
      'milestone_median_fill',
      'milestone_median_edge',
      'milestone_shoulder_edge',
      'milestone_lane_divider',
      'dummy_pavement_label',
    ]);

    this.roadSource.getFeatures()
      .filter((f) => f !== centerFeature && f.get('corridorId') === corridorId && decorationTypes.has(String(f.get('type'))))
      .forEach((f) => this.roadSource.removeFeature(f));

    const lanes = Number(centerFeature.get('lanes')) || 2;
    const roadWidthM = Number(centerFeature.get('roadWidthM')) || 12;
    const dividerWidthM = Number(centerFeature.get('dividerWidthM')) || 0;
    const dividerType = String(centerFeature.get('dividerType') || 'painted');
    const material = String(centerFeature.get('material') || 'bituminous');
    const corridorName = String(centerFeature.get('name') || 'Road');

    this.addMilestoneLaneGuides(
      this.roadSource,
      geom,
      lanes,
      roadWidthM,
      dividerWidthM,
      material,
      !!centerFeature.get('dummyRoadInfra'),
      corridorId,
      dividerType
    );

    const labelPt = geom.getCoordinateAt(0.5);
    const textFeature = new Feature({
      geometry: new Point(labelPt),
      type: 'dummy_pavement_label',
      dummyRoadInfra: !!centerFeature.get('dummyRoadInfra'),
      corridorId,
      info: `Pavement: ${material}`,
    });
    textFeature.setStyle(this.scaleAwareStyle(new Style({
      text: new Text({
        text: `${corridorName} | ${material}`,
        font: '700 11px Arial',
        fill: new Fill({ color: '#f8fafc' }),
        stroke: new Stroke({ color: '#0f172a', width: 4 }),
        backgroundFill: new Fill({ color: 'rgba(15, 23, 42, 0.82)' }),
        backgroundStroke: new Stroke({ color: '#334155', width: 1.4 }),
        padding: [4, 8, 4, 8],
        offsetY: -10,
      }),
    }), 10));
    this.roadSource.addFeature(textFeature);
  }

  saveRoadEditDetail(): void {
    if (!this.roadEditDetail) return;
    const d = this.roadEditDetail;
    const feature = d.feature;
    const lanes = Math.max(1, Number(d.lanes) || 2);
    const widthM = Math.max(3, Number(d.widthM) || 12);
    const depthM = Math.max(0.1, Number(d.depthM) || 0.5);
    const dividerWidthM = d.dividerType === 'none' ? 0 : Math.max(0, Number(d.dividerWidthM) || 0);
    const roadSpecs = {
      type: d.roadType,
      width: widthM,
      depth: depthM,
      lanes,
      surface: d.surface,
      separatorDistance: Math.max(0.5, Number(d.separatorDistanceM) || 2),
      dividerWidth: dividerWidthM,
      dividerType: d.dividerType,
    };

    feature.set('name', d.name.trim() || 'Road corridor');
    feature.set('roadType', d.roadType);
    feature.set('lanes', lanes);
    feature.set('roadWidthM', widthM);
    feature.set('depthM', depthM);
    feature.set('material', d.surface);
    feature.set('dividerType', d.dividerType);
    feature.set('dividerWidthM', dividerWidthM);
    feature.set('roadSpecs', { ...roadSpecs });
    feature.set('comment', d.comment);
    feature.set(
      'info',
      `${d.name} | ${d.roadType} | ${lanes}L | W ${widthM.toFixed(1)}m | D ${depthM.toFixed(1)}m | Divider ${dividerWidthM.toFixed(1)}m (${d.dividerType}) | ${d.surface}`
    );

    feature.setStyle(undefined);
    feature.changed();

    const corridorType = String(feature.get('type') || '');
    if (
      corridorType === 'dummy_road_corridor' ||
      corridorType === 'multi_lane_road' ||
      this.getSourceForFeature(feature) === this.roadSource
    ) {
      this.refreshRoadCorridorDecorations(feature);
    }

    this.map.getView().changed();
    this.saveToHistory();
    this.showInstructions('Road data updated — divider and lane guides refreshed');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private removeInteractions(): void {
    if (this.drawInteraction) this.map.removeInteraction(this.drawInteraction);
    if (this.selectInteraction) this.map.removeInteraction(this.selectInteraction);
    if (this.modifyInteraction) this.map.removeInteraction(this.modifyInteraction);
    if (this.translateInteraction) this.map.removeInteraction(this.translateInteraction);
    if (this.rotateInteraction) this.map.removeInteraction(this.rotateInteraction);
    this.rotateInteraction = undefined;
    this.rotateToolActive = false;
    if (this.rotatePivotLayer) {
      this.map.removeLayer(this.rotatePivotLayer);
      this.rotatePivotLayer = null;
    }
    if (this.measureInteraction) this.map.removeInteraction(this.measureInteraction);
    if (this.placeSymbolHandler) {
      this.map.un('click', this.placeSymbolHandler);
      this.placeSymbolHandler = null;
    }
    this.clearSeparatorInsertHandler();
    this.snapInteractions.forEach(s => this.map.removeInteraction(s));
    this.snapInteractions = [];
    this.applySnapState();
  }

  // Style creation methods
  private getDrawingStyle(feature: FeatureLike): Style[] {
    return [new Style({
      fill: new Fill({
        color: 'rgba(52, 152, 219, 0.3)'
      }),
      stroke: new Stroke({
        color: this.defaultSettings.lineColor,
        width: 2
      })
    })];
  }

  private getRoadStyle(feature: FeatureLike): Style[] {
    return [new Style({
      stroke: new Stroke({
        color: '#e67e22',
        width: 4
      })
    })];
  }

  private getProjectStyle(feature: FeatureLike): Style[] {
    return [new Style({
      fill: new Fill({
        color: 'rgba(46, 125, 50, 0.2)'
      }),
      stroke: new Stroke({
        color: '#2e7d32',
        width: 2
      })
    })];
  }

  private getBiometricStyle(feature: FeatureLike): Style[] {
    return [new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          color: '#27ae60'
        })
      })
    })];
  }

  private getGpsStyle(feature: FeatureLike): Style[] {
    return [new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          color: 'rgba(76, 175, 80, 0.8)'
        })
      })
    })];
  }

  private getWaterStyle(feature: FeatureLike): Style[] {
    return [new Style({
      stroke: new Stroke({
        color: '#3498db',
        width: 4,
        lineDash: [10, 5]
      })
    })];
  }

  private getSeparatorStyle(feature: FeatureLike): Style[] {
    const dividerType = String((feature as Feature).get?.('dividerType') || 'painted');
    return [this.getLaneSeparatorStyle(dividerType)];
  }

  private updateRoadSpecs(): void {
    const roadType = document.getElementById('roadType') as HTMLSelectElement;
    const roadWidth = document.getElementById('roadWidth') as HTMLInputElement;
    const roadDepth = document.getElementById('roadDepth') as HTMLInputElement;
    const roadLanes = document.getElementById('roadLanes') as HTMLSelectElement;
    const surfaceType = document.getElementById('surfaceType') as HTMLSelectElement;
    const separatorDistance = document.getElementById('separatorDistance') as HTMLInputElement;
    const separatorWidth = document.getElementById('separatorWidth') as HTMLInputElement;
    const dividerType = document.getElementById('dividerType') as HTMLSelectElement;

    if (roadType) this.currentRoadSpecs.type = roadType.value;
    if (roadWidth) this.currentRoadSpecs.width = parseFloat(roadWidth.value);
    if (roadDepth) this.currentRoadSpecs.depth = parseFloat(roadDepth.value);
    if (roadLanes) this.currentRoadSpecs.lanes = parseInt(roadLanes.value);
    if (surfaceType) this.currentRoadSpecs.surface = surfaceType.value;
    if (separatorDistance) this.currentRoadSpecs.separatorDistance = parseFloat(separatorDistance.value);
    if (dividerType) this.currentRoadSpecs.dividerType = dividerType.value;
    if (separatorWidth) {
      const requestedDividerWidth = parseFloat(separatorWidth.value);
      this.currentRoadSpecs.dividerWidth = (dividerType?.value === 'none') ? 0 : requestedDividerWidth;
    }
  }

  private scaleAwareStyle(style: Style | Style[], maxResolution: number): (feature: FeatureLike, resolution: number) => Style | Style[] | null {
    return (_feature: FeatureLike, resolution: number) => {
      if (resolution == null) return style;
      return resolution <= maxResolution ? style : null;
    };
  }

  private autoNumberSegment(feature: Feature): void {
    const center = feature.getGeometry()!.getExtent();
    const centerPoint = [(center[0] + center[2]) / 2, (center[1] + center[3]) / 2];
    
    let segmentLabel: string;
    let segmentId: string;
    let segmentNumber: number;
    let isSubSegment = false;
    
    // Determine segment number
    if (this.numberingMode === 'auto') {
      segmentNumber = this.currentSegment;
    } else {
      segmentNumber = this.manualSegmentNumber;
    }
    
    // Check if a main segment with this number already exists (making this a subsegment)
    const currentSource = this.getCurrentDrawingSource();
    const existingMainSegment = currentSource.getFeatures().find(f => {
      const fSegmentNumber = f.get('segmentNumber');
      const fIsSubSegment = f.get('isSubSegment');
      const fType = f.get('type');
      return fSegmentNumber === segmentNumber && 
             !fIsSubSegment && 
             (fType === 'segment' || fType === 'LineString' || fType === 'MultiLineString');
    });
    
    if (existingMainSegment) {
      // This is a subsegment - increment subsegment count for this segment number
      isSubSegment = true;
      if (!this.segmentSubSegmentCount[segmentNumber]) {
        this.segmentSubSegmentCount[segmentNumber] = 1;
      } else {
        this.segmentSubSegmentCount[segmentNumber]++;
      }
      const subSegmentNum = this.segmentSubSegmentCount[segmentNumber];
      segmentId = `s${segmentNumber}_${subSegmentNum}`;
      // Subsegment label: "Gb s1_1", "Gb s1_2", etc.
      segmentLabel = `${this.nomenclatureAbbreviation} ${segmentId}`;
    } else {
      // This is a new main segment
      isSubSegment = false;
      segmentId = `s${segmentNumber}`;
      // Main segment label: "Gandhi baba segment 1", "Gandhi baba segment 2", etc.
      segmentLabel = `${this.nomenclature} segment ${segmentNumber}`;
      // Initialize subsegment count for this segment
      this.segmentSubSegmentCount[segmentNumber] = 0;
      
      // Increment current segment counter only for new main segments in auto mode
      if (this.numberingMode === 'auto') {
        this.currentSegment++;
      }
    }
    
    // Create label feature
    const textFeature = new Feature({
      geometry: new Point(centerPoint),
      text: segmentLabel,
      textSize: 14,
      textColor: '#000',
      type: 'segment_label'
    });
    
    currentSource.addFeature(textFeature);
    
    // Update feature properties
    feature.set('segmentNumber', segmentNumber);
    feature.set('segmentId', segmentId);
    feature.set('label', segmentLabel);
    feature.set('nomenclature', this.nomenclature);
    feature.set('nomenclatureAbbreviation', this.nomenclatureAbbreviation);
    feature.set('isSubSegment', isSubSegment);
    
    // Update UI
    const currentSegmentElement = document.getElementById('currentSegment');
    if (currentSegmentElement) {
      currentSegmentElement.innerText = this.currentSegment.toString();
    }
  }

  private generateSegmentId(): string {
    const segmentNumber = this.numberingMode === 'auto' ? this.currentSegment : this.manualSegmentNumber;
    
    // Check if a main segment with this number already exists (making this a subsegment)
    const currentSource = this.getCurrentDrawingSource();
    const existingMainSegment = currentSource.getFeatures().find(f => {
      const fSegmentNumber = f.get('segmentNumber');
      const fIsSubSegment = f.get('isSubSegment');
      const fType = f.get('type');
      return fSegmentNumber === segmentNumber && 
             !fIsSubSegment && 
             (fType === 'segment' || fType === 'LineString' || fType === 'MultiLineString');
    });
    
    if (existingMainSegment) {
      // This is a subsegment - increment subsegment count
      if (!this.segmentSubSegmentCount[segmentNumber]) {
        this.segmentSubSegmentCount[segmentNumber] = 1;
      } else {
        this.segmentSubSegmentCount[segmentNumber]++;
      }
      const subSegmentNum = this.segmentSubSegmentCount[segmentNumber];
      return `s${segmentNumber}_${subSegmentNum}`;
    } else {
      // This is a new main segment
      this.segmentSubSegmentCount[segmentNumber] = 0;
      if (this.numberingMode === 'auto') {
        this.currentSegment++;
      }
      return `s${segmentNumber}`;
    }
  }

  private generateSegmentLabel(segmentNumber: number, segmentId: string): string {
    // Check if this is a subsegment
    const isSubSegment = segmentId.includes('_');
    
    if (isSubSegment) {
      // Subsegment: "Gb s1_1", "Gb s1_2", etc.
      return `${this.nomenclatureAbbreviation} ${segmentId}`;
    } else {
      // Main segment: "Gandhi baba segment 1", "Gandhi baba segment 2", etc.
      return `${this.nomenclature} segment ${segmentNumber}`;
    }
  }

  private updateLineCount(): void {
    const activeLineCountElement = document.getElementById('activeLineCount');
    if (activeLineCountElement) {
      activeLineCountElement.innerText = this.currentLines.length.toString();
    }
  }

  private publishBoqGeometry(): void {
    if (!this.compactForBoqValue || !this.map) return;
    const sources = new Set<VectorSource>();
    if (this.vectorSource) sources.add(this.vectorSource);
    if (this.roadSource) sources.add(this.roadSource);
    try {
      const current = this.getCurrentDrawingSource();
      if (current) sources.add(current);
    } catch {
      /* drawing source not ready */
    }
    const proj = this.map.getView().getProjection();
    const lineCoords: number[][][] = [];
    sources.forEach((src) => {
      src.getFeatures().forEach((f) => {
        if (f.get('lrsKind') || f.get('type') === 'alignment_chainage') return;
        const g = f.getGeometry();
        if (!g) return;
        const type = g.getType();
        if (type !== 'LineString' && type !== 'MultiLineString') return;
        try {
          const obj = this.format.writeGeometryObject(g, {
            featureProjection: proj,
            dataProjection: 'EPSG:4326',
          }) as { type: string; coordinates: any };
          if (obj.type === 'LineString' && Array.isArray(obj.coordinates)) {
            lineCoords.push(obj.coordinates);
          } else if (obj.type === 'MultiLineString' && Array.isArray(obj.coordinates)) {
            obj.coordinates.forEach((c: number[][]) => lineCoords.push(c));
          }
        } catch {
          /* skip unwritable geometry */
        }
      });
    });
    if (!lineCoords.length) {
      this.geoMapBridge.setBoqGeometry(null);
      return;
    }
    if (lineCoords.length === 1) {
      this.geoMapBridge.setBoqGeometry({ type: 'LineString', coordinates: lineCoords[0] });
      return;
    }
    this.geoMapBridge.setBoqGeometry({ type: 'MultiLineString', coordinates: lineCoords });
  }

  private saveToHistory(): void {
    // Implementation for saving to history
    this.drawingHistory = this.drawingHistory.slice(0, this.historyStep + 1);
    this.drawingHistory.push({
      vectorSource: this.snapshotFeatures(this.vectorSource.getFeatures()),
      roadSource: this.snapshotFeatures(this.roadSource.getFeatures()),
    });
    this.historyStep++;
    const maxHistory = 50;
    if (this.drawingHistory.length > maxHistory) {
      const drop = this.drawingHistory.length - maxHistory;
      this.drawingHistory.splice(0, drop);
      this.historyStep -= drop;
    }
    this.publishBoqGeometry();
  }

  private updateMeasurementDisplay(feature: Feature): void {
    const measurementResults = document.getElementById('measurementResults');
    if (measurementResults) {
      const length = this.calculateLength(feature);
      const meters = parseFloat(length.meters) || 0;
      measurementResults.innerHTML = `Length: ${this.formatLengthM(meters)}`;
    }
  }

  private calculateLength(feature: Feature): { meters: string; km: string } {
    const geom = feature.getGeometry();
    if (!geom) {
      return { meters: '0.00', km: '0.00' };
    }
    
    let length = 0;
    
    try {
      if (geom.getType() === 'LineString' || geom.getType() === 'MultiLineString') {
        // Use OpenLayers geodesic calculation (most accurate)
        length = getGeodesicLength(geom, { projection: 'EPSG:3857' });
        
        // Fallback to turf.js if available and OL calculation fails
        if (isNaN(length) || length === 0) {
          const geojsonString = this.format.writeGeometry(geom, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          if (typeof turf !== 'undefined' && geojsonString) {
            try {
              const geojsonObj = JSON.parse(geojsonString);
              length = turf.length(geojsonObj, { units: 'meters' });
            } catch (e) {
              console.warn('Turf.js calculation failed:', e);
            }
          }
        }
      } else if (geom.getType() === 'Circle') {
        // For circles, calculate circumference
        const radius = (geom as Circle).getRadius();
        // Convert from Web Mercator units to meters (approximate at center latitude)
        const center = (geom as Circle).getCenter();
        const centerLonLat = toLonLat(center);
        const latFactor = Math.cos(centerLonLat[1] * Math.PI / 180);
        const radiusMeters = radius * latFactor;
        length = 2 * Math.PI * radiusMeters;
      }
    } catch (error) {
      console.error('Error calculating length:', error);
    }
    
    const km = length / 1000;
    return {
      meters: length.toFixed(2),
      km: km.toFixed(4)
    };
  }

  private showFeatureInfo(feature: Feature): void {
    const details = document.getElementById('featureDetails');
    const featureInfo = document.getElementById('featureInfo');
    
    if (details && featureInfo) {
      details.innerHTML = '';
      
      if (feature.get('roadSpecs')) {
        const specs = feature.get('roadSpecs');
        details.innerHTML += `Width (m): <input id="editWidth" type="number" value="${specs.width}"><br>`;
        details.innerHTML += `Depth (m): <input id="editDepth" type="number" value="${specs.depth}"><br>`;
      } else if (feature.get('junction')) {
        const specs = feature.get('specs') || {width: 10, depth: 0.5};
        details.innerHTML += `Junction Width: <input id="editWidth" type="number" value="${specs.width}"><br>`;
        details.innerHTML += `Junction Depth: <input id="editDepth" type="number" value="${specs.depth}"><br>`;
      } else if (feature.get('symbol') || feature.get('type') === 'road_symbol') {
        details.innerHTML += `Symbol: <input id="editSymbol" type="text" value="${feature.get('symbol') || ''}"><br>`;
        details.innerHTML += `Label: <input id="editSymbolTitle" type="text" value="${feature.get('title') || ''}"><br>`;
      }
      
      details.innerHTML += `Comment: <input id="editComment" type="text" value="${feature.get('comment') || ''}"><br>`;
      featureInfo.style.display = 'block';
      
      const saveBtn = document.getElementById('saveFeatureProps');
      if (saveBtn) {
        saveBtn.onclick = () => {
          const newWidth = parseFloat((document.getElementById('editWidth') as HTMLInputElement)?.value || '0');
          const newDepth = parseFloat((document.getElementById('editDepth') as HTMLInputElement)?.value || '0');
          const newComment = (document.getElementById('editComment') as HTMLInputElement)?.value || '';
          
          if (feature.get('roadSpecs')) {
            let specs = feature.get('roadSpecs');
            specs.width = newWidth;
            specs.depth = newDepth;
            feature.set('roadSpecs', specs);
          } else if (feature.get('junction')) {
            feature.set('specs', {width: newWidth, depth: newDepth});
          } else if (feature.get('symbol') || feature.get('type') === 'road_symbol') {
            const newSymbol = (document.getElementById('editSymbol') as HTMLInputElement)?.value || feature.get('symbol');
            const newTitle = (document.getElementById('editSymbolTitle') as HTMLInputElement)?.value || '';
            feature.set('symbol', newSymbol);
            feature.set('title', newTitle);
          }
          
          feature.set('comment', newComment);
          feature.changed();
          this.map.getView().changed();
          featureInfo.style.display = 'none';
        };
      }
    }
  }

  private createDrawingStyle(thickness: number = 2, roadSpecs?: any): Style[] {
    if (roadSpecs && roadSpecs.lanes > 1) {
      // Multi-lane road implementation would go here
      // For now, return simple style
    }
    
    return [new Style({
      fill: new Fill({
        color: 'rgba(52, 152, 219, 0.3)'
      }),
      stroke: new Stroke({
        color: this.defaultSettings.lineColor,
        width: thickness
      }),
      image: new CircleStyle({
        radius: 5 + thickness,
        fill: new Fill({
          color: this.defaultSettings.lineColor
        })
      })
    })];
  }

  private updateActiveDrawingStyle(): void {
    // Update the active drawing interaction's style if it exists
    if (this.drawInteraction && this.currentDrawingMode) {
      // Recreate the drawing interaction with the updated thickness
      this.handleDrawingModeChange(this.currentDrawingMode);
    }
  }

  /** LRS layers are read-only — exclude them from edit selections. Skip corridor decorations so Edit hits the road centerline. */
  private editableFeatureFilter = (feature: FeatureLike, layer: unknown): boolean => {
    if ((layer as VectorLayer | null)?.get?.('lrsReadonly') || (layer as VectorLayer | null)?.get?.('gisOverlay')) return false;
    const type = String((feature as Feature).get?.('type') || '');
    return !this.isRoadCorridorDecorationType(type);
  };

  private isRoadCorridorDecorationType(type: string): boolean {
    return (
      type === 'milestone_carriageway' ||
      type === 'milestone_median_fill' ||
      type === 'milestone_median_edge' ||
      type === 'milestone_shoulder_edge' ||
      type === 'milestone_lane_divider' ||
      type === 'dummy_pavement_label'
    );
  }

  private enableModify(): void {
    this.removeInteractions();
    this.selectInteraction = new Select({
      multi: true,
      hitTolerance: 16,
      filter: this.editableFeatureFilter,
    });
    this.map.addInteraction(this.selectInteraction);
    const selected = this.selectInteraction.getFeatures();
    this.modifyInteraction = new Modify({
      features: selected,
      pixelTolerance: 16,
      hitDetection: true,
    });
    this.map.addInteraction(this.modifyInteraction);
    this.applySnapState();
    this.selectInteraction.on('select', (e) => {
      if (e.selected.length > 0) {
        const feature = e.selected[0] as Feature;
        if (this.isEditableRoadFeature(feature)) {
          this.openRoadEditDetail(feature);
        } else if (feature.get('symbol') || feature.get('type') === 'road_symbol') {
          this.closeRoadEditDetail();
          this.showFeatureInfo(feature);
        } else {
          this.closeRoadEditDetail();
          this.showFeatureInfo(feature);
        }
      } else {
        this.closeRoadEditDetail();
      }
    });
    this.modifyInteraction.on('modifyend', () => {
      const selected = this.selectInteraction?.getFeatures();
      selected?.forEach((f) => {
        const feat = f as Feature;
        if (this.isEditableRoadFeature(feat)) {
          this.refreshRoadCorridorDecorations(feat);
        }
      });
      this.saveToHistory();
    });
  }

  private enableTranslate(): void {
    this.removeInteractions();
    this.selectInteraction = new Select({
      multi: true,
      hitTolerance: 16,
      filter: this.editableFeatureFilter,
    });
    this.map.addInteraction(this.selectInteraction);
    const selected = this.selectInteraction.getFeatures();
    this.translateInteraction = new Translate({ features: selected });
    this.translateInteraction.on('translateend', (e) => {
      this.syncRoadDecorations(e.features.getArray() as Feature[]);
      this.saveToHistory();
    });
    this.map.addInteraction(this.translateInteraction);
    this.applySnapState();
  }

  private enableRotate(): void {
    this.removeInteractions();
    this.rotateToolActive = true;
    this.selectInteraction = new Select({
      multi: true,
      hitTolerance: 16,
      filter: this.editableFeatureFilter,
    });
    this.map.addInteraction(this.selectInteraction);
    const selected = this.selectInteraction.getFeatures();

    const pivotSource = new VectorSource();
    this.rotatePivotLayer = new VectorLayer({
      source: pivotSource,
      zIndex: 1000,
      style: new Style({
        image: new RegularShape({
          points: 4,
          radius: 9,
          radius2: 0,
          angle: 0,
          stroke: new Stroke({ color: '#2563eb', width: 2 }),
        }),
      }),
    });
    this.map.addLayer(this.rotatePivotLayer);
    const showPivot = (pivot: number[] | null) => {
      pivotSource.clear();
      if (pivot) pivotSource.addFeature(new Feature(new Point(pivot)));
    };
    selected.on(['add', 'remove'], () => showPivot(this.extentCenterOf(selected.getArray() as Feature[])));

    let pivot: number[] | null = null;
    let startPixel: number[] | null = null;
    let startAngle = 0;
    let applied = 0;
    let dragging = false;
    const angleAt = (coord: number[]) => Math.atan2(coord[1] - pivot![1], coord[0] - pivot![0]);

    this.rotateInteraction = new PointerInteraction({
      handleDownEvent: (evt: MapBrowserEvent) => {
        if (!selected.getLength()) return false;
        pivot = this.extentCenterOf(selected.getArray() as Feature[]);
        if (!pivot) return false;
        startPixel = evt.pixel.slice();
        startAngle = angleAt(evt.coordinate);
        applied = 0;
        dragging = false;
        return true;
      },
      handleDragEvent: (evt: MapBrowserEvent) => {
        if (!pivot || !startPixel) return;
        if (!dragging) {
          if (Math.hypot(evt.pixel[0] - startPixel[0], evt.pixel[1] - startPixel[1]) < 3) return;
          dragging = true;
        }
        let angle = angleAt(evt.coordinate) - startAngle;
        if ((evt.originalEvent as PointerEvent).shiftKey) {
          const step = Math.PI / 12;
          angle = Math.round(angle / step) * step;
        }
        const delta = angle - applied;
        if (delta) {
          this.rotateFeatures(selected.getArray() as Feature[], delta, pivot);
          applied = angle;
        }
        this.showInstructions(`Rotating ${this.formatRotationDeg(-angle)} — release to apply`);
      },
      handleUpEvent: () => {
        if (dragging && applied) {
          this.syncRoadDecorations(selected.getArray() as Feature[]);
          this.saveToHistory();
          this.showInstructions(`Rotated ${this.formatRotationDeg(-applied)}`);
        }
        pivot = null;
        startPixel = null;
        dragging = false;
        return false;
      },
    });
    this.map.addInteraction(this.rotateInteraction);
    this.showInstructions('Rotate: select features, then drag around the + (Shift snaps 15°). [ ] rotate 15°, A for an exact angle');
  }

  private formatRotationDeg(clockwiseRad: number): string {
    let deg = (clockwiseRad * 180) / Math.PI;
    deg = ((deg + 540) % 360) - 180;
    return `${deg >= 0 ? '+' : ''}${deg.toFixed(1)}°`;
  }

  /** Positive angles turn counter-clockwise, matching OpenLayers geometry rotation. */
  private rotateFeatures(features: Feature[], angleRad: number, pivot: number[]): void {
    features.forEach((f) => {
      const geom = f.getGeometry();
      if (!geom) return;
      geom.rotate(angleRad, pivot);
      if (geom instanceof Point) {
        const prev = Number(f.get('rotationDeg') || 0);
        const next = (((prev - (angleRad * 180) / Math.PI) % 360) + 360) % 360;
        f.set('rotationDeg', Math.round(next * 100) / 100);
      }
    });
  }

  /** Positive degrees turn counter-clockwise. */
  private rotateSelectionBy(degrees: number): void {
    const selected = this.selectedEditableFeatures('Rotate');
    if (!selected) return;
    const pivot = this.extentCenterOf(selected);
    if (!pivot) return;
    this.rotateFeatures(selected, (degrees * Math.PI) / 180, pivot);
    this.syncRoadDecorations(selected);
    this.saveToHistory();
    this.showInstructions(`Rotated ${this.formatRotationDeg((-degrees * Math.PI) / 180)}`);
  }

  private promptRotateSelection(): void {
    if (!this.selectedEditableFeatures('Rotate')) return;
    const raw = window.prompt('Rotate by (degrees, positive = clockwise)', '90');
    if (raw == null) return;
    const deg = parseFloat(raw);
    if (!Number.isFinite(deg) || deg === 0) {
      this.showInstructions('Enter a non-zero angle in degrees');
      return;
    }
    this.rotateSelectionBy(-deg);
  }

  private enableDelete(): void {
    if (this.selectInteraction && this.selectInteraction.getFeatures().getLength() > 0) {
      const features = this.selectInteraction.getFeatures();
      features.forEach((feature) => {
        const src = this.getSourceForFeature(feature as Feature);
        if (src) src.removeFeature(feature as Feature);
      });
      features.clear();
      this.saveToHistory();
    }
  }

  private zoomIn(): void {
    const view = this.map.getView();
    const zoom = view.getZoom()!;
    view.setZoom(zoom + 1);
    this.osmNetworkOverlay?.refresh();
  }

  private zoomOut(): void {
    const view = this.map.getView();
    const zoom = view.getZoom()!;
    view.setZoom(zoom - 1);
    this.osmNetworkOverlay?.refresh();
  }

  // ==================== VIEW HISTORY (PREVIOUS/NEXT VIEW) ====================

  /**
   * Initialize view history tracking - call this after map is created
   */
  private initializeViewHistory(): void {
    const view = this.map.getView();
    
    // Save initial view state
    this.saveViewState();
    
    // Listen for view changes
    view.on('change:center', () => {
      if (!this.isNavigatingHistory) {
        this.saveViewState();
      }
    });
    
    view.on('change:resolution', () => {
      if (!this.isNavigatingHistory) {
        this.saveViewState();
      }
    });
  }

  /**
   * Save current view state to history
   */
  private saveViewState(): void {
    const view = this.map.getView();
    const center = view.getCenter();
    const zoom = view.getZoom();
    
    if (!center || zoom === undefined) return;
    
    const newState = {
      center: center,
      zoom: zoom,
      timestamp: Date.now()
    };
    
    // If we're not at the end of history, truncate forward history
    if (this.viewHistoryIndex < this.viewHistory.length - 1) {
      this.viewHistory = this.viewHistory.slice(0, this.viewHistoryIndex + 1);
    }
    
    // Don't save if same as current position (debounce)
    const lastState = this.viewHistory[this.viewHistory.length - 1];
    if (lastState) {
      const centerSame = Math.abs(lastState.center[0] - newState.center[0]) < 1 &&
                         Math.abs(lastState.center[1] - newState.center[1]) < 1;
      const zoomSame = Math.abs(lastState.zoom - newState.zoom) < 0.1;
      if (centerSame && zoomSame) return;
    }
    
    // Add new state
    this.viewHistory.push(newState);
    
    // Limit history size
    if (this.viewHistory.length > this.maxViewHistorySize) {
      this.viewHistory.shift();
    }
    
    this.viewHistoryIndex = this.viewHistory.length - 1;
  }

  /**
   * Navigate to previous view in history
   */
  previousView(): void {
    if (this.viewHistoryIndex <= 0) {
      this.showInstructions('No previous view available');
      setTimeout(() => this.hideInstructions(), 1500);
      return;
    }
    
    this.isNavigatingHistory = true;
    this.viewHistoryIndex--;
    
    const state = this.viewHistory[this.viewHistoryIndex];
    const view = this.map.getView();
    
    view.animate({
      center: state.center,
      zoom: state.zoom,
      duration: 300
    }, () => {
      this.isNavigatingHistory = false;
    });
    
    this.showInstructions(`View ${this.viewHistoryIndex + 1} of ${this.viewHistory.length}`);
    setTimeout(() => this.hideInstructions(), 1500);
  }

  /**
   * Navigate to next view in history
   */
  nextView(): void {
    if (this.viewHistoryIndex >= this.viewHistory.length - 1) {
      this.showInstructions('No next view available');
      setTimeout(() => this.hideInstructions(), 1500);
      return;
    }
    
    this.isNavigatingHistory = true;
    this.viewHistoryIndex++;
    
    const state = this.viewHistory[this.viewHistoryIndex];
    const view = this.map.getView();
    
    view.animate({
      center: state.center,
      zoom: state.zoom,
      duration: 300
    }, () => {
      this.isNavigatingHistory = false;
    });
    
    this.showInstructions(`View ${this.viewHistoryIndex + 1} of ${this.viewHistory.length}`);
    setTimeout(() => this.hideInstructions(), 1500);
  }

  /**
   * Check if previous view is available
   */
  canGoPreviousView(): boolean {
    return this.viewHistoryIndex > 0;
  }

  /**
   * Check if next view is available
   */
  canGoNextView(): boolean {
    return this.viewHistoryIndex < this.viewHistory.length - 1;
  }

  /**
   * Clear view history
   */
  clearViewHistory(): void {
    this.viewHistory = [];
    this.viewHistoryIndex = -1;
    this.saveViewState();
  }

  private clearAll(): void {
    this.vectorSource.clear();
    this.roadSource.clear();
    this.projectSource.clear();
    this.biometricSource.clear();
    this.gpsSource.clear();
    this.waterSource.clear();
    this.separatorSource.clear();
  }

  private exportData(): void {
    const features = this.vectorSource.getFeatures();
    const geojson = this.format.writeFeatures(features, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326'
    });
    
    const blob = new Blob([geojson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `map-data-${new Date().toISOString().slice(0, 19)}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private importData(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const features = this.format.readFeatures(text, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857'
            });
            this.vectorSource.addFeatures(features);
          } catch (e) {
            alert('Invalid GeoJSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  // Segment drawing methods
  startSegmentDrawing(): void {
    this.isSegmentDrawing = true;
    this.currentSegmentPoints = [];
    this.removeInteractions();
    
    // Update UI to show drawing mode is active
    const startBtn = document.getElementById('startSegmentDrawing');
    const stopBtn = document.getElementById('stopSegmentDrawing');
    const instructions = document.getElementById('drawingInstructions');
    
    if (startBtn) startBtn.classList.add('segment-drawing-active');
    if (stopBtn) stopBtn.style.display = 'inline-block';
    if (instructions) instructions.classList.add('active');
    
    // Add click listener for drawing segments
    this.segmentDrawingListener = this.map.on('click', (event: any) => {
      this.addSegmentPoint(event.coordinate);
    });
    
    // Add double-click listener to finish segment
    this.map.on('dblclick', (event: any) => {
      this.finishSegmentDrawing();
    });
    
    // Add right-click listener to cancel segment
    this.map.on('pointerdown' as any, (event: any) => {
      if (event.originalEvent && event.originalEvent.button === 2) { // Right click
        event.preventDefault();
        this.cancelSegmentDrawing();
      }
    });
  }

  stopSegmentDrawing(): void {
    this.isSegmentDrawing = false;
    this.currentSegmentPoints = [];
    if (this.segmentDrawingListener) {
      this.map.un('click', this.segmentDrawingListener);
      this.segmentDrawingListener = null;
    }
    
    // Update UI to show drawing mode is inactive
    const startBtn = document.getElementById('startSegmentDrawing');
    const stopBtn = document.getElementById('stopSegmentDrawing');
    const instructions = document.getElementById('drawingInstructions');
    
    if (startBtn) startBtn.classList.remove('segment-drawing-active');
    if (stopBtn) stopBtn.style.display = 'none';
    if (instructions) instructions.classList.remove('active');
  }

  private addSegmentPoint(coordinate: number[]): void {
    this.currentSegmentPoints.push(coordinate);
    
    // Create a node at this point
    this.createNode(coordinate);
    
    // If we have at least 2 points, draw a line segment
    if (this.currentSegmentPoints.length >= 2) {
      this.drawSegmentLine();
    }
  }

  private createNode(coordinate: number[]): void {
    const nodeNumber = this.numberingMode === 'auto' ? this.nodeCounter : this.manualNodeNumber;
    const nodeId = `N${nodeNumber}`;
    
    const nodeFeature = new Feature({
      geometry: new Point(coordinate),
      type: 'node',
      nodeId: nodeId,
      nodeNumber: nodeNumber,
      info: `Node ${nodeId}`,
      style: 'node'
    });
    
    this.getCurrentDrawingSource().addFeature(nodeFeature);
    
    // Add node label
    const labelFeature = new Feature({
      geometry: new Point(coordinate),
      text: nodeId,
      textSize: 12,
      textColor: '#000000',
      type: 'label'
    });
    
    this.getCurrentDrawingSource().addFeature(labelFeature);
    
    // Update counters
    if (this.numberingMode === 'auto') {
      this.nodeCounter++;
    } else {
      this.manualNodeNumber++;
    }
    
    this.updateNodeCounter();
  }

  private drawSegmentLine(): void {
    if (this.currentSegmentPoints.length < 2) return;
    
    // Get the last two points
    const startPoint = this.currentSegmentPoints[this.currentSegmentPoints.length - 2];
    const endPoint = this.currentSegmentPoints[this.currentSegmentPoints.length - 1];
    
    // Create the line feature first
    const segmentFeature = new Feature({
      geometry: new LineString([startPoint, endPoint]),
      type: 'segment'
    });
    
    // Use autoNumberSegment to generate proper ID and label
    this.autoNumberSegment(segmentFeature);
    
    // Add the feature to the source
    this.getCurrentDrawingSource().addFeature(segmentFeature);
    
    // Update counters (only for main segments, autoNumberSegment handles subsegments)
    if (!segmentFeature.get('isSubSegment')) {
      if (this.numberingMode === 'auto') {
        this.segmentCounter++;
      } else {
        this.manualSegmentNumber++;
      }
    }
    
    this.updateSegmentCounter();
  }

  private finishSegmentDrawing(): void {
    this.stopSegmentDrawing();
    this.saveToHistory();
  }

  private cancelSegmentDrawing(): void {
    // Remove any temporary features
    this.currentSegmentPoints = [];
    this.stopSegmentDrawing();
  }

  private updateNodeCounter(): void {
    const nodeCounterElement = document.getElementById('nodeCounter');
    if (nodeCounterElement) {
      nodeCounterElement.textContent = this.nodeCounter.toString();
    }
  }

  private updateSegmentCounter(): void {
    const segmentCounterElement = document.getElementById('segmentCounter');
    if (segmentCounterElement) {
      segmentCounterElement.textContent = this.segmentCounter.toString();
    }
  }

  setNumberingMode(mode: 'auto' | 'manual'): void {
    this.numberingMode = mode;
    const numberingTypeSelect = document.getElementById('numberingType') as HTMLSelectElement;
    if (numberingTypeSelect) {
      numberingTypeSelect.value = mode;
    }
  }

  resetNumbering(): void {
    this.nodeCounter = 1;
    this.segmentCounter = 1;
    this.manualNodeNumber = 1;
    this.manualSegmentNumber = 1;
    this.updateNodeCounter();
    this.updateSegmentCounter();
  }

  setManualNodeNumber(number: number): void {
    this.manualNodeNumber = number;
  }

  setManualSegmentNumber(number: number): void {
    this.manualSegmentNumber = number;
  }

  // Enhanced segment management methods
  addSegmentComment(): void {
    const commentInput = document.getElementById('segmentComment') as HTMLInputElement;
    const comment = commentInput?.value || '';
    
    if (comment) {
      // Store comment for current segment
      console.log('Adding comment to segment:', comment);
      this.updateSegmentDisplay();
      commentInput.value = '';
    }
  }

  private updateSegmentDisplay(): void {
    const currentSegmentElement = document.getElementById('currentSegment');
    if (currentSegmentElement) {
      currentSegmentElement.textContent = `Current: Node ${this.nodeCounter}, Segment ${this.segmentCounter}`;
    }
  }

  // Junction and circle tools
  addJunction(type: string): void {
    this.removeInteractions();
    
    this.map.on('click', (event: any) => {
      const junctionFeature = this.createJunctionFeature(event.coordinate, type);
      this.getCurrentDrawingSource().addFeature(junctionFeature);
      this.saveToHistory();
    });
  }

  private createJunctionFeature(coordinate: number[], type: string): Feature {
    const junctionFeature = new Feature({
      geometry: new Point(coordinate),
      type: 'junction',
      junctionType: type,
      info: `${type} Junction`,
      timestamp: new Date(),
      properties: {
        width: 10,
        lanes: 2,
        surface: 'bituminous',
        status: 'active'
      }
    });

    // Add junction label
    const labelFeature = new Feature({
      geometry: new Point(coordinate),
      text: this.getJunctionLabel(type),
      textSize: 12,
      textColor: '#000000',
      type: 'junction-label',
      junctionType: type
    });

    this.getCurrentDrawingSource().addFeature(labelFeature);
    
    // If it's a roundabout, create the circular geometry
    if (type === 'roundabout') {
      this.createRoundaboutGeometry(coordinate, junctionFeature);
    }
    
    return junctionFeature;
  }

  private createRoundaboutGeometry(center: number[], junctionFeature: Feature): void {
    const radius = 20; // meters
    const points = 32; // number of points for smooth circle
    
    const circleCoords: number[][] = [];
    for (let i = 0; i < points; i++) {
      const angle = (i * 2 * Math.PI) / points;
      const x = center[0] + radius * Math.cos(angle);
      const y = center[1] + radius * Math.sin(angle);
      circleCoords.push([x, y]);
    }
    circleCoords.push(circleCoords[0]); // Close the circle
    
    const roundaboutFeature = new Feature({
      geometry: new LineString(circleCoords),
      type: 'roundabout',
      junctionId: junctionFeature.get('id'),
      radius: radius,
      info: 'Roundabout Circle'
    });
    
    this.getCurrentDrawingSource().addFeature(roundaboutFeature);
  }

  private getJunctionLabel(type: string): string {
    const labels: { [key: string]: string } = {
      '1': '→',
      '2': '↔',
      '3': '3',
      '4': '4',
      'roundabout': '⭕'
    };
    return labels[type] || 'J';
  }

  connectRoadToJunction(): void {
    this.removeInteractions();
    this.isConnectingRoads = true;
    
    // Show instructions
    this.showInstructions('Click on a junction first, then click on a road to connect them');
    
    this.map.on('click', (event: any) => {
      if (!this.selectedJunction) {
        // Select junction
        const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
          return feature.get('type') === 'junction' ? feature : null;
        });
        
        if (feature) {
          this.selectedJunction = feature as Feature;
          this.highlightJunction(feature as Feature);
          this.showInstructions('Now click on a road to connect to this junction');
        }
      } else {
        // Connect to road
        const roadFeature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
          return feature.get('type') === 'segment' || feature.get('type') === 'LineString' ? feature : null;
        });
        
        if (roadFeature) {
          this.createRoadJunctionConnection(this.selectedJunction!, roadFeature as Feature);
          this.clearJunctionSelection();
        }
      }
    });
  }

  connectSegments(): void {
    this.removeInteractions();
    this.isConnectingSegments = true;
    
    this.showInstructions('Click on two segments to connect them');
    
    this.map.on('click', (event: any) => {
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature.get('type') === 'segment' ? feature : null;
      });
      
      if (feature) {
        if (!this.firstSegment) {
          this.firstSegment = feature as Feature;
          this.highlightSegment(feature as Feature);
          this.showInstructions('Now click on the second segment to connect');
        } else if (feature !== this.firstSegment) {
          this.createSegmentConnection(this.firstSegment, feature as Feature);
          this.clearSegmentSelection();
        }
      }
    });
  }

  private createRoadJunctionConnection(junction: Feature, road: Feature): void {
    const junctionCoord = (junction.getGeometry() as Point).getCoordinates();
    const roadGeom = road.getGeometry() as LineString;
    const roadCoords = roadGeom.getCoordinates();
    
    // Find closest point on road to junction
    const closestPoint = this.findClosestPointOnLine(junctionCoord, roadCoords);
    
    // Create connection line
    const connectionFeature = new Feature({
      geometry: new LineString([junctionCoord, closestPoint]),
      type: 'connection',
      junctionId: junction.get('id'),
      roadId: road.get('id'),
      info: 'Road-Junction Connection'
    });
    
    this.getCurrentDrawingSource().addFeature(connectionFeature);
    this.saveToHistory();
  }

  private createSegmentConnection(segment1: Feature, segment2: Feature): void {
    const geom1 = segment1.getGeometry() as LineString;
    const geom2 = segment2.getGeometry() as LineString;
    
    const coords1 = geom1.getCoordinates();
    const coords2 = geom2.getCoordinates();
    
    // Find closest endpoints
    const connectionPoints = this.findClosestEndpoints(coords1, coords2);
    
    // Create connection line
    const connectionFeature = new Feature({
      geometry: new LineString(connectionPoints),
      type: 'segment-connection',
      segment1Id: segment1.get('id'),
      segment2Id: segment2.get('id'),
      info: 'Segment Connection'
    });
    
    this.getCurrentDrawingSource().addFeature(connectionFeature);
    this.saveToHistory();
  }

  private findClosestPointOnLine(point: number[], lineCoords: number[][]): number[] {
    let minDistance = Infinity;
    let closestPoint = lineCoords[0];
    
    for (let i = 0; i < lineCoords.length - 1; i++) {
      const segmentStart = lineCoords[i];
      const segmentEnd = lineCoords[i + 1];
      const closest = this.getClosestPointOnSegment(point, segmentStart, segmentEnd);
      const distance = this.calculateDistance(point, closest);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = closest;
      }
    }
    
    return closestPoint;
  }

  private getClosestPointOnSegment(point: number[], segStart: number[], segEnd: number[]): number[] {
    const A = point[0] - segStart[0];
    const B = point[1] - segStart[1];
    const C = segEnd[0] - segStart[0];
    const D = segEnd[1] - segStart[1];
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return segStart;
    
    const param = dot / lenSq;
    
    if (param < 0) return segStart;
    if (param > 1) return segEnd;
    
    return [
      segStart[0] + param * C,
      segStart[1] + param * D
    ];
  }

  private findClosestEndpoints(coords1: number[][], coords2: number[][]): number[][] {
    const endpoints1 = [coords1[0], coords1[coords1.length - 1]];
    const endpoints2 = [coords2[0], coords2[coords2.length - 1]];
    
    let minDistance = Infinity;
    let closestPair = [endpoints1[0], endpoints2[0]];
    
    for (const ep1 of endpoints1) {
      for (const ep2 of endpoints2) {
        const distance = this.calculateDistance(ep1, ep2);
        if (distance < minDistance) {
          minDistance = distance;
          closestPair = [ep1, ep2];
        }
      }
    }
    
    return closestPair;
  }

  private highlightJunction(feature: Feature): void {
    // Remove previous highlights
    this.clearHighlights();
    
    // Add highlight style
    feature.set('highlighted', true);
    this.highlightedFeature = feature;
    this.map.getView().changed();
  }

  private highlightSegment(feature: Feature): void {
    // Remove previous highlights
    this.clearHighlights();
    
    // Add highlight style
    feature.set('highlighted', true);
    this.highlightedFeature = feature;
    this.map.getView().changed();
  }

  private clearHighlights(): void {
    if (this.highlightedFeature) {
      this.highlightedFeature.set('highlighted', false);
      this.highlightedFeature = null;
      this.map.getView().changed();
    }
  }

  private clearJunctionSelection(): void {
    this.selectedJunction = null;
    this.isConnectingRoads = false;
    this.clearHighlights();
    this.hideInstructions();
  }

  private clearSegmentSelection(): void {
    this.firstSegment = null;
    this.isConnectingSegments = false;
    this.clearHighlights();
    this.hideInstructions();
  }

  private showInstructions(message: string): void {
    const instructions = document.getElementById('drawingInstructions');
    if (instructions) {
      instructions.textContent = message;
      instructions.classList.add('active');
    }
  }

  private hideInstructions(): void {
    const instructions = document.getElementById('drawingInstructions');
    if (instructions) {
      instructions.classList.remove('active');
    }
  }

  // Junction management methods
  editJunctionProperties(junction: Feature): void {
    const properties = junction.get('properties') || {};
    const junctionType = junction.get('junctionType');
    
    // Create properties dialog
    const dialog = document.createElement('div');
    dialog.className = 'junction-properties-dialog';
    dialog.innerHTML = `
      <div class="dialog-content">
        <h3>Edit Junction Properties</h3>
        <div class="form-group">
          <label>Junction Type:</label>
          <select id="junctionTypeSelect">
            <option value="1" ${junctionType === '1' ? 'selected' : ''}>One Way</option>
            <option value="2" ${junctionType === '2' ? 'selected' : ''}>Two Way</option>
            <option value="3" ${junctionType === '3' ? 'selected' : ''}>3-Way</option>
            <option value="4" ${junctionType === '4' ? 'selected' : ''}>4-Way</option>
            <option value="roundabout" ${junctionType === 'roundabout' ? 'selected' : ''}>Roundabout</option>
          </select>
        </div>
        <div class="form-group">
          <label>Width (m):</label>
          <input type="number" id="junctionWidth" value="${properties.width || 10}" min="3" max="50">
        </div>
        <div class="form-group">
          <label>Lanes:</label>
          <input type="number" id="junctionLanes" value="${properties.lanes || 2}" min="1" max="8">
        </div>
        <div class="form-group">
          <label>Surface:</label>
          <select id="junctionSurface">
            <option value="bituminous" ${properties.surface === 'bituminous' ? 'selected' : ''}>Bituminous</option>
            <option value="concrete" ${properties.surface === 'concrete' ? 'selected' : ''}>Concrete</option>
            <option value="gravel" ${properties.surface === 'gravel' ? 'selected' : ''}>Gravel</option>
          </select>
        </div>
        <div class="form-group">
          <label>Status:</label>
          <select id="junctionStatus">
            <option value="active" ${properties.status === 'active' ? 'selected' : ''}>Active</option>
            <option value="under-construction" ${properties.status === 'under-construction' ? 'selected' : ''}>Under Construction</option>
            <option value="planned" ${properties.status === 'planned' ? 'selected' : ''}>Planned</option>
            <option value="closed" ${properties.status === 'closed' ? 'selected' : ''}>Closed</option>
          </select>
        </div>
        <div class="button-group">
          <button id="saveJunctionProps" class="btn">Save</button>
          <button id="cancelJunctionProps" class="btn">Cancel</button>
          <button id="deleteJunction" class="btn delete">Delete</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    // Event listeners
    document.getElementById('saveJunctionProps')?.addEventListener('click', () => {
      this.saveJunctionProperties(junction, dialog);
    });
    
    document.getElementById('cancelJunctionProps')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });
    
    document.getElementById('deleteJunction')?.addEventListener('click', () => {
      this.deleteJunction(junction);
      document.body.removeChild(dialog);
    });
  }

  private saveJunctionProperties(junction: Feature, dialog: HTMLElement): void {
    const type = (document.getElementById('junctionTypeSelect') as HTMLSelectElement)?.value;
    const width = parseFloat((document.getElementById('junctionWidth') as HTMLInputElement)?.value || '10');
    const lanes = parseInt((document.getElementById('junctionLanes') as HTMLInputElement)?.value || '2');
    const surface = (document.getElementById('junctionSurface') as HTMLSelectElement)?.value;
    const status = (document.getElementById('junctionStatus') as HTMLSelectElement)?.value;
    
    // Update junction properties
    junction.set('junctionType', type);
    junction.set('properties', {
      width: width,
      lanes: lanes,
      surface: surface,
      status: status
    });
    
    // Update label
    const labelFeature = this.vectorSource.getFeatures().find(f => 
      f.get('type') === 'junction-label' && 
      f.get('junctionType') === junction.get('junctionType')
    );
    
    if (labelFeature) {
      labelFeature.set('text', this.getJunctionLabel(type));
      labelFeature.set('junctionType', type);
    }
    
    // If type changed to/from roundabout, update geometry
    if (type === 'roundabout' && junction.get('junctionType') !== 'roundabout') {
      this.createRoundaboutGeometry(
        (junction.getGeometry() as Point).getCoordinates(), 
        junction
      );
    } else if (type !== 'roundabout' && junction.get('junctionType') === 'roundabout') {
      // Remove roundabout geometry
      const roundaboutFeatures = this.vectorSource.getFeatures().filter(f => 
        f.get('type') === 'roundabout' && f.get('junctionId') === junction.get('id')
      );
      roundaboutFeatures.forEach(f => this.vectorSource.removeFeature(f));
    }
    
    this.map.getView().changed();
    document.body.removeChild(dialog);
  }

  private deleteJunction(junction: Feature): void {
    // Remove junction
    this.vectorSource.removeFeature(junction);
    
    // Remove associated features
    const junctionId = junction.get('id');
    const associatedFeatures = this.vectorSource.getFeatures().filter(f => 
      f.get('junctionId') === junctionId || 
      f.get('type') === 'junction-label' ||
      f.get('type') === 'roundabout'
    );
    
    associatedFeatures.forEach(f => this.vectorSource.removeFeature(f));
    
    // Remove connections
    const connections = this.vectorSource.getFeatures().filter(f => 
      f.get('junctionId') === junctionId
    );
    
    connections.forEach(f => this.vectorSource.removeFeature(f));
    
    this.saveToHistory();
  }

  // Add right-click context menu for junctions
  private setupJunctionContextMenu(): void {
    this.map.on('contextmenu' as any, (event: any) => {
      event.preventDefault();
      
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature.get('type') === 'junction' ? feature : null;
      });
      
      if (feature) {
        this.showJunctionContextMenu(event.originalEvent, feature as Feature);
      }
    });
  }

  private showJunctionContextMenu(event: MouseEvent, junction: Feature): void {
    const contextMenu = document.createElement('div');
    contextMenu.className = 'context-menu';
    contextMenu.style.position = 'fixed';
    contextMenu.style.left = event.clientX + 'px';
    contextMenu.style.top = event.clientY + 'px';
    contextMenu.style.zIndex = '10000';
    contextMenu.innerHTML = `
      <div class="context-menu-item" data-action="edit">Edit Properties</div>
      <div class="context-menu-item" data-action="connect">Connect Road</div>
      <div class="context-menu-item" data-action="delete">Delete Junction</div>
    `;
    
    document.body.appendChild(contextMenu);
    
    // Event listeners
    contextMenu.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.getAttribute('data-action');
      
      switch (action) {
        case 'edit':
          this.editJunctionProperties(junction);
          break;
        case 'connect':
          this.selectedJunction = junction;
          this.connectRoadToJunction();
          break;
        case 'delete':
          this.deleteJunction(junction);
          break;
      }
      
      document.body.removeChild(contextMenu);
    });
    
    // Remove context menu when clicking elsewhere
    document.addEventListener('click', () => {
      if (document.body.contains(contextMenu)) {
        document.body.removeChild(contextMenu);
      }
    }, { once: true });
  }

  // Overlay controls
  overlayRoads(): void {
    this.showInstructions('Loading road overlay data...');
    
    // Create mock road overlay data
    setTimeout(() => {
      const center = this.map.getView().getCenter()!;
      const mockRoads = this.generateMockRoadOverlay(center);
      
      mockRoads.forEach(road => {
        this.roadSource.addFeature(road);
      });
      
      this.showInstructions(`Loaded ${mockRoads.length} road segments`);
      setTimeout(() => this.hideInstructions(), 2000);
    }, 1000);
  }

  private generateMockRoadOverlay(center: number[]): Feature[] {
    const roads: Feature[] = [];
    
    // Generate a grid of roads around the center
    for (let i = -3; i <= 3; i++) {
      for (let j = -3; j <= 3; j++) {
        const startX = center[0] + i * 500;
        const startY = center[1] + j * 500;
        const endX = center[0] + (i + 1) * 500;
        const endY = center[1] + (j + 1) * 500;
        
        // Horizontal road
        const hRoad = new Feature({
          geometry: new LineString([[startX, startY], [endX, startY]]),
          type: 'overlay_road',
          roadType: 'highway',
          lanes: 4
        });
        
        hRoad.setStyle(new Style({
          stroke: new Stroke({
            color: '#ff6b6b',
            width: 4,
            lineDash: [10, 5]
          })
        }));
        
        roads.push(hRoad);
        
        // Vertical road
        const vRoad = new Feature({
          geometry: new LineString([[startX, startY], [startX, endY]]),
          type: 'overlay_road',
          roadType: 'highway',
          lanes: 4
        });
        
        vRoad.setStyle(new Style({
          stroke: new Stroke({
            color: '#ff6b6b',
            width: 4,
            lineDash: [10, 5]
          })
        }));
        
        roads.push(vRoad);
      }
    }
    
    return roads;
  }

  clearOverlay(): void {
    // Clear overlay layers
    this.roadSource.clear();
    this.projectSource.clear();
  }

  applyLayerOffset(): void {
    const offsetX = parseFloat((document.getElementById('layerOffsetX') as HTMLInputElement)?.value || '0');
    const offsetY = parseFloat((document.getElementById('layerOffsetY') as HTMLInputElement)?.value || '0');
    
    this.layerOffsets['drawing'] = { x: offsetX, y: offsetY };
    this.updateLayerOffsets();
  }

  private updateLayerOffsets(): void {
    Object.keys(this.layerOffsets).forEach((layerName) => {
      const offset = this.layerOffsets[layerName];
      const layer = this.layerRegistry[layerName];
      if (!layer || !offset) return;
      layer.set('pixelOffsetX', offset.x);
      layer.set('pixelOffsetY', offset.y);
      layer.changed();
    });
    this.showInstructions('Layer pixel offset saved (thematic display)');
    setTimeout(() => this.hideInstructions(), 1500);
  }

  // Enhanced style methods for segments and nodes (resolution for dynamic symbol scaling)
  private getEnhancedDrawingStyle(feature: FeatureLike, resolution?: number): Style | Style[] {
    const featureType = feature.get('type');
    const symbol = feature.get('symbol');
    const text = feature.get('text');
    const thickness = feature.get('thickness') || 2;
    const scale = this.getResolutionScale(resolution);
    const size = (feature.get('symbolSize') || 1.0) * scale;
    const roadSpecs = feature.get('roadSpecs');
    const type = feature.get('type');
    const isUnderground = feature.get('underground') || feature.get('grade') === 'underground' || false;
    const rotationRad = ((Number(feature.get('rotationDeg')) || 0) * Math.PI) / 180;
    
    let style: Style[] = [];
    
    // Handle different feature types
    if (featureType === 'node') {
      style.push(this.createNodeStyle(feature));
    } else if (featureType === 'segment') {
      style.push(this.createSegmentStyle(feature));
    } else if (featureType === 'junction') {
      style.push(this.createJunctionStyle(feature));
    } else if (featureType === 'connection' || featureType === 'segment-connection') {
      style.push(this.createConnectionStyle(feature));
    } else if (featureType === 'roundabout') {
      style.push(this.createRoundaboutStyle(feature));
    } else if (featureType === 'label' || featureType === 'segment-label' || featureType === 'junction-label') {
      const color = feature.get('textColor') || '#000000';
      const fontSize = feature.get('textSize') || 12;
      const textStyle = this.createTextStyle(text, color, fontSize, rotationRad);
      if (textStyle) style.push(textStyle);
    } else if (feature.get('iconUrl')) {
      style.push(this.createIconUrlStyle(feature.get('iconUrl'), size, rotationRad));
    } else if (symbol) {
      const iconSize = Math.min(1.5, size || 1);
      style.push(this.createSymbolStyle(symbol, iconSize, rotationRad));
      const title = feature.get('title');
      if (title) {
        style.push(
          new Style({
            text: new Text({
              text: String(title),
              font: '700 13px Arial',
              offsetY: -28,
              fill: new Fill({ color: '#0f172a' }),
              stroke: new Stroke({ color: '#ffffff', width: 3 }),
              overflow: true,
            }),
          })
        );
      }
    } else if (text) {
      const color = feature.get('textColor') || '#000000';
      const fontSize = feature.get('textSize') || 14;
      const textStyle = this.createTextStyle(text, color, fontSize, rotationRad);
      if (textStyle) style.push(textStyle);
    } else if (roadSpecs) {
      style = this.createDrawingStyle(thickness, roadSpecs);
    } else if (type && type.endsWith('Duct')) {
      const ductStyle = this.createDuctStyle(type, feature.get('grade') || (isUnderground ? 'underground' : 'overground'));
      if (ductStyle) style.push(ductStyle);
    } else {
      const singleStyle = this.createDrawingStyle(thickness);
      if (Array.isArray(singleStyle)) {
        style.push(...singleStyle);
      } else {
        style.push(singleStyle);
      }
    }
    
    return style;
  }

  private createTextStyle(text: string, color: string, fontSize: number, rotation = 0): Style | null {
    if (!text) return null;
    
    return new Style({
      text: new TextStyle({
        text: text,
        rotation,
        font: `${fontSize}px Arial`,
        fill: new Fill({
          color: color
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  private createSymbolStyle(symbol: string, size: number, rotation = 0): Style {
    const fontPx = Math.max(28, Math.round(32 * (size || 1)));
    const hitRadius = Math.max(16, Math.round(fontPx * 0.42));
    return new Style({
      image: new CircleStyle({
        radius: hitRadius,
        fill: new Fill({ color: 'rgba(255,255,255,0.08)' }),
        stroke: new Stroke({ color: 'rgba(15,23,42,0.12)', width: 1 }),
      }),
      text: new Text({
        text: symbol || '•',
        rotation,
        font: `${fontPx}px sans-serif`,
        textBaseline: 'middle',
        textAlign: 'center',
        offsetY: 0,
        fill: new Fill({ color: '#0f172a' }),
        stroke: new Stroke({ color: '#ffffff', width: 4 }),
        overflow: true,
      }),
    });
  }

  /** Style for custom icon (SVG or PNG URL). */
  private createIconUrlStyle(url: string, size: number, rotation = 0): Style {
    return new Style({
      image: new Icon({
        src: url,
        scale: size * 0.15,
        rotation,
        anchor: [0.5, 0.5],
        crossOrigin: 'anonymous'
      })
    });
  }

  private createDuctStyle(type: string, gradeOrUnderground: string | boolean): Style {
    const grade = typeof gradeOrUnderground === 'boolean'
      ? (gradeOrUnderground ? 'underground' : 'overground')
      : gradeOrUnderground;
    const opts = strokeOptsForGrade(grade);
    return new Style({
      stroke: new Stroke({
        color: opts.color,
        width: opts.width,
        lineDash: opts.lineDash,
      }),
    });
  }

  private createNodeStyle(feature: FeatureLike): Style {
    const nodeId = feature.get('nodeId') || 'N1';
    const isSelected = feature.get('selected') || false;
    
    return new Style({
      image: new CircleStyle({
        radius: isSelected ? 12 : 8,
        fill: new Fill({
          color: isSelected ? '#ff6b6b' : '#4ecdc4'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      }),
      text: new TextStyle({
        text: nodeId,
        font: 'bold 10px Arial',
        fill: new Fill({
          color: '#ffffff'
        }),
        stroke: new Stroke({
          color: '#000000',
          width: 1
        }),
        offsetY: -20
      })
    });
  }

  private createJunctionStyle(feature: FeatureLike): Style {
    const junctionType = feature.get('junctionType') || '1';
    const isHighlighted = feature.get('highlighted') || false;
    const isSelected = feature.get('selected') || false;
    
    let color = '#e74c3c'; // Default red
    let radius = 10;
    
    // Different colors for different junction types
    switch (junctionType) {
      case '1':
        color = '#3498db'; // Blue for one-way
        break;
      case '2':
        color = '#2ecc71'; // Green for two-way
        break;
      case '3':
        color = '#f39c12'; // Orange for 3-way
        break;
      case '4':
        color = '#9b59b6'; // Purple for 4-way
        break;
      case 'roundabout':
        color = '#e67e22'; // Dark orange for roundabout
        radius = 15;
        break;
    }
    
    if (isHighlighted) {
      color = '#ff6b6b';
      radius = 15;
    } else if (isSelected) {
      color = '#ffa500';
      radius = 12;
    }
    
    return new Style({
      image: new CircleStyle({
        radius: radius,
        fill: new Fill({
          color: color
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  private createConnectionStyle(feature: FeatureLike): Style {
    const connectionType = feature.get('type');
    let color = '#34495e';
    let width = 2;
    let lineDash: number[] = [];
    
    if (connectionType === 'connection') {
      color = '#e74c3c';
      width = 3;
      lineDash = [5, 5];
    } else if (connectionType === 'segment-connection') {
      color = '#3498db';
      width = 2;
      lineDash = [3, 3];
    }
    
    return new Style({
      stroke: new Stroke({
        color: color,
        width: width,
        lineDash: lineDash
      })
    });
  }

  private createRoundaboutStyle(feature: FeatureLike): Style {
    const radius = feature.get('radius') || 20;
    const isHighlighted = feature.get('highlighted') || false;
    
    let color = '#e67e22';
    let width = 4;
    
    if (isHighlighted) {
      color = '#ff6b6b';
      width = 6;
    }
    
    return new Style({
      stroke: new Stroke({
        color: color,
        width: width,
        lineCap: 'round'
      }),
      fill: new Fill({
        color: 'rgba(230, 126, 34, 0.1)'
      })
    });
  }

  private createSegmentStyle(feature: FeatureLike): Style {
    const segmentId = feature.get('segmentId') || 'S1';
    const isSelected = feature.get('selected') || false;
    
    return new Style({
      stroke: new Stroke({
        color: isSelected ? '#ff6b6b' : '#e74c3c',
        width: isSelected ? 4 : 3,
        lineDash: isSelected ? [] : [5, 5]
      }),
      text: new TextStyle({
        text: segmentId,
        font: 'bold 10px Arial',
        fill: new Fill({
          color: '#000000'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  // Toolbar Methods
  private zoomFit(): void {
    const view = this.map.getView();
    const features = this.vectorSource.getFeatures();
    if (features.length > 0) {
      const extent = this.vectorSource.getExtent();
      view.fit(extent, { padding: [20, 20, 20, 20] });
    }
  }

  private zoomExtent(): void {
    const view = this.map.getView();
    const extent = this.map.getView().getProjection().getExtent();
    view.fit(extent, { padding: [20, 20, 20, 20] });
  }

  private enablePan(): void {
    this.removeInteractions();
    // Pan is enabled by default in OpenLayers
  }

  private enableDistanceMeasurement(): void {
    this.removeInteractions();
    this.measureInteraction = new Draw({
      source: this.vectorSource,
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    });

    this.measureInteraction.on('drawend', (event: any) => {
      const feature = event.feature;
      const length = this.calculateLength(feature);
      const meters = parseFloat(length.meters) || 0;
      this.showMeasurementResult(`Distance: ${this.formatLengthM(meters)}`);
    });

    this.map.addInteraction(this.measureInteraction);
  }

  private enableAreaMeasurement(): void {
    this.removeInteractions();
    this.measureInteraction = new Draw({
      source: this.vectorSource,
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 0, 0, 0.2)'
        }),
        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    });

    this.measureInteraction.on('drawend', (event: any) => {
      const feature = event.feature;
      const area = this.calculateArea(feature);
      const m2 = parseFloat(area.squareMeters) || 0;
      this.showMeasurementResult(`Area: ${this.formatAreaM2(m2)}`);
    });

    this.map.addInteraction(this.measureInteraction);
  }

  private enableBearingMeasurement(): void {
    this.removeInteractions();
    this.measureInteraction = new Draw({
      source: this.vectorSource,
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    });

    this.measureInteraction.on('drawend', (event: any) => {
      const feature = event.feature;
      const bearing = this.calculateBearing(feature);
      this.showMeasurementResult(`Bearing: ${bearing}°`);
    });

    this.map.addInteraction(this.measureInteraction);
  }

  private calculateArea(feature: Feature): { squareMeters: string; squareKm: string } {
    const geom = feature.getGeometry();
    if (!geom) {
      return { squareMeters: '0.00', squareKm: '0.00' };
    }
    
    let area = 0;
    
    try {
      if (geom.getType() === 'Polygon' || geom.getType() === 'MultiPolygon') {
        // Use OpenLayers geodesic calculation (most accurate)
        area = getGeodesicArea(geom, { projection: 'EPSG:3857' });
        
        // Fallback to turf.js if available and OL calculation fails
        if (isNaN(area) || area === 0) {
          const geojsonString = this.format.writeGeometry(geom, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          if (typeof turf !== 'undefined' && geojsonString) {
            try {
              const geojsonObj = JSON.parse(geojsonString);
              area = turf.area(geojsonObj);
            } catch (e) {
              console.warn('Turf.js area calculation failed:', e);
            }
          }
        }
      } else if (geom.getType() === 'Circle') {
        // For circles, calculate area
        const radius = (geom as Circle).getRadius();
        // Convert from Web Mercator units to meters (approximate at center latitude)
        const center = (geom as Circle).getCenter();
        const centerLonLat = toLonLat(center);
        const latFactor = Math.cos(centerLonLat[1] * Math.PI / 180);
        const radiusMeters = radius * latFactor;
        area = Math.PI * radiusMeters * radiusMeters;
      }
    } catch (error) {
      console.error('Error calculating area:', error);
    }
    
    // Ensure positive area (some calculations might return negative for counter-clockwise polygons)
    area = Math.abs(area);
    
    const squareKm = area / 1000000;
    return {
      squareMeters: area.toFixed(2),
      squareKm: squareKm.toFixed(6)
    };
  }

  private calculateBearing(feature: Feature): string {
    const geom = feature.getGeometry() as LineString;
    const coordinates = geom.getCoordinates();
    if (coordinates.length >= 2) {
      const start = coordinates[0];
      const end = coordinates[coordinates.length - 1];
      const bearing = Math.atan2(end[0] - start[0], end[1] - start[1]) * 180 / Math.PI;
      return ((bearing + 360) % 360).toFixed(1);
    }
    return '0';
  }

  private showMeasurementResult(result: string): void {
    const measurementDisplay = document.getElementById('measurementDisplay');
    if (measurementDisplay) {
      measurementDisplay.style.display = 'block';
      const measurementResults = document.getElementById('measurementResults');
      if (measurementResults) {
        measurementResults.innerHTML = result;
      }
    }
  }

  private undo(): void {
    if (this.historyStep > 0) {
      this.historyStep--;
      this.restoreFromHistory();
    }
  }

  private redo(): void {
    if (this.historyStep < this.drawingHistory.length - 1) {
      this.historyStep++;
      this.restoreFromHistory();
    }
  }

  /** Snapshots hold clones so later in-place edits (move, rotate, vertex edit) can be undone. */
  private snapshotFeatures(features: Feature[]): Feature[] {
    return features.map((f) => {
      const c = f.clone();
      c.setId(f.getId());
      return c;
    });
  }

  private restoreFromHistory(): void {
    if (this.drawingHistory[this.historyStep]) {
      const state = this.drawingHistory[this.historyStep];
      this.selectInteraction?.getFeatures().clear();
      this.closeRoadEditDetail();
      this.vectorSource.clear();
      this.vectorSource.addFeatures(this.snapshotFeatures(state.vectorSource));
      this.roadSource.clear();
      this.roadSource.addFeatures(this.snapshotFeatures(state.roadSource));
    }
  }

  private printMap(): void {
    this.openDialog('printDialog');
  }

  /**
   * Execute print with configured options
   */
  executePrint(): void {
    this.showInstructions('Preparing map for print...');
    
    // Get print options from dialog
    const paperSize = (document.getElementById('paperSize') as HTMLSelectElement)?.value || 'a4';
    const orientation = (document.getElementById('orientation') as HTMLSelectElement)?.value || 'landscape';
    const printTitle = (document.getElementById('printTitle') as HTMLInputElement)?.value || 'GIS Map';
    const printScale = (document.getElementById('printScale') as HTMLInputElement)?.value || '';
    
    // Get include options
    const includeLegend = (document.getElementById('includeLegend') as HTMLInputElement)?.checked ?? true;
    const includeScale = (document.getElementById('includeScale') as HTMLInputElement)?.checked ?? true;
    const includeNorth = (document.getElementById('includeNorth') as HTMLInputElement)?.checked ?? true;
    const includeDate = (document.getElementById('includeDate') as HTMLInputElement)?.checked ?? true;
    
    // Paper dimensions (in mm)
    const paperDimensions: { [key: string]: { width: number; height: number } } = {
      'a4': { width: 297, height: 210 },
      'a3': { width: 420, height: 297 },
      'letter': { width: 279, height: 216 },
      'legal': { width: 356, height: 216 }
    };
    
    const paper = paperDimensions[paperSize] || paperDimensions['a4'];
    const isLandscape = orientation === 'landscape';
    const pageWidth = isLandscape ? paper.width : paper.height;
    const pageHeight = isLandscape ? paper.height : paper.width;
    
    // Convert mm to pixels (assuming 96 DPI for screen)
    const dpi = 150; // Higher DPI for better print quality
    const mmToPixels = (mm: number) => Math.round(mm * dpi / 25.4);
    const mapWidth = mmToPixels(pageWidth - 20); // 10mm margin on each side
    const mapHeight = mmToPixels(pageHeight - 50); // Space for header/footer
    
    // Render map to canvas
    this.map.once('rendercomplete', () => {
      const mapCanvas = document.createElement('canvas');
      const mapContext = mapCanvas.getContext('2d')!;
      
      mapCanvas.width = mapWidth;
      mapCanvas.height = mapHeight;
      
      // Draw white background
      mapContext.fillStyle = 'white';
      mapContext.fillRect(0, 0, mapWidth, mapHeight);
      
      // Get all canvas elements from the map
      const mapElement = this.mapElement.nativeElement;
      const canvases = mapElement.querySelectorAll('canvas');
      
      // Calculate scaling to fit map in print area
      const sourceWidth = this.map.getSize()![0];
      const sourceHeight = this.map.getSize()![1];
      const scale = Math.min(mapWidth / sourceWidth, mapHeight / sourceHeight);
      
      // Center the map
      const offsetX = (mapWidth - sourceWidth * scale) / 2;
      const offsetY = (mapHeight - sourceHeight * scale) / 2;
      
      canvases.forEach((canvas: HTMLCanvasElement) => {
        if (canvas.width > 0) {
          mapContext.drawImage(canvas, offsetX, offsetY, sourceWidth * scale, sourceHeight * scale);
        }
      });
      
      // Create print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        this.showInstructions('Failed to open print window. Check popup blocker.');
        return;
      }
      
      const currentDate = new Date().toLocaleDateString();
      const currentView = this.map.getView();
      const center = toLonLat(currentView.getCenter()!);
      const zoom = currentView.getZoom();
      
      // Generate legend HTML if needed
      let legendHtml = '';
      if (includeLegend) {
        legendHtml = `
          <div class="legend">
            <h4>Legend</h4>
            <div class="legend-item"><span class="legend-color" style="background: #3498db;"></span> Drawing Features</div>
            <div class="legend-item"><span class="legend-color" style="background: #e74c3c;"></span> Road Network</div>
            <div class="legend-item"><span class="legend-color" style="background: #27ae60;"></span> Project Areas</div>
            <div class="legend-item"><span class="legend-color" style="background: #9b59b6;"></span> Imported Points</div>
          </div>
        `;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${printTitle} - Print</title>
            <style>
              @page {
                size: ${pageWidth}mm ${pageHeight}mm;
                margin: 10mm;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: Arial, sans-serif;
                color: #333;
              }
              .print-container {
                width: 100%;
                max-width: ${pageWidth - 20}mm;
                margin: 0 auto;
              }
              .header {
                text-align: center;
                padding: 10px 0;
                border-bottom: 2px solid #2c3e50;
                margin-bottom: 10px;
              }
              .header h1 {
                margin: 0;
                font-size: 24px;
                color: #2c3e50;
              }
              .header .subtitle {
                font-size: 12px;
                color: #7f8c8d;
                margin-top: 5px;
              }
              .map-container {
                text-align: center;
                position: relative;
              }
              .map-container img {
                max-width: 100%;
                height: auto;
                border: 1px solid #ccc;
              }
              .footer {
                display: flex;
                justify-content: space-between;
                padding: 10px 0;
                border-top: 1px solid #ccc;
                margin-top: 10px;
                font-size: 10px;
                color: #7f8c8d;
              }
              .north-arrow {
                position: absolute;
                top: 10px;
                right: 10px;
                font-size: 24px;
                background: rgba(255,255,255,0.9);
                padding: 5px 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
              }
              .scale-bar {
                position: absolute;
                bottom: 10px;
                left: 10px;
                background: rgba(255,255,255,0.9);
                padding: 5px 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
                font-size: 10px;
              }
              .legend {
                position: absolute;
                bottom: 10px;
                right: 10px;
                background: rgba(255,255,255,0.95);
                padding: 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
                font-size: 10px;
              }
              .legend h4 {
                margin: 0 0 8px 0;
                font-size: 12px;
              }
              .legend-item {
                display: flex;
                align-items: center;
                margin: 4px 0;
              }
              .legend-color {
                width: 20px;
                height: 10px;
                margin-right: 8px;
                border: 1px solid #999;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              <div class="header">
                <h1>${printTitle}</h1>
                <div class="subtitle">
                  ${printScale ? `Scale: ${printScale} | ` : ''}
                  Center: ${center[1].toFixed(4)}°N, ${center[0].toFixed(4)}°E | Zoom: ${zoom?.toFixed(1)}
                </div>
              </div>
              <div class="map-container">
                <img src="${mapCanvas.toDataURL('image/png')}" alt="Map">
                ${includeNorth ? '<div class="north-arrow">⬆️ N</div>' : ''}
                ${includeScale ? '<div class="scale-bar">Scale: 1:' + Math.round(this.getMapScale()) + '</div>' : ''}
                ${legendHtml}
              </div>
              <div class="footer">
                ${includeDate ? `<span>Printed: ${currentDate}</span>` : '<span></span>'}
                <span>Generated by GIS Map Editor</span>
                <span>Project: ${this.projectData?.project_name || 'N/A'}</span>
              </div>
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      this.closeDialog('printDialog');
      this.showInstructions('Print dialog opened');
    });
    
    // Force a render to capture the current state
    this.map.renderSync();
  }

  /**
   * Preview print layout
   */
  previewPrint(): void {
    this.executePrint();
  }

  /**
   * Calculate current map scale
   */
  private getMapScale(): number {
    const view = this.map.getView();
    const resolution = view.getResolution();
    if (!resolution) return 10000;
    
    // Convert resolution (meters per pixel) to scale
    // Assuming 0.28mm pixel size (standard screen)
    const metersPerPixel = resolution;
    const pixelsPerMeter = 1 / 0.00028; // 0.28mm = 0.00028m
    
    return Math.round(metersPerPixel * pixelsPerMeter);
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      this.mapElement.nativeElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  private toggleMiniMap(): void {
    if (!this.lrsOverviewMap) {
      this.lrsOverviewMap = this.createLrsOverviewMap();
      this.map?.addControl(this.lrsOverviewMap);
      return;
    }
    this.lrsOverviewMap.setCollapsed(!this.lrsOverviewMap.getCollapsed());
  }

  private createLrsOverviewMap(): OverviewMap {
    return new OverviewMap({
      collapsed: false,
      collapsible: true,
      className: 'ol-overviewmap lrs-overviewmap',
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({
          source: this.overviewRouteSource,
          style: new Style({
            stroke: new Stroke({ color: '#dc2626', width: 2.5 }),
          }),
        }),
      ],
    });
  }

  private syncOverviewCenterline(features: Feature[]): void {
    this.overviewRouteSource.clear();
    this.overviewRouteSource.addFeatures(features.map((f) => f.clone()));
  }

  private getCapabilityStatusIcon(status: GISCapabilityStatus): string {
    if (status === 'available') return '✓';
    if (status === 'partial') return '~';
    return '○';
  }

  /**
   * Returns a compact summary of capability implementation state.
   * Useful for diagnostics, planning, and operator handover notes.
   */
  getGISCapabilitySummary(): { available: number; partial: number; planned: number; total: number } {
    const allItems = this.gisCapabilitySections.flatMap(section => section.items);
    const summary = { available: 0, partial: 0, planned: 0, total: allItems.length };
    allItems.forEach(item => {
      if (item.status === 'available') summary.available += 1;
      else if (item.status === 'partial') summary.partial += 1;
      else summary.planned += 1;
    });
    return summary;
  }

  /**
   * Human-readable capability matrix for Help dialog.
   */
  private buildGISCapabilityHelpText(): string {
    const summary = this.getGISCapabilitySummary();
    const sections = this.gisCapabilitySections
      .map(section => {
        const lines = section.items.map(item => {
          const icon = this.getCapabilityStatusIcon(item.status);
          const note = item.notes ? ` — ${item.notes}` : '';
          return `  ${icon} ${item.label}${note}`;
        });
        return `${section.title}\n${lines.join('\n')}`;
      })
      .join('\n\n');

    return `GIS Map Editor - Capability Matrix

Status Legend:
✓ Available   ~ Partial   ○ Planned

Coverage Summary:
Available: ${summary.available}
Partial: ${summary.partial}
Planned: ${summary.planned}
Total: ${summary.total}

${sections}

Core Usage Notes:
• Use Layer Manager to organize, style, lock, and filter layers.
• Use Draw/Modify/Snap tools for geometry editing with precision.
• Use Analysis tools for buffer, proximity, density, network, and elevation workflows.
• Use Import/Export dialogs for GeoJSON/KML/GPX and backend-connected persistence.
• Use offline mode when internet is unavailable; sync once connected.`;
  }

  private showHelp(): void {
    alert(this.buildGISCapabilityHelpText());
  }

  private checkForProjectId(): void {
    this.projectContextSub?.unsubscribe();
    this.projectContextSub = combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
      ([params, query]) => {
        // `project_id` is the canonical geospatial-shell parameter. Keep the older
        // `id` spelling so saved links and /gis-project/:id continue to work.
        const nextProjectId =
          params.get('id') ||
          query.get('project_id') ||
          query.get('projectId') ||
          query.get('id') ||
          null;

        if (nextProjectId === this.projectId) return;

        if (this.projectId && this.gisPending.size) void this.flushGisChanges().catch(() => undefined);
        this.projectId = nextProjectId;
        this.projectData = null;
        this.lastGisMetricSummary = null;
        this.showProjectDetails = false;
        if (this.projectId) this.fetchProjectDetails();
      }
    );
  }


  centerMapOnProjectLocation(location: any): void {
    if (!this.map || !location.lat || !location.long) return;
    
    try {
      const coordinate = fromLonLat([location.long, location.lat]);
      this.map.getView().setCenter(coordinate);
      this.map.getView().setZoom(17); // Set appropriate zoom level
      
      // Add a marker for the project location
      this.addProjectLocationMarker(location);
    } catch (error) {
      console.error('Error centering map on project location:', error);
    }
  }

  onGisOpsMapAction(action: GisOpsMapAction): void {
    if (action.type === 'project') {
      const loc = this.projectData?.location;
      if (loc?.lat && loc?.long) {
        this.centerMapOnProjectLocation(loc);
        this.toggleLayerEnhanced('project', true);
        return;
      }
      this.showInstructions('This project has no saved map location.');
      return;
    }
    if (action.type === 'survey-lrs') {
      this.focusSurveyLrsOnMap(action.lrs);
      return;
    }
    this.plotGisOpsPoints(action.layer, action.points);
  }

  private plotProjectAttendanceOnMap(): void {
    if (!this.projectId) {
      this.showInstructions('Open a project to plot attendance.');
      return;
    }
    this.gisProjectOps.load(this.projectId).subscribe({
      next: (snap) => {
        const points = this.gisProjectOps.pointsFromAttendance(snap.attendance);
        if (!points.length) {
          this.onGisOpsMapAction({ type: 'project' });
          this.showInstructions('No GPS punches to plot. Showing the project location.');
          return;
        }
        this.plotGisOpsPoints('biometric', points);
      },
      error: () => this.showInstructions('Could not load attendance for the map.'),
    });
  }

  private plotGisOpsPoints(layerId: 'biometric' | 'gps' | 'project', points: GisOpsMapPoint[]): void {
    const source = layerId === 'biometric'
      ? this.biometricSource
      : layerId === 'gps'
        ? this.gpsSource
        : this.projectSource;
    if (!source || !this.map) return;
    this.clearGisOpsFeatures(source);
    const features = points.map((pt) => this.makeGisOpsFeature(pt));
    source.addFeatures(features);
    this.toggleLayerEnhanced(layerId, true);
    this.updateLayerStatistics(layerId);
    this.fitMapToGisOpsFeatures(features);
    this.showInstructions(`Plotted ${features.length} ${layerId === 'biometric' ? 'attendance' : layerId === 'gps' ? 'survey' : 'project'} point(s).`);
  }

  private makeGisOpsFeature(pt: GisOpsMapPoint): Feature {
    const feature = new Feature({
      geometry: new Point(fromLonLat([pt.lon, pt.lat])),
      name: pt.label,
      type: 'point',
      gisOps: true,
      opsKind: pt.kind,
      status: pt.status,
    });
    const fill = pt.kind === 'attendance'
      ? (String(pt.status || '').toLowerCase().includes('absent')
        ? '#dc2626'
        : String(pt.status || '').toLowerCase().includes('late')
          ? '#ea580c'
          : '#16a34a')
      : pt.kind === 'survey'
        ? '#2563eb'
        : pt.kind === 'machine'
          ? '#7c3aed'
          : '#0f766e';
    feature.setStyle(new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color: fill }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      text: new TextStyle({
        text: pt.label,
        offsetY: -12,
        font: '600 10px sans-serif',
        fill: new Fill({ color: '#1e293b' }),
        stroke: new Stroke({ color: '#ffffff', width: 3 }),
      }),
    }));
    return feature;
  }

  private clearGisOpsFeatures(source: VectorSource): void {
    source.getFeatures()
      .filter((f) => f.get('gisOps') === true)
      .forEach((f) => source.removeFeature(f));
  }

  private fitMapToGisOpsFeatures(features: Feature[]): void {
    if (!this.map || !features.length) return;
    if (features.length === 1) {
      const geom = features[0].getGeometry();
      const coord = geom && 'getCoordinates' in geom ? (geom as Point).getCoordinates() : null;
      if (coord) {
        this.map.getView().animate({ center: coord, zoom: Math.max(this.map.getView().getZoom() || 14, 15), duration: 400 });
      }
      return;
    }
    const extent = features
      .map((f) => f.getGeometry()?.getExtent())
      .filter((e): e is number[] => Array.isArray(e) && e.length >= 4)
      .reduce((acc, e) => [
        Math.min(acc[0], e[0]),
        Math.min(acc[1], e[1]),
        Math.max(acc[2], e[2]),
        Math.max(acc[3], e[3]),
      ], [Infinity, Infinity, -Infinity, -Infinity]);
    if (!Number.isFinite(extent[0])) return;
    this.map.getView().fit(extent as Extent, { padding: [60, 60, 60, 60], maxZoom: 17, duration: 400 });
  }

  private focusSurveyLrsOnMap(lrs: GisOpsSurveyLrs): void {
    if (this.enableLrsTab) this.switchSidebarTab('lrs');
    this.selectedLrsRouteId = lrs.routeId;
    this.lrsDashboard.setRouteId(lrs.routeId);
    this.loadLrsRouteToMap();
    const km = lrs.fromKm ?? lrs.toKm;
    if (km != null) {
      this.panToLrsStation({
        measure_m: km * 1000,
        label: lrs.chainage || `${km} km`,
      });
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        route_id: lrs.routeId,
        from_km: lrs.fromKm,
        to_km: lrs.toKm,
        tab: 'lrs',
      },
      queryParamsHandling: 'merge',
    });
  }

  private addProjectLocationMarker(location: any): void {
    if (!this.map) return;
    
    try {
      const coordinate = fromLonLat([location.long, location.lat]);
      
      // Create a feature for the project location
      const marker = new Feature({
        geometry: new Point(coordinate),
        name: this.projectData?.project_name || 'Project Location',
        type: 'project_location'
      });

      // Style the marker
      const markerStyle = new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#ffd700' }),
          stroke: new Stroke({ color: '#ffffff', width: 3 })
        }),
        text: new TextStyle({
          text: '📍',
          font: '16px Arial',
          offsetY: -20,
          fill: new Fill({ color: '#ffffff' }),
          stroke: new Stroke({ color: '#000000', width: 2 })
        })
      });

      marker.setStyle(markerStyle);

      // Add to project layer
      if (this.projectSource) {
        this.gisTransientFeatures.add(marker);
        this.projectSource.addFeature(marker);
      }
    } catch (error) {
      console.error('Error adding project location marker:', error);
    }
  }

  toggleProjectDetails(): void {
    this.showProjectDetails = !this.showProjectDetails;
  }

  closeProjectDetails(): void {
    this.showProjectDetails = false;
  }

  loadProjectDetails(projectId: string): void {
    this.projectId = projectId;
    this.fetchProjectDetails();
  }

  /**
   * Initialize backend integration
   */
  private initializeBackendIntegration(): void {
    // Initialize auto-save if enabled
    if (this.defaultSettings.autoSaveInterval > 0) {
      this.initializeAutoSave();
    }

    // Load available GIS layers from backend
    this.getAvailableLayers();

    console.log('Backend integration initialized');
  }

  /**
   * Enhanced fetch project details with GIS data loading
   */
  private fetchProjectDetails(): void {
    if (!this.projectId) return;

    this.isLoadingProject = true;
    this.showProjectDetails = !true;

    this.apiService.getProjectId(this.projectId).subscribe({
      next: (res: any) => {
        console.log('Project data:', res);
        this.projectData = res;
        this.isLoadingProject = false;
        // Legacy projects may have an array while new project_common records hold
        // one location object. Normalise without discarding an object location.
        res.location = Array.isArray(res.location) ? res.location[0] : res.location;
        // Center map on project location if available
        if (res.location && res.location.lat && res.location.long) {
          this.centerMapOnProjectLocation(res.location);
        }

        this.hydrateRoadSpecsFromProject(res);

        // Load GIS layers from backend after project is loaded
        this.loadAllLayersFromBackend();
        if (this.activeSidebarTab === 'survey') this.loadFieldSurveyGeometry();
      },
      error: (err) => {
        console.error('Error fetching project details:', err);
        this.isLoadingProject = false;
        this.showProjectDetails = false;
      },
      complete: () => console.log('Project fetch complete'),
    });
  }

  /** Apply saved project road settings to new GIS road drawings. */
  private hydrateRoadSpecsFromProject(project: any): void {
    const width = Number(project?.lane_width_adjustment);
    const lanes = Number(project?.lane_configuration);
    const type = String(project?.road_type || '').trim();

    if (Number.isFinite(width) && width > 0) this.currentRoadSpecs.width = width;
    if (Number.isFinite(lanes) && lanes > 0) this.currentRoadSpecs.lanes = Math.round(lanes);
    if (type) this.currentRoadSpecs.type = type;

    // The editor still uses a few legacy DOM controls. Reflect the loaded project
    // values there as well, otherwise their default values overwrite this state on
    // the first draw action.
    const setValue = (id: string, value: string) => {
      const control = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
      if (control) control.value = value;
    };
    if (Number.isFinite(width) && width > 0) setValue('roadWidth', String(width));
    if (Number.isFinite(lanes) && lanes > 0) setValue('roadLanes', String(Math.round(lanes)));
    if (type) setValue('roadType', type);
  }

  /**
   * Collect only road centerlines, never lane guides, labels, or utility lines.
   * This prevents decorated corridors from inflating a project's road length.
   */
  private isDesignedRoadAlignment(feature: Feature, source: VectorSource): boolean {
    const geometry = feature.getGeometry();
    if (!geometry || (geometry.getType() !== 'LineString' && geometry.getType() !== 'MultiLineString')) {
      return false;
    }
    if (feature.get('lrsReadonly') || feature.get('demoCorridorOverlay')) return false;

    const type = String(feature.get('type') || '').toLowerCase();
    const centerlineTypes = new Set([
      'dummy_road_corridor',
      'milestone_corridor',
      'multi_lane_road',
      'road_corridor',
      'auto_generated_road',
      'road',
    ]);
    if (centerlineTypes.has(type)) return true;

    // Draw Road writes these attributes even if imported geometry uses a custom type.
    return source === this.roadSource && !!(
      feature.get('roadSpecs') ||
      feature.get('roadWidthM') != null ||
      feature.get('roadType')
    );
  }

  private getDesignedRoadAlignments(): Feature[] {
    const result: Feature[] = [];
    const seen = new Set<Feature>();
    [this.roadSource, this.vectorSource].forEach((source) => {
      source?.getFeatures().forEach((feature) => {
        if (!seen.has(feature) && this.isDesignedRoadAlignment(feature, source)) {
          seen.add(feature);
          result.push(feature);
        }
      });
    });
    return result;
  }

  /** Use the active LRS centerline only when the project has no designed alignment. */
  private getActiveLrsCenterline(): Feature[] {
    if (!this.demoActiveRouteCode) return [];
    const layer = this.layerRegistry[`LRS ${this.demoActiveRouteCode} centerline`];
    const source = layer?.getSource();
    if (!(source instanceof VectorSource)) return [];
    return source.getFeatures().filter((feature) => {
      const geometry = feature.getGeometry();
      return geometry?.getType() === 'LineString' || geometry?.getType() === 'MultiLineString';
    });
  }

  private getProjectAreaFeatures(): Feature[] {
    const result: Feature[] = [];
    const seen = new Set<Feature>();
    [this.vectorSource, this.projectSource].forEach((source) => {
      source?.getFeatures().forEach((feature) => {
        const geometry = feature.getGeometry();
        const type = geometry?.getType();
        if (
          !seen.has(feature) &&
          !feature.get('lrsReadonly') &&
          (type === 'Polygon' || type === 'MultiPolygon' || type === 'Circle')
        ) {
          seen.add(feature);
          result.push(feature);
        }
      });
    });
    return result;
  }

  private roundProjectMetric(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision);
    return Math.round(value * multiplier) / multiplier;
  }

  /**
   * Derive project-ready road and area metrics from the map. This is public so the
   * calculation can be verified independently of the API write.
   */
  getRoadProjectMetricSummary(): GisRoadProjectMetricSummary {
    const designedAlignments = this.getDesignedRoadAlignments();
    const alignments = designedAlignments.length ? designedAlignments : this.getActiveLrsCenterline();
    const source: GisRoadProjectMetricSummary['source'] = designedAlignments.length
      ? 'designed-alignment'
      : alignments.length
        ? 'lrs-centerline'
        : 'none';
    const areaFeatures = this.getProjectAreaFeatures();

    let totalLengthM = 0;
    let weightedWidth = 0;
    let weightedLanes = 0;
    alignments.forEach((feature) => {
      const lengthM = Number(this.calculateLength(feature).meters) || 0;
      if (!(lengthM > 0)) return;
      totalLengthM += lengthM;
      const specs = feature.get('roadSpecs') || {};
      const width = Number(feature.get('roadWidthM') ?? specs.width ?? this.currentRoadSpecs.width);
      const lanes = Number(feature.get('lanes') ?? specs.lanes ?? this.currentRoadSpecs.lanes);
      if (Number.isFinite(width) && width > 0) weightedWidth += width * lengthM;
      if (Number.isFinite(lanes) && lanes > 0) weightedLanes += lanes * lengthM;
    });

    let totalAreaM2 = 0;
    areaFeatures.forEach((feature) => {
      totalAreaM2 += Number(this.calculateArea(feature).squareMeters) || 0;
    });

    const metrics: GisRoadProjectMetrics = {};
    if (totalLengthM > 0) {
      metrics.road_length = this.roundProjectMetric(totalLengthM / 1000, 3);
      metrics.lane_width_adjustment = this.roundProjectMetric(
        weightedWidth > 0 ? weightedWidth / totalLengthM : this.currentRoadSpecs.width,
        2
      );
      metrics.lane_configuration = Math.max(
        1,
        Math.round(weightedLanes > 0 ? weightedLanes / totalLengthM : this.currentRoadSpecs.lanes)
      );
    }
    if (totalAreaM2 > 0) metrics.size = this.roundProjectMetric(totalAreaM2, 2);

    return {
      metrics,
      roadFeatureCount: alignments.length,
      areaFeatureCount: areaFeatures.length,
      source,
    };
  }

  /** Persist the calculated road length, lane parameters, and project footprint. */
  pushGisMetricsToProject(): void {
    if (!this.projectId) {
      this.showInstructions('Open a road project before updating project quantities.');
      return;
    }
    if (this.gisMetricsSaving) return;

    const summary = this.getRoadProjectMetricSummary();
    const metrics = summary.metrics;
    if (!Object.keys(metrics).length) {
      this.showInstructions('Draw a road alignment or project area, or load an LRS route first.');
      return;
    }

    this.gisMetricsSaving = true;
    this.lastGisMetricSummary = summary;
    this.apiService.pushGisMetricsToProject(this.projectId, metrics).subscribe({
      next: (res: any) => {
        this.gisMetricsSaving = false;
        if (this.projectData) Object.assign(this.projectData, metrics);
        const length = metrics.road_length != null ? `${metrics.road_length} km` : null;
        const area = metrics.size != null ? `${metrics.size.toLocaleString()} m²` : null;
        const values = [length, area].filter(Boolean).join(' · ');
        const autoBoq = res?.auto_boq?.boq_code;
        this.showInstructions(
          `Project quantities updated${values ? `: ${values}` : ''}${autoBoq ? ` · BOQ ${autoBoq} generated` : ''}`
        );
      },
      error: (err) => {
        this.gisMetricsSaving = false;
        this.showInstructions(err?.error?.error || err?.error?.message || 'Could not update project quantities.');
      },
    });
  }

  // ==================== BACKEND INTEGRATION METHODS ====================

  // Map features live in `gis_project_features` (PostGIS, EPSG:4326). Edits are tracked
  // per feature from vector-source events and synced in debounced batches.

  private static readonly GIS_SYNC_DEBOUNCE_MS = 2000;
  private static readonly GIS_SYNC_CHUNK = 2000;

  private layerIdOf(layer: VectorLayer): string | null {
    for (const id of Object.keys(this.layerRegistry)) {
      if (this.layerRegistry[id] === layer) return id;
    }
    return null;
  }

  private isPersistableLayer(layerId: string): boolean {
    const layer = this.layerRegistry[layerId];
    if (!layer || layer.get('lrsReadonly') || layer.get('gisTransient')) return false;
    if (/^(LRS |wfs_|dem_)/.test(layerId)) return false;
    const source = layer.getSource();
    return source instanceof VectorSource && !(source instanceof Cluster);
  }

  private isPersistableFeature(feature: Feature): boolean {
    if (this.gisTransientFeatures.has(feature)) return false;
    return !this.fieldSurveyTypes.has(String(feature.get('type') || ''));
  }

  private withGisTrackingSuspended<T>(fn: () => T): T {
    this.gisTrackingSuspended++;
    try {
      return fn();
    } finally {
      this.gisTrackingSuspended--;
    }
  }

  private trackLayerForStore(layer: VectorLayer): void {
    const source = layer.getSource();
    if (!(source instanceof VectorSource) || source instanceof Cluster || this.gisTrackedSources.has(source)) return;
    this.gisTrackedSources.add(source);
    const onChange = (feature: Feature | undefined, op: 'upsert' | 'delete') => {
      if (!feature || this.gisTrackingSuspended > 0 || !this.projectId) return;
      const layerId = this.layerIdOf(layer);
      if (!layerId || !this.isPersistableLayer(layerId) || !this.isPersistableFeature(feature)) return;
      this.markGisDirty(layerId, feature, op);
      this.scheduleGisFlush();
    };
    source.on('addfeature', (e) => onChange(e.feature as Feature | undefined, 'upsert'));
    source.on('changefeature', (e) => onChange(e.feature as Feature | undefined, 'upsert'));
    source.on('removefeature', (e) => onChange(e.feature as Feature | undefined, 'delete'));
  }

  private markGisDirty(layerId: string, feature: Feature, op: 'upsert' | 'delete'): void {
    if (op === 'delete') {
      const uid = this.gisStore.uidOf(feature);
      if (uid) this.gisPending.set(uid, { layer: layerId, feature: null });
      return;
    }
    const uid = this.withGisTrackingSuspended(() => this.gisStore.ensureUid(feature));
    this.gisPending.set(uid, { layer: layerId, feature });
  }

  /** Queues every persistable feature of a layer (unchanged ones are skipped when sending). */
  private markLayerDirty(layerId: string): void {
    const source = this.layerRegistry[layerId]?.getSource();
    if (!(source instanceof VectorSource)) return;
    source.getFeatures().forEach((f) => {
      if (!this.isPersistableFeature(f)) return;
      const uid = this.withGisTrackingSuspended(() => this.gisStore.ensureUid(f));
      const pending = this.gisPending.get(uid);
      if (!pending || pending.feature) this.gisPending.set(uid, { layer: layerId, feature: f });
    });
  }

  private scheduleGisFlush(): void {
    if (this.defaultSettings.autoSaveInterval <= 0) return;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      this.flushGisChanges().catch((err) =>
        this.showInstructions(this.gisErrorMessage(err, 'Auto-save failed; changes are kept and will retry'))
      );
    }, GisavieditorComponent.GIS_SYNC_DEBOUNCE_MS);
  }

  private gisErrorMessage(err: any, fallback: string): string {
    const msg = err?.error?.error || err?.error?.message || err?.message;
    return msg ? `${fallback}: ${msg}` : fallback;
  }

  /**
   * Sends all pending changes for the current project, one request per chunk, in order.
   * Changes that fail go back into the pending set (unless edited again meanwhile).
   * Resolves with the number of changes written.
   */
  private flushGisChanges(): Promise<number> {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    const projectId = this.projectId;
    const batch = this.gisPending;
    if (!projectId || !batch.size) return this.gisSyncQueue.then(() => 0, () => 0);
    if (this.offlineMode || !navigator.onLine) {
      this.persistLayersToOfflineStorage();
      return Promise.resolve(0);
    }
    this.gisPending = new globalThis.Map();

    const restore = (entries: [string, { layer: string; feature: Feature | null }][]) => {
      entries.forEach(([uid, entry]) => {
        if (!this.gisPending.has(uid)) this.gisPending.set(uid, entry);
      });
    };

    const task = async (): Promise<number> => {
      const entries = [...batch.entries()];
      let written = 0;
      for (let i = 0; i < entries.length; i += GisavieditorComponent.GIS_SYNC_CHUNK) {
        const chunk = entries.slice(i, i + GisavieditorComponent.GIS_SYNC_CHUNK);
        const upserts: GisStoredFeature[] = [];
        const deletes: string[] = [];
        chunk.forEach(([uid, entry]) => {
          if (!entry.feature) {
            deletes.push(uid);
            return;
          }
          const stored = this.gisStore.toStoredFeature(entry.feature, entry.layer);
          if (stored && !this.gisStore.isUnchanged(stored)) upserts.push(stored);
        });
        if (!upserts.length && !deletes.length) continue;
        try {
          await firstValueFrom(this.gisStore.sync(projectId, { upserts, deletes }));
        } catch (err: any) {
          const badUid = err?.status === 422 ? /Feature ([0-9a-f-]{36})/i.exec(err?.error?.error || '')?.[1] : null;
          restore(entries.slice(i).filter(([uid]) => uid !== badUid));
          if (badUid) {
            this.showInstructions(this.gisErrorMessage(err, 'A feature could not be saved and was skipped'));
            this.scheduleGisFlush();
          }
          throw err;
        }
        upserts.forEach((f) => this.gisStore.markSynced(f));
        written += upserts.length + deletes.length;
      }
      return written;
    };

    const run = this.gisSyncQueue.then(task, task);
    this.gisSyncQueue = run.catch(() => undefined);
    return run;
  }

  /** Soft-deletes a layer in the store after any queued writes for it. */
  private deleteStoredLayer(layerId: string): void {
    const projectId = this.projectId;
    if (!projectId) return;
    this.gisPending.forEach((entry, uid) => {
      if (entry.layer === layerId) this.gisPending.delete(uid);
    });
    const run = this.gisSyncQueue.then(() => firstValueFrom(this.gisStore.deleteLayer(projectId, layerId)));
    this.gisSyncQueue = run.catch(() => undefined);
    run
      .then((res) => this.showInstructions(`Layer '${layerId}' removed (${res.deleted_count} stored feature(s))`))
      .catch((err) => this.showInstructions(this.gisErrorMessage(err, `Could not remove layer '${layerId}' from the database`)));
  }

  /**
   * Replaces map content with stored features. Features with unsaved local changes are kept.
   * When `onlyLayer` is given, just that layer is refreshed.
   */
  private applyStoredFeatures(fc: GisStoredFeatureCollection, onlyLayer?: string, done?: () => void): void {
    const byLayer = new globalThis.Map<string, GisStoredFeature[]>();
    fc.features.forEach((f) => {
      const key = f.layer || 'drawing';
      if (onlyLayer && key !== onlyLayer) return;
      if (!byLayer.has(key)) byLayer.set(key, []);
      byLayer.get(key)!.push(f);
    });

    this.withGisTrackingSuspended(() => {
      byLayer.forEach((_, key) => {
        if (!this.layerRegistry[key]) this.addLayer(key);
      });
      const targets = onlyLayer ? [onlyLayer] : Object.keys(this.layerRegistry);
      targets.forEach((id) => {
        if (!this.isPersistableLayer(id)) return;
        const source = this.layerRegistry[id].getSource() as VectorSource;
        source.getFeatures().forEach((f) => {
          const uid = this.gisStore.uidOf(f);
          if (!uid || !this.isPersistableFeature(f) || this.gisPending.has(uid)) return;
          source.removeFeature(f);
        });
      });
    });

    const applyLayer = (layerId: string) => {
      if (!this.isPersistableLayer(layerId)) return;
      const source = this.layerRegistry[layerId].getSource() as VectorSource;
      const features = this.gisStore
        .toOlFeatures({ features: byLayer.get(layerId) || [] })
        .filter((f) => !this.gisPending.has(String(f.getId())));
      this.withGisTrackingSuspended(() => source.addFeatures(features));
      features.forEach((f) => {
        const stored = this.gisStore.toStoredFeature(f, layerId);
        if (stored) this.gisStore.markSynced(stored);
      });
      this.updateLayerStatistics(layerId);
    };

    const rest = [...byLayer.keys()].filter((k) => k !== 'road');
    const finish = () => {
      rest.forEach(applyLayer);
      if (!onlyLayer) {
        // Loaded state is the undo baseline; undoing past it would delete stored features.
        this.drawingHistory = [];
        this.historyStep = -1;
        this.saveToHistory();
      }
      done?.();
    };
    if (byLayer.has('road')) {
      applyLayer('road');
      this.afterRoadGeometryReady(finish);
    } else {
      finish();
    }
  }

  /** Queues a layer and saves pending changes. */
  saveLayerToBackend(layerId: string): void {
    if (!this.projectId) {
      this.showInstructions('Open a project before saving');
      return;
    }
    if (!this.isPersistableLayer(layerId)) {
      this.showInstructions(`Layer '${layerId}' is read-only and is not saved`);
      return;
    }
    this.markLayerDirty(layerId);
    this.flushGisChanges()
      .then((n) => this.showInstructions(n ? `Layer '${layerId}' saved (${n} change(s))` : `Layer '${layerId}' is already saved`))
      .catch((err) => this.showInstructions(this.gisErrorMessage(err, `Error saving layer '${layerId}'`)));
  }

  /** Reloads one layer from the store (unsaved local edits are kept). */
  loadLayerFromBackend(layerId: string): void {
    const projectId = this.projectId;
    if (!projectId) return;
    this.gisStore.load(projectId, { layer: layerId, limit: 20000 }).subscribe({
      next: (fc) => {
        if (projectId !== this.projectId) return;
        this.applyStoredFeatures(fc, layerId, () =>
          this.showInstructions(`Layer '${layerId}' loaded (${fc.features.length} feature(s))`)
        );
      },
      error: (err) => this.showInstructions(this.gisErrorMessage(err, `Error loading layer '${layerId}'`)),
    });
  }

  /**
   * Perform spatial query on backend
   */
  performSpatialQuery(queryData: any): void {
    this.apiService.performSpatialQuery(queryData).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Spatial query results:', response.data);
          this.showInstructions(`Spatial query completed: ${response.data.features.length} results`);

          // Display results in a new layer
          const resultsLayerId = `query_results_${Date.now()}`;
          this.importLayer(resultsLayerId, typeof response.data === 'string' ? response.data : JSON.stringify(response.data), { persist: false });
          this.showInstructions(`Query results shown in layer '${resultsLayerId}'`);
        }
      },
      error: (error) => {
        console.error('Error performing spatial query:', error);
        this.showInstructions('Error performing spatial query');
      }
    });
  }

  /**
   * Get layer statistics from backend
   */
  getLayerStatsFromBackend(layerId: string): void {
    this.apiService.getLayerStats(layerId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log(`Layer stats for ${layerId}:`, response.data);
          this.showInstructions(`Layer statistics: ${response.data.feature_count} features`);
        }
      },
      error: (error) => {
        console.error(`Error getting layer stats for ${layerId}:`, error);
        this.showInstructions('Error retrieving layer statistics');
      }
    });
  }

  /**
   * Validate geometry using backend
   */
  validateLayerGeometry(layerId: string): void {
    this.apiService.validateGeometry(layerId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const { invalid_features, invalid_count } = response.data;

          if (invalid_count === 0) {
            this.showInstructions('All geometries are valid');
          } else {
            this.showInstructions(`Found ${invalid_count} invalid geometries`);
            console.log('Invalid geometries:', invalid_features);

            // Optionally repair geometries
            if (confirm(`Found ${invalid_count} invalid geometries. Repair them?`)) {
              this.repairLayerGeometry(layerId);
            }
          }
        }
      },
      error: (error) => {
        console.error(`Error validating geometry for ${layerId}:`, error);
        this.showInstructions('Error validating geometries');
      }
    });
  }

  /**
   * Repair geometry using backend
   */
  repairLayerGeometry(layerId: string): void {
    this.apiService.repairGeometry(layerId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const { repaired_count } = response.data;

          if (repaired_count > 0) {
            this.showInstructions(`${repaired_count} geometries repaired`);
            this.updateLayerStatistics(layerId);
          } else {
            this.showInstructions('No geometries needed repair');
          }
        }
      },
      error: (error) => {
        console.error(`Error repairing geometry for ${layerId}:`, error);
        this.showInstructions('Error repairing geometries');
      }
    });
  }

  /**
   * Create buffer analysis using backend
   */
  createBufferAnalysis(layerId: string, distance: number, unit: string = 'meters'): void {
    const source = this.layerRegistry[layerId]?.getSource() as VectorSource;
    if (!source) {
      this.showInstructions(`Layer '${layerId}' not found`);
      return;
    }

    const features = source.getFeatures();
    if (features.length === 0) {
      this.showInstructions(`No features found in layer '${layerId}'`);
      return;
    }

    // Get the first feature for buffer analysis
    const feature = features[0];
    const geometry = this.format.writeGeometry(feature.getGeometry()!);

    const analysisData = {
      layerName: layerId,
      geometry: geometry,
      distance: distance,
      unit: unit
    };

    this.apiService.bufferAnalysis(analysisData).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Buffer analysis results:', response.data);
          this.showInstructions(`Buffer analysis completed`);

          // Import buffer results as new layer
          const bufferLayerId = `${layerId}_buffer_${distance}${unit}`;
          this.importLayer(bufferLayerId, typeof response.data === 'string' ? response.data : JSON.stringify(response.data), { persist: false });
          this.showInstructions(`Buffer results shown in layer '${bufferLayerId}'`);
        }
      },
      error: (error) => {
        console.error('Error performing buffer analysis:', error);
        this.showInstructions('Error performing buffer analysis');
      }
    });
  }

  /**
   * Perform intersection analysis using backend
   */
  performIntersectionAnalysisBackend(layerId1: string, layerId2: string): void {
    const analysisData = {
      layerName1: layerId1,
      layerName2: layerId2,
      operation: 'intersects'
    };

    this.apiService.intersectionAnalysis(analysisData).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Intersection analysis results:', response.data);
          this.showInstructions(`Intersection analysis completed: ${response.data.length} relationships found`);

          // Import intersection results as new layer
          const intersectionLayerId = `${layerId1}_${layerId2}_intersection`;
          const intersectionGeoJson = {
            type: 'FeatureCollection',
            features: response.data.map((result: any) => ({
              type: 'Feature',
              geometry: result.intersection_geometry,
              properties: {
                layer1_feature: result.layer1_feature,
                layer2_feature: result.layer2_feature,
                intersection_area: result.intersection_area
              }
            }))
          };

          this.importLayer(intersectionLayerId, JSON.stringify(intersectionGeoJson), { persist: false });
          this.showInstructions(`Intersection results shown in layer '${intersectionLayerId}'`);
        }
      },
      error: (error) => {
        console.error('Error performing intersection analysis:', error);
        this.showInstructions('Error performing intersection analysis');
      }
    });
  }

  /**
   * Batch save all layers to backend
   */
  saveAllLayersToBackend(): void {
    if (!this.projectId) {
      this.showInstructions('Open a project before saving');
      return;
    }
    if (this.isSavingAllLayers) return;
    Object.keys(this.layerRegistry)
      .filter((id) => this.isPersistableLayer(id))
      .forEach((id) => this.markLayerDirty(id));
    this.isSavingAllLayers = true;
    this.showInstructions('Saving all layers to database…');
    this.flushGisChanges()
      .then((n) => this.showInstructions(n ? `Saved ${n} change(s) to database` : 'All layers are already saved'))
      .catch((err) => this.showInstructions(this.gisErrorMessage(err, 'Save failed; unsaved changes are kept')))
      .finally(() => (this.isSavingAllLayers = false));
  }

  /** Loads all stored layers for the project (road geometry first, then other layers). */
  loadAllLayersFromBackend(): void {
    const projectId = this.projectId;
    if (!projectId) return;
    this.showInstructions('Loading road geometry, then map layers…');
    this.gisStore.load(projectId, { limit: 20000 }).subscribe({
      next: (fc) => {
        if (projectId !== this.projectId) return;
        this.applyStoredFeatures(fc, undefined, () => {
          const layers = new Set(fc.features.map((f) => f.layer)).size;
          this.showInstructions(
            fc.truncated
              ? `Loaded the first ${fc.features.length} features; zoom in or filter by layer for the rest`
              : `Loaded ${fc.features.length} feature(s) in ${layers} layer(s) from database`
          );
        });
      },
      error: (err) => this.showInstructions(this.gisErrorMessage(err, 'Could not load map layers')),
    });
  }

  /** Changes are always tracked; auto-save only controls whether they are sent automatically. */
  initializeAutoSave(): void {
    if (this.defaultSettings.autoSaveInterval > 0 && this.gisPending.size) this.scheduleGisFlush();
  }

  /**
   * Get all available GIS layers from backend
   */
  getAvailableLayers(): void {
    this.apiService.getGisLayers().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Available GIS layers:', response.data);
          this.showInstructions(`Found ${response.data.length} GIS layers in database`);
        }
      },
      error: (error) => {
        console.error('Error getting available layers:', error);
        this.showInstructions('Error retrieving available layers');
      }
    });
  }

  // ==================== PUBLIC UI INTEGRATION METHODS ====================

  /**
   * Save current layer to backend (public method for UI binding)
   */
  saveCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.saveLayerToBackend(targetLayerId);
    } else {
      this.showInstructions('No active layer to save');
    }
  }

  /**
   * Load current layer from backend (public method for UI binding)
   */
  loadCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.loadLayerFromBackend(targetLayerId);
    } else {
      this.showInstructions('No active layer to load');
    }
  }

  /**
   * Save all layers (public method for UI binding)
   */
  saveAllLayers(): void {
    if (this.offlineMode || !navigator.onLine) {
      this.persistLayersToOfflineStorage();
      this.showInstructions('Saved locally (offline). Will sync when back online.');
      return;
    }
    this.saveAllLayersToBackend();
  }

  /**
   * Load all layers (public method for UI binding)
   */
  loadAllLayers(): void {
    if (this.offlineMode || !navigator.onLine) {
      this.restoreLayersFromOfflineStorage();
      return;
    }
    this.loadAllLayersFromBackend();
  }

  /**
   * Validate current layer geometry (public method for UI binding)
   */
  validateCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.validateLayerGeometry(targetLayerId);
    } else {
      this.showInstructions('No active layer to validate');
    }
  }

  /**
   * Repair current layer geometry (public method for UI binding)
   */
  repairCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.repairLayerGeometry(targetLayerId);
    } else {
      this.showInstructions('No active layer to repair');
    }
  }

  /**
   * Export current layer (public method for UI binding)
   */
  exportCurrentLayer(layerId?: string, format: string = 'geojson'): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (!targetLayerId) {
      this.showInstructions('No active layer to export');
      return;
    }
    const fmt = (format || 'geojson').toLowerCase();
    if (fmt === 'geojson') {
      this.exportLayerToFile(targetLayerId);
      return;
    }
    if (fmt === 'kml') {
      this.exportLayerAsKml(targetLayerId);
      return;
    }
    if (fmt === 'gpx') {
      this.exportLayerAsGpx(targetLayerId);
      return;
    }
    if (fmt === 'shp' || fmt === 'shapefile') {
      this.showInstructions('Shapefile export is available per LRS route (LRS tab). For map layers, export GeoJSON, KML or GPX.');
      return;
    }
    this.apiService.exportGisToFormat(targetLayerId, format).subscribe({
      next: (response) => {
        if (response.success) {
          this.showInstructions(`Layer exported as ${format.toUpperCase()}`);
        }
      },
      error: () => {
        this.showInstructions(`Error exporting layer as ${format.toUpperCase()}`);
      }
    });
  }

  /**
   * Perform buffer analysis on current layer (public method for UI binding)
   */
  performBufferAnalysis(distance: number = 100, unit: string = 'meters'): void {
    const activeLayerId = this.getActiveLayerId();
    if (activeLayerId) {
      this.createBufferAnalysis(activeLayerId, distance, unit);
    } else {
      this.showInstructions('No active layer for buffer analysis');
    }
  }

  /**
   * Perform intersection analysis (public method for UI binding)
   */
  performIntersectionAnalysisUI(layerId2: string): void {
    const layerId1 = this.getActiveLayerId();
    if (layerId1 && layerId2) {
      this.performIntersectionAnalysisBackend(layerId1, layerId2);
    } else {
      this.showInstructions('Select two layers for intersection analysis');
    }
  }

  /**
   * Get layer statistics (public method for UI binding)
   */
  getLayerStatistics(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.getLayerStatsFromBackend(targetLayerId);
    } else {
      this.showInstructions('No active layer for statistics');
    }
  }

  /**
   * Toggle auto-save (public method for UI binding)
   */
  toggleAutoSave(): void {
    if (this.defaultSettings.autoSaveInterval > 0) {
      this.defaultSettings.autoSaveInterval = 0;
      this.showInstructions('Auto-save disabled');
    } else {
      this.defaultSettings.autoSaveInterval = 5; // Default 5 seconds
      this.initializeAutoSave();
      this.showInstructions('Auto-save enabled (5s interval)');
    }
    localStorage.setItem('gisEditorDefaults', JSON.stringify(this.defaultSettings));
  }

  /**
   * Get active layer ID (helper method)
   */
  private getActiveLayerId(): string | null {
    // Get the currently selected layer from layer management UI
    const activeTab = document.querySelector('.layer-tab.active');
    if (activeTab) {
      return activeTab.getAttribute('data-layer');
    }

    // Fallback to first available layer
    const layerIds = Object.keys(this.layerRegistry);
    return layerIds.length > 0 ? layerIds[0] : null;
  }

  /**
   * Enhanced save layer method with UI feedback
   */
  saveLayerWithFeedback(layerId: string): void {
    this.showInstructions(`Saving layer '${layerId}'...`);
    this.saveLayerToBackend(layerId);
  }

  /**
   * Enhanced load layer method with UI feedback
   */
  loadLayerWithFeedback(layerId: string): void {
    this.showInstructions(`Loading layer '${layerId}'...`);
    this.loadLayerFromBackend(layerId);
  }

  // ==================== MISSING FEATURES IMPLEMENTATION ====================

  /**
   * Initialize all sidebar tab switching
   */
  private setupTabSwitching(): void {
    const tabs = document.querySelectorAll('.layer-tab');
    const tabContents = document.querySelectorAll('.tab-content');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('data-tab');
        
        // Remove active from all tabs and contents
        tabs.forEach(t => t.classList.remove('active'));
        tabContents.forEach(tc => tc.classList.remove('active'));
        
        // Add active to clicked tab
        tab.classList.add('active');
        
        // Show corresponding content
        const content = document.getElementById(`${tabId}-tab`);
        if (content) {
          content.classList.add('active');
        }
      });
    });
  }

  // ==================== ANALYSIS TAB FEATURES ====================

  /**
   * Buffer analysis - creates buffer zones around features
   */
  private bufferAnalysis(): void {
    const bufferSize = parseInt((document.getElementById('analysisBufferSize') as HTMLInputElement)?.value || '100');
    const features = this.vectorSource.getFeatures();
    
    if (features.length === 0) {
      this.showInstructions('No features to analyze');
      return;
    }

    this.showInstructions(`Creating ${bufferSize}m buffer zones...`);

    features.forEach(feature => {
      const geometry = feature.getGeometry();
      if (geometry && typeof turf !== 'undefined') {
        try {
          const geoJson = this.format.writeGeometryObject(geometry);
          const buffered = turf.buffer(geoJson, bufferSize / 1000, { units: 'kilometers' });
          
          if (buffered) {
            const bufferFeature = new Feature({
              geometry: this.format.readGeometry(buffered.geometry),
              type: 'buffer',
              bufferSize: bufferSize,
              sourceFeatureId: feature.getId()
            });

            bufferFeature.setStyle(new Style({
              fill: new Fill({ color: 'rgba(52, 152, 219, 0.2)' }),
              stroke: new Stroke({ color: '#3498db', width: 2, lineDash: [5, 5] })
            }));

            this.vectorSource.addFeature(bufferFeature);
          }
        } catch (error) {
          console.error('Buffer analysis error:', error);
        }
      }
    });

    this.showInstructions(`Buffer analysis complete: ${features.length} buffers created`);
    setTimeout(() => this.hideInstructions(), 3000);
  }

  /**
   * Intersection analysis - finds intersecting features between layers
   */
  private intersectionAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    const roadFeatures = this.roadSource.getFeatures();
    let intersections = 0;

    this.showInstructions('Analyzing intersections...');

    features.forEach(f1 => {
      const geom1 = f1.getGeometry();
      if (!geom1) return;

      roadFeatures.forEach(f2 => {
        const geom2 = f2.getGeometry();
        if (!geom2) return;

        if (typeof turf !== 'undefined') {
          try {
            const gj1 = this.format.writeGeometryObject(geom1);
            const gj2 = this.format.writeGeometryObject(geom2);
            const intersection = turf.intersect(turf.feature(gj1), turf.feature(gj2));

            if (intersection) {
              intersections++;
              const intersectFeature = new Feature({
                geometry: this.format.readGeometry(intersection.geometry),
                type: 'intersection'
              });

              intersectFeature.setStyle(new Style({
                fill: new Fill({ color: 'rgba(231, 76, 60, 0.5)' }),
                stroke: new Stroke({ color: '#e74c3c', width: 3 })
              }));

              this.vectorSource.addFeature(intersectFeature);
            }
          } catch (error) {
            // Features don't intersect
          }
        }
      });
    });

    this.showMeasurementResult(`Intersection Analysis:\nFound ${intersections} intersections`);
  }

  /**
   * Overlay analysis - overlays multiple layers
   */
  private overlayAnalysisFunc(): void {
    this.showInstructions('Performing overlay analysis...');

    const layers = Object.keys(this.layerRegistry);
    let overlayResults: any[] = [];

    layers.forEach((layerId, index) => {
      const layer = this.layerRegistry[layerId];
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();

      overlayResults.push({
        layer: layerId,
        featureCount: features.length,
        visible: layer.getVisible()
      });
    });

    const result = overlayResults.map(r => `${r.layer}: ${r.featureCount} features`).join('\n');
    this.showMeasurementResult(`Overlay Analysis:\n${result}`);
  }

  /**
   * Proximity analysis - finds features within a distance
   */
  private proximityAnalysis(): void {
    const bufferSize = parseInt((document.getElementById('analysisBufferSize') as HTMLInputElement)?.value || '100');
    
    this.showInstructions('Click on a feature to analyze proximity...');

    const clickHandler = (event: any) => {
      const features = this.map.getFeaturesAtPixel(event.pixel);
      if (features.length > 0) {
        const centerFeature = features[0] as Feature;
        const centerGeom = centerFeature.getGeometry();
        
        if (centerGeom && typeof turf !== 'undefined') {
          let nearbyCount = 0;
          const allFeatures = this.vectorSource.getFeatures();

          allFeatures.forEach(f => {
            if (f !== centerFeature) {
              const geom = f.getGeometry();
              if (geom) {
                const center1 = turf.centroid(this.format.writeGeometryObject(centerGeom));
                const center2 = turf.centroid(this.format.writeGeometryObject(geom));
                const distance = turf.distance(center1, center2, { units: 'meters' });

                if (distance <= bufferSize) {
                  nearbyCount++;
                  f.setStyle(new Style({
                    stroke: new Stroke({ color: '#f39c12', width: 3 }),
                    fill: new Fill({ color: 'rgba(243, 156, 18, 0.3)' })
                  }));
                }
              }
            }
          });

          this.showMeasurementResult(`Proximity Analysis:\n${nearbyCount} features within ${bufferSize}m`);
        }
      }

      this.map.un('click', clickHandler);
    };

    this.map.on('click', clickHandler);
  }

  /**
   * Network analysis - analyzes connectivity
   */
  private networkAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    const lineFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof LineString;
    });

    let totalLength = 0;
    let nodeCount = 0;
    let endpoints: number[][] = [];

    lineFeatures.forEach(f => {
      const geom = f.getGeometry() as LineString;
      const coords = geom.getCoordinates();
      totalLength += geom.getLength();
      
      endpoints.push(coords[0]);
      endpoints.push(coords[coords.length - 1]);
    });

    // Count unique nodes (simplified)
    const uniqueNodes = new Set(endpoints.map(e => `${Math.round(e[0])},${Math.round(e[1])}`));
    nodeCount = uniqueNodes.size;

    const result = `
Network Analysis:
Total Links: ${lineFeatures.length}
Total Nodes: ${nodeCount}
Total Length: ${(totalLength / 1000).toFixed(2)} km
Avg Link Length: ${lineFeatures.length > 0 ? (totalLength / lineFeatures.length).toFixed(2) : 0} m
Network Density: ${(lineFeatures.length / nodeCount).toFixed(2)} links/node
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Terrain analysis - analyzes elevation data
   */
  private terrainAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    let minElev = Infinity;
    let maxElev = -Infinity;
    let totalElev = 0;
    let elevCount = 0;

    features.forEach(f => {
      const elev = f.get('elevation') || f.get('z') || 0;
      if (typeof elev === 'number') {
        minElev = Math.min(minElev, elev);
        maxElev = Math.max(maxElev, elev);
        totalElev += elev;
        elevCount++;
      }
    });

    const avgElev = elevCount > 0 ? totalElev / elevCount : 0;
    const elevRange = maxElev - minElev;

    const result = `
Terrain Analysis:
Features Analyzed: ${elevCount}
Min Elevation: ${minElev !== Infinity ? minElev.toFixed(2) : 'N/A'} m
Max Elevation: ${maxElev !== -Infinity ? maxElev.toFixed(2) : 'N/A'} m
Elevation Range: ${elevRange !== Infinity ? elevRange.toFixed(2) : 'N/A'} m
Average Elevation: ${avgElev.toFixed(2)} m
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Calculate area statistics
   */
  private calculateAreaStats(): void {
    const features = this.vectorSource.getFeatures();
    const polygonFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof Polygon || (geom && geom.getType() === 'Polygon');
    });

    if (polygonFeatures.length === 0) {
      this.showInstructions('No polygon features found. Draw some polygons first.');
      return;
    }

    let totalArea = 0;
    let minArea = Infinity;
    let maxArea = 0;

    polygonFeatures.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        // Use geodesic area calculation for accuracy
        const area = Math.abs(getGeodesicArea(geom, { projection: 'EPSG:3857' }));
        totalArea += area;
        minArea = Math.min(minArea, area);
        maxArea = Math.max(maxArea, area);
      }
    });

    const avgArea = totalArea / polygonFeatures.length;

    const result = `
📐 Area Statistics:
━━━━━━━━━━━━━━━━━━━━━
Polygon Count: ${polygonFeatures.length}
Total Area: ${(totalArea / 1000000).toFixed(4)} km² (${totalArea.toFixed(2)} m²)
Min Area: ${minArea.toFixed(2)} m²
Max Area: ${maxArea.toFixed(2)} m²
Average Area: ${avgArea.toFixed(2)} m²
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Calculate length statistics
   */
  private calculateLengthStats(): void {
    const features = this.vectorSource.getFeatures();
    const lineFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof LineString || (geom && geom.getType() === 'LineString');
    });

    if (lineFeatures.length === 0) {
      this.showInstructions('No line features found. Draw some lines first.');
      return;
    }

    let totalLength = 0;
    let minLength = Infinity;
    let maxLength = 0;

    lineFeatures.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        // Use geodesic length calculation for accuracy
        const length = getGeodesicLength(geom, { projection: 'EPSG:3857' });
        totalLength += length;
        minLength = Math.min(minLength, length);
        maxLength = Math.max(maxLength, length);
      }
    });

    const avgLength = totalLength / lineFeatures.length;

    const result = `
📏 Length Statistics:
━━━━━━━━━━━━━━━━━━━━━
Line Count: ${lineFeatures.length}
Total Length: ${(totalLength / 1000).toFixed(4)} km (${totalLength.toFixed(2)} m)
Min Length: ${minLength.toFixed(2)} m
Max Length: ${maxLength.toFixed(2)} m
Average Length: ${avgLength.toFixed(2)} m
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Density analysis
   */
  private densityAnalysisFunc(): void {
    const features = this.vectorSource.getFeatures();
    const extent = this.vectorSource.getExtent();
    
    if (!extent || extent[0] === Infinity) {
      this.showInstructions('No features to analyze');
      return;
    }

    const width = extent[2] - extent[0];
    const height = extent[3] - extent[1];
    const areaKm2 = (width * height) / 1000000;

    const featureDensity = features.length / areaKm2;

    const result = `
Density Analysis:
Total Features: ${features.length}
Extent Area: ${areaKm2.toFixed(4)} km²
Feature Density: ${featureDensity.toFixed(2)} features/km²
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Clustering analysis
   */
  private clusteringAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    const pointFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof Point;
    });

    if (pointFeatures.length === 0) {
      this.showInstructions('No point features for clustering');
      return;
    }

    // Simple clustering based on proximity
    const clusterDistance = 100; // meters
    let clusters: Feature[][] = [];
    let assigned = new Set<Feature>();

    pointFeatures.forEach(f => {
      if (assigned.has(f)) return;

      const cluster: Feature[] = [f];
      assigned.add(f);

      const fCoords = (f.getGeometry() as Point).getCoordinates();

      pointFeatures.forEach(other => {
        if (assigned.has(other) || other === f) return;

        const otherCoords = (other.getGeometry() as Point).getCoordinates();
        const dx = fCoords[0] - otherCoords[0];
        const dy = fCoords[1] - otherCoords[1];
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= clusterDistance) {
          cluster.push(other);
          assigned.add(other);
        }
      });

      clusters.push(cluster);
    });

    const result = `
Clustering Analysis:
Total Points: ${pointFeatures.length}
Clusters Found: ${clusters.length}
Cluster Distance: ${clusterDistance}m
Avg Cluster Size: ${(pointFeatures.length / clusters.length).toFixed(1)} points
Largest Cluster: ${Math.max(...clusters.map(c => c.length))} points
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Hotspot analysis
   */
  private hotspotAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    
    // Simple grid-based hotspot analysis
    const gridSize = 500; // meters
    const extent = this.vectorSource.getExtent();
    
    if (!extent || extent[0] === Infinity) {
      this.showInstructions('No features for hotspot analysis');
      return;
    }

    const gridCounts: { [key: string]: number } = {};

    features.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        let coord: number[];
        if (geom instanceof Point) {
          coord = geom.getCoordinates();
        } else {
          // Use centroid for other geometries
          const ext = geom.getExtent();
          coord = [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2];
        }

        const gridX = Math.floor(coord[0] / gridSize);
        const gridY = Math.floor(coord[1] / gridSize);
        const key = `${gridX},${gridY}`;
        gridCounts[key] = (gridCounts[key] || 0) + 1;
      }
    });

    const counts = Object.values(gridCounts);
    const maxCount = Math.max(...counts);
    const avgCount = counts.reduce((a, b) => a + b, 0) / counts.length;

    const hotspots = Object.entries(gridCounts).filter(([_, count]) => count > avgCount * 1.5).length;

    const result = `
Hotspot Analysis:
Grid Size: ${gridSize}m
Total Cells: ${counts.length}
Max Density: ${maxCount} features/cell
Avg Density: ${avgCount.toFixed(1)} features/cell
Hotspots (>1.5x avg): ${hotspots}
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Pattern analysis
   */
  private patternAnalysis(): void {
    const features = this.vectorSource.getFeatures();

    // Count feature types
    const typeCounts: { [key: string]: number } = {};
    features.forEach(f => {
      const type = f.get('type') || 'unknown';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    const typeList = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => `${type}: ${count}`)
      .join('\n');

    const result = `
Pattern Analysis:
Total Features: ${features.length}
Unique Types: ${Object.keys(typeCounts).length}

Feature Types:
${typeList}
    `;

    this.showMeasurementResult(result);
  }

  // ==================== CARTOGRAPHIC TOOLS ====================

  /**
   * Generate map layout
   */
  private generateMap(): void {
    this.showInstructions('Generating map layout...');
    
    const title = (document.getElementById('mapTitle') as HTMLInputElement)?.value || 'Map';
    const scale = (document.getElementById('mapScale') as HTMLSelectElement)?.value || '1:10000';

    // Create map title overlay
    const titleDiv = document.createElement('div');
    titleDiv.className = 'map-title-overlay';
    titleDiv.innerHTML = `<h2>${title}</h2><p>Scale: ${scale}</p>`;
    titleDiv.style.cssText = 'position: absolute; top: 10px; left: 50%; transform: translateX(-50%); background: white; padding: 10px 20px; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); z-index: 1000;';

    const mapContainer = document.querySelector('.map-container');
    if (mapContainer) {
      // Remove existing title
      const existingTitle = mapContainer.querySelector('.map-title-overlay');
      if (existingTitle) existingTitle.remove();
      mapContainer.appendChild(titleDiv);
    }

    this.showInstructions('Map layout generated');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Create layout
   */
  private createLayout(): void {
    this.openDialog('printDialog');
  }

  /**
   * Add legend to map
   */
  private addLegend(): void {
    const legendDiv = document.createElement('div');
    legendDiv.className = 'map-legend';
    legendDiv.style.cssText = 'position: absolute; bottom: 50px; right: 10px; background: white; padding: 15px; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); z-index: 1000; max-width: 200px;';

    // Build legend content from layer styles
    let legendContent = '<h4 style="margin: 0 0 10px 0;">Legend</h4>';
    
    Object.keys(this.layerRegistry).forEach(layerId => {
      const layer = this.layerRegistry[layerId];
      if (layer.getVisible()) {
        legendContent += `
          <div style="display: flex; align-items: center; margin-bottom: 5px;">
            <span style="width: 20px; height: 3px; background: #3498db; margin-right: 8px;"></span>
            <span style="font-size: 12px;">${layerId}</span>
          </div>
        `;
      }
    });

    legendDiv.innerHTML = legendContent;

    const mapContainer = document.querySelector('.map-container');
    if (mapContainer) {
      const existingLegend = mapContainer.querySelector('.map-legend');
      if (existingLegend) existingLegend.remove();
      mapContainer.appendChild(legendDiv);
    }

    this.showInstructions('Legend added');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Add scale bar
   */
  private addScaleBar(): void {
    // ScaleLine control is already added in map initialization
    // This toggles its visibility
    const scaleControls = this.map.getControls().getArray().filter(c => c instanceof ScaleLine);
    
    if (scaleControls.length === 0) {
      this.scaleLineControl = new ScaleLine({
        units: this.measureDisplayUnit === 'mi' ? 'imperial' : 'metric',
      });
      this.map.addControl(this.scaleLineControl);
      this.showInstructions('Scale bar added');
    } else {
      this.scaleLineControl = scaleControls[0] as ScaleLine;
      this.syncScaleLineUnits();
      this.showInstructions('Scale bar already exists');
    }
    setTimeout(() => this.hideInstructions(), 2000);
  }

  // ==================== DATA TAB FEATURES ====================

  /**
   * Load contour layer from GeoJSON (e.g. contour lines). Adds as vector layer.
   */
  loadContourLayer(geoJsonContent: string, layerName: string = 'contours'): void {
    try {
      const features = this.format.readFeatures(geoJsonContent, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
      });
      const id = layerName || `contour_${Date.now()}`;
      const source = new VectorSource({ features });
      const layer = new VectorLayer({
        source,
        style: new Style({
          stroke: new Stroke({ color: '#2c3e50', width: 1, lineDash: [4, 4] }),
          fill: new Fill({ color: 'transparent' })
        }),
        zIndex: 40
      });
      this.map.addLayer(layer);
      this.contourLayerIds.push(id);
      this.showInstructions(`Contour layer "${id}" loaded (${features.length} features)`);
    } catch (e) {
      this.showInstructions('Invalid contour GeoJSON');
    }
  }

  /**
   * Add DEM (elevation) tile layer from XYZ URL template. Use {z},{x},{y} for tile coords.
   */
  addDemLayer(urlTemplate: string, layerName: string = 'DEM'): void {
    const id = `dem_${Date.now()}`;
    const layer = new TileLayer({
      source: new XYZ({ url: urlTemplate }),
      opacity: 0.6,
      zIndex: 35
    });
    this.map.addLayer(layer);
    this.showInstructions(`DEM layer added. Remove via layer list if needed.`);
  }

  /**
   * Toggle heatmap overlay for current layer (density visualization)
   */
  toggleHeatmapOverlay(layerId?: string): void {
    const targetId = layerId || this.getActiveLayerId();
    if (this.heatmapLayer) {
      this.map.removeLayer(this.heatmapLayer);
      this.heatmapLayer = null;
      this.showInstructions('Heatmap overlay removed');
      return;
    }
    const layer = targetId ? this.layerRegistry[targetId] : null;
    const source = layer?.getSource();
    if (!source || !(source instanceof VectorSource)) {
      this.showInstructions('Select a vector layer for heatmap');
      return;
    }
    const features = (source as VectorSource).getFeatures().filter(f => f.getGeometry()?.getType() === 'Point');
    if (features.length === 0) {
      this.showInstructions('Layer has no point features for heatmap');
      return;
    }
    features.forEach(f => f.set('weight', 1));
    this.heatmapLayer = new Heatmap({
      source: source as VectorSource,
      blur: 15,
      radius: 8,
      weight: (f) => (f.get('weight') as number) ?? 1,
      zIndex: 45
    });
    this.map.addLayer(this.heatmapLayer);
    this.showInstructions('Heatmap overlay added');
  }

  /**
   * Add WMS layer (opens dialog)
   */
  private addWmsLayer(): void {
    this.openDialog('wmsDialog');
  }

  /**
   * Submit WMS dialog: create ImageWMS layer from form and add to map
   */
  private submitWmsLayer(): void {
    const url = (document.getElementById('wmsUrl') as HTMLInputElement)?.value?.trim();
    const layerName = (document.getElementById('wmsLayerName') as HTMLInputElement)?.value?.trim();
    const format = (document.getElementById('wmsFormat') as HTMLSelectElement)?.value || 'image/png';
    const transparency = parseInt((document.getElementById('wmsTransparency') as HTMLInputElement)?.value || '0', 10) / 100;
    if (!url || !layerName) {
      this.showInstructions('Please enter WMS URL and layer name');
      return;
    }
    try {
      const wmsSource = new ImageWMS({
        url,
        params: { LAYERS: layerName, FORMAT: format },
        ratio: 1,
        serverType: 'geoserver'
      });
      const wmsId = `wms_${layerName}_${Date.now()}`;
      const imageLayer = new ImageLayer({
        source: wmsSource,
        opacity: 1 - transparency,
        zIndex: 50 + this.wmsLayers.length
      });
      this.map.addLayer(imageLayer);
      this.wmsLayers.push({ id: wmsId, layer: imageLayer });
      this.closeDialog('wmsDialog');
      this.showInstructions(`WMS layer "${layerName}" added`);
    } catch (err) {
      console.error('WMS layer error:', err);
      this.showInstructions('Failed to add WMS layer. Check URL and layer name.');
    }
  }

  /**
   * Add WFS layer: load features from WFS GetFeature (1.1.0) and add as vector layer.
   */
  private addWfsLayer(): void {
    const baseUrl = prompt('WFS base URL (e.g. https://geo.example.com/geoserver/wfs):', '');
    if (!baseUrl) return;
    const typeName = prompt('Feature type name (e.g. namespace:layername or layername):', '');
    if (!typeName) return;
    this.showInstructions(`Loading WFS: ${typeName}...`);
    const view = this.map.getView();
    const extent = view.calculateExtent(this.map.getSize() || [800, 600]);
    const [minX, minY, maxX, maxY] = extent;
    const sep = baseUrl.includes('?') ? '&' : '?';
    const bbox = `${minX},${minY},${maxX},${maxY}`;
    const url = `${baseUrl}${sep}SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&TYPENAME=${encodeURIComponent(typeName)}&BBOX=${bbox}&SRSNAME=EPSG:3857`;
    fetch(url)
      .then(res => res.text())
      .then(text => {
        const wfsFormat = new WFS({ version: '1.1.0' });
        const features = wfsFormat.readFeatures(text, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
        if (features.length === 0) {
          this.showInstructions('No features returned. Check URL and type name.');
          return;
        }
        const layerId = `wfs_${typeName.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}`;
        const source = new VectorSource({ features });
        const layer = new VectorLayer({
          source,
          style: (f, res) => this.getLayerStyleWithTheme(layerId, f, res),
          zIndex: 42
        });
        this.map.addLayer(layer);
        this.registerLayer(layerId, layer);
        this.showInstructions(`WFS loaded: ${features.length} features`);
      })
      .catch(err => {
        console.error('WFS load error:', err);
        this.showInstructions('Failed to load WFS. Check URL and CORS.');
      });
  }

  /**
   * Add vector layer
   */
  private addVectorLayerDialog(): void {
    const name = (document.getElementById('newLayerName') as HTMLInputElement)?.value || `Layer_${Date.now()}`;
    const type = (document.getElementById('newLayerType') as HTMLSelectElement)?.value || 'point';

    this.addLayer(name);
    this.showInstructions(`Vector layer '${name}' created`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /** Raster (image overlay) layers for removal */
  private rasterImageLayers: Array<ImageLayer<ImageStatic>> = [];

  /**
   * Add raster layer: georeferenced image overlay using current view extent (or user extent).
   */
  private addRasterLayer(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.tif,.tiff,.png,.jpg,.jpeg';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      this.showInstructions(`Loading raster: ${file.name}...`);
      const url = URL.createObjectURL(file);
      const view = this.map.getView();
      const extent = view.calculateExtent(this.map.getSize() || [800, 600]);
      try {
        const source = new ImageStatic({
          url,
          imageExtent: extent,
          projection: view.getProjection()
        });
        const layer = new ImageLayer({ source, opacity: 0.8, zIndex: 30 });
        this.map.addLayer(layer);
        this.rasterImageLayers.push(layer);
        this.showInstructions('Raster layer added. Use layer opacity to blend.');
      } catch (err) {
        this.showInstructions('Failed to add raster layer.');
      }
      setTimeout(() => this.hideInstructions(), 2500);
    };
    input.click();
  }

  /**
   * Add database layer
   */
  private addDatabaseLayer(): void {
    this.showInstructions('Database Layer: Configure connection');
    
    const connectionString = prompt('Enter database connection (host:port/database):');
    if (connectionString) {
      this.showInstructions(`Connecting to: ${connectionString}...`);
      // In production, would connect to PostGIS database
      setTimeout(() => {
        this.showInstructions('Database connected (simulated)');
        setTimeout(() => this.hideInstructions(), 2000);
      }, 1500);
    }
  }

  /**
   * Add API layer
   */
  private addApiLayer(): void {
    this.showInstructions('API Layer: Enter endpoint URL');
    
    const apiUrl = prompt('Enter API Endpoint URL:');
    if (apiUrl) {
      this.showInstructions(`Fetching from: ${apiUrl}...`);
      
      fetch(apiUrl)
        .then(res => res.json())
        .then(data => {
          if (data.type === 'FeatureCollection' || data.features) {
            this.importGeoJSONData(data);
            this.showInstructions('API data loaded successfully');
          } else {
            this.showInstructions('Invalid GeoJSON response');
          }
        })
        .catch(err => {
          console.error('API fetch error:', err);
          this.showInstructions('Error fetching API data');
        });
    }
  }

  /**
   * Import Shapefile
   */
  private importShapefile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip';
    input.onchange = (e: any) => {
      const file: File | undefined = e.target.files[0];
      if (file) this.importShapefileToLrs(file);
    };
    input.click();
  }

  /** Shapefiles (zipped .shp/.dbf/.prj) are imported server-side as LRS routes/events. */
  private importShapefileToLrs(file: File): void {
    if (!/\.zip$/i.test(file.name)) {
      this.showInstructions('Upload the shapefile as a .zip containing .shp, .shx, .dbf and .prj');
      return;
    }
    if (!this.projectId) {
      this.showInstructions('Open a project before importing a shapefile');
      return;
    }
    this.showInstructions(`Importing ${file.name} into LRS…`);
    this.lrsService.importShapefile(file, { project_id: String(this.projectId) }).subscribe({
      next: (res) => {
        const d = res.data;
        const errors = d.errors?.length ? `, ${d.errors.length} error(s): ${d.errors[0].error}` : '';
        this.showInstructions(`Shapefile imported: ${d.routes} route(s), ${d.events} event(s)${errors}`);
        this.loadLrsRouteList();
      },
      error: (err) => this.showInstructions(this.gisErrorMessage(err, 'Shapefile import failed')),
    });
  }

  /**
   * Import GeoJSON file
   */
  private importGeoJSONFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target?.result as string);
            this.importGeoJSONData(data);
            this.showInstructions(`GeoJSON '${file.name}' imported successfully`);
          } catch (error) {
            this.showInstructions('Error parsing GeoJSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Import KML file
   */
  private importKMLFile(): void {
    this.showInstructions('KML import: Select KML/KMZ file');
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.kml,.kmz';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const kmlContent = event.target?.result as string;
            const kmlFormat = new KML({
              extractStyles: true,
              showPointNames: true
            });
            
            const features = kmlFormat.readFeatures(kmlContent, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857'
            });
            
            if (features.length > 0) {
              features.forEach(feature => {
                feature.set('source', 'kml');
                feature.set('importedFrom', file.name);
                feature.set('importDate', new Date().toISOString());
                
                // Apply default style if no style extracted
                if (!feature.getStyle()) {
                  const geomType = feature.getGeometry()?.getType();
                  feature.setStyle(this.getDefaultImportStyle(geomType || 'Point'));
                }
                
                this.vectorSource.addFeature(feature);
              });
              
              // Zoom to imported features
              const extent = this.vectorSource.getExtent();
              this.map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 16 });
              
              this.showInstructions(`KML '${file.name}' imported: ${features.length} features`);
              this.saveToHistory();
            } else {
              this.showInstructions('No features found in KML file');
            }
          } catch (error) {
            console.error('KML import error:', error);
            this.showInstructions('Error parsing KML file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Import GPX file
   */
  private importGPXFile(): void {
    this.showInstructions('GPX import: Select GPX file');
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.gpx';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const gpxContent = event.target?.result as string;
            const gpxFormat = new GPX();
            
            const features = gpxFormat.readFeatures(gpxContent, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857'
            });
            
            if (features.length > 0) {
              let trackCount = 0;
              let waypointCount = 0;
              let routeCount = 0;
              
              features.forEach(feature => {
                feature.set('source', 'gpx');
                feature.set('importedFrom', file.name);
                feature.set('importDate', new Date().toISOString());
                
                const geomType = feature.getGeometry()?.getType();
                
                // Categorize feature type
                if (geomType === 'Point') {
                  waypointCount++;
                  feature.setStyle(new Style({
                    image: new CircleStyle({
                      radius: 8,
                      fill: new Fill({ color: '#e74c3c' }),
                      stroke: new Stroke({ color: '#fff', width: 2 })
                    }),
                    text: new Text({
                      text: feature.get('name') || '',
                      font: '11px Arial',
                      fill: new Fill({ color: '#2c3e50' }),
                      stroke: new Stroke({ color: '#fff', width: 2 }),
                      offsetY: -15
                    })
                  }));
                } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
                  trackCount++;
                  feature.setStyle(new Style({
                    stroke: new Stroke({
                      color: '#3498db',
                      width: 3
                    })
                  }));
                }
                
                this.vectorSource.addFeature(feature);
              });
              
              // Zoom to imported features
              const extent = this.vectorSource.getExtent();
              this.map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 16 });
              
              this.showInstructions(`GPX '${file.name}' imported: ${waypointCount} waypoints, ${trackCount} tracks`);
              this.saveToHistory();
            } else {
              this.showInstructions('No features found in GPX file');
            }
          } catch (error) {
            console.error('GPX import error:', error);
            this.showInstructions('Error parsing GPX file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Get default style for imported features based on geometry type
   */
  private getDefaultImportStyle(geomType: string): Style {
    switch (geomType) {
      case 'Point':
      case 'MultiPoint':
        return new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: '#27ae60' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          })
        });
      case 'LineString':
      case 'MultiLineString':
        return new Style({
          stroke: new Stroke({
            color: '#2980b9',
            width: 3
          })
        });
      case 'Polygon':
      case 'MultiPolygon':
        return new Style({
          stroke: new Stroke({
            color: '#8e44ad',
            width: 2
          }),
          fill: new Fill({
            color: 'rgba(142, 68, 173, 0.3)'
          })
        });
      default:
        return new Style({
          stroke: new Stroke({ color: '#333', width: 2 }),
          fill: new Fill({ color: 'rgba(51, 51, 51, 0.3)' }),
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: '#333' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          })
        });
    }
  }

  /**
   * Import CSV file with coordinates
   */
  private importCSVFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const csv = event.target?.result as string;
            const lines = csv.split('\n');
            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            
            const latIdx = headers.findIndex(h => h.includes('lat'));
            const lonIdx = headers.findIndex(h => h.includes('lon') || h.includes('lng'));
            
            if (latIdx === -1 || lonIdx === -1) {
              this.showInstructions('CSV must contain lat/lon columns');
              return;
            }

            let imported = 0;
            for (let i = 1; i < lines.length; i++) {
              const values = lines[i].split(',');
              const lat = parseFloat(values[latIdx]);
              const lon = parseFloat(values[lonIdx]);
              
              if (!isNaN(lat) && !isNaN(lon)) {
                const coord = fromLonLat([lon, lat]);
                const feature = new Feature({
                  geometry: new Point(coord),
                  type: 'csv_point',
                  ...headers.reduce((obj, h, idx) => ({ ...obj, [h]: values[idx] }), {})
                });
                
                feature.setStyle(new Style({
                  image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({ color: '#9b59b6' }),
                    stroke: new Stroke({ color: '#fff', width: 2 })
                  })
                }));
                
                this.vectorSource.addFeature(feature);
                imported++;
              }
            }
            
            this.showInstructions(`CSV imported: ${imported} points created`);
          } catch (error) {
            console.error('CSV import error:', error);
            this.showInstructions('Error parsing CSV file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Check for duplicate features
   */
  private checkDuplicates(): void {
    const features = this.vectorSource.getFeatures();
    const geometryHashes: { [key: string]: Feature[] } = {};

    features.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        const coords = JSON.stringify(this.format.writeGeometryObject(geom));
        const hash = coords.substring(0, 100); // Simple hash
        
        if (!geometryHashes[hash]) {
          geometryHashes[hash] = [];
        }
        geometryHashes[hash].push(f);
      }
    });

    let duplicateCount = 0;
    Object.values(geometryHashes).forEach(group => {
      if (group.length > 1) {
        duplicateCount += group.length - 1;
        // Highlight duplicates
        group.slice(1).forEach(f => {
          f.setStyle(new Style({
            stroke: new Stroke({ color: '#e74c3c', width: 3 }),
            fill: new Fill({ color: 'rgba(231, 76, 60, 0.3)' })
          }));
        });
      }
    });

    this.showMeasurementResult(`Duplicate Check:\nFound ${duplicateCount} duplicate features\n(Highlighted in red)`);
  }

  /**
   * Check for gaps in polygon coverage
   */
  private checkGaps(): void {
    this.validateLrsNetworkTopology();
  }

  private validateLrsNetworkTopology(): void {
    this.lrsService.validateNetworkQc({ project_id: this.projectId || undefined, snap_m: 5 }).subscribe({
      next: (res) => {
        const d = res.data;
        const lines = (d.issues || []).slice(0, 8).map((i) => `${i.code}: ${i.message}`);
        const msg = d.ok
          ? `Network QC OK (${d.route_count} routes) — no blocking gaps/overlaps`
          : `Network QC: ${d.issue_count} issue(s)\n${lines.join('\n')}`;
        this.showMeasurementResult(msg);
        this.showInstructions(d.ok ? 'Topology OK' : 'Fix SNAP/GAP/OVERLAP in the LRS tab, then re-run');
      },
      error: (e) => this.showInstructions(e?.error?.error || 'Network QC failed — create a road network and routes first'),
    });
  }

  /**
   * Clean data - remove invalid features
   */
  private cleanData(): void {
    const features = this.vectorSource.getFeatures();
    let removedCount = 0;

    features.forEach(f => {
      const geom = f.getGeometry();
      if (!geom) {
        this.vectorSource.removeFeature(f);
        removedCount++;
        return;
      }

      // Check for empty geometries
      if (geom instanceof LineString && geom.getCoordinates().length < 2) {
        this.vectorSource.removeFeature(f);
        removedCount++;
      } else if (geom instanceof Polygon && geom.getCoordinates()[0].length < 4) {
        this.vectorSource.removeFeature(f);
        removedCount++;
      }
    });

    this.showInstructions(`Data cleaned: ${removedCount} invalid features removed`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  // ==================== DIALOG MANAGEMENT ====================

  /**
   * Open a dialog
   */
  openDialog(dialogId: string): void {
    const dialog = document.getElementById(dialogId);
    if (dialog) {
      dialog.style.display = 'flex';
    }
  }

  /**
   * Close a dialog
   */
  closeDialog(dialogId: string): void {
    const dialog = document.getElementById(dialogId);
    if (dialog) {
      dialog.style.display = 'none';
    }
  }

  /**
   * Show analysis tab in analysis dialog
   */
  showAnalysisTab(tabName: string): void {
    const tabs = document.querySelectorAll('#analysisDialog .tab-btn');
    const contents = document.querySelectorAll('#analysisDialog .analysis-tab-content');

    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));

    const activeTab = document.querySelector(`#analysisDialog .tab-btn[onclick*="${tabName}"]`);
    const activeContent = document.getElementById(`analysis${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);

    if (activeTab) activeTab.classList.add('active');
    if (activeContent) activeContent.classList.add('active');
  }

  /**
   * Show properties tab in layer properties dialog
   */
  showPropertiesTab(tabName: string): void {
    const tabs = document.querySelectorAll('#layerPropertiesDialog .tab-btn');
    const contents = document.querySelectorAll('#layerPropertiesDialog .properties-tab-content');

    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));

    const activeTab = document.querySelector(`#layerPropertiesDialog .tab-btn[onclick*="${tabName}"]`);
    const activeContent = document.getElementById(`properties${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);

    if (activeTab) activeTab.classList.add('active');
    if (activeContent) activeContent.classList.add('active');
  }

  /**
   * Show search tab in search dialog
   */
  showSearchTab(tabName: string): void {
    const tabs = document.querySelectorAll('#searchDialog .tab-btn');
    const contents = document.querySelectorAll('#searchDialog .search-tab-content');

    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));

    const activeTab = document.querySelector(`#searchDialog .tab-btn[onclick*="${tabName}"]`);
    const activeContent = document.getElementById(`search${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);

    if (activeTab) activeTab.classList.add('active');
    if (activeContent) activeContent.classList.add('active');
  }

  /**
   * Select import type in import dialog
   */
  selectImportType(importType: string): void {
    const importContent = document.getElementById('importContent');
    if (!importContent) return;

    if (importType === 'file') {
      importContent.innerHTML = `
        <div class="form-group">
          <label>Select File:</label>
          <input type="file" id="importFile" accept=".shp,.geojson,.kml,.gpx,.csv,.dxf">
        </div>
        <div class="form-group">
          <label>Layer Name:</label>
          <input type="text" id="importLayerName" placeholder="Enter layer name">
        </div>
      `;
    } else if (importType === 'url') {
      importContent.innerHTML = `
        <div class="form-group">
          <label>Service URL:</label>
          <input type="url" id="importUrl" placeholder="https://...">
        </div>
        <div class="form-group">
          <label>Service Type:</label>
          <select id="serviceType">
            <option value="wms">WMS</option>
            <option value="wfs">WFS</option>
            <option value="geojson">GeoJSON API</option>
          </select>
        </div>
      `;
    } else if (importType === 'database') {
      importContent.innerHTML = `
        <div class="form-group">
          <label>Host:</label>
          <input type="text" id="dbHost" placeholder="localhost">
        </div>
        <div class="form-group">
          <label>Port:</label>
          <input type="number" id="dbPort" value="5432">
        </div>
        <div class="form-group">
          <label>Database:</label>
          <input type="text" id="dbName" placeholder="gis_database">
        </div>
        <div class="form-group">
          <label>Table/View:</label>
          <input type="text" id="dbTable" placeholder="features">
        </div>
      `;
    }
  }

  // ==================== ADDITIONAL TOOLBAR HANDLERS ====================

  /**
   * Select all features
   */
  private selectAll(): void {
    const features = this.vectorSource.getFeatures();
    
    if (!this.selectInteraction) {
      this.enableSelect();
    }
    
    // Highlight all features
    features.forEach(f => {
      const originalStyle = f.getStyle();
      f.set('_originalStyle', originalStyle);
      
      f.setStyle(new Style({
        stroke: new Stroke({ color: '#f39c12', width: 3 }),
        fill: new Fill({ color: 'rgba(243, 156, 18, 0.3)' }),
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#f39c12' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        })
      }));
    });

    this.showInstructions(`Selected ${features.length} features`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Clear selection
   */
  private clearSelection(): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(f => {
      const originalStyle = f.get('_originalStyle');
      if (originalStyle) {
        f.setStyle(originalStyle);
        f.unset('_originalStyle');
      }
    });

    if (this.selectInteraction) {
      this.selectInteraction.getFeatures().clear();
    }

    this.showInstructions('Selection cleared');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Setup additional event listeners for Analysis tab
   */
  private setupAnalysisTabListeners(): void {
    // Spatial Analysis
    const bufferAnalysisBtn = document.getElementById('bufferAnalysis');
    if (bufferAnalysisBtn) {
      bufferAnalysisBtn.addEventListener('click', () => this.bufferAnalysis());
    }

    const intersectionAnalysisBtn = document.getElementById('intersectionAnalysis');
    if (intersectionAnalysisBtn) {
      intersectionAnalysisBtn.addEventListener('click', () => this.intersectionAnalysis());
    }

    const overlayAnalysisBtn = document.getElementById('overlayAnalysis');
    if (overlayAnalysisBtn) {
      overlayAnalysisBtn.addEventListener('click', () => this.overlayAnalysisFunc());
    }

    const proximityAnalysisBtn = document.getElementById('proximityAnalysis');
    if (proximityAnalysisBtn) {
      proximityAnalysisBtn.addEventListener('click', () => this.proximityAnalysis());
    }

    const networkAnalysisBtn = document.getElementById('networkAnalysis');
    if (networkAnalysisBtn) {
      networkAnalysisBtn.addEventListener('click', () => this.networkAnalysis());
    }

    const terrainAnalysisBtn = document.getElementById('terrainAnalysis');
    if (terrainAnalysisBtn) {
      terrainAnalysisBtn.addEventListener('click', () => this.terrainAnalysis());
    }

    // Statistical Analysis
    const calculateAreaBtn = document.getElementById('calculateArea');
    if (calculateAreaBtn) {
      calculateAreaBtn.addEventListener('click', () => this.calculateAreaStats());
    }

    const calculateLengthBtn = document.getElementById('calculateLength');
    if (calculateLengthBtn) {
      calculateLengthBtn.addEventListener('click', () => this.calculateLengthStats());
    }

    const densityAnalysisBtn = document.getElementById('densityAnalysis');
    if (densityAnalysisBtn) {
      densityAnalysisBtn.addEventListener('click', () => this.densityAnalysisFunc());
    }

    const clusteringAnalysisBtn = document.getElementById('clusteringAnalysis');
    if (clusteringAnalysisBtn) {
      clusteringAnalysisBtn.addEventListener('click', () => this.clusteringAnalysis());
    }

    const hotspotAnalysisBtn = document.getElementById('hotspotAnalysis');
    if (hotspotAnalysisBtn) {
      hotspotAnalysisBtn.addEventListener('click', () => this.hotspotAnalysis());
    }

    const patternAnalysisBtn = document.getElementById('patternAnalysis');
    if (patternAnalysisBtn) {
      patternAnalysisBtn.addEventListener('click', () => this.patternAnalysis());
    }

    // Cartographic Tools
    const generateMapBtn = document.getElementById('generateMap');
    if (generateMapBtn) {
      generateMapBtn.addEventListener('click', () => this.generateMap());
    }

    const createLayoutBtn = document.getElementById('createLayout');
    if (createLayoutBtn) {
      createLayoutBtn.addEventListener('click', () => this.createLayout());
    }

    const addLegendBtn = document.getElementById('addLegend');
    if (addLegendBtn) {
      addLegendBtn.addEventListener('click', () => this.addLegend());
    }

    const addScaleBtn = document.getElementById('addScale');
    if (addScaleBtn) {
      addScaleBtn.addEventListener('click', () => this.addScaleBar());
    }
  }

  /**
   * Setup Data tab listeners
   */
  private setupDataTabListeners(): void {
    // Data Sources
    const addWmsLayerBtn = document.getElementById('addWmsLayer');
    if (addWmsLayerBtn) {
      addWmsLayerBtn.addEventListener('click', () => this.addWmsLayer());
    }
    const submitWmsLayerBtn = document.getElementById('submitWmsLayerBtn');
    if (submitWmsLayerBtn) {
      submitWmsLayerBtn.addEventListener('click', () => this.submitWmsLayer());
    }

    const addWfsLayerBtn = document.getElementById('addWfsLayer');
    if (addWfsLayerBtn) {
      addWfsLayerBtn.addEventListener('click', () => this.addWfsLayer());
    }

    const addVectorLayerBtn = document.getElementById('addVectorLayer');
    if (addVectorLayerBtn) {
      addVectorLayerBtn.addEventListener('click', () => this.addVectorLayerDialog());
    }

    const addRasterLayerBtn = document.getElementById('addRasterLayer');
    if (addRasterLayerBtn) {
      addRasterLayerBtn.addEventListener('click', () => this.addRasterLayer());
    }

    const addDatabaseLayerBtn = document.getElementById('addDatabaseLayer');
    if (addDatabaseLayerBtn) {
      addDatabaseLayerBtn.addEventListener('click', () => this.addDatabaseLayer());
    }

    const addApiLayerBtn = document.getElementById('addApiLayer');
    if (addApiLayerBtn) {
      addApiLayerBtn.addEventListener('click', () => this.addApiLayer());
    }

    // Import buttons
    const importShapefileBtn = document.getElementById('importShapefile');
    if (importShapefileBtn) {
      importShapefileBtn.addEventListener('click', () => this.importShapefile());
    }

    const importGeoJSONBtn = document.getElementById('importGeoJSON');
    if (importGeoJSONBtn) {
      importGeoJSONBtn.addEventListener('click', () => this.importGeoJSONFile());
    }

    const importKMLBtn = document.getElementById('importKML');
    if (importKMLBtn) {
      importKMLBtn.addEventListener('click', () => this.importKMLFile());
    }

    const importGPXBtn = document.getElementById('importGPX');
    if (importGPXBtn) {
      importGPXBtn.addEventListener('click', () => this.importGPXFile());
    }

    const importCSVBtn = document.getElementById('importCSV');
    if (importCSVBtn) {
      importCSVBtn.addEventListener('click', () => this.importCSVFile());
    }

    // Validation buttons
    const checkDuplicatesBtn = document.getElementById('checkDuplicates');
    if (checkDuplicatesBtn) {
      checkDuplicatesBtn.addEventListener('click', () => this.checkDuplicates());
    }

    const checkGapsBtn = document.getElementById('checkGaps');
    if (checkGapsBtn) {
      checkGapsBtn.addEventListener('click', () => this.checkGaps());
    }

    const validateTopologyBtn = document.getElementById('validateTopology');
    if (validateTopologyBtn) {
      validateTopologyBtn.addEventListener('click', () => this.validateLrsNetworkTopology());
    }

    const cleanDataBtn = document.getElementById('cleanData');
    if (cleanDataBtn) {
      cleanDataBtn.addEventListener('click', () => this.cleanData());
    }
  }

  /**
   * Setup additional toolbar listeners
   */
  private setupAdditionalToolbarListeners(): void {
    // Select All button
    const selectAllBtn = document.getElementById('selectAllBtn');
    if (selectAllBtn) {
      selectAllBtn.addEventListener('click', () => this.selectAll());
    }

    // Clear Selection button
    const clearSelectionBtn = document.getElementById('clearSelectionBtn');
    if (clearSelectionBtn) {
      clearSelectionBtn.addEventListener('click', () => this.clearSelection());
    }

    // Save button (to backend)
    const saveBtn = document.getElementById('saveBtn');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => this.saveAllLayers());
    }

    // Load button (from backend)
    const loadBtn = document.getElementById('loadBtn');
    if (loadBtn) {
      loadBtn.addEventListener('click', () => this.loadAllLayers());
    }

    // Angle measurement button
    const angleBtn = document.getElementById('angleBtn');
    if (angleBtn) {
      angleBtn.addEventListener('click', () => this.enableAngleMeasurement());
    }

    // Layers button – switch sidebar to Layers tab (and expand if collapsed)
    const layersBtn = document.getElementById('layersBtn');
    if (layersBtn) {
      layersBtn.addEventListener('click', () => this.switchToLayersTab());
    }

    // Print dialog buttons
    const printMapBtn = document.getElementById('printMapBtn');
    if (printMapBtn) {
      printMapBtn.addEventListener('click', () => this.executePrint());
    }

    const previewPrintBtn = document.getElementById('previewPrintBtn');
    if (previewPrintBtn) {
      previewPrintBtn.addEventListener('click', () => this.previewPrint());
    }

    // Help button
    const helpBtn = document.getElementById('helpBtn');
    if (helpBtn) {
      helpBtn.addEventListener('click', () => this.showHelp());
    }
  }

  private setupOfflineSupport(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('offline', () => {
      this.offlineMode = true;
      this.showOfflineBanner();
      this.persistLayersToOfflineStorage();
    });
    window.addEventListener('online', () => {
      this.offlineMode = false;
      this.hideOfflineBanner();
      if (this.gisPending.size) {
        this.showInstructions('Back online. Syncing offline edits…');
        void this.flushGisChanges()
          .then((n) => this.showInstructions(`Synced ${n} offline change(s)`))
          .catch((err) => this.showInstructions(this.gisErrorMessage(err, 'Could not sync offline edits')));
      } else {
        this.showInstructions('Back online. Edits can be synced to server.');
      }
    });
  }

  private showOfflineBanner(): void {
    if (this.offlineBannerEl) return;
    const el = document.createElement('div');
    el.id = 'gis-offline-banner';
    el.style.cssText = 'position:absolute;top:8px;left:50%;transform:translateX(-50%);background:#e74c3c;color:#fff;padding:8px 16px;border-radius:6px;z-index:1000;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,0.2);';
    el.textContent = 'Offline — edits saved locally and will sync when back online';
    const container = document.querySelector('.map-container');
    if (container) container.appendChild(el);
    this.offlineBannerEl = el;
  }

  private hideOfflineBanner(): void {
    if (this.offlineBannerEl?.parentNode) {
      this.offlineBannerEl.parentNode.removeChild(this.offlineBannerEl);
      this.offlineBannerEl = null;
    }
  }

  private persistLayersToOfflineStorage(): void {
    try {
      const data: { drawing: string; road: string } = {
        drawing: this.format.writeFeatures(this.vectorSource.getFeatures(), { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' }),
        road: this.format.writeFeatures(this.roadSource.getFeatures(), { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' })
      };
      localStorage.setItem(GisavieditorComponent.OFFLINE_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Offline persist failed', e);
    }
  }

  private restoreLayersFromOfflineStorage(): void {
    try {
      const raw = localStorage.getItem(GisavieditorComponent.OFFLINE_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.drawing) {
        const features = this.format.readFeatures(data.drawing, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
        this.vectorSource.addFeatures(features);
      }
      if (data.road) {
        const features = this.format.readFeatures(data.road, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
        this.roadSource.addFeatures(features);
      }
      this.showInstructions('Restored locally saved layers (offline mode)');
    } catch (e) {
      console.warn('Offline restore failed', e);
    }
  }

  private cleanup(): void {
    this.osmNetworkOverlay?.detach();
    this.osmNetworkOverlay = undefined;
    this.hideOfflineBanner();
    this.geoMapBridge.clearGisMap(this.map);
    if (this.mapResizeObserver && this.mapElement?.nativeElement) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
    this.removeInteractions();
    this.stopSegmentDrawing();
    if (this.gpsWatchId) {
      navigator.geolocation.clearWatch(this.gpsWatchId);
    }
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
  }
}
